import "../styles.css";
import { ApiClient } from "./apiClient.js";
import { createBodyHeatmapFromEntries, formatMuscleLabel } from "./bodyHeatmap.js";
import { CUSTOM_MUSCLE_OPTIONS } from "./data/customMuscles.js";
import { getFirebaseClient } from "./firebaseClient.js";
import { createAuthObserver } from "./features/auth/authObserver.js";
import { handleLogsClick } from "./features/logs/clickController.js";
import { createMappedClickActionHandler } from "./features/navigation/clickController.js";
import { handleProfileClick } from "./features/profile/clickController.js";
import { handleProgramsClick } from "./features/programs/clickController.js";
import { createSearchOverlayController } from "./features/search/overlayController.js";
import { handleSessionClick } from "./features/session/clickController.js";
import { createAndroidTwaAdapter } from "./platform/androidTwaAdapter.js";
import {
  CLIENT_PLATFORM_ANDROID_TWA,
  resolveClientPlatform,
  shouldForceRedirectGoogleSignIn,
} from "./platform/runtime.js";
import { deepClone } from "./lib/clone.js";
import {
  createExerciseSearchSession,
  createExerciseSearchSessionMap,
  renderExerciseSearchResults,
} from "./lib/exerciseSearch.js";
import { formatWeekdayLabel, escapeHtml as escapeHtmlText, normalizeDateKey } from "./lib/formatters.js";
import { parseInputNumberOrNull } from "./lib/inputParsers.js";
import {
  MAX_PERCENT_1RM,
  MAX_PROGRAM_LENGTH_DAYS,
  MAX_REPS,
  MAX_REST_SEC,
  MAX_SETS_PER_EXERCISE,
  MAX_WEIGHT,
  MIN_REST_SEC,
} from "./constants/workoutConstraints.js";
import {
  createProgramPayloadFromTemplate,
  listProgramTemplates,
} from "./repository.js";
import { getState, mutateState, setState, subscribe } from "./store.js";
import {
  renderAnalyticsTab,
  renderHomeTab,
  renderLogsCalendar,
  renderLogsDateList,
  renderLogsDateListHead,
  renderLogsExerciseList,
  renderLogsExerciseSearchCard,
  renderLogsTab,
  renderProgramTemplateSearchResults,
  renderProfileTab,
  renderProgramsTab,
  renderSessionInlineNotice,
  renderSessionPanel,
  renderWorkoutCompleteSummary,
} from "./views.js";

const els = {
  authSplash: document.getElementById("authSplash"),
  authSplashMessage: document.getElementById("authSplashMessage"),
  topbar: document.getElementById("topbar"),
  authBadge: document.getElementById("authBadge"),
  loginPanel: document.getElementById("loginPanel"),
  signupPanel: document.getElementById("signupPanel"),
  consentPanel: document.getElementById("consentPanel"),
  appMain: document.getElementById("appMain"),
  bottomNav: document.getElementById("bottomNav"),
  loginError: document.getElementById("loginError"),
  signupError: document.getElementById("signupError"),
  consentError: document.getElementById("consentError"),
  emailInput: document.getElementById("emailInput"),
  passwordInput: document.getElementById("passwordInput"),
  signupEmailInput: document.getElementById("signupEmailInput"),
  signupPasswordInput: document.getElementById("signupPasswordInput"),
  signupPasswordConfirmInput: document.getElementById("signupPasswordConfirmInput"),
  signupPasswordConfirmHelper: document.getElementById("signupPasswordConfirmHelper"),
  signupNicknameInput: document.getElementById("signupNicknameInput"),
  signupAgreeTermsInput: document.getElementById("signupAgreeTermsInput"),
  signupAgreePrivacyInput: document.getElementById("signupAgreePrivacyInput"),
  consentAgreeTermsInput: document.getElementById("consentAgreeTermsInput"),
  consentAgreePrivacyInput: document.getElementById("consentAgreePrivacyInput"),
  googleLoginBtn: document.getElementById("googleLoginBtn"),
  emailLoginBtn: document.getElementById("emailLoginBtn"),
  emailSignupBtn: document.getElementById("emailSignupBtn"),
  emailSignupSubmitBtn: document.getElementById("emailSignupSubmitBtn"),
  signupBackBtn: document.getElementById("signupBackBtn"),
  consentSubmitBtn: document.getElementById("consentSubmitBtn"),
  consentLogoutBtn: document.getElementById("consentLogoutBtn"),
  autoLoginToggleBtn: document.getElementById("autoLoginToggleBtn"),
  autoLoginToggleState: document.getElementById("autoLoginToggleState"),
  autoLoginToggleMeta: document.getElementById("autoLoginToggleMeta"),
  globalLoadingBar: document.getElementById("globalLoadingBar"),
  globalLoadingProgress: document.getElementById("globalLoadingProgress"),
  pwaInstallGuideModal: document.getElementById("pwaInstallGuideModal"),
  pwaInstallGuideTitle: document.getElementById("pwaInstallGuideTitle"),
  pwaInstallGuideMeta: document.getElementById("pwaInstallGuideMeta"),
  pwaInstallGuideBody: document.getElementById("pwaInstallGuideBody"),
  pwaInstallGuideCloseBtn: document.getElementById("pwaInstallGuideCloseBtn"),
  pwaInstallGuideConfirmBtn: document.getElementById("pwaInstallGuideConfirmBtn"),
  programDayPickerModal: document.getElementById("programDayPickerModal"),
  programDayPickerSubtitle: document.getElementById("programDayPickerSubtitle"),
  programDayPickerList: document.getElementById("programDayPickerList"),
  programDayPickerCloseBtn: document.getElementById("programDayPickerCloseBtn"),
  programDayImportModal: document.getElementById("programDayImportModal"),
  programDayImportTitle: document.getElementById("programDayImportTitle"),
  programDayImportSubtitle: document.getElementById("programDayImportSubtitle"),
  programDayImportList: document.getElementById("programDayImportList"),
  programDayImportCloseBtn: document.getElementById("programDayImportCloseBtn"),
  analyticsSearchModal: document.getElementById("analyticsSearchModal"),
  analyticsSearchModalTitle: document.getElementById("analyticsSearchModalTitle"),
  analyticsSearchModalMeta: document.getElementById("analyticsSearchModalMeta"),
  analyticsSearchModalInputLabel: document.getElementById("analyticsSearchModalInputLabel"),
  programTemplateSearchModal: document.getElementById("programTemplateSearchModal"),
  programTemplateSearchModalInput: document.getElementById("programTemplateSearchModalInput"),
  programTemplateSearchModalResults: document.getElementById("programTemplateSearchModalResults"),
  programTemplateSearchModalCloseBtn: document.getElementById("programTemplateSearchModalCloseBtn"),
  customMusclePickerModal: document.getElementById("customMusclePickerModal"),
  customMusclePickerTitle: document.getElementById("customMusclePickerTitle"),
  customMusclePickerMeta: document.getElementById("customMusclePickerMeta"),
  customMusclePickerSearchInput: document.getElementById("customMusclePickerSearchInput"),
  customMusclePickerSelection: document.getElementById("customMusclePickerSelection"),
  customMusclePickerResults: document.getElementById("customMusclePickerResults"),
  customMusclePickerClearBtn: document.getElementById("customMusclePickerClearBtn"),
  customMusclePickerCloseBtn: document.getElementById("customMusclePickerCloseBtn"),
  programDeleteModal: document.getElementById("programDeleteModal"),
  programDeleteModalName: document.getElementById("programDeleteModalName"),
  programDeleteModalCloseBtn: document.getElementById("programDeleteModalCloseBtn"),
  programDeleteModalCancelBtn: document.getElementById("programDeleteModalCancelBtn"),
  confirmProgramDeleteBtn: document.getElementById("confirmProgramDeleteBtn"),
  sessionCancelModal: document.getElementById("sessionCancelModal"),
  sessionCancelModalCloseBtn: document.getElementById("sessionCancelModalCloseBtn"),
  sessionCancelModalKeepBtn: document.getElementById("sessionCancelModalKeepBtn"),
  sessionCancelModalConfirmBtn: document.getElementById("confirmCancelSessionBtn"),
  sessionFinishModal: document.getElementById("sessionFinishModal"),
  sessionFinishModalCloseBtn: document.getElementById("sessionFinishModalCloseBtn"),
  sessionFinishModalKeepBtn: document.getElementById("sessionFinishModalKeepBtn"),
  sessionFinishModalConfirmBtn: document.getElementById("confirmFinishWorkoutBtn"),
  sessionConflictModal: document.getElementById("sessionConflictModal"),
  sessionConflictModalCloseBtn: document.getElementById("sessionConflictModalCloseBtn"),
  sessionConflictModalResumeBtn: document.getElementById("sessionConflictModalResumeBtn"),
  sessionConflictModalConfirmBtn: document.getElementById("confirmReplaceActiveSessionBtn"),
  sessionNoticeModal: document.getElementById("sessionNoticeModal"),
  sessionNoticeModalTitle: document.getElementById("sessionNoticeModalTitle"),
  sessionNoticeModalMeta: document.getElementById("sessionNoticeModalMeta"),
  sessionNoticeModalCopy: document.getElementById("sessionNoticeModalCopy"),
  sessionNoticeModalCloseBtn: document.getElementById("sessionNoticeModalCloseBtn"),
  sessionNoticeModalConfirmBtn: document.getElementById("sessionNoticeModalConfirmBtn"),
  appAlertModal: document.getElementById("appAlertModal"),
  appAlertModalTitle: document.getElementById("appAlertModalTitle"),
  appAlertModalMeta: document.getElementById("appAlertModalMeta"),
  appAlertModalCopy: document.getElementById("appAlertModalCopy"),
  appAlertModalCloseBtn: document.getElementById("appAlertModalCloseBtn"),
  appAlertModalConfirmBtn: document.getElementById("appAlertModalConfirmBtn"),
  appConfirmModal: document.getElementById("appConfirmModal"),
  appConfirmModalTitle: document.getElementById("appConfirmModalTitle"),
  appConfirmModalMeta: document.getElementById("appConfirmModalMeta"),
  appConfirmModalCopy: document.getElementById("appConfirmModalCopy"),
  appConfirmModalCloseBtn: document.getElementById("appConfirmModalCloseBtn"),
  appConfirmModalCancelBtn: document.getElementById("appConfirmModalCancelBtn"),
  appConfirmModalConfirmBtn: document.getElementById("appConfirmModalConfirmBtn"),
  pwaInstallPrompt: document.getElementById("pwaInstallPrompt"),
  programItemRuleModal: document.getElementById("programItemRuleModal"),
  programItemRuleModalTitle: document.getElementById("programItemRuleModalTitle"),
  programItemRuleModalSubtitle: document.getElementById("programItemRuleModalSubtitle"),
  programItemRuleModalBody: document.getElementById("programItemRuleModalBody"),
  programItemRuleModalCloseBtn: document.getElementById("programItemRuleModalCloseBtn"),
  programItemRuleModalDoneBtn: document.getElementById("programItemRuleModalDoneBtn"),
  pwaInstallMessage: document.getElementById("pwaInstallMessage"),
  pwaInstallActionBtn: document.getElementById("pwaInstallActionBtn"),
  pwaInstallDismissBtn: document.getElementById("pwaInstallDismissBtn"),
  tabs: {
    home: document.getElementById("tab-home"),
    logs: document.getElementById("tab-logs"),
    programs: document.getElementById("tab-programs"),
    analytics: document.getElementById("tab-analytics"),
    profile: document.getElementById("tab-profile"),
    session: document.getElementById("tab-session"),
  },
};
const DEFAULT_BOTTOM_NAV_HTML = els.bottomNav?.innerHTML || "";

let auth = null;
let api = null;
let sessionElapsedTimer = null;
let restTimerInterval = null;
let restTimerTimeout = null;
let restAlarmAudioContext = null;
let restAlarmFallbackAudio = null;
let restAlarmPlayedForEndAt = null;
let restAlarmPrimed = false;
let screenWakeLockSentinel = null;
let wakeLockSyncPromise = null;
let pushSubscriptionSyncPromise = null;
let serviceWorkerRegistration = null;
let androidTwaAdapter = null;
let lastSyncedAndroidDeviceTokenFingerprint = "";
let lastNativeRestAlarmContext = null;
let pendingSessionScrollToCurrent = false;
let pendingSessionReorderFocus = null;
let pendingSessionSetFocus = null;
let pendingUiTransitionScrollFrame = 0;
let uiPendingLoadingCount = 0;
let uiLoadingProgressValue = 0;
let uiLoadingTicker = null;
let uiLoadingHideTimer = null;
let uiLoadingResetTimer = null;
let sessionDraftSyncQueue = Promise.resolve();
let sessionSetInputAutosaveTimer = null;
let sessionItemReorderRevealTimer = null;
let sessionSetInputAutosaveWorkoutId = "";
let sessionDraftLastNetworkSyncAt = 0;
let sessionInlineNotice = null;
const sessionDraftPendingByWorkout = new Map();
let sessionDraftLifecycleFlushAt = 0;
let androidTwaBridgeReadyRefreshPromise = null;
let pendingSignupNickname = "";
let pendingSignupConsents = null;
let programDayPickerResolve = null;
let programDayPickerLastFocused = null;
let programDayImportLastFocused = null;
let analyticsSearchModalLastState = null;
let programTemplateSearchModalLastFocused = null;
let programDeleteModalLastFocused = null;
let sessionCancelModalLastFocused = null;
let sessionFinishModalLastFocused = null;
let sessionConflictModalLastFocused = null;
let sessionConflictModalResolve = null;
let sessionNoticeModalLastFocused = null;
let pwaInstallGuideModalLastFocused = null;
let appAlertModalLastFocused = null;
let appAlertModalResolve = null;
let appAlertModalShouldRestoreFocus = true;
let appConfirmModalLastFocused = null;
let appConfirmModalResolve = null;
let appConfirmModalShouldRestoreFocus = true;
let appConfirmModalAfterConfirmHistoryConsumed = null;
let customMusclePickerLastFocused = null;
let restNotificationAutoPromptAttempted = false;
const ANDROID_TWA_BRIDGE_READY_TIMEOUT_MS = 4000;
let programDayImportRequestSeq = 0;
let programDayImportWorkoutMap = new Map();
let deferredInstallPromptEvent = null;
let authSplashReleased = false;
let authSplashExitTimer = null;
let authSplashHideTimer = null;
let consentGatePromise = null;
let consentGateResolve = null;
let consentGateReject = null;
let authFlowHistoryDepth = 0;
let authFlowPendingRootContinuation = null;

function setAppHistoryAutoReplaceSuspended(value) {
  appHistoryAutoReplaceSuspended = !!value;
}

function resetAuthObserverRuntimeState() {
  restNotificationAutoPromptAttempted = false;
  appHistoryInitialized = false;
  appHistoryDepth = 0;
  appHistoryIgnoreNextPop = false;
  authFlowHistoryDepth = 0;
  authFlowPendingRootContinuation = null;
  lastSyncedAndroidDeviceTokenFingerprint = "";
  lastNativeRestAlarmContext = null;
}

function getPendingSignupNickname() {
  return pendingSignupNickname;
}

function setPendingSignupNicknameValue(value) {
  pendingSignupNickname = String(value || "");
}

function getPendingSignupConsents() {
  return pendingSignupConsents;
}

function setPendingSignupConsentsValue(value) {
  pendingSignupConsents = value && typeof value === "object" ? { ...value } : null;
}
let appHistoryDepth = 0;
let appHistoryInitialized = false;
let appHistorySyncSuspended = false;
let appHistoryIgnoreNextPop = false;
let appHistoryApplySnapshotOnConsume = false;
let appHistoryAutoReplaceSuspended = false;
let appHistoryConsumeContinuation = null;
let appHistoryPendingRootContinuation = null;
const REST_TIMER_VIBRATION_PATTERN = [220, 100, 220, 100, 360];
const REST_TIMER_ALARM_TONES = [
  { frequency: 880, type: "triangle", offsetSec: 0, durationSec: 0.24, peakGain: 0.95 },
  { frequency: 1320, type: "square", offsetSec: 0.26, durationSec: 0.22, peakGain: 0.85 },
  { frequency: 988, type: "square", offsetSec: 0.5, durationSec: 0.28, peakGain: 0.82 },
];
const REST_TIMER_FALLBACK_AUDIO_DATA_URI = "data:audio/wav;base64,UklGRqQPAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YYAPAAAAADEAmwDYAI4Ar/+S/tT9A/5J/z4BAwOqA6oCOgBP/Tz7HPs+/eYAjAR/BqwFLwJj/Vv5//ck+hf/0ATJCBgJWgX5/qj4PPVo9uv7jgMFCmEMXQkMAnf5X/OU8rD3swDKCfMOuQ1lBvT74/JA79nyX/zXB0QQ1hGfCxcAIvT/7Pnt5PYWBOUPFxUxEaUFSvdX7LHpvvCr/o0N5xZ5FjQMVfyp7aXmiurq9yQJyRbLGjETBwMt8WXl+eRX8MoCaxSDHesZ8Qri9mTmvOCb6NX6qw8VHqgfdhOO/uPped524c/xoggbHK4j2Bu/B+7vtN6v223opf9nF10lSSPSEVH4weH/13/fQfUFEDok+yj7G50CvOcE1+DXMupDBgIgMyxZJSkOf/Aq2WTSW9+u+q8YXSwFLR8aoPui3sTPqtUI7oQOGSkqMowldwha59PQqc7M4fgBSyE+MoItIBWg83jXzsw+2Nf0QhYRLv0xVSC5AMzgWM5T0XTovwnVJigzZinFDTLsLNOAzaDdl/wHHe8wuC/nGen4+doGzRPVqe9LEXcr4DJSJBcGO+Xtz2DP0ONrBB4jqDJVLN4SQ/EE1uXM1dk/93AYFS9nMWUeRf7k3s7NYtKo6iQMYygyM+knZQv26QrS9s173wr/Ax+ZMcYuwxd99lPZ28x21gDylRO2LIoyjiKnAyzjI88y0OTl2gbcJPQyDiuSEO/uqdQazYLbrPmQGv4vtDBkHNP7D91hzY3T6OyCDtgpHzNUJv4IxucD0YnOaeF9Ae0gJjK6LZAVGPTD183M8ddf9NMV2i0XMrQgNAEu4XbOIdEH6EYJhSYjM64pOw6k7GjTbM1F3Rz8oRzKMOQvURpj+U/bEs3R1DTv1xA2K+0yqSSRBqTlGNA6z2nj8APEIpYykixRE7nxS9bezIPZxvYEGOQuhzHIHsD+Qt/mzSvSOOqsCxcoMzM1KNwLZepB0tzNHN+P/qEeejH4Li8Y9vak2eHML9aK8SMTeSydMugiIQSS40nPB9B65WAGhiToMlArBhFj7+zUDc0s2zL5JxrSL9kwyhxN/GrddM1Q03bsDA6RKSUzpSZ3CTPoNdFqzgfhAgGOIA0y8C3/FZD0ENjNzKXX6PNjFaMtMDITIa8BkeGWzvDQmufNCDMmHDP1KbEOFu2l01rN69yh+zscpDAPMLsa3fml2x/Nj9TA7mMQ8yr5Mv4kCwcO5kTQFc8D43UDaSKCMs4swhMv8pPW2Mwz2U32lxeyLqUxKh87/6HfAM710cnpNAvKJzIzgShUDNXqedLEzb7eFP4+HloxKC+cGHD39tnozOjVFPGwEjwsrzJCI5wE+eNvz9zPEeXmBS8k2jKRK3oR1+8v1QHN19q4+L0Zpi/9MC8dyPzF3YjNFNMF7JUNSSkqM/Um8Amg6GfRTM6l4IcALyDyMSYubhYI9V3Yz8xZ13zz/hRwLUYyZyEeAurhs87E0DnnYAjpJRUzNCocD37t3dNKzZvcM/veG4EwNTAZG0v69NsszVXUWO76D7YqAzNLJXkHbuZs0PTOqOIGAxYicDIDLSkUmvLV1tTM69jg9TQXhC7AMYIfqv/43xnOxNFl6cgKgycwM8UowAw666zSr81q3qX95B08MVMv/Rjd90Ha78yp1arwSRIELL8ykiMKBVbkk8+3z7PkeAXhI80yyyviEUHwbNX4zIvaSvhdGX0vHTGKHTf9GN6bzeDSn+sqDQcpLjM9J1wKA+mW0TLOTuAYANgf2TFWLtIWdPWk2NHMFtcF844UNy1cMsQhmQJO4tXOltDO5uYHliUMM3kqkQ/x7RzUOc1C3Lj6dhtZMF4wgRvF+kvcO80V1OTthA9yKg0znyXzB9nmmtDSzkTijAK6IVoyPS2aFBHzHtfRzJTYXPW7Fksu3zHsHzEAYeA4zovR7OhECi0nLTMWKUINtuvr0pfNBd4e/XYdFjGGL3IZY/id2vrMXtUp8MoRvivQMvMjkQXI5L/Pis9B5PEEgCO7MhEsYRLC8LfV7sww2sT35xhJL0Mx+B2+/X3etM2g0iPrpwy1KDEzkyfhCnzpz9ETzuTfkf9uH7oxjy5LF/n1+9jVzMbWgvIRFPcsdDIpIiADveL8zmPQWOZgBzklATPEKhIQb+5i1CnN4tsy+gQbLDCJMPMbTPut3E3N0NNm7QMPJioXM/oleAhP587Qrc7W4QQCVCFBMnwtFRWU83DXzsxG2OP0TRYWLvoxTCCtAMPgVc5Y0X/oywndJikzXym5DSfsJtOCzando/wRHfIwsy/dGd348doFzRrVtO9XEX4r3jJKJAsGMeXpz2PP2uN3BCcjqjJPLNMSN/H91ebM3dlL93sYGi9kMVweOf7b3svNaNKz6jAMaigyM+EnWQvr6QXS+M2E3xb/DR+cMcEuuBdx9kvZ2sx91gzyoBO8LIgyhSKaAyLjIM820O7l5gbkJPYyByuGEOPuo9QbzYvbuPmbGgIwsDBaHMf7Bt1fzZPT9OyODt8pHjNMJvIIu+f/0IzOc+GJAfYgKTK0LYUVDPS0183M+ddr9N4V4C0VMqsgKAEk4XPOJtES6FIJjSYjM6cpMA6Z7GLTbs1O3Sj8qxzOMOAvRxpX+UbbEc3X1D/v4xA8K+wyoCSFBprlFNA9z3Pj/APNIpgyjCxGE63xRNbfzIvZ0vYPGOkuhDG/HrT+Od/kzTDSQ+q4Cx4oMzMuKNELWuo80t/NJt+b/qoefTHzLiUY6/ac2eDMNtaV8S4TfyycMt8iFQSI40XPC9CE5WwGjyTpMkor+xBX7+XUDs012z75MRrXL9UwwBxB/GHdcs1W04LsFw6YKSQznSZrCSjoMNFtzhDhDgGYIA8y6y31FYT0CNjNzKzX8/NuFaktLjIKIaMBh+GTzvXQpefZCDsmHTPuKaYOC+2f01vN9Nyt+0UcqDALMLAa0fmd2x7NltTL7m4Q+ir4MvYk/wYE5kDQGM8N44EDciKEMsgstxMk8ozW2cw72Vn2ohe3LqIxIR8v/5jf/s360dTpQAvRJzIzeihIDMrqc9LGzcfeIP5HHl0xJC+RGGT37tnnzO/VH/G8EkIsrjI5I5AE7+Nrz+DPG+XyBTgk2zKLK28RzO8o1QLN4NrE+McZqi/6MCUdvPy83YbNGtMQ7KENUCkqM+4m5AmV6GLRT86v4JMAOCD1MSEuYxb89FbYz8xh13zz/hRwLUYyZyEeAurhs87E0DnnYAjpJRUzNCocD37t3dNKzZvcM/veG4EwNTAZG0v69NsszVXUWO76D7YqAzNLJXkHbuZs0PTOqOIGAxYicDIDLSkUmvLV1tTM69jg9TQXhC7AMYIfqv/43xnOxNFl6cgKgycwM8UowAw666zSr81q3qX95B08MVMv/Rjd90Ha78yp1arwSRIELL8ykiMKBVbkk8+3z7PkeAXhI80yyyviEUHwbNX4zIvaSvhdGX0vHTGKHTf9GN6bzeDSn+sqDQcpLjM9J1wKA+mW0TLOTuAYANgf2TFWLtIWdPWk2NHMFtcF844UNy1cMsQhmQJO4tXOltDO5uYHliUMM3kqkQ/x7RzUOc1C3Lj6dhtZMF4wgRvF+kvcO80V1OTthA9yKg0znyXzB9nmmtDSzkTijAK6IVoyPS2aFBHzHtfRzJTYXPW7Fksu3zHsHzEAYeA4zovR7OhECi0nLTMWKUINtuvr0pfNBd4q/YAdGTGCL2gZV/iU2vnMZNU08NYRxCvOMuojhQW+5LvPjs9L5P0EiCO9MgosVRK28LHV78w42tD38RhOLz8x7h2y/XTesc2m0i7rswy9KDAzjCfVCnHpytEWzu3fnf94H70xii5AF+3189jVzM3WjvIdFP0scjIgIhQDs+L4zmjQY+ZsB0IlAjO+KgYQZO5c1CrN6ts++g4bMDCFMOkbQPuk3EzN1tNx7Q8PLSoWM/IlbAhE58rQsM7g4RECXSFDMnctChWJ82nXzsxN2O/0WBYbLvgxQyCgALngUs5d0Yno1wnlJikzWCmuDRzsIdOEzbLdr/wbHfYwry/TGdH46doEzSHVv+9iEYQr3TJBJP8FJuXlz2fP5OODBC8jrDJJLMgSLPH21ebM5dlX94YYHi9gMVIeLf7S3snNbdK+6jwMcigyM9knTQvg6QDS+82O3yL/Fh+fMbwurRdm9kPZ2cyE1hfyqxPCLIYyfCKOAxjjHM870Pjl8gbtJPcyASt7ENjundQczZPbxPmlGgYwrDBQHLv7/dxdzZnT/+yZDuYpHTNEJuYIsef60I/OfOGWAQAhKzKvLXoVAPS0183MANh39OkV5S0SMqIgHAEb4XDOK9Ec6F4JlCYkM6ApJA6O7FzTcM1X3TT8tRzRMNsvPRpL+T7bD83e1Evv7hBDK+oymCR5BpDlD9BBz33jCATWIpoyhiw6E6LxPdbfzJPZ3vYZGO4ugDG1Hqj+MN/hzTbSTurECyYoMzMmKMULT+o20uHNL9+n/rQegDHuLhoY3/aU2d/MPdah8TkThSyaMtYiCQR+40HPD9CP5XgGlyTqMkMr7xBM797UD80920r5PBrbL9Iwthw1/FjdcM1c043sIw6fKSQzlSZfCR3oK9FwzhrhGgGhIBIy5i3qFXj0AdjNzLTX//N5Fa4tKzIAIZcBfeGQzvnQsOflCEMmHTPnKZoOAO2Z013N/dy5+08crDAGMKYaxfmU2xzNnNTX7noQACv3Mu0k8wb55TvQHM8X440DeyKGMsIsrBMY8oXW2cxC2WX2rBe8Lp8xFx8j/47f+83/0d/pTAvZJzIzcig9DL/qbtLJzdHeLP5RHmAxHy+GGFj35tnmzPbVK/HHEkgsrDIwI4QE5eNnz+TPT+XsBZgjoTEzKsEQcfA118vPKN1K+fEX7SvPLHYaAv074QbTS9iE7u8L1CMHLD0hWgg97P/YwNY45oIAMhpHKOMkvhFJ9/fgSdjB4Hr28A8qInglrBiEARjqcNw83nbu6wVyGksj4hw5CoXzj+KB3tvo6/ztEeAeYB7kEHH86ukx4dDlivVrCd4YXh06FTAEuPHA5UHlMfCjAf4RSxotFz4KPfmH6+HmEe0t+/0KuBXpFk4O1P/Q8TjqHexu9ocETBDJFEwQAQXt96/uGe2Z8yn/tApQEVgQdwhG/aPzme+q8kb7jgUUDcMOHwpkAXL4FfNs8xD5XwG1CAIMFQoABI789vZ89YT4g/7GBKMIoQgCBYn/p/pd+HD5Kv3DAToFNAaEBCABof1/+3n7UP0DAFICUwPPAj4Bff9U/iT+w/6w/2AAjABOAA==";
const PROGRAM_TEMPLATES = listProgramTemplates();
const APP_VERSION = "0.7.29";
const APP_HISTORY_STATE_KEY = "__liftflowUi";
const AUTH_FLOW_HISTORY_STATE_KEY = "__liftflowAuthUi";
const AUTH_SPLASH_MIN_DISPLAY_MS = 520;
const AUTH_SPLASH_EXIT_MS = 320;
const AUTH_SPLASH_STARTED_AT = typeof performance !== "undefined" ? performance.now() : Date.now();
const TERMS_CONSENT_VERSION = "2026-03-28";
const PRIVACY_CONSENT_VERSION = "2026-03-28";
const CONSENT_GATE_CANCELLED_CODE = "app/consent-gate-cancelled";
const AUTH_FLOW_PANELS = {
  login: "login",
  signup: "signup",
  consent: "consent",
};
const RM_BASE_LABELS = {
  squat: "Squat",
  bench: "Bench",
  deadlift: "Deadlift",
};
const RM_BASE_INPUT_IDS = {
  squat: "squatInput",
  bench: "benchInput",
  deadlift: "deadliftInput",
};
const CUSTOM_MUSCLE_SEARCH_INDEX = CUSTOM_MUSCLE_OPTIONS.map((option) => ({
  ...option,
  searchTokens: Array.from(
    new Set(
      [option.id, option.label, ...(option.aliases || [])]
        .flatMap((value) => tokenizeCustomMuscleSearch(value)),
    ),
  ),
}));
const AUTO_LOGIN_STORAGE_KEY = "liftflow:auto-login-enabled";
const DROP_SET_APPLIED_STORAGE_KEY = "liftflow:drop-set-applied-v1";
const PUSH_SUBSCRIPTION_FINGERPRINT_STORAGE_KEY = "liftflow:push-subscription-fingerprint-v1";
const PUSH_SUBSCRIPTION_ID_STORAGE_KEY = "liftflow:push-subscription-id-v1";
const PUSH_SUBSCRIPTION_UID_STORAGE_KEY = "liftflow:push-subscription-uid-v1";
const SESSION_DRAFT_BACKUP_STORAGE_KEY = "liftflow:session-draft-backup-v1";
const SESSION_DRAFT_SYNC_DEBOUNCE_MS = 1200;
const SESSION_DRAFT_SYNC_THROTTLE_MS = 15000;
const HOME_PREVIEW_ITEM_LIMIT = 3;
const WORKOUT_LIST_LIMIT = 20;
const SESSION_COMMIT_MODE_INTERACTIVE = "interactive";
const SESSION_COMMIT_MODE_BACKGROUND = "background";
const HTML2CANVAS_CDN_URL = "https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js";
const SHELL_RENDER_KEYS = new Set(["selectedTab", "profile", "user", "activeSession", "activeWorkout"]);
const EXERCISE_SEARCH_MODAL_CONTEXTS = {
  analytics: {
    title: "운동 검색",
    meta: "분석할 운동을 검색하세요.",
    inputLabel: "운동 검색",
    placeholder: "벤치, ohp, 삼두 ...",
    action: "analytics.select-exercise",
  },
  "logs-exercise": {
    title: "운동 기록 검색",
    meta: "기록을 확인할 운동을 검색하세요.",
    inputLabel: "운동 검색",
    placeholder: "벤치, ohp, 삼두 ...",
    action: "logs.select-exercise",
  },
  "program-item": {
    title: "운동 검색",
    meta: "DAY에 넣을 운동을 검색하세요.",
    inputLabel: "운동 검색",
    placeholder: "벤치, ohp, 삼두 ...",
    action: "programs.select-item-exercise",
  },
};
const searchOverlayController = createSearchOverlayController({
  exerciseSearchModalContexts: EXERCISE_SEARCH_MODAL_CONTEXTS,
  getAnalyticsResultsElement: () => document.getElementById("analyticsModalSearchResults"),
  getExerciseSearchModalContext,
  getProgramTemplateResultsElement: () => els.programTemplateSearchModalResults,
  mutateState,
  programTemplates: PROGRAM_TEMPLATES,
  renderExerciseSearchResults,
  renderProgramTemplateSearchResults,
  setElementHtml,
  setState,
});
const analyticsExerciseSearch = createExerciseSearchSession({
  getApi: () => api,
});
const programItemExerciseSearchSessions = createExerciseSearchSessionMap({
  getApi: () => api,
});
const sessionAddExerciseSearch = createExerciseSearchSession({
  getApi: () => api,
});
const TAB_RENDER_DEPENDENCIES = {
  home: new Set(["activeProgram", "activeSession", "workouts", "homePreview"]),
  logs: new Set([
    "workoutDetailsById",
    "logsMode",
    "logsSelectedDate",
    "logsCalendarMonth",
    "logsCalendarDatesByMonth",
    "logsWorkoutsByDate",
    "logsSelectedExerciseId",
    "logsSelectedExerciseName",
    "logsExerciseHistory",
    "logsDateExpandedWorkoutId",
    "logsExerciseExpandedWorkoutId",
    "logsLoadingMonth",
    "logsLoadingDate",
    "logsLoadingExercise",
    "logsLoadingWorkoutId",
  ]),
  programs: new Set([
    "programs",
    "programBuilder",
    "programBuilderDayIndex",
    "programBuilderStage",
    "exerciseNameMap",
  ]),
  analytics: new Set(["analytics", "estimatedOneRM", "customExercises"]),
  profile: new Set([
    "profile",
    "oneRM",
    "customExercises",
    "user",
    "settings",
    "pushPermission",
    "pushSubscriptionReady",
    "wakeLockActive",
    "pushConfig",
    "clientPlatform",
    "notificationCapabilities",
    "androidBridgeReady",
    "androidNotificationPermission",
    "androidExactAlarmAllowed",
    "androidExactAlarmSupported",
    "androidExactAlarmPermissionRequested",
  ]),
  session: new Set(["activeWorkout", "activeSession", "sessionPreviousByExercise", "sessionItemEditOrder", "sessionItemReorderOrder"]),
};
const LOGS_DATE_CALENDAR_RENDER_KEYS = new Set([
  "logsSelectedDate",
  "logsCalendarMonth",
  "logsCalendarDatesByMonth",
  "logsLoadingMonth",
]);
const LOGS_DATE_HEADER_RENDER_KEYS = new Set(["logsSelectedDate"]);
const LOGS_DATE_LIST_RENDER_KEYS = new Set([
  "logsSelectedDate",
  "logsWorkoutsByDate",
  "logsDateExpandedWorkoutId",
  "workoutDetailsById",
  "logsLoadingDate",
  "logsLoadingWorkoutId",
]);
const LOGS_EXERCISE_SEARCH_RENDER_KEYS = new Set([
  "logsSelectedExerciseId",
  "logsSelectedExerciseName",
]);
const LOGS_EXERCISE_LIST_RENDER_KEYS = new Set([
  "logsSelectedExerciseId",
  "logsExerciseHistory",
  "logsExerciseExpandedWorkoutId",
  "logsLoadingExercise",
]);

function padDateNumber(value) {
  return String(value).padStart(2, "0");
}

function getLocalDateKey(date = new Date()) {
  const safeDate = date instanceof Date ? date : new Date(date);
  return `${safeDate.getFullYear()}-${padDateNumber(safeDate.getMonth() + 1)}-${padDateNumber(safeDate.getDate())}`;
}

function getMonthKeyFromDateKey(dateKey) {
  const normalized = normalizeDateKey(dateKey);
  return normalized ? normalized.slice(0, 7) : "";
}

function getMonthDateFromKey(monthKey) {
  const safeMonthKey = String(monthKey || "").trim();
  const match = safeMonthKey.match(/^(\d{4})-(\d{2})$/);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (!Number.isInteger(year) || !Number.isInteger(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    return null;
  }
  return new Date(year, monthIndex, 1);
}

function buildDateKeyFromParts(year, monthIndex, day) {
  return `${year}-${padDateNumber(monthIndex + 1)}-${padDateNumber(day)}`;
}

function getDaysInMonth(monthKey) {
  const monthDate = getMonthDateFromKey(monthKey);
  if (!(monthDate instanceof Date)) {
    return 30;
  }
  return new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
}

function shiftMonthKey(monthKey, delta = 0) {
  const monthDate = getMonthDateFromKey(monthKey) || new Date();
  const shifted = new Date(monthDate.getFullYear(), monthDate.getMonth() + Number(delta || 0), 1);
  return getMonthKeyFromDateKey(getLocalDateKey(shifted));
}

function clampDateKeyToMonth(dateKey, monthKey) {
  const normalizedDate = normalizeDateKey(dateKey);
  const monthDate = getMonthDateFromKey(monthKey);
  if (!normalizedDate || !(monthDate instanceof Date)) {
    return normalizedDate || getLocalDateKey();
  }

  const rawDay = Number(normalizedDate.slice(8, 10)) || 1;
  const daysInMonth = getDaysInMonth(monthKey);
  const clampedDay = Math.min(Math.max(1, rawDay), daysInMonth);
  return buildDateKeyFromParts(monthDate.getFullYear(), monthDate.getMonth(), clampedDay);
}

function createDefaultLogsState() {
  const today = getLocalDateKey();
  return {
    logsMode: "date",
    logsSelectedDate: today,
    logsCalendarMonth: getMonthKeyFromDateKey(today),
    logsCalendarDatesByMonth: {},
    logsWorkoutsByDate: {},
    logsSelectedExerciseId: "",
    logsSelectedExerciseName: "",
    logsExerciseHistory: {},
    logsDateExpandedWorkoutId: "",
    logsExerciseExpandedWorkoutId: "",
    logsLoadingMonth: "",
    logsLoadingDate: "",
    logsLoadingExercise: "",
    logsLoadingWorkoutId: "",
  };
}

document.documentElement.dataset.appVersion = APP_VERSION;

let autoLoginEnabled = true;
const PWA_INSTALL_PROMPT_SNOOZE_STORAGE_KEY = "pwa_install_prompt_snooze_until_ms";
const PWA_INSTALL_PROMPT_SNOOZE_DURATION_MS = 30 * 60 * 1000;
let pwaInstalledInSession = false;
let pwaRelatedAppInstalled = false;
let pwaRelatedAppDetectionPending = false;
let pwaRelatedAppDetectionSeq = 0;
let html2canvasLoadPromise = null;
const dropSetAppliedKeys = new Set();
const pendingDropSetKeys = new Set();

function loadHtml2Canvas() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("이 기능은 현재 환경에서는 사용할 수 없습니다."));
  }

  if (typeof window.html2canvas === "function") {
    return Promise.resolve(window.html2canvas);
  }

  if (html2canvasLoadPromise) {
    return html2canvasLoadPromise;
  }

  html2canvasLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector("script[data-html2canvas-loader='true']");
    if (existingScript instanceof HTMLScriptElement) {
      if (existingScript.dataset.loadState === "error") {
        existingScript.remove();
      } else {
        existingScript.addEventListener(
          "load",
          () => {
            if (typeof window.html2canvas === "function") {
              resolve(window.html2canvas);
              return;
            }
            html2canvasLoadPromise = null;
            reject(new Error("html2canvas 초기화에 실패했습니다."));
          },
          { once: true },
        );
        existingScript.addEventListener(
          "error",
          () => {
            html2canvasLoadPromise = null;
            reject(new Error("공유 이미지 라이브러리 로딩에 실패했습니다."));
          },
          { once: true },
        );
        return;
      }
    }

    const script = document.createElement("script");
    script.src = HTML2CANVAS_CDN_URL;
    script.async = true;
    script.dataset.html2canvasLoader = "true";
    script.dataset.loadState = "loading";
    script.onload = () => {
      script.dataset.loadState = "loaded";
      if (typeof window.html2canvas === "function") {
        resolve(window.html2canvas);
        return;
      }
      html2canvasLoadPromise = null;
      reject(new Error("html2canvas 초기화에 실패했습니다."));
    };
    script.onerror = () => {
      script.dataset.loadState = "error";
      html2canvasLoadPromise = null;
      reject(new Error("공유 이미지 라이브러리 로딩에 실패했습니다."));
    };
    document.head.appendChild(script);
  });

  return html2canvasLoadPromise;
}

function getDropSetItemKey(workoutId, order) {
  const safeWorkoutId = String(workoutId || "").trim();
  const safeOrder = Number(order);
  if (!safeWorkoutId || !Number.isInteger(safeOrder) || safeOrder < 0) {
    return "";
  }
  return `${safeWorkoutId}:${safeOrder}`;
}

function loadDropSetAppliedKeys() {
  try {
    const raw = window.sessionStorage.getItem(DROP_SET_APPLIED_STORAGE_KEY);
    if (!raw) {
      return;
    }
    const keys = JSON.parse(raw);
    if (!Array.isArray(keys)) {
      return;
    }
    keys.forEach((key) => {
      const safeKey = String(key || "").trim();
      if (safeKey) {
        dropSetAppliedKeys.add(safeKey);
      }
    });
  } catch (_) {
    // Ignore invalid sessionStorage contents.
  }
}

function persistDropSetAppliedKeys() {
  try {
    window.sessionStorage.setItem(
      DROP_SET_APPLIED_STORAGE_KEY,
      JSON.stringify(Array.from(dropSetAppliedKeys)),
    );
  } catch (_) {
    // Ignore sessionStorage failures.
  }
}

function isDropSetAppliedForItem(workoutId, order) {
  const key = getDropSetItemKey(workoutId, order);
  if (!key) {
    return false;
  }
  return dropSetAppliedKeys.has(key);
}

function markDropSetAppliedForItem(workoutId, order) {
  const key = getDropSetItemKey(workoutId, order);
  if (!key) {
    return;
  }
  dropSetAppliedKeys.add(key);
  persistDropSetAppliedKeys();
}

function clearDropSetAppliedForWorkout(workoutId) {
  const safeWorkoutId = String(workoutId || "").trim();
  if (!safeWorkoutId) {
    return;
  }
  const prefix = `${safeWorkoutId}:`;
  let touched = false;
  Array.from(dropSetAppliedKeys).forEach((key) => {
    if (key.startsWith(prefix)) {
      dropSetAppliedKeys.delete(key);
      touched = true;
    }
  });
  if (touched) {
    persistDropSetAppliedKeys();
  }
}

function clearAllDropSetAppliedKeys() {
  if (dropSetAppliedKeys.size <= 0) {
    return;
  }
  dropSetAppliedKeys.clear();
  persistDropSetAppliedKeys();
}

function normalizeDraftSetUpdates(setUpdates) {
  if (!Array.isArray(setUpdates)) {
    return [];
  }

  return setUpdates
    .map((row) => {
      const order = Number(row?.order);
      const setIndex = Number(row?.setIndex);
      if (!Number.isInteger(order) || order < 0 || !Number.isInteger(setIndex) || setIndex < 0) {
        return null;
      }

      let weight = null;
      if (row?.weight !== null && row?.weight !== undefined) {
        const parsedWeight = Number(row.weight);
        if (!Number.isFinite(parsedWeight)) {
          return null;
        }
        weight = parsedWeight;
      }

      let reps = null;
      if (row?.reps !== null && row?.reps !== undefined) {
        const parsedReps = Number(row.reps);
        if (!Number.isFinite(parsedReps)) {
          return null;
        }
        reps = parsedReps;
      }

      return {
        order,
        setIndex,
        weight,
        reps,
        done: !!row?.done,
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.order - b.order) || (a.setIndex - b.setIndex));
}

function readSessionDraftBackup() {
  try {
    const raw = window.localStorage.getItem(SESSION_DRAFT_BACKUP_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    const workoutId = String(parsed.workoutId || "").trim();
    if (!workoutId) {
      return null;
    }

    const sets = normalizeDraftSetUpdates(parsed.sets);
    const session = normalizeSessionDraftBackupSnapshot(parsed.session, workoutId);
    if (sets.length <= 0 && !session) {
      return null;
    }

    return {
      workoutId,
      sets,
      session,
      savedAt: Number(parsed.savedAt) || Date.now(),
    };
  } catch (_) {
    return null;
  }
}

function normalizeSessionDraftBackupSnapshot(session, workoutId = "") {
  if (!session || typeof session !== "object") {
    return null;
  }

  const safeWorkoutId = String(workoutId || session.workoutId || "").trim();
  if (!safeWorkoutId) {
    return null;
  }

  const rawOrder = Number(session.current?.order);
  const rawSetIndex = Number(session.current?.setIndex);
  const restEnabled = session.rest?.enabled ?? true;
  const restRunning = !!session.rest?.running;
  const rawRestEndAt = session.rest?.endAt;
  const parsedRestEndAt =
    rawRestEndAt === null || rawRestEndAt === undefined ? null : Number(rawRestEndAt);

  return {
    workoutId: safeWorkoutId,
    startedAt: Number(session.startedAt) || 0,
    current: {
      order: Number.isInteger(rawOrder) && rawOrder >= 0 ? rawOrder : 0,
      setIndex: Number.isInteger(rawSetIndex) && rawSetIndex >= 0 ? rawSetIndex : 0,
    },
    rest: {
      enabled: !!restEnabled,
      running: restRunning,
      endAt: restRunning && Number.isFinite(parsedRestEndAt) && parsedRestEndAt > 0
        ? parsedRestEndAt
        : null,
    },
    lastHeartbeatAt: Number(session.lastHeartbeatAt) || 0,
  };
}

function writeSessionDraftBackup(workoutId, setUpdates, options = {}) {
  const safeWorkoutId = String(workoutId || "").trim();
  const normalizedSets = normalizeDraftSetUpdates(setUpdates);
  const normalizedSession = normalizeSessionDraftBackupSnapshot(options.activeSession, safeWorkoutId);
  if (!safeWorkoutId || (normalizedSets.length <= 0 && !normalizedSession)) {
    return null;
  }

  const payload = {
    workoutId: safeWorkoutId,
    sets: normalizedSets,
    session: normalizedSession,
    savedAt: Date.now(),
  };
  try {
    window.localStorage.setItem(SESSION_DRAFT_BACKUP_STORAGE_KEY, JSON.stringify(payload));
  } catch (_) {
    // Ignore storage failures in restricted environments.
  }
  return payload;
}

function buildRestStateFromDraftBackup(rest) {
  if (!rest || typeof rest !== "object") {
    return {
      enabled: true,
      running: false,
      endAt: null,
    };
  }

  return {
    enabled: rest.enabled !== false,
    running: !!rest.running,
    endAt:
      rest.running && Number.isFinite(Number(rest.endAt)) && Number(rest.endAt) > 0
        ? Number(rest.endAt)
        : null,
  };
}

function restoreActiveSessionFromDraftBackup(workout, activeSession, backup) {
  const safeWorkoutId = String(workout?.workoutId || "").trim();
  const backupSession = normalizeSessionDraftBackupSnapshot(backup?.session, safeWorkoutId);
  if (!safeWorkoutId || !backupSession || backupSession.workoutId !== safeWorkoutId) {
    return null;
  }

  const serverWorkoutId = String(activeSession?.workoutId || "").trim();
  if (serverWorkoutId && serverWorkoutId !== safeWorkoutId) {
    return null;
  }

  const backupSavedAt = Number(backup?.savedAt) || 0;
  const serverHeartbeat = Number(activeSession?.lastHeartbeatAt) || 0;
  if (activeSession && backupSavedAt > 0 && backupSavedAt < serverHeartbeat) {
    return null;
  }

  return {
    workoutId: safeWorkoutId,
    startedAt:
      Number(activeSession?.startedAt) ||
      Number(backupSession.startedAt) ||
      Number(workout?.meta?.startedAt) ||
      backupSavedAt ||
      Date.now(),
    current: {
      order: Number(backupSession.current?.order) || 0,
      setIndex: Number(backupSession.current?.setIndex) || 0,
    },
    rest: buildRestStateFromDraftBackup(backupSession.rest),
    lastHeartbeatAt: Math.max(
      Number(activeSession?.lastHeartbeatAt) || 0,
      Number(backupSession.lastHeartbeatAt) || 0,
      backupSavedAt,
    ),
  };
}

function clearSessionDraftBackup(workoutId = "") {
  const safeWorkoutId = String(workoutId || "").trim();
  if (!safeWorkoutId) {
    try {
      window.localStorage.removeItem(SESSION_DRAFT_BACKUP_STORAGE_KEY);
    } catch (_) {
      // Ignore storage failures.
    }
    return;
  }

  const backup = readSessionDraftBackup();
  if (backup?.workoutId !== safeWorkoutId) {
    return;
  }

  try {
    window.localStorage.removeItem(SESSION_DRAFT_BACKUP_STORAGE_KEY);
  } catch (_) {
    // Ignore storage failures.
  }
}

function clearAllSessionDraftBackups() {
  clearSessionDraftBackup();
}

function getSessionDraftRowKey(order, setIndex) {
  const safeOrder = Number(order);
  const safeSetIndex = Number(setIndex);
  if (!Number.isInteger(safeOrder) || safeOrder < 0 || !Number.isInteger(safeSetIndex) || safeSetIndex < 0) {
    return "";
  }
  return `${safeOrder}:${safeSetIndex}`;
}

function getSessionDraftPendingMap(workoutId, create = false) {
  const safeWorkoutId = String(workoutId || "").trim();
  if (!safeWorkoutId) {
    return null;
  }
  if (!sessionDraftPendingByWorkout.has(safeWorkoutId) && create) {
    sessionDraftPendingByWorkout.set(safeWorkoutId, new Map());
  }
  return sessionDraftPendingByWorkout.get(safeWorkoutId) || null;
}

function getSessionDraftPendingRows(workoutId) {
  const map = getSessionDraftPendingMap(workoutId);
  if (!map || map.size <= 0) {
    return [];
  }
  return Array.from(map.values()).sort((a, b) => (a.order - b.order) || (a.setIndex - b.setIndex));
}

function clearSessionDraftPending(workoutId = "") {
  const safeWorkoutId = String(workoutId || "").trim();
  if (!safeWorkoutId) {
    sessionDraftPendingByWorkout.clear();
    return;
  }
  sessionDraftPendingByWorkout.delete(safeWorkoutId);
}

function isDraftSetRowChangedAgainstWorkout(workout, row) {
  const baseSet = workout?.items?.[String(row.order)]?.sets?.[String(row.setIndex)];
  if (!baseSet) {
    return false;
  }
  const baseWeight = typeof baseSet.weight === "number" ? baseSet.weight : null;
  const baseReps = typeof baseSet.reps === "number" ? baseSet.reps : null;
  const baseDone = !!baseSet.done;
  return baseWeight !== row.weight || baseReps !== row.reps || baseDone !== !!row.done;
}

function isSameDraftSetRow(a, b) {
  if (!a || !b) {
    return false;
  }
  return (
    Number(a.order) === Number(b.order) &&
    Number(a.setIndex) === Number(b.setIndex) &&
    a.weight === b.weight &&
    a.reps === b.reps &&
    !!a.done === !!b.done
  );
}

function enqueueSessionDraftPendingRows(workoutId, setUpdates, options = {}) {
  const safeWorkoutId = String(workoutId || "").trim();
  if (!safeWorkoutId) {
    return [];
  }

  const normalizedRows = normalizeDraftSetUpdates(setUpdates);
  if (normalizedRows.length <= 0) {
    return getSessionDraftPendingRows(safeWorkoutId);
  }

  const compareWorkout = options.compareWorkout || getState().activeWorkout;
  const compareWorkoutId = String(compareWorkout?.workoutId || "").trim();
  if (!compareWorkout || compareWorkoutId !== safeWorkoutId) {
    return getSessionDraftPendingRows(safeWorkoutId);
  }

  const pendingMap = getSessionDraftPendingMap(safeWorkoutId, true);
  normalizedRows.forEach((row) => {
    const rowKey = getSessionDraftRowKey(row.order, row.setIndex);
    if (!rowKey) {
      return;
    }
    if (isDraftSetRowChangedAgainstWorkout(compareWorkout, row)) {
      pendingMap.set(rowKey, row);
    } else {
      pendingMap.delete(rowKey);
    }
  });

  if (pendingMap.size <= 0) {
    sessionDraftPendingByWorkout.delete(safeWorkoutId);
  }

  return getSessionDraftPendingRows(safeWorkoutId);
}

function removeSessionDraftPendingRows(workoutId, setUpdates, options = {}) {
  const safeWorkoutId = String(workoutId || "").trim();
  if (!safeWorkoutId) {
    return;
  }
  const pendingMap = getSessionDraftPendingMap(safeWorkoutId);
  if (!pendingMap) {
    return;
  }

  normalizeDraftSetUpdates(setUpdates).forEach((row) => {
    const rowKey = getSessionDraftRowKey(row.order, row.setIndex);
    if (rowKey) {
      if (options.onlyIfSame) {
        const latest = pendingMap.get(rowKey);
        if (!isSameDraftSetRow(latest, row)) {
          return;
        }
      }
      pendingMap.delete(rowKey);
    }
  });

  if (pendingMap.size <= 0) {
    sessionDraftPendingByWorkout.delete(safeWorkoutId);
  }
}

function persistCurrentSessionDraftBackup(workoutId, options = {}) {
  const { activeWorkout, activeSession } = getState();
  const activeWorkoutId = String(activeWorkout?.workoutId || "").trim();
  const targetWorkoutId = String(workoutId || activeWorkoutId).trim();
  if (!activeWorkoutId || !targetWorkoutId || activeWorkoutId !== targetWorkoutId) {
    return null;
  }

  const sets = normalizeDraftSetUpdates(
    collectSessionSetUpdatesFromDom(activeWorkout, options.overrideSet),
  );
  const targetSession =
    String(activeSession?.workoutId || "").trim() === activeWorkoutId
      ? activeSession
      : null;
  if (sets.length <= 0 && !targetSession) {
    return null;
  }

  writeSessionDraftBackup(activeWorkoutId, sets, {
    activeSession: targetSession,
  });
  return sets;
}

function restoreWorkoutFromSessionDraftBackup(workout, activeSession = null) {
  const safeWorkoutId = String(workout?.workoutId || "").trim();
  if (!safeWorkoutId) {
    return {
      workout,
      activeSession,
      restored: false,
      sets: [],
    };
  }

  const backup = readSessionDraftBackup();
  if (!backup || backup.workoutId !== safeWorkoutId) {
    return {
      workout,
      activeSession,
      restored: false,
      sets: [],
    };
  }

  const backupSets = normalizeDraftSetUpdates(backup.sets);
  const restoredSession = restoreActiveSessionFromDraftBackup(workout, activeSession, backup);
  const shouldRestoreSets = backupSets.length > 0 && hasSetChangesAgainstWorkout(workout, backupSets);

  if (!shouldRestoreSets && !restoredSession) {
    clearSessionDraftBackup(safeWorkoutId);
    return {
      workout,
      activeSession,
      restored: false,
      sets: [],
    };
  }

  return {
    workout: shouldRestoreSets ? applySetUpdatesToWorkout(workout, backupSets) : workout,
    activeSession: restoredSession || activeSession,
    restored: shouldRestoreSets,
    sets: shouldRestoreSets ? backupSets : [],
  };
}

function cancelSessionInputAutosave() {
  if (sessionSetInputAutosaveTimer) {
    clearTimeout(sessionSetInputAutosaveTimer);
    sessionSetInputAutosaveTimer = null;
  }
  sessionSetInputAutosaveWorkoutId = "";
}

function resolveScheduledSessionDraftSyncDelay(options = {}) {
  const immediate = !!options.immediate;
  const bypassThrottle = !!options.bypassThrottle;
  const now = Date.now();
  let delay = immediate ? 0 : SESSION_DRAFT_SYNC_DEBOUNCE_MS;
  if (!bypassThrottle) {
    const throttleDelay = Math.max(
      0,
      (sessionDraftLastNetworkSyncAt + SESSION_DRAFT_SYNC_THROTTLE_MS) - now,
    );
    delay = Math.max(delay, throttleDelay);
  }
  return delay;
}

function scheduleSessionInputAutosave(workoutId, options = {}) {
  const safeWorkoutId = String(workoutId || "").trim();
  if (!safeWorkoutId) {
    return;
  }
  if (getSessionDraftPendingRows(safeWorkoutId).length <= 0) {
    return;
  }

  sessionSetInputAutosaveWorkoutId = safeWorkoutId;
  if (sessionSetInputAutosaveTimer) {
    clearTimeout(sessionSetInputAutosaveTimer);
  }
  const delay = resolveScheduledSessionDraftSyncDelay(options);

  sessionSetInputAutosaveTimer = setTimeout(() => {
    sessionSetInputAutosaveTimer = null;
    const targetWorkoutId = sessionSetInputAutosaveWorkoutId;
    sessionSetInputAutosaveWorkoutId = "";
    if (!targetWorkoutId) {
      return;
    }
    const pendingRows = getSessionDraftPendingRows(targetWorkoutId);
    if (pendingRows.length <= 0) {
      return;
    }

    commitSessionWorkoutDraft(targetWorkoutId, {
      mode: SESSION_COMMIT_MODE_BACKGROUND,
      sets: pendingRows,
      force: true,
      skipBackupWrite: true,
    }).catch((error) => {
      const normalizedMessage = normalizeSessionNoticeMessage(
        error instanceof Error ? error.message : "세트 입력값을 자동 저장하지 못했습니다.",
      );
      setSessionInlineNotice({
        workoutId: targetWorkoutId,
        title: "자동 저장에 실패했습니다",
        meta: "현재 입력값은 화면에 남아 있지만 아직 서버에 반영되지 않았습니다. 값을 수정하면 자동으로 다시 저장합니다.",
        message: normalizedMessage,
      });
      // eslint-disable-next-line no-console
      console.warn("session draft autosave failed", error);
    });
  }, delay);
}

function flushSessionDraftForLifecycle(reason) {
  const now = Date.now();
  if (now - sessionDraftLifecycleFlushAt < 600) {
    return;
  }
  sessionDraftLifecycleFlushAt = now;

  const workoutId = String(getState().activeWorkout?.workoutId || "").trim();
  if (!workoutId) {
    return;
  }

  persistCurrentSessionDraftBackup(workoutId);
  captureSessionDraftPendingFromDom(workoutId);
  if (getSessionDraftPendingRows(workoutId).length <= 0) {
    return;
  }
  cancelSessionInputAutosave();
  commitSessionWorkoutDraft(workoutId, {
    mode: SESSION_COMMIT_MODE_BACKGROUND,
    force: true,
    skipBackupWrite: true,
  }).catch((error) => {
    // eslint-disable-next-line no-console
    console.warn(`${reason} session draft sync failed`, error);
  });
}

function bindSessionDraftLifecycleEvents() {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushSessionDraftForLifecycle("visibilitychange");
      void syncScreenWakeLock();
      return;
    }

    refreshPushPermissionState();
    reconcileRestTimerFromLifecycle();
    refreshAndroidTwaBridgeStatusInBackground();
    void syncScreenWakeLock();
    void syncRestPushAvailability({ silent: true });
    void maybeAutoPrepareRestNotificationsOnLaunch().catch(() => undefined);
  });

  window.addEventListener("pagehide", () => {
    flushSessionDraftForLifecycle("pagehide");
  });

  window.addEventListener("pageshow", () => {
    refreshPushPermissionState();
    reconcileRestTimerFromLifecycle();
    refreshAndroidTwaBridgeStatusInBackground();
    void syncScreenWakeLock();
    void syncRestPushAvailability({ silent: true });
    void maybeAutoPrepareRestNotificationsOnLaunch().catch(() => undefined);
  });

  window.addEventListener("focus", () => {
    refreshPushPermissionState();
    reconcileRestTimerFromLifecycle();
    refreshAndroidTwaBridgeStatusInBackground();
    void syncScreenWakeLock();
    void syncRestPushAvailability({ silent: true });
    void maybeAutoPrepareRestNotificationsOnLaunch().catch(() => undefined);
  });
}

function roundToNearestHalf(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return Math.max(0, Math.round(numeric * 2) / 2);
}

function normalizeCustomMuscleSearch(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFKC")
    .replace(/[()\[\]{}]/g, " ")
    .replace(/[\s,._/-]+/g, " ")
    .trim();
}

function compactCustomMuscleSearch(value) {
  return normalizeCustomMuscleSearch(value).replace(/\s+/g, "");
}

function tokenizeCustomMuscleSearch(value) {
  const normalized = normalizeCustomMuscleSearch(value);
  if (!normalized) {
    return [];
  }

  const tokenSet = new Set(normalized.split(" ").filter(Boolean));
  const compact = compactCustomMuscleSearch(value);
  if (compact) {
    tokenSet.add(compact);
  }
  return Array.from(tokenSet);
}

function getCustomMuscleTargetConfig(targetKey) {
  if (targetKey === "secondary") {
    return {
      inputId: "customSecondaryMuscles",
      summaryId: "customSecondaryMusclesSummary",
      displayId: "customSecondaryMusclesDisplay",
      triggerId: "openCustomSecondaryMusclePickerBtn",
      modalTitle: "보조 부위 검색",
      modalMeta: "보조 부위는 여러 개를 추가하거나 다시 눌러 해제할 수 있습니다.",
      clearLabel: "보조 부위 초기화",
      emptyText: "선택된 보조 부위가 없습니다.",
      summaryText: (count) => (count > 0 ? `${count}개 선택됨` : "보조 부위를 선택하세요"),
      selectionTitle: (count) => `현재 선택된 보조 부위${count > 0 ? ` ${count}개` : ""}`,
      selectionCopy: "검색 결과에서 추가하거나, 아래 칩의 x 버튼으로 바로 제거할 수 있습니다.",
    };
  }

  return {
    inputId: "customPrimaryMuscle",
    summaryId: "customPrimaryMuscleSummary",
    displayId: "customPrimaryMuscleDisplay",
    triggerId: "openCustomPrimaryMusclePickerBtn",
    modalTitle: "주요 부위 검색",
    modalMeta: "주요 부위는 한 가지만 선택됩니다. 다른 항목을 누르면 바로 교체됩니다.",
    clearLabel: "주요 부위 지우기",
    emptyText: "선택된 주요 부위가 없습니다.",
    summaryText: (_count, labels) => labels[0] || "주요 부위를 선택하세요",
    selectionTitle: () => "현재 선택된 주요 부위",
    selectionCopy: "검색 결과에서 원하는 부위를 누르면 바로 선택됩니다.",
  };
}

function getCustomMuscleInput(targetKey) {
  const config = getCustomMuscleTargetConfig(targetKey);
  const input = document.getElementById(config.inputId);
  return input instanceof HTMLInputElement ? input : null;
}

function getCustomMuscleSelections(targetKey) {
  const input = getCustomMuscleInput(targetKey);
  if (!input) {
    return [];
  }

  if (targetKey === "secondary") {
    return Array.from(
      new Set(
        String(input.value || "")
          .split(",")
          .map((entry) => String(entry || "").trim())
          .filter(Boolean),
      ),
    );
  }

  const value = String(input.value || "").trim();
  return value ? [value] : [];
}

function writeCustomMuscleSelections(targetKey, values) {
  const input = getCustomMuscleInput(targetKey);
  if (!input) {
    return;
  }

  const deduped = Array.from(
    new Set(
      (values || [])
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  );

  input.value = targetKey === "secondary" ? deduped.join(",") : (deduped[0] || "");
}

function getCustomMuscleLabel(muscleId) {
  const safeId = String(muscleId || "").trim();
  if (!safeId) {
    return "";
  }

  const option = CUSTOM_MUSCLE_OPTIONS.find((entry) => entry.id === safeId);
  return option?.label || formatMuscleLabel(safeId) || safeId;
}

function createCustomMuscleRemoveIcon() {
  const svgNs = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(svgNs, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2.2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");

  const lineA = document.createElementNS(svgNs, "line");
  lineA.setAttribute("x1", "18");
  lineA.setAttribute("y1", "6");
  lineA.setAttribute("x2", "6");
  lineA.setAttribute("y2", "18");

  const lineB = document.createElementNS(svgNs, "line");
  lineB.setAttribute("x1", "6");
  lineB.setAttribute("y1", "6");
  lineB.setAttribute("x2", "18");
  lineB.setAttribute("y2", "18");

  svg.append(lineA, lineB);
  return svg;
}

function buildCustomMuscleChipNodes(targetKey, values, options = {}) {
  const safeValues = Array.isArray(values) ? values : [];
  const removable = options.removable !== false;
  const emptyText = String(options.emptyText || getCustomMuscleTargetConfig(targetKey).emptyText);
  const fragment = document.createDocumentFragment();

  if (safeValues.length <= 0) {
    const empty = document.createElement("p");
    empty.className = "custom-muscle-selection-empty";
    empty.textContent = emptyText;
    fragment.append(empty);
    return fragment;
  }

  safeValues.forEach((muscleId) => {
    const label = getCustomMuscleLabel(muscleId);
    const chip = document.createElement("span");
    chip.className = "custom-muscle-chip";

    const labelEl = document.createElement("span");
    labelEl.className = "custom-muscle-chip-label";
    const labelTextEl = document.createElement("span");
    labelTextEl.className = "custom-muscle-chip-label-text";
    labelTextEl.textContent = label;
    labelEl.append(labelTextEl);
    chip.append(labelEl);

    if (removable) {
      const button = document.createElement("button");
      button.className = "custom-muscle-chip-remove-btn";
      button.type = "button";
      button.dataset.muscleTarget = String(targetKey || "");
      button.dataset.muscleId = String(muscleId || "");
      button.setAttribute("aria-label", `${label} 제거`);
      button.append(createCustomMuscleRemoveIcon());
      chip.append(button);
    }

    fragment.append(chip);
  });

  return fragment;
}

function updateCustomMuscleFieldUi(targetKey) {
  const config = getCustomMuscleTargetConfig(targetKey);
  const summaryEl = document.getElementById(config.summaryId);
  const displayEl = document.getElementById(config.displayId);
  const triggerEl = document.getElementById(config.triggerId);
  const selectedValues = getCustomMuscleSelections(targetKey);
  const labels = selectedValues.map((value) => getCustomMuscleLabel(value)).filter(Boolean);

  if (summaryEl instanceof HTMLElement) {
    summaryEl.textContent = config.summaryText(labels.length, labels);
  }

  if (displayEl instanceof HTMLElement) {
    displayEl.replaceChildren(buildCustomMuscleChipNodes(targetKey, selectedValues));
  }

  if (triggerEl instanceof HTMLElement) {
    triggerEl.classList.toggle("is-selected", selectedValues.length > 0);
  }
}

function syncCustomMuscleFieldUi() {
  updateCustomMuscleFieldUi("primary");
  updateCustomMuscleFieldUi("secondary");
}

function syncProfileCustomExerciseFilter() {
  const list = document.getElementById("profileCustomExerciseList");
  const countEl = document.getElementById("profileCustomExerciseCount");
  const input = document.getElementById("profileCustomExerciseSearchInput");
  const filteredEmptyEl = document.getElementById("profileCustomExerciseFilteredEmpty");
  if (!(list instanceof HTMLElement) || !(countEl instanceof HTMLElement)) {
    return;
  }

  const items = Array.from(list.querySelectorAll("[data-role='profile-custom-exercise-item']")).filter(
    (node) => node instanceof HTMLElement,
  );
  const totalCount = items.length;
  const query = input instanceof HTMLInputElement ? String(input.value || "").trim().toLowerCase() : "";
  let visibleCount = 0;

  items.forEach((item) => {
    const searchText = String(item.dataset.searchText || "").toLowerCase();
    const isVisible = !query || searchText.includes(query);
    item.classList.toggle("hidden", !isVisible);
    if (isVisible) {
      visibleCount += 1;
    }
  });

  countEl.textContent = query ? `검색 ${visibleCount}/${totalCount}` : `총 ${totalCount}개`;
  if (filteredEmptyEl instanceof HTMLElement) {
    filteredEmptyEl.classList.toggle("hidden", !(totalCount > 0 && query && visibleCount === 0));
  }
}

function getCustomMusclePickerTarget() {
  return String(els.customMusclePickerModal?.dataset.target || "").trim();
}

function renderCustomMusclePickerSelectionSummary() {
  const selectionEl = els.customMusclePickerSelection;
  if (!(selectionEl instanceof HTMLElement)) {
    return;
  }

  const targetKey = getCustomMusclePickerTarget();
  if (targetKey !== "primary" && targetKey !== "secondary") {
    selectionEl.classList.add("hidden");
    selectionEl.replaceChildren();
    return;
  }

  const config = getCustomMuscleTargetConfig(targetKey);
  const selectedValues = getCustomMuscleSelections(targetKey);
  selectionEl.classList.remove("hidden");
  selectionEl.replaceChildren();

  const title = document.createElement("p");
  title.className = "custom-muscle-modal-selection-title";
  title.textContent = config.selectionTitle(selectedValues.length);

  const copy = document.createElement("p");
  copy.className = "custom-muscle-modal-selection-copy";
  copy.textContent = config.selectionCopy;

  const chipList = document.createElement("div");
  chipList.className = "custom-muscle-chip-list";
  chipList.append(buildCustomMuscleChipNodes(targetKey, selectedValues));

  selectionEl.append(title, copy, chipList);
}

function scoreCustomMuscleOption(option, query, selectedSet) {
  const compactQuery = compactCustomMuscleSearch(query);
  let score = selectedSet.has(option.id) ? 30 : 0;

  if (!compactQuery) {
    return score + 1;
  }

  if (compactCustomMuscleSearch(option.label) === compactQuery) score += 12;
  if (compactCustomMuscleSearch(option.id) === compactQuery) score += 11;
  if ((option.aliases || []).some((alias) => compactCustomMuscleSearch(alias) === compactQuery)) score += 10;
  if (compactCustomMuscleSearch(option.label).includes(compactQuery)) score += 6;
  if (compactCustomMuscleSearch(option.id).includes(compactQuery)) score += 5;
  if (option.searchTokens.some((token) => token.includes(compactQuery))) score += 4;

  return score;
}

function searchCustomMuscleOptions(query, targetKey) {
  const target = targetKey === "secondary" ? "secondary" : "primary";
  const selectedSet = new Set(getCustomMuscleSelections(target));
  const safeQuery = String(query || "").trim();

  return CUSTOM_MUSCLE_SEARCH_INDEX
    .map((option) => ({
      ...option,
      score: scoreCustomMuscleOption(option, safeQuery, selectedSet),
    }))
    .filter((option) => option.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      return a.label.localeCompare(b.label, "ko");
    });
}

function renderCustomMusclePickerResults(query = "") {
  const resultsEl = els.customMusclePickerResults;
  if (!(resultsEl instanceof HTMLElement)) {
    return;
  }

  const targetKey = getCustomMusclePickerTarget();
  if (targetKey !== "primary" && targetKey !== "secondary") {
    resultsEl.replaceChildren();
    return;
  }

  const primarySelection = getCustomMuscleSelections("primary")[0] || "";
  const selectedSet = new Set(getCustomMuscleSelections(targetKey));
  const items = searchCustomMuscleOptions(query, targetKey);

  if (items.length <= 0) {
    const empty = document.createElement("p");
    empty.className = "meta program-item-search-empty";
    empty.textContent = "검색 결과가 없습니다.";
    resultsEl.replaceChildren(empty);
    return;
  }

  const fragment = document.createDocumentFragment();
  items.slice(0, 12).forEach((option) => {
    const isSelected = selectedSet.has(option.id);
    const isPrimaryLocked = targetKey === "secondary" && primarySelection === option.id;
    const buttonLabel = isPrimaryLocked
      ? "주요 부위"
      : targetKey === "secondary"
        ? (isSelected ? "해제" : "추가")
        : (isSelected ? "선택됨" : "선택");

    const row = document.createElement("div");
    row.className = "program-search-result";

    const content = document.createElement("div");
    content.className = "program-search-result-content";

    const name = document.createElement("p");
    name.className = "program-search-result-name";
    name.textContent = option.label;

    const meta = document.createElement("p");
    meta.className = "small";
    const aliasText = (option.aliases || []).length > 0 ? ` · ${option.aliases.slice(0, 4).join(", ")}` : "";
    meta.textContent = `${option.id}${aliasText}`;

    content.append(name, meta);

    const button = document.createElement("button");
    button.className = `secondary custom-muscle-result-btn${isSelected ? " is-selected" : ""}`;
    button.type = "button";
    button.dataset.muscleId = String(option.id || "");
    button.disabled = isPrimaryLocked;
    button.textContent = buttonLabel;

    row.append(content, button);
    fragment.append(row);
  });

  resultsEl.replaceChildren(fragment);
}

function refreshCustomMusclePickerUi() {
  renderCustomMusclePickerSelectionSummary();
  renderCustomMusclePickerResults(els.customMusclePickerSearchInput?.value || "");
}

function setCustomMuscleSelections(targetKey, values) {
  const target = targetKey === "secondary" ? "secondary" : "primary";
  const deduped = Array.from(
    new Set(
      (values || [])
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  );

  writeCustomMuscleSelections(target, deduped);

  if (target === "primary") {
    const primaryValue = deduped[0] || "";
    const filteredSecondary = getCustomMuscleSelections("secondary").filter((value) => value !== primaryValue);
    writeCustomMuscleSelections("secondary", filteredSecondary);
    updateCustomMuscleFieldUi("secondary");
  }

  updateCustomMuscleFieldUi(target);
  refreshCustomMusclePickerUi();
}

function isStandalonePwaMode() {
  const byDisplayMode = window.matchMedia?.("(display-mode: standalone)")?.matches ?? false;
  const byNavigatorFlag = window.navigator?.standalone === true;
  return byDisplayMode || byNavigatorFlag;
}

function isIosDevice() {
  const ua = String(window.navigator?.userAgent || "").toLowerCase();
  return ua.includes("iphone") || ua.includes("ipad") || ua.includes("ipod");
}

function isAndroidTwaEnvironment() {
  return resolveClientPlatform() === CLIENT_PLATFORM_ANDROID_TWA;
}

function isInstalledPwaEnvironment() {
  return isAndroidTwaEnvironment() || isStandalonePwaMode() || pwaInstalledInSession || pwaRelatedAppInstalled;
}

function hidePwaInstallPrompt() {
  const promptEl = els.pwaInstallPrompt;
  if (!(promptEl instanceof HTMLElement)) {
    return;
  }
  promptEl.classList.add("hidden");
}

function showPwaInstallPrompt(message, options = {}) {
  const promptEl = els.pwaInstallPrompt;
  const messageEl = els.pwaInstallMessage;
  const actionBtn = els.pwaInstallActionBtn;
  if (!(promptEl instanceof HTMLElement) || !(messageEl instanceof HTMLElement) || !(actionBtn instanceof HTMLButtonElement)) {
    return;
  }

  const showAction = options.showAction !== false;
  const actionLabel = String(options.actionLabel || "설치");
  const actionMode = String(options.actionMode || (showAction ? "prompt" : ""));
  messageEl.textContent = message;
  actionBtn.textContent = actionLabel;
  actionBtn.classList.toggle("hidden", !showAction);
  actionBtn.dataset.actionMode = showAction ? actionMode : "";
  promptEl.classList.remove("hidden");
}

function getPwaInstallPromptSnoozeUntil() {
  const raw = readLocalStorageValue(PWA_INSTALL_PROMPT_SNOOZE_STORAGE_KEY);
  const snoozeUntil = Number(raw);
  if (!Number.isFinite(snoozeUntil) || snoozeUntil <= 0) {
    if (raw) {
      writeLocalStorageValue(PWA_INSTALL_PROMPT_SNOOZE_STORAGE_KEY, "");
    }
    return 0;
  }

  if (snoozeUntil <= Date.now()) {
    writeLocalStorageValue(PWA_INSTALL_PROMPT_SNOOZE_STORAGE_KEY, "");
    return 0;
  }

  return snoozeUntil;
}

function isPwaInstallPromptSnoozed() {
  return getPwaInstallPromptSnoozeUntil() > 0;
}

function snoozePwaInstallPrompt() {
  writeLocalStorageValue(
    PWA_INSTALL_PROMPT_SNOOZE_STORAGE_KEY,
    String(Date.now() + PWA_INSTALL_PROMPT_SNOOZE_DURATION_MS),
  );
}

function clearPwaInstallPromptSnooze() {
  writeLocalStorageValue(PWA_INSTALL_PROMPT_SNOOZE_STORAGE_KEY, "");
}

function getPwaInstallGuideMessage(actionMode) {
  if (actionMode === "ios-guide") {
    return "공유 버튼을 누른 뒤 '홈 화면에 추가'를 선택하면 앱처럼 설치할 수 있습니다.";
  }

  if (actionMode === "in-app-guide") {
    return "지금 열린 곳에서는 설치가 막힐 수 있습니다. 다른 앱에서 이 페이지를 다시 열어 주세요.";
  }

  return "메뉴에서 '앱 설치' 또는 '홈 화면에 추가'를 선택해 주세요.";
}

function renderPwaInstallGuideBody(bodyEl, content) {
  if (!(bodyEl instanceof HTMLElement)) {
    return;
  }

  bodyEl.replaceChildren();

  const stepList = document.createElement("ol");
  stepList.className = "pwa-install-guide-step-list";
  (content.steps || []).forEach((step) => {
    const item = document.createElement("li");
    item.textContent = String(step || "");
    stepList.append(item);
  });
  bodyEl.append(stepList);

  if (content.note) {
    const note = document.createElement("p");
    note.className = "pwa-install-guide-note";
    note.textContent = String(content.note || "");
    bodyEl.append(note);
  }
}

function openPwaInstallGuide(actionMode, options = {}) {
  const modal = els.pwaInstallGuideModal;
  const titleEl = els.pwaInstallGuideTitle;
  const metaEl = els.pwaInstallGuideMeta;
  const bodyEl = els.pwaInstallGuideBody;
  if (
    !(modal instanceof HTMLElement) ||
    !(titleEl instanceof HTMLElement) ||
    !(metaEl instanceof HTMLElement) ||
    !(bodyEl instanceof HTMLElement)
  ) {
    void openAppAlertModal(getPwaInstallGuideMessage(actionMode), {
      title: "앱 설치 방법",
      meta: "설치 방법은 기기마다 조금 다를 수 있습니다.",
    });
    return;
  }

  const content = getPwaInstallGuideContent(actionMode);
  const { wasOpen, restoreState } = getModalInteractionState(modal, options);
  if (!wasOpen) {
    pwaInstallGuideModalLastFocused = restoreState?.focusTarget instanceof HTMLElement
      ? restoreState.focusTarget
      : null;
  }
  titleEl.textContent = content.title;
  metaEl.textContent = content.meta;
  renderPwaInstallGuideBody(bodyEl, content);
  modal.dataset.actionMode = String(actionMode || "browser-guide");
  showModalWithInteraction(modal, restoreState, wasOpen, options);
}

function getPwaInstallGuideContent(actionMode) {
  if (actionMode === "ios-guide") {
    return {
      title: "iPhone에서 설치하기",
      meta: "공유 버튼에서 홈 화면에 추가를 선택하면 됩니다.",
      steps: [
        "이 페이지를 연 상태로 공유 버튼을 누르세요.",
        "'홈 화면에 추가'를 선택하세요.",
        "이름을 확인한 뒤 '추가'를 누르면 설치가 완료됩니다.",
      ],
      note: "공유 메뉴가 보이지 않으면 페이지를 다시 연 뒤 시도해 보세요.",
    };
  }

  if (actionMode === "in-app-guide") {
    return {
      title: "다른 앱에서 설치하기",
      meta: "지금 열린 곳에서는 설치가 제한될 수 있습니다.",
      steps: [
        "다른 앱에서 이 페이지를 다시 여세요.",
        "메뉴의 '앱 설치' 또는 '홈 화면에 추가'를 선택하세요.",
      ],
      note: "앱마다 메뉴 이름은 조금 다를 수 있습니다.",
    };
  }

  return {
    title: "설치하기",
    meta: "메뉴에서 '앱 설치' 또는 '홈 화면에 추가'를 선택하면 됩니다.",
    steps: [
      "메뉴를 여세요.",
      "'앱 설치' 또는 '홈 화면에 추가' 항목을 선택하세요.",
      "설치 확인 화면이 나오면 승인하면 됩니다.",
    ],
    note: "설치 메뉴가 보이지 않으면 페이지를 새로고침한 뒤 다시 확인해 보세요.",
  };
}

function browserSupportsInstalledRelatedApps() {
  return typeof window.navigator?.getInstalledRelatedApps === "function";
}

async function syncInstalledRelatedPwaState() {
  if (isStandalonePwaMode() || !browserSupportsInstalledRelatedApps()) {
    pwaRelatedAppInstalled = false;
    pwaRelatedAppDetectionPending = false;
    refreshPwaInstallPromptVisibility();
    return;
  }

  const detectionSeq = ++pwaRelatedAppDetectionSeq;
  pwaRelatedAppDetectionPending = true;
  refreshPwaInstallPromptVisibility();

  try {
    const relatedApps = await window.navigator.getInstalledRelatedApps();
    if (detectionSeq !== pwaRelatedAppDetectionSeq) {
      return;
    }
    pwaRelatedAppInstalled = Array.isArray(relatedApps)
      && relatedApps.some((app) => app?.platform === "webapp");
  } catch (_) {
    if (detectionSeq !== pwaRelatedAppDetectionSeq) {
      return;
    }
    pwaRelatedAppInstalled = false;
  }

  if (detectionSeq !== pwaRelatedAppDetectionSeq) {
    return;
  }

  pwaRelatedAppDetectionPending = false;
  refreshPwaInstallPromptVisibility();
}

function getPwaInstallPromptConfig() {
  if (isAndroidTwaEnvironment()) {
    return null;
  }

  if (isPwaInstallPromptSnoozed() || isInstalledPwaEnvironment()) {
    return null;
  }

  if (pwaRelatedAppDetectionPending && !deferredInstallPromptEvent) {
    return null;
  }

  if (deferredInstallPromptEvent) {
    return {
      message: "앱처럼 바로 열 수 있도록 설치할 수 있습니다.",
      actionLabel: "설치",
      actionMode: "prompt",
    };
  }

  if (isInAppBrowser()) {
    return {
      message: "지금 열린 곳에서는 설치가 제한될 수 있어요. 다른 앱에서 다시 열어 주세요.",
      actionLabel: "설치 방법",
      actionMode: "in-app-guide",
    };
  }

  if (isIosDevice()) {
    return {
      message: "공유 버튼을 눌러 ‘홈 화면에 추가’로 설치해 주세요.",
      actionLabel: "설치 방법",
      actionMode: "ios-guide",
    };
  }

  return {
    message: "메뉴에서 ‘홈 화면에 추가’로 설치할 수 있습니다.",
    actionLabel: "설치 방법",
    actionMode: "browser-guide",
  };
}

function refreshPwaInstallPromptVisibility() {
  const promptConfig = getPwaInstallPromptConfig();
  if (!promptConfig) {
    hidePwaInstallPrompt();
    return;
  }

  showPwaInstallPrompt(promptConfig.message, promptConfig);
}

function bindPwaInstallPromptEvents() {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredInstallPromptEvent = event;
    pwaInstalledInSession = false;
    pwaRelatedAppInstalled = false;
    pwaRelatedAppDetectionPending = false;
    refreshPwaInstallPromptVisibility();
  });

  window.addEventListener("appinstalled", () => {
    deferredInstallPromptEvent = null;
    pwaInstalledInSession = true;
    pwaRelatedAppInstalled = true;
    pwaRelatedAppDetectionPending = false;
    clearPwaInstallPromptSnooze();
    hidePwaInstallPrompt();
    refreshPushPermissionState();
    void syncRestPushAvailability({ silent: true });
  });

  const displayModeMedia = window.matchMedia?.("(display-mode: standalone)");
  if (displayModeMedia?.addEventListener) {
    displayModeMedia.addEventListener("change", () => {
      void syncInstalledRelatedPwaState();
    });
  } else if (displayModeMedia?.addListener) {
    displayModeMedia.addListener(() => {
      void syncInstalledRelatedPwaState();
    });
  }

  window.addEventListener("pageshow", () => {
    if (!isStandalonePwaMode()) {
      pwaInstalledInSession = false;
    }
    void syncInstalledRelatedPwaState();
    refreshPushPermissionState();
    void syncRestPushAvailability({ silent: true });
  });

  void syncInstalledRelatedPwaState();
}

function isWakeLockSupported() {
  return typeof navigator !== "undefined" && typeof navigator.wakeLock?.request === "function";
}

function isPushApiSupported() {
  if (isAndroidTwaEnvironment()) {
    return false;
  }
  return (
    typeof window !== "undefined" &&
    typeof Notification !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

function getNotificationPermissionState() {
  if (isAndroidTwaEnvironment()) {
    return String(getState().androidNotificationPermission || "default");
  }
  if (!isPushApiSupported()) {
    return "unsupported";
  }
  return String(Notification.permission || "default");
}

function refreshPushPermissionState() {
  const nextPermission = getNotificationPermissionState();
  if (getState().pushPermission !== nextPermission) {
    setState({
      pushPermission: nextPermission,
    });
  }
  return nextPermission;
}

function getAndroidDeviceTokenFingerprint(status = {}) {
  const installationId = String(status.installationId || "").trim();
  const token = String(status.token || "").trim();
  if (!installationId || !token) {
    return "";
  }
  return `${installationId}:${token}`;
}

async function syncAndroidDeviceTokenWithServer(status = {}) {
  const installationId = String(status.installationId || "").trim();
  const token = String(status.token || "").trim();
  const currentUid = String(getState().uid || "").trim();
  if (!installationId || !token || !currentUid || !api) {
    return;
  }

  const fingerprint = getAndroidDeviceTokenFingerprint(status);
  if (fingerprint && fingerprint === lastSyncedAndroidDeviceTokenFingerprint) {
    return;
  }

  await api.registerAndroidDeviceToken({
    installationId,
    token,
    appVersion: status.appVersion || "",
    appBuild: status.appBuild || "",
    notificationPermission: status.notificationPermission || "default",
    exactAlarmAllowed:
      status.exactAlarmAllowed === null || status.exactAlarmAllowed === undefined
        ? undefined
        : !!status.exactAlarmAllowed,
    device: status.device || undefined,
  });

  lastSyncedAndroidDeviceTokenFingerprint = fingerprint;
  setState({
    androidDeviceTokenReady: true,
  });
}

async function unregisterAndroidDeviceTokenForCurrentUser() {
  const installationId = String(getState().androidInstallationId || "").trim();
  if (!installationId || !api || !getState().uid) {
    return;
  }

  await api.deleteAndroidDeviceToken(installationId).catch(() => undefined);
  lastSyncedAndroidDeviceTokenFingerprint = "";
  setState({
    androidDeviceTokenReady: false,
  });
}

function applyAndroidBridgeStatus(status = {}) {
  const nextFingerprint = getAndroidDeviceTokenFingerprint(status);
  setState({
    clientPlatform: resolveClientPlatform(),
    androidBridgeReady: !!status.bridgeReady,
    androidNotificationPermission: String(status.notificationPermission || "default"),
    androidExactAlarmAllowed:
      status.exactAlarmAllowed === null || status.exactAlarmAllowed === undefined
        ? null
        : !!status.exactAlarmAllowed,
    androidExactAlarmSupported: status.exactAlarmSupported !== false,
    androidExactAlarmPermissionRequested:
      status.exactAlarmAllowed === true
        ? false
        : status.exactAlarmPermissionRequested === true,
    androidInstallationId: String(status.installationId || "").trim(),
    androidAppVersion: String(status.appVersion || "").trim(),
    androidAppBuild: String(status.appBuild || "").trim(),
    androidDeviceInfo: status.device || null,
    androidDeviceTokenReady: !!nextFingerprint,
    pushPermission: isAndroidTwaEnvironment()
      ? String(status.notificationPermission || "default")
      : getState().pushPermission,
  });

  if (nextFingerprint && getState().uid) {
    void syncAndroidDeviceTokenWithServer(status).catch(() => undefined);
  }
  if (status.bridgeReady) {
    void syncNativeRestAlarmState();
  }
}

function getRestAlarmKey(workoutId, endAt) {
  const safeWorkoutId = String(workoutId || "").trim();
  const safeEndAt = Number(endAt);
  if (!safeWorkoutId || !Number.isFinite(safeEndAt) || safeEndAt <= 0) {
    return "";
  }
  return `${safeWorkoutId}:${safeEndAt}`;
}

function getActiveRestAlarmContext(state = getState()) {
  const workoutId = String(state.activeSession?.workoutId || state.activeWorkout?.workoutId || "").trim();
  const rest = state.activeSession?.rest;
  const endAt = Number(rest?.endAt);
  if (!workoutId || !rest?.enabled || !rest?.running || !Number.isFinite(endAt) || endAt <= 0) {
    return null;
  }

  return {
    workoutId,
    rest,
    endAt,
    key: getRestAlarmKey(workoutId, endAt),
  };
}

function shouldHoldScreenWakeLock(state = getState()) {
  return (
    isWakeLockSupported() &&
    state.selectedTab === "session" &&
    !!state.activeSession?.workoutId &&
    state.settings?.restWakeLockEnabled !== false &&
    document.visibilityState === "visible"
  );
}

function ensureRestAlarmFallbackAudio() {
  if (!(restAlarmFallbackAudio instanceof HTMLAudioElement)) {
    const audio = new Audio(REST_TIMER_FALLBACK_AUDIO_DATA_URI);
    audio.preload = "auto";
    audio.playsInline = true;
    restAlarmFallbackAudio = audio;
  }
  return restAlarmFallbackAudio;
}

function primeRestAlarmAudio() {
  if (restAlarmPrimed) {
    return;
  }
  restAlarmPrimed = true;

  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  if (AudioContextCtor) {
    try {
      if (!restAlarmAudioContext) {
        restAlarmAudioContext = new AudioContextCtor();
      }
      restAlarmAudioContext.resume().catch(() => undefined);
    } catch (_) {
      // Ignore priming failures on restricted browsers.
    }
  }

  try {
    const audio = ensureRestAlarmFallbackAudio();
    audio.muted = true;
    const playPromise = audio.play();
    if (playPromise?.then) {
      playPromise
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.muted = false;
        })
        .catch(() => {
          audio.muted = false;
        });
    } else {
      audio.muted = false;
    }
  } catch (_) {
    // Ignore fallback priming failures.
  }
}

function playRestTimerFallbackAudio() {
  const alarmEnabled = getState().settings?.alarmEnabled ?? true;
  if (!alarmEnabled) {
    return false;
  }

  try {
    const template = ensureRestAlarmFallbackAudio();
    const audio = template.cloneNode(true);
    if (audio instanceof HTMLAudioElement) {
      audio.currentTime = 0;
      audio.play().catch(() => undefined);
      return true;
    }
  } catch (_) {
    // Ignore fallback playback failures.
  }

  return false;
}

function computeWakeLockStatus(state = getState()) {
  if (!isWakeLockSupported()) {
    return {
      label: "미지원",
      tone: "disabled",
    };
  }

  if (state.settings?.restWakeLockEnabled === false) {
    return {
      label: "OFF",
      tone: "disabled",
    };
  }

  if (!state.activeSession?.workoutId || state.selectedTab !== "session") {
    return {
      label: "준비됨",
      tone: "idle",
    };
  }

  if (document.visibilityState !== "visible") {
    return {
      label: "준비됨",
      tone: "warning",
    };
  }

  return {
    label: "준비됨",
    tone: state.wakeLockActive ? "active" : "warning",
  };
}

function isAndroidTwaBridgeReady(state = getState()) {
  return state?.androidBridgeReady === true || androidTwaAdapter?.isReady?.() === true;
}

function isAndroidTwaRestNotificationReady(state = getState()) {
  if (!isAndroidTwaBridgeReady(state)) {
    return false;
  }

  if (state.androidNotificationPermission !== "granted") {
    return false;
  }

  if (state.androidExactAlarmSupported !== false && state.androidExactAlarmAllowed !== true) {
    return false;
  }

  return true;
}

function computeRestNotificationStatus(state = getState()) {
  const notificationsEnabled = state.settings?.restNotificationsEnabled !== false;
  if (!notificationsEnabled) {
    return {
      label: "OFF",
      tone: "disabled",
      action: "",
      actionMode: "",
    };
  }

  if (isAndroidTwaEnvironment()) {
    if (!isAndroidTwaBridgeReady(state)) {
      return {
        label: "알림 준비 중",
        tone: "warning",
        action: "",
        actionMode: "",
        detail: "기기 상태를 확인하고 있어요.",
      };
    }

    if (state.androidNotificationPermission === "denied") {
      return {
        label: "권한 차단됨",
        tone: "blocked",
        action: "permission",
        actionMode: "",
        detail: "알림 권한을 허용해 주세요.",
      };
    }

    if (state.androidNotificationPermission !== "granted") {
      return {
        label: "알림 권한 필요",
        tone: "warning",
        action: "permission",
        actionMode: "",
        detail: "알림 권한을 허용해 주세요.",
      };
    }

    if (state.androidExactAlarmSupported !== false && state.androidExactAlarmAllowed === false) {
      if (state.androidExactAlarmPermissionRequested) {
        return {
          label: "설정 반영 확인 중",
          tone: "warning",
          action: "exact-alarm",
          actionMode: "",
          detail: "기기 설정을 확인하고 있어요.",
        };
      }

      return {
        label: "기기 알림 설정 필요",
        tone: "warning",
        action: "exact-alarm",
        actionMode: "",
        detail: "기기 설정을 켜 주세요.",
      };
    }

    if (isAndroidTwaRestNotificationReady(state)) {
      return {
        label: "준비됨",
        tone: "ready",
        action: "",
        actionMode: "",
        detail: "",
      };
    }

    return {
      label: "알림 준비 중",
      tone: "warning",
      action: "",
      actionMode: "",
      detail: "기기 상태를 다시 확인하고 있어요.",
    };
  }

  if (!state.pushConfig?.supported || !isPushApiSupported()) {
    return {
      label: "미지원",
      tone: "blocked",
      action: "",
      actionMode: "",
      detail: "이 환경에서는 알림을 사용할 수 없습니다.",
    };
  }

  if (!isInstalledPwaEnvironment()) {
    const installPrompt = getPwaInstallPromptConfig();
    return {
      label: "설치 필요",
      tone: "warning",
      action: "install",
      actionMode: String(installPrompt?.actionMode || "browser-guide"),
      detail: "앱을 설치하면 알림을 받을 수 있습니다.",
    };
  }

  if (state.pushPermission === "denied") {
    return {
      label: "권한 차단됨",
      tone: "blocked",
      action: "",
      actionMode: "",
      detail: "알림 권한을 다시 허용해 주세요.",
    };
  }

  if (state.pushPermission !== "granted") {
    return {
      label: "권한 필요",
      tone: "warning",
      action: "permission",
      actionMode: "",
      detail: "알림 권한을 허용해 주세요.",
    };
  }

  if (state.pushSubscriptionReady) {
    return {
      label: "준비됨",
      tone: "ready",
      action: "",
      actionMode: "",
      detail: "알림을 받을 준비가 되었어요.",
    };
  }

  return {
    label: "연결 중",
    tone: "warning",
    action: "permission",
    actionMode: "",
    detail: "알림을 연결하는 중입니다.",
  };
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replaceAll("-", "+").replaceAll("_", "/");
  const rawData = window.atob(base64);
  return Uint8Array.from(rawData, (char) => char.charCodeAt(0));
}

function serializePushSubscription(subscription) {
  const json = subscription?.toJSON?.() || {};
  return {
    endpoint: String(subscription?.endpoint || json.endpoint || ""),
    expirationTime:
      json.expirationTime === undefined
        ? (subscription?.expirationTime ?? null)
        : json.expirationTime,
    keys: {
      p256dh: String(json?.keys?.p256dh || ""),
      auth: String(json?.keys?.auth || ""),
    },
  };
}

function createPushSubscriptionFingerprint(subscription) {
  const serialized = serializePushSubscription(subscription);
  if (!serialized.endpoint) {
    return "";
  }

  return JSON.stringify({
    endpoint: serialized.endpoint,
    keys: serialized.keys,
  });
}

function readLocalStorageValue(key) {
  try {
    return String(localStorage.getItem(key) || "").trim();
  } catch (_) {
    return "";
  }
}

function writeLocalStorageValue(key, value) {
  try {
    if (!value) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, String(value));
  } catch (_) {
    // Ignore storage failures.
  }
}

function getStoredPushSubscriptionId() {
  return readLocalStorageValue(PUSH_SUBSCRIPTION_ID_STORAGE_KEY);
}

function storePushSubscriptionSyncState(subscriptionId, fingerprint, uid) {
  writeLocalStorageValue(PUSH_SUBSCRIPTION_ID_STORAGE_KEY, subscriptionId);
  writeLocalStorageValue(PUSH_SUBSCRIPTION_FINGERPRINT_STORAGE_KEY, fingerprint);
  writeLocalStorageValue(PUSH_SUBSCRIPTION_UID_STORAGE_KEY, uid);
}

function clearStoredPushSubscriptionSyncState() {
  writeLocalStorageValue(PUSH_SUBSCRIPTION_ID_STORAGE_KEY, "");
  writeLocalStorageValue(PUSH_SUBSCRIPTION_FINGERPRINT_STORAGE_KEY, "");
  writeLocalStorageValue(PUSH_SUBSCRIPTION_UID_STORAGE_KEY, "");
}

async function getReadyServiceWorkerRegistration() {
  if (!isPushApiSupported()) {
    return null;
  }
  if (serviceWorkerRegistration) {
    return serviceWorkerRegistration;
  }

  try {
    serviceWorkerRegistration = await navigator.serviceWorker.ready;
  } catch (_) {
    serviceWorkerRegistration = null;
  }

  return serviceWorkerRegistration;
}

async function unregisterRestPushSubscription(options = {}) {
  const registration = await getReadyServiceWorkerRegistration();
  const subscription = registration?.pushManager
    ? await registration.pushManager.getSubscription().catch(() => null)
    : null;
  const canUseApi = !!getState().uid && !!auth?.currentUser && !!api;
  const subscriptionId = String(getState().pushSubscriptionId || getStoredPushSubscriptionId() || "").trim();

  if (subscriptionId && canUseApi) {
    await api.deletePushSubscription(subscriptionId).catch(() => undefined);
  }

  if (subscription) {
    await subscription.unsubscribe().catch(() => undefined);
  }

  setState({
    pushSubscriptionId: "",
    pushSubscriptionReady: false,
  });
  clearStoredPushSubscriptionSyncState();

  if (!options.silent) {
    refreshPushPermissionState();
  }
}

async function ensureRestPushSubscription(options = {}) {
  if (pushSubscriptionSyncPromise) {
    return pushSubscriptionSyncPromise;
  }

  pushSubscriptionSyncPromise = (async () => {
    let permission = refreshPushPermissionState();
    try {
      const state = getState();
      const notificationsEnabled = state.settings?.restNotificationsEnabled !== false;
      if (!notificationsEnabled) {
        await unregisterRestPushSubscription({ silent: true });
        return {
          ready: false,
          reason: "disabled",
        };
      }

      if (!state.pushConfig?.supported || !isPushApiSupported()) {
        setState({
          pushSubscriptionId: "",
          pushSubscriptionReady: false,
        });
        return {
          ready: false,
          reason: "unsupported",
        };
      }

      if (permission === "default" && options.promptPermission) {
        permission = await Notification.requestPermission().catch(() => permission);
        setState({
          pushPermission: permission,
        });
      }

      if (permission !== "granted") {
        setState({
          pushSubscriptionId: "",
          pushSubscriptionReady: false,
        });
        return {
          ready: false,
          reason: permission,
        };
      }

      if (!isInstalledPwaEnvironment()) {
        setState({
          pushSubscriptionId: "",
          pushSubscriptionReady: false,
        });
        return {
          ready: false,
          reason: "not-installed",
        };
      }

      const registration = await getReadyServiceWorkerRegistration();
      if (!registration?.pushManager) {
        setState({
          pushSubscriptionId: "",
          pushSubscriptionReady: false,
        });
        return {
          ready: false,
          reason: "no-registration",
        };
      }

      let subscription = await registration.pushManager.getSubscription();
      if (options.forceResubscribe && subscription) {
        await subscription.unsubscribe().catch(() => undefined);
        subscription = null;
      }

      if (!subscription) {
        const vapidPublicKey = String(state.pushConfig?.vapidPublicKey || "").trim();
        if (!vapidPublicKey) {
          setState({
            pushSubscriptionId: "",
            pushSubscriptionReady: false,
          });
          return {
            ready: false,
            reason: "missing-vapid-key",
          };
        }

        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        });
      }

      const serializedSubscription = serializePushSubscription(subscription);
      const fingerprint = createPushSubscriptionFingerprint(subscription);
      const currentUid = String(getState().uid || "").trim();
      const result = await api.registerPushSubscription(serializedSubscription);
      const nextSubscriptionId = String(result?.subscriptionId || "").trim();
      if (!nextSubscriptionId) {
        throw new Error("푸시 구독 동기화에 실패했습니다.");
      }

      storePushSubscriptionSyncState(nextSubscriptionId, fingerprint, currentUid);
      setState({
        pushPermission: permission,
        pushSubscriptionId: nextSubscriptionId,
        pushSubscriptionReady: true,
      });

      return {
        ready: true,
        subscriptionId: nextSubscriptionId,
      };
    } catch (error) {
      setState({
        pushPermission: permission,
        pushSubscriptionReady: false,
      });
      throw error;
    }
  })()
    .finally(() => {
      pushSubscriptionSyncPromise = null;
    });

  return pushSubscriptionSyncPromise;
}

async function syncRestPushAvailability(options = {}) {
  refreshPushPermissionState();

  if (isAndroidTwaEnvironment()) {
    const bridgeReady = await refreshAndroidTwaBridgeStatus({ timeoutMs: 0 }).catch(() => false);
    if (!bridgeReady) {
      void ensureAndroidTwaBridgeStatusEventually().catch(() => undefined);
    }
    setState({
      pushSubscriptionReady: false,
      pushSubscriptionId: "",
    });
    return {
      ready: isAndroidTwaRestNotificationReady(getState()),
      reason: "android-twa",
    };
  }

  const state = getState();
  const shouldRegister =
    state.settings?.restNotificationsEnabled !== false &&
    !!state.pushConfig?.supported &&
    isInstalledPwaEnvironment() &&
    getNotificationPermissionState() === "granted";

  if (!shouldRegister) {
    if (
      state.pushSubscriptionReady &&
      (
        state.settings?.restNotificationsEnabled === false ||
        !isInstalledPwaEnvironment() ||
        !state.pushConfig?.supported
      )
    ) {
      await unregisterRestPushSubscription({ silent: true });
    } else {
      setState({
        pushSubscriptionReady: false,
        pushSubscriptionId: shouldRegister ? state.pushSubscriptionId : "",
      });
    }
    return {
      ready: false,
    };
  }

  return ensureRestPushSubscription({
    promptPermission: false,
    forceResubscribe: false,
    ...options,
  }).catch((error) => {
    setState({
      pushSubscriptionReady: false,
    });
    if (!options.silent) {
      throw error;
    }
    return {
      ready: false,
      error,
    };
  });
}

async function releaseScreenWakeLock() {
  const sentinel = screenWakeLockSentinel;
  screenWakeLockSentinel = null;
  if (sentinel) {
    try {
      await sentinel.release();
    } catch (_) {
      // Ignore release failures from already released sentinels.
    }
  }
  if (getState().wakeLockActive) {
    setState({
      wakeLockActive: false,
    });
  }
}

async function syncScreenWakeLock() {
  if (wakeLockSyncPromise) {
    return wakeLockSyncPromise;
  }

  wakeLockSyncPromise = (async () => {
    if (!shouldHoldScreenWakeLock()) {
      await releaseScreenWakeLock();
      return false;
    }

    if (screenWakeLockSentinel) {
      if (!getState().wakeLockActive) {
        setState({
          wakeLockActive: true,
        });
      }
      return true;
    }

    try {
      const sentinel = await navigator.wakeLock.request("screen");
      screenWakeLockSentinel = sentinel;
      sentinel.addEventListener("release", () => {
        if (screenWakeLockSentinel === sentinel) {
          screenWakeLockSentinel = null;
        }
        if (getState().wakeLockActive) {
          setState({
            wakeLockActive: false,
          });
        }
        if (shouldHoldScreenWakeLock()) {
          void syncScreenWakeLock();
        }
      }, { once: true });
      setState({
        wakeLockActive: true,
      });
      return true;
    } catch (_) {
      setState({
        wakeLockActive: false,
      });
      return false;
    }
  })().finally(() => {
    wakeLockSyncPromise = null;
  });

  return wakeLockSyncPromise;
}

function triggerRestAlarmIfNeeded(context, options = {}) {
  if (!context?.key) {
    return false;
  }
  if (!options.force && context.endAt > Date.now()) {
    return false;
  }
  if (restAlarmPlayedForEndAt === context.key) {
    return false;
  }

  restAlarmPlayedForEndAt = context.key;
  triggerRestTimerVibration();
  playRestTimerAlarm();
  return true;
}

function buildNativeRestAlarmPayload(context) {
  return {
    workoutId: String(context?.workoutId || "").trim(),
    endAt: Number(context?.endAt) || 0,
    title: "휴식이 끝났습니다",
    body: "다음 세트를 진행할 시간입니다.",
    notificationUrl: "/?notification=rest&source=android_notification",
    soundEnabled: getState().settings?.alarmEnabled !== false,
    vibrationEnabled: getState().settings?.vibrationEnabled !== false,
  };
}

async function syncNativeRestAlarmState(state = getState()) {
  if (!isAndroidTwaEnvironment() || !androidTwaAdapter?.isReady?.()) {
    return;
  }

  const context = getActiveRestAlarmContext(state);
  if (!context || state.settings?.restNotificationsEnabled === false) {
    const previousContext = lastNativeRestAlarmContext;
    if (!previousContext) {
      return;
    }
    lastNativeRestAlarmContext = null;
    await androidTwaAdapter.cancelRestAlarm({
      workoutId: previousContext.workoutId,
      endAt: previousContext.endAt,
    }).catch(() => undefined);
    return;
  }

  if (lastNativeRestAlarmContext?.key === context.key) {
    return;
  }

  const previousContext = lastNativeRestAlarmContext;
  const scheduleResult = await androidTwaAdapter.scheduleRestAlarm(buildNativeRestAlarmPayload(context)).catch(() => null);
  if (scheduleResult?.scheduled !== true) {
    return;
  }

  if (previousContext && previousContext.key !== context.key) {
    await androidTwaAdapter.cancelRestAlarm({
      workoutId: previousContext.workoutId,
      endAt: previousContext.endAt,
    }).catch(() => undefined);
  }

  lastNativeRestAlarmContext = {
    workoutId: context.workoutId,
    endAt: context.endAt,
    key: context.key,
  };
}

async function handlePwaInstallAction(actionMode = "prompt") {
  const promptEvent = deferredInstallPromptEvent;
  if (!promptEvent || actionMode !== "prompt") {
    openPwaInstallGuide(actionMode);
    refreshPwaInstallPromptVisibility();
    return {
      prompted: false,
      accepted: false,
    };
  }

  deferredInstallPromptEvent = null;
  await promptEvent.prompt();
  const choiceResult = await promptEvent.userChoice.catch(() => null);
  const accepted = choiceResult?.outcome === "accepted";

  if (accepted) {
    hidePwaInstallPrompt();
  } else {
    snoozePwaInstallPrompt();
    hidePwaInstallPrompt();
    refreshPwaInstallPromptVisibility();
  }

  return {
    prompted: true,
    accepted,
  };
}

function setGlobalLoadingProgress(value) {
  const progressEl = els.globalLoadingProgress;
  if (!(progressEl instanceof HTMLElement)) {
    return;
  }
  const clamped = Math.max(0, Math.min(1, value));
  progressEl.style.width = `${(clamped * 100).toFixed(2)}%`;
}

function bindGlobalLoadingEvents() {
  window.addEventListener("app:network-loading", (event) => {
    if (!(event instanceof CustomEvent)) {
      return;
    }
    applyGlobalLoadingDelta(event.detail?.delta);
  });
}

function beginGlobalLoadingVisual() {
  const barEl = els.globalLoadingBar;
  if (!(barEl instanceof HTMLElement)) {
    return;
  }

  if (uiLoadingHideTimer) {
    clearTimeout(uiLoadingHideTimer);
    uiLoadingHideTimer = null;
  }
  if (uiLoadingResetTimer) {
    clearTimeout(uiLoadingResetTimer);
    uiLoadingResetTimer = null;
  }
  if (uiLoadingTicker) {
    clearInterval(uiLoadingTicker);
    uiLoadingTicker = null;
  }

  uiLoadingProgressValue = Math.max(uiLoadingProgressValue, 0.08);
  setGlobalLoadingProgress(uiLoadingProgressValue);
  barEl.classList.add("active");

  uiLoadingTicker = setInterval(() => {
    const next = uiLoadingProgressValue + (0.92 - uiLoadingProgressValue) * 0.1;
    // Never move the bar backwards while requests are active.
    uiLoadingProgressValue = Math.max(uiLoadingProgressValue, next);
    setGlobalLoadingProgress(uiLoadingProgressValue);
  }, 120);
}

function endGlobalLoadingVisual() {
  const barEl = els.globalLoadingBar;
  if (!(barEl instanceof HTMLElement)) {
    return;
  }

  if (uiLoadingTicker) {
    clearInterval(uiLoadingTicker);
    uiLoadingTicker = null;
  }

  uiLoadingProgressValue = 1;
  setGlobalLoadingProgress(uiLoadingProgressValue);

  uiLoadingHideTimer = setTimeout(() => {
    barEl.classList.remove("active");
    uiLoadingHideTimer = null;

    // Reset width only after fade-out completes to avoid visible "rollback".
    uiLoadingResetTimer = setTimeout(() => {
      if (uiPendingLoadingCount > 0) {
        uiLoadingResetTimer = null;
        return;
      }
      uiLoadingProgressValue = 0;
      setGlobalLoadingProgress(0);
      uiLoadingResetTimer = null;
    }, 200);
  }, 260);
}

function applyGlobalLoadingDelta(delta) {
  const safeDelta = Number(delta) || 0;
  if (safeDelta === 0) {
    return;
  }

  const prev = uiPendingLoadingCount;
  uiPendingLoadingCount = Math.max(0, uiPendingLoadingCount + safeDelta);

  if (prev === 0 && uiPendingLoadingCount > 0) {
    beginGlobalLoadingVisual();
  }
  if (prev > 0 && uiPendingLoadingCount === 0) {
    endGlobalLoadingVisual();
  }
}

async function withGlobalLoading(task) {
  applyGlobalLoadingDelta(1);
  try {
    return await task();
  } finally {
    applyGlobalLoadingDelta(-1);
  }
}

function createBuilderItem() {
  return {
    exerciseId: "",
    exerciseDisplayName: "",
    exerciseQuery: "",
    exerciseSearchResults: [],
    pickerOpen: false,
    sets: "3",
    restSec: "90",
    loadType: "none",
    percent: "",
    rmBase: "bench",
    repsMode: "none",
    fixedReps: "",
    rangeMin: "",
    rangeMax: "",
  };
}

function createBuilderDay(index) {
  return {
    alias: `Day ${index + 1}`,
    weekday: "",
    items: [createBuilderItem()],
  };
}

function createDefaultProgramBuilder(lengthDays = 4) {
  return {
    targetProgramId: "",
    name: "",
    cycleType: "sequence",
    lengthDays: String(lengthDays),
    isActive: true,
    trial: 0,
    days: Array.from({ length: lengthDays }, (_, index) => createBuilderDay(index)),
  };
}

function syncBuilderDayLength(builder) {
  const numericLength = Math.max(1, Math.min(MAX_PROGRAM_LENGTH_DAYS, Number(builder.lengthDays) || 1));
  builder.cycleType = "sequence";
  builder.lengthDays = String(numericLength);

  while (builder.days.length < numericLength) {
    builder.days.push(createBuilderDay(builder.days.length));
  }
  if (builder.days.length > numericLength) {
    builder.days = builder.days.slice(0, numericLength);
  }
}

function clampProgramBuilderDayIndex(index, builder) {
  const max = Math.max(0, (builder?.days?.length || 1) - 1);
  const parsed = Number(index);
  if (!Number.isInteger(parsed) || parsed < 0) {
    return 0;
  }
  return Math.min(parsed, max);
}

function ensureProgramBuilder() {
  const current = getState().programBuilder;
  if (current) {
    return current;
  }
  const next = createDefaultProgramBuilder();
  setState({
    programBuilder: next,
    programBuilderDayIndex: 0,
  });
  return next;
}

function normalizeProgramBuilderForDirtyCheck(builderLike) {
  const normalized = builderLike ? deepClone(builderLike) : createDefaultProgramBuilder();
  if (!Array.isArray(normalized.days)) {
    normalized.days = [];
  }
  syncBuilderDayLength(normalized);
  normalized.cycleType = "sequence";
  normalized.lengthDays = String(Math.max(1, Math.min(MAX_PROGRAM_LENGTH_DAYS, Number(normalized.lengthDays) || 1)));
  normalized.targetProgramId = String(normalized.targetProgramId || "").trim();
  normalized.name = String(normalized.name || "");
  normalized.isActive = !!normalized.isActive;
  normalized.trial = Math.max(0, Number(normalized.trial) || 0);
  normalized.days = normalized.days.map((day, dayIndex) => ({
    alias: String(day?.alias || `Day ${dayIndex + 1}`),
    items: (Array.isArray(day?.items) ? day.items : [createBuilderItem()]).map((item) => ({
      exerciseId: String(item?.exerciseId || "").trim(),
      sets: String(item?.sets || "3"),
      restSec: String(item?.restSec || "90"),
      loadType: item?.loadType === "percent1RM" ? "percent1RM" : "none",
      percent: String(item?.percent || ""),
      rmBase: item?.rmBase === "squat" || item?.rmBase === "deadlift" ? item.rmBase : "bench",
      repsMode:
        item?.repsMode === "fixed" || item?.repsMode === "range"
          ? item.repsMode
          : "none",
      fixedReps: String(item?.fixedReps || ""),
      rangeMin: String(item?.rangeMin || ""),
      rangeMax: String(item?.rangeMax || ""),
    })),
  }));
  return normalized;
}

function createProgramBuilderBaseline(builderLike) {
  return normalizeProgramBuilderForDirtyCheck(builderLike);
}

function setProgramBuilderBaseline(builderLike) {
  setState({
    programBuilderBaseline: createProgramBuilderBaseline(builderLike),
  });
}

function clearProgramBuilderBaseline() {
  if (getState().programBuilderBaseline === null) {
    return;
  }
  setState({
    programBuilderBaseline: null,
  });
}

function isProgramBuilderDirty() {
  const state = getState();
  if (String(state.selectedTab || "home") !== "programs" || String(state.programBuilderStage || "list") === "list") {
    return false;
  }
  if (!state.programBuilderBaseline) {
    return false;
  }
  syncProgramBuilderFromDom();
  return JSON.stringify(createProgramBuilderBaseline(getState().programBuilder)) !== JSON.stringify(state.programBuilderBaseline);
}

function isProgramBuilderLengthDaysReady(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return false;
  }
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= MAX_PROGRAM_LENGTH_DAYS;
}

function isProgramBuilderInfoReady(builderLike) {
  const nameReady = String(builderLike?.name || "").trim().length > 0;
  const lengthDaysReady = isProgramBuilderLengthDaysReady(builderLike?.lengthDays);
  return nameReady && lengthDaysReady;
}

function readProgramBuilderInfoDraftFromDom() {
  const nameInput = document.querySelector('[data-builder-root="name"]');
  const lengthDaysInput = document.querySelector('[data-builder-root="lengthDays"]');
  if (!(nameInput instanceof HTMLInputElement) || !(lengthDaysInput instanceof HTMLInputElement)) {
    return null;
  }
  return {
    name: nameInput.value,
    lengthDays: lengthDaysInput.value,
  };
}

function syncProgramBuilderInfoStepUi() {
  const goDayBtn = document.getElementById("programBuilderGoDayBtn");
  if (!(goDayBtn instanceof HTMLButtonElement)) {
    return;
  }

  const draft = readProgramBuilderInfoDraftFromDom();
  const ready = draft ? isProgramBuilderInfoReady(draft) : isProgramBuilderInfoReady(getState().programBuilder);

  goDayBtn.disabled = !ready;
  if (ready) {
    goDayBtn.removeAttribute("title");
  } else {
    goDayBtn.title = "프로그램명과 루틴 길이를 먼저 입력하세요.";
  }
}

function areProgramBuilderValuesEqual(left, right) {
  try {
    return JSON.stringify(left || null) === JSON.stringify(right || null);
  } catch (_) {
    return false;
  }
}

function commitProgramBuilderState(nextBuilder, nextDayIndex, options = {}) {
  const currentDayIndex = getState().programBuilderDayIndex ?? 0;
  const currentBuilder = getState().programBuilder;
  if (areProgramBuilderValuesEqual(currentBuilder, nextBuilder) && currentDayIndex === nextDayIndex) {
    return false;
  }

  if (options.notify === false) {
    mutateState((state) => {
      state.programBuilder = nextBuilder;
      state.programBuilderDayIndex = nextDayIndex;
    });
    return true;
  }

  setState({
    programBuilder: nextBuilder,
    programBuilderDayIndex: nextDayIndex,
  });
  return true;
}

function updateProgramBuilder(mutator, options = {}) {
  const currentDayIndex = getState().programBuilderDayIndex ?? 0;
  const next = deepClone(ensureProgramBuilder());
  mutator(next);
  if (options.normalize !== false) {
    syncBuilderDayLength(next);
  }
  commitProgramBuilderState(next, clampProgramBuilderDayIndex(currentDayIndex, next), options);
  return next;
}

function collectExerciseIdsFromBuilder(builder) {
  const set = new Set();
  for (const day of builder?.days || []) {
    for (const item of day?.items || []) {
      const exerciseId = String(item?.exerciseId || "").trim();
      if (exerciseId) {
        set.add(exerciseId);
      }
    }
  }
  return Array.from(set);
}

async function fetchExerciseMapByIds(exerciseIds) {
  const safeExerciseIds = Array.from(
    new Set(
      (exerciseIds || [])
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  );
  if (safeExerciseIds.length === 0) {
    return {};
  }

  try {
    const result = await api.getExercises(safeExerciseIds);
    if (result?.items && typeof result.items === "object") {
      return result.items;
    }
  } catch (_) {
    // Fall through to per-item lookup to avoid blocking the UI on partial rollout failures.
  }

  const entries = await Promise.all(
    safeExerciseIds.map(async (exerciseId) => {
      try {
        const result = await api.getExercise(exerciseId);
        return [exerciseId, result?.exercise || null];
      } catch (_) {
        return [exerciseId, null];
      }
    }),
  );

  return entries.reduce((acc, [exerciseId, exercise]) => {
    acc[exerciseId] = exercise;
    return acc;
  }, {});
}

async function resolveExerciseNameMap(exerciseIds, baseMap = {}) {
  const nextMap = { ...(baseMap || {}) };
  const missingIds = exerciseIds.filter((exerciseId) => !nextMap[exerciseId]);

  if (missingIds.length === 0) {
    return nextMap;
  }

  const exerciseMap = await fetchExerciseMapByIds(missingIds);
  missingIds.forEach((exerciseId) => {
    const exercise = exerciseMap[exerciseId];
    const name = String(exercise?.nameKo || exercise?.nameEn || "").trim();
    if (name) {
      nextMap[exerciseId] = name;
    }
  });

  return nextMap;
}

function buildProgramPayloadFromBuilder(builder) {
  const name = String(builder.name || "").trim();
  if (!name) {
    throw new Error("프로그램명을 입력하세요");
  }

  const cycleType = "sequence";
  const lengthDays = Math.max(1, Math.min(MAX_PROGRAM_LENGTH_DAYS, Number(builder.lengthDays) || builder.days.length || 1));

  const dayAliases = {};
  const days = {};

  for (let dayIndex = 0; dayIndex < lengthDays; dayIndex += 1) {
    const day = builder.days[dayIndex] || createBuilderDay(dayIndex);
    dayAliases[String(dayIndex)] = String(day.alias || `Day ${dayIndex + 1}`).trim();
    const itemEntries = (day.items || [])
      .filter((item) => String(item.exerciseId || "").trim().length > 0)
      .map((item) => {
        const sets = Number(item.sets);
        if (!Number.isInteger(sets) || sets < 1 || sets > MAX_SETS_PER_EXERCISE) {
          throw new Error(`Day ${dayIndex + 1}의 sets 값이 올바르지 않습니다`);
        }

        const payloadItem = {
          exerciseId: String(item.exerciseId).trim(),
          sets,
          restSec: undefined,
          loadRule: null,
          repsRule: null,
        };

        const restSec = Number(item.restSec);
        if (!Number.isInteger(restSec) || restSec < MIN_REST_SEC || restSec > MAX_REST_SEC) {
          throw new Error(`Day ${dayIndex + 1}의 휴식 시간 값이 올바르지 않습니다`);
        }
        payloadItem.restSec = restSec;

        if (item.loadType === "percent1RM") {
          const percent = Number(item.percent);
          if (!Number.isFinite(percent) || percent <= 0 || percent > MAX_PERCENT_1RM) {
            throw new Error(`Day ${dayIndex + 1}의 %1RM 값이 올바르지 않습니다`);
          }
          const rmBase = ["squat", "bench", "deadlift"].includes(item.rmBase) ? item.rmBase : "bench";
          payloadItem.loadRule = {
            type: "percent1RM",
            percent,
            rmBase,
          };
        }

        if (item.repsMode === "fixed") {
          const reps = Number(item.fixedReps);
          if (!Number.isInteger(reps) || reps < 1 || reps > 100) {
            throw new Error(`Day ${dayIndex + 1}의 고정 reps 값이 올바르지 않습니다`);
          }
          payloadItem.repsRule = {
            type: "fixed",
            reps,
          };
        }

        if (item.repsMode === "range") {
          const min = Number(item.rangeMin);
          const max = Number(item.rangeMax);
          if (!Number.isInteger(min) || !Number.isInteger(max) || min < 1 || max < min || max > 100) {
            throw new Error(`Day ${dayIndex + 1}의 reps 범위 값이 올바르지 않습니다`);
          }
          payloadItem.repsRule = {
            type: "range",
            min,
            max,
          };
        }

        return payloadItem;
      });

    if (itemEntries.length === 0) {
      throw new Error(`Day ${dayIndex + 1}에 운동을 최소 1개 이상 입력하세요`);
    }

    days[String(dayIndex)] = {
      items: itemEntries.reduce((acc, item, itemIndex) => {
        acc[String(itemIndex)] = item;
        return acc;
      }, {}),
    };

  }

  return {
    schemaVersion: 1,
    name,
    isActive: !!builder.isActive,
    cycle: {
      type: cycleType,
      lengthDays,
    },
    trial: Math.max(0, Number(builder.trial) || 0),
    dayAliases,
    fixedWeekdayMap: null,
    days,
  };
}

function resolveProgramLengthDays(program) {
  return Math.max(1, Number(program?.cycle?.lengthDays) || Object.keys(program?.days || {}).length || 1);
}

function resolveProgramTodayDayIndex(program) {
  const lengthDays = resolveProgramLengthDays(program);
  if (program?.cycle?.type === "fixedWeekday") {
    const weekdayKey = WEEKDAY_NAMES[new Date().getDay()];
    const mapped = Number(program?.fixedWeekdayMap?.[weekdayKey]);
    if (Number.isInteger(mapped) && mapped >= 0 && mapped < lengthDays) {
      return mapped;
    }
  }
  const trial = Math.max(0, Number(program?.trial) || 0);
  return trial % lengthDays;
}

function getMissingOneRMBasesForProgramStart(program, oneRM, preferredDayIndex) {
  if (!program?.days) {
    return [];
  }

  const lengthDays = resolveProgramLengthDays(program);
  const hasPreferredDayIndex = Number.isInteger(preferredDayIndex);
  const safePreferredDayIndex = hasPreferredDayIndex
    ? Math.min(Math.max(0, Number(preferredDayIndex)), lengthDays - 1)
    : resolveProgramTodayDayIndex(program);
  const dayIndex = safePreferredDayIndex;
  const day = program.days?.[String(dayIndex)];
  if (!day) {
    return [];
  }

  const missing = new Set();
  Object.values(day.items || {}).forEach((item) => {
    const loadRule = item?.loadRule;
    if (!loadRule || loadRule.type !== "percent1RM") {
      return;
    }

    const rmBase = ["squat", "bench", "deadlift"].includes(loadRule.rmBase)
      ? loadRule.rmBase
      : "bench";
    const rmValue = Number(oneRM?.[rmBase] ?? 0);
    if (!Number.isFinite(rmValue) || rmValue <= 0) {
      missing.add(rmBase);
    }
  });

  return Array.from(missing);
}

function isProgramDayPickerOpen() {
  return els.programDayPickerModal instanceof HTMLElement && !els.programDayPickerModal.classList.contains("hidden");
}

function isProgramDayImportOpen() {
  return els.programDayImportModal instanceof HTMLElement && !els.programDayImportModal.classList.contains("hidden");
}

function isAnalyticsSearchModalOpen() {
  return els.analyticsSearchModal instanceof HTMLElement && !els.analyticsSearchModal.classList.contains("hidden");
}

function isProgramTemplateSearchModalOpen() {
  return els.programTemplateSearchModal instanceof HTMLElement
    && !els.programTemplateSearchModal.classList.contains("hidden");
}

function isCustomMusclePickerModalOpen() {
  return els.customMusclePickerModal instanceof HTMLElement && !els.customMusclePickerModal.classList.contains("hidden");
}

function isProgramItemRuleModalOpen() {
  return els.programItemRuleModal instanceof HTMLElement && !els.programItemRuleModal.classList.contains("hidden");
}

function captureWindowScrollPosition() {
  return {
    scrollX: Number(window.scrollX || window.pageXOffset || 0),
    scrollY: Number(window.scrollY || window.pageYOffset || 0),
  };
}

function restoreWindowScrollPosition(position) {
  if (!position) {
    return;
  }

  window.scrollTo({
    left: position.scrollX,
    top: position.scrollY,
    behavior: "auto",
  });
}

function captureModalInteractionState() {
  return {
    focusTarget: document.activeElement instanceof HTMLElement ? document.activeElement : null,
    ...captureWindowScrollPosition(),
  };
}

function focusElementWithoutScroll(target, options = {}) {
  if (!(target instanceof HTMLElement)) {
    return;
  }

  const scrollPosition = captureWindowScrollPosition();
  try {
    target.focus({ preventScroll: true });
  } catch {
    try {
      target.focus();
    } catch {
      // Ignore detached-node focus failures.
    }
  }

  if (options.select === true && target instanceof HTMLInputElement) {
    try {
      target.select();
    } catch {
      // Ignore detached-node selection failures.
    }
  }

  const restoreScroll = () => {
    const currentScrollX = Number(window.scrollX || window.pageXOffset || 0);
    const currentScrollY = Number(window.scrollY || window.pageYOffset || 0);
    if (currentScrollX !== scrollPosition.scrollX || currentScrollY !== scrollPosition.scrollY) {
      restoreWindowScrollPosition(scrollPosition);
    }
  };

  restoreScroll();
  window.requestAnimationFrame(restoreScroll);
}

function scheduleFocusWithoutScroll(targetOrResolver, options = {}) {
  const delay = Math.max(0, Number(options.delay || 0));
  window.setTimeout(() => {
    const target = typeof targetOrResolver === "function" ? targetOrResolver() : targetOrResolver;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    focusElementWithoutScroll(target, options);
  }, delay);
}

function restoreModalInteractionState(state, options = {}) {
  if (!state) {
    return;
  }

  const shouldRestoreFocus = options.restoreFocus !== false;
  const shouldRestoreScroll = options.restoreScroll !== false;

  window.setTimeout(() => {
    if (shouldRestoreFocus) {
      focusElementWithoutScroll(state.focusTarget);
    }
    if (shouldRestoreScroll) {
      window.requestAnimationFrame(() => {
        restoreWindowScrollPosition(state);
      });
    }
  }, 0);
}

function getModalInteractionState(modal, options = {}) {
  const wasOpen = !modal.classList.contains("hidden");
  const restoreState = wasOpen ? null : captureModalInteractionState();
  if (!wasOpen && options.syncHistory !== false) {
    appHistoryAutoReplaceSuspended = true;
  }
  return { wasOpen, restoreState };
}

function showModalWithInteraction(modal, restoreState, wasOpen, options = {}) {
  modal.classList.remove("hidden");
  syncModalBodyLock();
  restoreModalInteractionState(restoreState, {
    restoreFocus: false,
  });
  if (!wasOpen && options.syncHistory !== false) {
    pushCurrentAppHistorySnapshot();
    appHistoryAutoReplaceSuspended = false;
  }
}

function hideModalWithInteraction(modal, restoreState, options = {}) {
  modal.classList.add("hidden");
  syncModalBodyLock();
  restoreModalInteractionState(restoreState, {
    restoreFocus: options.restoreFocus !== false,
  });
}

function closeModalWithInteraction(modal, restoreFocusTarget, options = {}) {
  hideModalWithInteraction(modal, {
    focusTarget: restoreFocusTarget instanceof HTMLElement ? restoreFocusTarget : null,
    ...captureWindowScrollPosition(),
  }, options);
  if (options.syncHistory !== false) {
    consumeOrReplaceCurrentAppHistoryEntry();
  }
}

function isProgramDeleteModalOpen() {
  return els.programDeleteModal instanceof HTMLElement && !els.programDeleteModal.classList.contains("hidden");
}

function isSessionCancelModalOpen() {
  return els.sessionCancelModal instanceof HTMLElement && !els.sessionCancelModal.classList.contains("hidden");
}

function isSessionFinishModalOpen() {
  return els.sessionFinishModal instanceof HTMLElement && !els.sessionFinishModal.classList.contains("hidden");
}

function isSessionConflictModalOpen() {
  return els.sessionConflictModal instanceof HTMLElement && !els.sessionConflictModal.classList.contains("hidden");
}

function isSessionNoticeModalOpen() {
  return els.sessionNoticeModal instanceof HTMLElement && !els.sessionNoticeModal.classList.contains("hidden");
}

function isAppAlertModalOpen() {
  return els.appAlertModal instanceof HTMLElement && !els.appAlertModal.classList.contains("hidden");
}

function isAppConfirmModalOpen() {
  return els.appConfirmModal instanceof HTMLElement && !els.appConfirmModal.classList.contains("hidden");
}

function isPwaInstallGuideModalOpen() {
  return els.pwaInstallGuideModal instanceof HTMLElement && !els.pwaInstallGuideModal.classList.contains("hidden");
}

function isSessionAddPanelOpen() {
  const panel = getSessionAddPanelEl();
  return panel instanceof HTMLElement && !panel.classList.contains("hidden");
}

function resolveAppConfirmTone() {
  const confirmBtn = els.appConfirmModalConfirmBtn;
  if (!(confirmBtn instanceof HTMLElement)) {
    return "primary";
  }
  if (confirmBtn.classList.contains("danger")) {
    return "danger";
  }
  if (confirmBtn.classList.contains("warn")) {
    return "warn";
  }
  if (confirmBtn.classList.contains("secondary")) {
    return "secondary";
  }
  return "primary";
}

function getCurrentTopLevelTab(state = getState()) {
  const selectedTab = String(state.selectedTab || "home").trim() || "home";
  if (selectedTab === "session") {
    const sessionReturnTab = String(state.sessionReturnTab || "home").trim() || "home";
    return ["logs", "programs", "analytics", "profile"].includes(sessionReturnTab) ? sessionReturnTab : "home";
  }
  return ["logs", "programs", "analytics", "profile"].includes(selectedTab) ? selectedTab : "home";
}

function hasActiveAnalyticsSearchInteraction() {
  if (!isAnalyticsSearchModalOpen()) {
    return false;
  }
  const input = document.getElementById("analyticsModalSearchInput");
  const query = String(input?.value || "").trim();
  if (query) {
    return true;
  }
  const context = getExerciseSearchModalContext();
  if (context === "analytics") {
    return !!String(getState().analytics?.query || "").trim() || (getState().analytics?.searchItems || []).length > 0;
  }
  const resultWrap = document.getElementById("analyticsModalSearchResults");
  return !!resultWrap?.querySelector("[data-action='logs.select-exercise']");
}

function clearAnalyticsSearchModalState(options = {}) {
  analyticsExerciseSearch.invalidate();
  const input = document.getElementById("analyticsModalSearchInput");
  if (input instanceof HTMLInputElement) {
    input.value = "";
  }
  const context = getExerciseSearchModalContext();
  searchOverlayController.resetAnalyticsSearchState(context);
  if (options.closeModal === true) {
    closeAnalyticsSearchModal({
      syncHistory: false,
      restoreFocus: options.restoreFocus,
    });
  }
}

function hasActiveProgramTemplateSearchInteraction() {
  return !!String(getState().programTemplateQuery || "").trim();
}

function clearProgramTemplateSearchState() {
  const input = els.programTemplateSearchModalInput;
  if (input instanceof HTMLInputElement) {
    input.value = "";
  }
  searchOverlayController.updateProgramTemplateQuery("");
}

function captureCurrentAppTask() {
  const state = getState();
  if (isAnalyticsSearchModalOpen()) {
    const context = getExerciseSearchModalContext();
    const result = { type: "analytics-search", context, tab: getCurrentTopLevelTab(state) };
    if (context === "program-item") {
      result.dayIndex = Number(els.analyticsSearchModal?.dataset.dayIndex);
      result.itemIndex = Number(els.analyticsSearchModal?.dataset.itemIndex);
    }
    return result;
  }
  if (isProgramTemplateSearchModalOpen()) {
    return {
      type: "program-template-search",
      tab: "programs",
    };
  }
  if (String(state.selectedTab || "home").trim() === "session") {
    return {
      type: "session",
      workoutId: String(state.activeWorkout?.workoutId || state.activeSession?.workoutId || "").trim(),
      tab: getCurrentTopLevelTab(state),
    };
  }
  if (
    String(state.selectedTab || "home").trim() === "programs" &&
    String(state.programBuilderStage || "list").trim() !== "list"
  ) {
    const result = {
      type: "program-builder",
      tab: "programs",
      stage: String(state.programBuilderStage || "info").trim() || "info",
    };
    if (result.stage === "day") {
      result.dayIndex = Number(state.programBuilderDayIndex);
    }
    return result;
  }
  return null;
}

function captureCurrentAppOverlay() {
  if (isAppAlertModalOpen()) {
    return {
      type: "app-alert",
      title: String(els.appAlertModalTitle?.textContent || "").trim(),
      meta: String(els.appAlertModalMeta?.textContent || "").trim(),
      message: String(els.appAlertModalCopy?.textContent || "").trim(),
      confirmLabel: String(els.appAlertModalConfirmBtn?.textContent || "").trim(),
    };
  }
  if (isAppConfirmModalOpen()) {
    return {
      type: "app-confirm",
      title: String(els.appConfirmModalTitle?.textContent || "").trim(),
      meta: String(els.appConfirmModalMeta?.textContent || "").trim(),
      message: String(els.appConfirmModalCopy?.textContent || "").trim(),
      cancelLabel: String(els.appConfirmModalCancelBtn?.textContent || "").trim(),
      confirmLabel: String(els.appConfirmModalConfirmBtn?.textContent || "").trim(),
      confirmTone: resolveAppConfirmTone(),
    };
  }
  if (isSessionNoticeModalOpen()) {
    return {
      type: "session-notice",
      title: String(els.sessionNoticeModalTitle?.textContent || "").trim(),
      meta: String(els.sessionNoticeModalMeta?.textContent || "").trim(),
      message: String(els.sessionNoticeModalCopy?.textContent || "").trim(),
    };
  }
  if (isSessionConflictModalOpen()) {
    return {
      type: "session-conflict",
    };
  }
  if (isSessionFinishModalOpen()) {
    return {
      type: "session-finish",
      workoutId: String(els.sessionFinishModal?.dataset.workoutId || "").trim(),
    };
  }
  if (isSessionCancelModalOpen()) {
    return {
      type: "session-cancel",
    };
  }
  if (isProgramDeleteModalOpen()) {
    return {
      type: "program-delete",
      programId: String(els.programDeleteModal?.dataset.targetProgramId || "").trim(),
    };
  }
  if (isCustomMusclePickerModalOpen()) {
    return {
      type: "custom-muscle-picker",
      target: String(els.customMusclePickerModal?.dataset.target || "primary").trim() || "primary",
    };
  }
  if (isProgramDayImportOpen()) {
    return {
      type: "program-day-import",
      dayIndex: Number(els.programDayImportModal?.dataset.dayIndex || ""),
      importTarget: String(els.programDayImportModal?.dataset.importTarget || "program-day").trim() || "program-day",
    };
  }
  if (isProgramDayPickerOpen()) {
    return {
      type: "program-day-picker",
      mode: String(els.programDayPickerModal?.dataset.mode || "").trim(),
      programId: String(els.programDayPickerModal?.dataset.programId || "").trim(),
    };
  }
  if (isProgramItemRuleModalOpen()) {
    return {
      type: "program-item-rule",
      dayIndex: Number(els.programItemRuleModal?.dataset.dayIndex || ""),
      itemIndex: Number(els.programItemRuleModal?.dataset.itemIndex || ""),
      ruleType: String(els.programItemRuleModal?.dataset.ruleType || "load").trim() || "load",
    };
  }
  if (isPwaInstallGuideModalOpen()) {
    return {
      type: "pwa-install-guide",
      actionMode: String(els.pwaInstallGuideModal?.dataset.actionMode || "browser-guide").trim() || "browser-guide",
    };
  }
  return null;
}

function captureAppHistorySnapshot() {
  const state = getState();
  const selectedTab = getCurrentTopLevelTab(state);
  const task = captureCurrentAppTask();
  return {
    selectedTab,
    task,
    overlay: captureCurrentAppOverlay(),
  };
}

function serializeAppHistorySnapshot(snapshot) {
  try {
    return JSON.stringify(snapshot || null);
  } catch (_) {
    return "";
  }
}

function normalizeAuthFlowPanel(panel) {
  const nextPanel = String(panel || "").trim();
  if (nextPanel === AUTH_FLOW_PANELS.signup || nextPanel === AUTH_FLOW_PANELS.consent) {
    return nextPanel;
  }
  return AUTH_FLOW_PANELS.login;
}

function normalizeAuthFlowSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== "object") {
    return {
      panel: normalizeAuthFlowPanel(snapshot),
    };
  }
  return {
    panel: normalizeAuthFlowPanel(snapshot.panel),
  };
}

function serializeAuthFlowSnapshot(snapshot) {
  try {
    return JSON.stringify(normalizeAuthFlowSnapshot(snapshot));
  } catch (_) {
    return "";
  }
}

function pickAppHistoryState(historyState) {
  if (!historyState?.[APP_HISTORY_STATE_KEY]) {
    return {};
  }
  return {
    [APP_HISTORY_STATE_KEY]: true,
    depth: Math.max(0, Number(historyState.depth) || 0),
    snapshot: historyState.snapshot,
  };
}

function pickAuthFlowHistoryState(historyState) {
  if (!historyState?.[AUTH_FLOW_HISTORY_STATE_KEY]) {
    return {};
  }
  return {
    [AUTH_FLOW_HISTORY_STATE_KEY]: true,
    authDepth: Math.max(0, Number(historyState.authDepth) || 0),
    authSnapshot: normalizeAuthFlowSnapshot(historyState.authSnapshot),
  };
}

function buildAppHistoryState(snapshot, depth = appHistoryDepth, baseState = window.history.state) {
  return {
    ...pickAuthFlowHistoryState(baseState),
    [APP_HISTORY_STATE_KEY]: true,
    depth: Number(depth) || 0,
    snapshot,
  };
}

function buildAuthFlowHistoryState(snapshot, depth = authFlowHistoryDepth, baseState = window.history.state) {
  return {
    ...pickAppHistoryState(baseState),
    [AUTH_FLOW_HISTORY_STATE_KEY]: true,
    authDepth: Math.max(0, Number(depth) || 0),
    authSnapshot: normalizeAuthFlowSnapshot(snapshot),
  };
}

function syncAuthFlowHistoryState(historyState = window.history.state) {
  const currentAuthState = pickAuthFlowHistoryState(historyState);
  if (!currentAuthState[AUTH_FLOW_HISTORY_STATE_KEY]) {
    authFlowHistoryDepth = 0;
    return null;
  }
  authFlowHistoryDepth = Math.max(0, Number(currentAuthState.authDepth) || 0);
  return currentAuthState.authSnapshot;
}

function replaceCurrentAuthFlowSnapshot(snapshot, options = {}) {
  if (typeof window === "undefined" || !window.history?.replaceState) {
    return;
  }

  const nextSnapshot = normalizeAuthFlowSnapshot(snapshot);
  const currentAuthState = pickAuthFlowHistoryState(window.history.state);
  const nextDepth = Number.isFinite(options?.depth)
    ? Math.max(0, Number(options.depth) || 0)
    : authFlowHistoryDepth;
  const shouldSkip =
    options?.force !== true &&
    currentAuthState[AUTH_FLOW_HISTORY_STATE_KEY] &&
    Number(currentAuthState.authDepth) === nextDepth &&
    serializeAuthFlowSnapshot(currentAuthState.authSnapshot) === serializeAuthFlowSnapshot(nextSnapshot);

  authFlowHistoryDepth = nextDepth;
  if (shouldSkip) {
    return;
  }

  window.history.replaceState(
    buildAuthFlowHistoryState(nextSnapshot, nextDepth),
    "",
    window.location.href,
  );
}

function pushCurrentAuthFlowSnapshot(snapshot) {
  if (typeof window === "undefined" || !window.history?.pushState) {
    return;
  }

  syncAuthFlowHistoryState();
  const nextSnapshot = normalizeAuthFlowSnapshot(snapshot);
  const currentAuthState = pickAuthFlowHistoryState(window.history.state);
  if (
    currentAuthState[AUTH_FLOW_HISTORY_STATE_KEY] &&
    serializeAuthFlowSnapshot(currentAuthState.authSnapshot) === serializeAuthFlowSnapshot(nextSnapshot)
  ) {
    return;
  }

  authFlowHistoryDepth += 1;
  window.history.pushState(
    buildAuthFlowHistoryState(nextSnapshot, authFlowHistoryDepth),
    "",
    window.location.href,
  );
}

function consumeCurrentAuthFlowEntry() {
  if (typeof window === "undefined" || !window.history?.back) {
    return false;
  }

  syncAuthFlowHistoryState();
  if (authFlowHistoryDepth <= 0) {
    return false;
  }

  window.history.back();
  return true;
}

function resetAuthFlowNavigation(continuation = null) {
  const nextContinuation = typeof continuation === "function" ? continuation : null;
  syncAuthFlowHistoryState();
  if (
    typeof window === "undefined" ||
    !window.history?.go ||
    authFlowHistoryDepth <= 0
  ) {
    authFlowPendingRootContinuation = null;
    nextContinuation?.();
    return;
  }
  authFlowPendingRootContinuation = nextContinuation;
  window.history.go(-authFlowHistoryDepth);
}

function clearAuthFlowHistoryState() {
  if (typeof window === "undefined" || !window.history?.replaceState) {
    authFlowHistoryDepth = 0;
    authFlowPendingRootContinuation = null;
    return;
  }
  authFlowHistoryDepth = 0;
  authFlowPendingRootContinuation = null;
  const appState = pickAppHistoryState(window.history.state);
  const nextState = appState[APP_HISTORY_STATE_KEY] ? appState : null;
  window.history.replaceState(nextState, "", window.location.href);
}

function initAppHistoryState() {
  if (appHistoryInitialized || typeof window === "undefined" || !window.history?.replaceState) {
    return;
  }
  const snapshot = captureAppHistorySnapshot();
  const currentState = window.history.state;
  if (currentState?.[APP_HISTORY_STATE_KEY]) {
    appHistoryDepth = Math.max(0, Number(currentState.depth) || 0);
    window.history.replaceState({
      ...currentState,
      snapshot,
    }, "", window.location.href);
  } else {
    appHistoryDepth = 0;
    window.history.replaceState(buildAppHistoryState(snapshot, 0), "", window.location.href);
  }
  appHistoryInitialized = true;
}

function replaceCurrentAppHistorySnapshot(options = {}) {
  const force = options?.force === true;
  if (
    appHistorySyncSuspended ||
    (!force && appHistoryAutoReplaceSuspended) ||
    typeof window === "undefined" ||
    !window.history?.replaceState
  ) {
    return;
  }
  initAppHistoryState();
  window.history.replaceState(
    buildAppHistoryState(captureAppHistorySnapshot(), appHistoryDepth),
    "",
    window.location.href,
  );
}

function pushCurrentAppHistorySnapshot() {
  if (appHistorySyncSuspended || typeof window === "undefined" || !window.history?.pushState) {
    return;
  }
  initAppHistoryState();
  const snapshot = captureAppHistorySnapshot();
  const currentSnapshot = window.history.state?.snapshot;
  if (
    window.history.state?.[APP_HISTORY_STATE_KEY] &&
    serializeAppHistorySnapshot(currentSnapshot) === serializeAppHistorySnapshot(snapshot)
  ) {
    return;
  }
  appHistoryDepth += 1;
  window.history.pushState(
    buildAppHistoryState(snapshot, appHistoryDepth),
    "",
    window.location.href,
  );
}

function consumeCurrentAppHistoryEntry(options = {}) {
  if (appHistorySyncSuspended || typeof window === "undefined" || !window.history?.back) {
    return false;
  }
  initAppHistoryState();
  if (appHistoryDepth <= 0) {
    appHistoryConsumeContinuation = null;
    return false;
  }
  if (appHistoryIgnoreNextPop && options.applySnapshot !== true) {
    appHistoryConsumeContinuation = typeof options.onConsumed === "function" ? options.onConsumed : null;
    return true;
  }
  appHistoryConsumeContinuation = typeof options.onConsumed === "function" ? options.onConsumed : null;
  appHistoryApplySnapshotOnConsume = options.applySnapshot === true;
  appHistoryIgnoreNextPop = options.applySnapshot === true ? false : true;
  window.history.back();
  return true;
}

function consumeOrReplaceCurrentAppHistoryEntry(options = {}) {
  if (consumeCurrentAppHistoryEntry(options)) {
    return true;
  }
  replaceCurrentAppHistorySnapshot({ force: true });
  return false;
}

function closeAllAppOverlays(options = {}) {
  closeAppAlertModal(options);
  closeAppConfirmModal(false, options);
  closeSessionNoticeModal(options);
  closeSessionConflictModal("cancel", options);
  closeSessionFinishModal(options);
  closeSessionCancelModal(options);
  closeProgramDeleteModal(options);
  closeCustomMusclePickerModal(options);
  closeAnalyticsSearchModal(options);
  closeProgramTemplateSearchModal(options);
  closeProgramDayImportModal(options);
  closeProgramDayPickerModal(null, options);
  closeProgramItemRuleModal(options);
  closePwaInstallGuideModal(options);
}

function openOverlayFromHistorySnapshot(overlay) {
  if (!overlay || typeof overlay !== "object") {
    return;
  }
  switch (overlay.type) {
    case "app-alert":
      void openAppAlertModal(overlay.message, {
        title: overlay.title,
        meta: overlay.meta,
        confirmLabel: overlay.confirmLabel,
        restoreFocus: false,
        syncHistory: false,
      });
      break;
    case "app-confirm":
      void openAppConfirmModal({
        message: overlay.message,
        title: overlay.title,
        meta: overlay.meta,
        cancelLabel: overlay.cancelLabel,
        confirmLabel: overlay.confirmLabel,
        confirmTone: overlay.confirmTone,
        restoreFocus: false,
        syncHistory: false,
      });
      break;
    case "session-notice":
      openSessionNoticeModal(overlay.message, {
        title: overlay.title,
        meta: overlay.meta,
        restoreFocus: false,
        syncHistory: false,
      });
      break;
    case "session-conflict":
      void openSessionConflictModal({
        syncHistory: false,
      });
      break;
    case "session-finish":
      openSessionFinishModal(overlay.workoutId, {
        syncHistory: false,
      });
      break;
    case "session-cancel":
      openSessionCancelModal({
        syncHistory: false,
      });
      break;
    case "program-delete":
      openProgramDeleteModal(overlay.programId, {
        syncHistory: false,
      });
      break;
    case "custom-muscle-picker":
      openCustomMusclePickerModal(overlay.target, {
        syncHistory: false,
      });
      break;
    case "program-day-import":
      void openProgramDayImportModal({
        dayIndex: Number(overlay.dayIndex),
        importTarget: overlay.importTarget,
        syncHistory: false,
      });
      break;
    case "program-day-picker":
      if (overlay.mode === "builder") {
        openProgramBuilderDayPickerModal({
          syncHistory: false,
        });
        break;
      }
      {
        const state = getState();
        const safeProgramId = String(overlay.programId || "").trim();
        const program = (state.programs || []).find(
          (entry) => String(entry?.id || "").trim() === safeProgramId,
        ) || (String(state.activeProgram?.id || "").trim() === safeProgramId ? state.activeProgram : null);
        if (program) {
          void openProgramDayPickerModal(program, {
            syncHistory: false,
          });
        }
      }
      break;
    case "program-item-rule":
      openProgramItemRuleModal(
        Number(overlay.dayIndex),
        Number(overlay.itemIndex),
        String(overlay.ruleType || "load"),
        {
          syncHistory: false,
        },
      );
      break;
    case "pwa-install-guide":
      openPwaInstallGuide(String(overlay.actionMode || "browser-guide"), {
        syncHistory: false,
      });
      break;
    default:
      break;
  }
}

async function applyAppHistorySnapshot(snapshot) {
  const targetSnapshot = snapshot && typeof snapshot === "object"
    ? snapshot
    : captureAppHistorySnapshot();
  const targetTab = String(targetSnapshot.selectedTab || "home").trim() || "home";
  const targetTask = targetSnapshot.task && typeof targetSnapshot.task === "object"
    ? targetSnapshot.task
    : null;

  appHistorySyncSuspended = true;
  try {
    closeAllAppOverlays({
      restoreFocus: false,
      syncHistory: false,
    });

    if (!targetTask || targetTask.type !== "analytics-search") {
      clearAnalyticsSearchModalState();
    }
    if (!targetTask || targetTask.type !== "program-template-search") {
      clearProgramTemplateSearchState();
    }

    const currentState = getState();
    if (String(currentState.selectedTab || "home") === "session" && targetTask?.type !== "session") {
      closeSession({
        syncHistory: false,
      });
    }

    setState({
      selectedTab: targetTab,
      programBuilderStage:
        targetTab === "programs" && targetTask?.type === "program-builder"
          ? String(targetTask.stage || "info").trim() || "info"
          : "list",
      programBuilderDayIndex:
        targetTab === "programs" &&
        targetTask?.type === "program-builder" &&
        String(targetTask.stage || "info").trim() === "day"
          ? Number(targetTask.dayIndex) || 0
          : 0,
    });

    if (targetTask?.type === "session") {
      const currentWorkoutId = String(getState().activeWorkout?.workoutId || getState().activeSession?.workoutId || "").trim();
      const workoutIdToOpen = String(targetTask.workoutId || "").trim() || currentWorkoutId;
      if (String(getState().selectedTab || "home") !== "session" && workoutIdToOpen) {
        await openSession(workoutIdToOpen, {
          syncHistory: false,
        }).catch(() => undefined);
      } else if (String(getState().selectedTab || "home") === "session") {
        setState({
          selectedTab: "session",
        });
      }
    } else if (targetTask?.type === "program-builder") {
      const programBuilderStage = String(targetTask.stage || "info").trim() || "info";
      setState({
        selectedTab: "programs",
        programBuilderStage,
        programBuilderDayIndex:
          programBuilderStage === "day"
            ? Number(targetTask.dayIndex) || 0
            : 0,
      });
    } else if (targetTask?.type === "analytics-search") {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      openAnalyticsSearchModal(targetTask.context, {
        dayIndex: Number.isInteger(Number(targetTask.dayIndex)) ? Number(targetTask.dayIndex) : undefined,
        itemIndex: Number.isInteger(Number(targetTask.itemIndex)) ? Number(targetTask.itemIndex) : undefined,
        syncHistory: false,
      });
    } else if (targetTask?.type === "program-template-search") {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      openProgramTemplateSearchModal({
        syncHistory: false,
      });
    }

    if (targetSnapshot.overlay) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      openOverlayFromHistorySnapshot(targetSnapshot.overlay);
    }
  } finally {
    appHistorySyncSuspended = false;
    replaceCurrentAppHistorySnapshot();
  }
}

function hasCurrentAppTask() {
  return !!captureCurrentAppTask();
}

function resetAppNavigationToHome(continuation = null) {
  const nextContinuation = typeof continuation === "function" ? continuation : null;
  if (
    appHistorySyncSuspended ||
    typeof window === "undefined" ||
    !window.history?.go ||
    appHistoryDepth <= 0
  ) {
    appHistoryPendingRootContinuation = null;
    void applyAppHistorySnapshot({
      selectedTab: "home",
      task: null,
      overlay: null,
    }).then(() => {
      if (nextContinuation) {
        nextContinuation();
      }
    });
    return;
  }
  appHistoryPendingRootContinuation = nextContinuation;
  window.history.go(-appHistoryDepth);
}

function openTopLevelTabFromRoot(nextTab) {
  const safeTab = String(nextTab || "home").trim() || "home";
  if (safeTab === "home") {
    return;
  }
  if (safeTab === "logs") {
    openLogsTab();
    return;
  }
  appHistoryAutoReplaceSuspended = true;
  setState({
    selectedTab: safeTab,
    programBuilderStage: safeTab === "programs" ? "list" : getState().programBuilderStage,
  });
  pushCurrentAppHistorySnapshot();
  appHistoryAutoReplaceSuspended = false;
}

function handleProgramBuilderLocalBackAction() {
  const state = getState();
  if (
    String(state.selectedTab || "home") === "programs" &&
    String(state.programBuilderStage || "list") === "day"
  ) {
    handleOpenProgramInfoStage();
    return true;
  }
  return false;
}

function handleSessionLocalBackAction() {
  if (hasOpenSessionContextMenus()) {
    closeSessionItemContextMenus();
    return true;
  }
  if (Number.isInteger(resolveSessionItemReorderOrder())) {
    closeSessionItemReorder({
      restoreFocus: false,
      syncHistory: false,
    });
    return true;
  }
  if (isSessionAddPanelOpen()) {
    closeSessionAddPanel({
      clearSelection: true,
      syncHistory: false,
    });
    return true;
  }
  return false;
}

function shouldConfirmProgramBuilderExitToSnapshot(targetSnapshot) {
  const currentTask = captureCurrentAppTask();
  if (currentTask?.type !== "program-builder") {
    return false;
  }
  const targetTask = targetSnapshot?.task && typeof targetSnapshot.task === "object"
    ? targetSnapshot.task
    : null;
  return targetTask?.type !== "program-builder" && isProgramBuilderDirty();
}

async function confirmProgramBuilderExit(onConfirm) {
  if (!isProgramBuilderDirty()) {
    if (typeof onConfirm === "function") {
      onConfirm();
    }
    return true;
  }
  const confirmed = await openAppConfirmModal({
    title: "변경 내용을 버릴까요?",
    meta: "저장하지 않은 프로그램 편집 내용이 사라집니다.",
    message: "프로그램 추가/편집 화면을 나가면 현재 변경 내용은 유지되지 않습니다.",
    confirmLabel: "나가기",
    cancelLabel: "계속 편집",
    confirmTone: "warn",
    afterConfirmHistoryConsumed: onConfirm,
  });
  return confirmed;
}

function exitProgramBuilderToParent() {
  appHistoryAutoReplaceSuspended = true;
  handleCancelProgramBuilder();
  if (!consumeCurrentAppHistoryEntry({ applySnapshot: true })) {
    appHistoryAutoReplaceSuspended = false;
    replaceCurrentAppHistorySnapshot({ force: true });
    return;
  }
  appHistoryAutoReplaceSuspended = false;
}

function handleLocalBackBeforeSnapshotApply(targetSnapshot) {
  if (hasOpenModal()) {
    return null;
  }
  if (handleSessionLocalBackAction()) {
    return "stay";
  }
  if (handleProgramBuilderLocalBackAction()) {
    return "stay";
  }
  const currentTask = captureCurrentAppTask();
  const targetTask = targetSnapshot?.task && typeof targetSnapshot.task === "object"
    ? targetSnapshot.task
    : null;
  if (currentTask?.type === "program-builder" && targetTask?.type !== "program-builder") {
    if (isProgramBuilderDirty()) {
      return "confirm-program-builder-exit";
    }
    handleCancelProgramBuilder();
  }
  if (shouldConfirmProgramBuilderExitToSnapshot(targetSnapshot)) {
    return "confirm-program-builder-exit";
  }
  if (isAnalyticsSearchModalOpen()) {
    if (targetTask?.type !== "analytics-search") {
      clearAnalyticsSearchModalState();
    }
  }
  if (isProgramTemplateSearchModalOpen()) {
    if (targetTask?.type !== "program-template-search") {
      clearProgramTemplateSearchState();
    }
  }
  return null;
}

function normalizeAppModalMessage(message, fallback = "요청을 완료하지 못했습니다.") {
  const safeMessage = String(message || "").trim();
  return safeMessage || fallback;
}

function setInlineTextMessage(target, message) {
  const element = typeof target === "string" ? document.getElementById(target) : target;
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  element.textContent = String(message || "").trim();
  return true;
}

function clearInlineTextMessage(target) {
  return setInlineTextMessage(target, "");
}

function applyModalActionTone(button, tone = "primary") {
  if (!(button instanceof HTMLElement)) {
    return;
  }

  button.classList.remove("primary", "secondary", "warn", "danger");
  button.classList.add(
    tone === "danger" || tone === "warn" || tone === "secondary"
      ? tone
    : "primary",
  );
}

let modalBodyScrollLockSnapshot = null;
let modalViewportSyncFrame = 0;
let modalViewportFocusedTarget = null;
const NON_TEXT_ENTRY_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

function captureModalBodyScrollLockState() {
  return {
    scrollX: Number(window.scrollX || window.pageXOffset || 0),
    scrollY: Number(window.scrollY || window.pageYOffset || 0),
    scrollbarGap: Math.max(0, window.innerWidth - document.documentElement.clientWidth),
  };
}

function applyModalBodyScrollLock(state) {
  if (!state) {
    return;
  }

  document.documentElement.classList.add("modal-open");
  document.body.classList.add("modal-open");
  document.body.style.setProperty("--modal-scroll-y", `${-state.scrollY}px`);
  document.body.style.setProperty("--modal-scrollbar-gap", `${state.scrollbarGap}px`);
}

function clearModalBodyScrollLock() {
  document.documentElement.classList.remove("modal-open");
  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("--modal-scroll-y");
  document.body.style.removeProperty("--modal-scrollbar-gap");
}

function hasOpenModal() {
  return (
    isAppAlertModalOpen() ||
    isAppConfirmModalOpen() ||
    isPwaInstallGuideModalOpen() ||
    isProgramDayPickerOpen() ||
    isProgramDayImportOpen() ||
    isAnalyticsSearchModalOpen() ||
    isProgramTemplateSearchModalOpen() ||
    isProgramItemRuleModalOpen() ||
    isCustomMusclePickerModalOpen() ||
    isProgramDeleteModalOpen() ||
    isSessionCancelModalOpen() ||
    isSessionFinishModalOpen() ||
    isSessionConflictModalOpen() ||
    isSessionNoticeModalOpen()
  );
}

function isModalTextEntryTarget(target) {
  if (!(target instanceof HTMLElement) || !(target.closest(".modal-overlay") instanceof HTMLElement)) {
    return false;
  }

  if (target instanceof HTMLTextAreaElement || target.isContentEditable) {
    return true;
  }

  if (!(target instanceof HTMLInputElement)) {
    return false;
  }

  return !NON_TEXT_ENTRY_INPUT_TYPES.has(String(target.type || "text").trim().toLowerCase());
}

function clearModalViewportCompensation() {
  document.documentElement.style.removeProperty("--modal-viewport-height");
  document.documentElement.style.removeProperty("--modal-keyboard-offset");
  modalViewportFocusedTarget = null;
}

function getModalViewportMetrics() {
  const layoutViewportHeight = Math.max(
    0,
    Number(window.innerHeight || document.documentElement.clientHeight || 0),
  );
  const viewport = window.visualViewport;
  if (!viewport) {
    return {
      viewportHeight: layoutViewportHeight,
      keyboardOffset: 0,
    };
  }

  const viewportHeight = Math.max(0, Math.round(Number(viewport.height || 0)));
  const viewportOffsetTop = Math.max(0, Math.round(Number(viewport.offsetTop || 0)));
  const keyboardOffset = Math.max(0, layoutViewportHeight - viewportHeight - viewportOffsetTop);

  return {
    viewportHeight: viewportHeight || layoutViewportHeight,
    keyboardOffset,
  };
}

function getModalScrollTarget(target) {
  if (!(target instanceof HTMLElement)) {
    return null;
  }

  return target.closest(
    ".field, .set-grid, .program-item-rule-modal-section, .session-item-card, .analytics-search-row",
  ) || target;
}

function ensureModalFocusTargetVisible(target = modalViewportFocusedTarget) {
  if (!(target instanceof HTMLElement) || !document.contains(target)) {
    return;
  }

  const modalSheet = target.closest(".modal-sheet");
  if (!(modalSheet instanceof HTMLElement)) {
    return;
  }

  const scrollTarget = getModalScrollTarget(target);
  if (!(scrollTarget instanceof HTMLElement)) {
    return;
  }

  const scrollTargetRect = scrollTarget.getBoundingClientRect();
  const modalSheetRect = modalSheet.getBoundingClientRect();
  const viewport = window.visualViewport;
  const viewportTop = Math.max(0, Math.round(Number(viewport?.offsetTop || 0)));
  const viewportHeight = Math.max(
    0,
    Math.round(Number(viewport?.height || window.innerHeight || document.documentElement.clientHeight || 0)),
  );
  const visibleTop = Math.max(modalSheetRect.top + 16, viewportTop + 12);
  const visibleBottom = Math.min(modalSheetRect.bottom - 16, viewportTop + viewportHeight - 12);

  if (scrollTargetRect.top >= visibleTop && scrollTargetRect.bottom <= visibleBottom) {
    return;
  }

  scrollTarget.scrollIntoView({
    block: "center",
    inline: "nearest",
  });
}

function syncModalViewportCompensation() {
  if (!hasOpenModal()) {
    clearModalViewportCompensation();
    return;
  }

  const { viewportHeight, keyboardOffset } = getModalViewportMetrics();
  document.documentElement.style.setProperty("--modal-viewport-height", `${viewportHeight}px`);
  document.documentElement.style.setProperty("--modal-keyboard-offset", `${keyboardOffset}px`);

  if (modalViewportFocusedTarget instanceof HTMLElement) {
    ensureModalFocusTargetVisible(modalViewportFocusedTarget);
  }
}

function scheduleModalViewportCompensation(target = null) {
  if (target instanceof HTMLElement) {
    modalViewportFocusedTarget = target;
  }

  if (modalViewportSyncFrame) {
    window.cancelAnimationFrame(modalViewportSyncFrame);
  }

  modalViewportSyncFrame = window.requestAnimationFrame(() => {
    modalViewportSyncFrame = 0;
    syncModalViewportCompensation();
  });
}

function syncModalBodyLock() {
  const hasAnyOpenModal = hasOpenModal();

  if (hasAnyOpenModal) {
    if (!modalBodyScrollLockSnapshot) {
      modalBodyScrollLockSnapshot = captureModalBodyScrollLockState();
    }
    applyModalBodyScrollLock(modalBodyScrollLockSnapshot);
    scheduleModalViewportCompensation();
    return;
  }

  if (modalBodyScrollLockSnapshot) {
    const restoreState = modalBodyScrollLockSnapshot;
    clearModalBodyScrollLock();
    modalBodyScrollLockSnapshot = null;
    restoreWindowScrollPosition(restoreState);
    scheduleModalViewportCompensation();
    return;
  }

  clearModalBodyScrollLock();
  scheduleModalViewportCompensation();
}

function closeAppAlertModal(options = {}) {
  const modal = els.appAlertModal;
  if (!(modal instanceof HTMLElement) || !isAppAlertModalOpen()) {
    return;
  }
  const shouldRestoreFocus = options.restoreFocus ?? appAlertModalShouldRestoreFocus;
  const restoreState = {
    focusTarget:
      shouldRestoreFocus !== false && appAlertModalLastFocused instanceof HTMLElement
        ? appAlertModalLastFocused
        : null,
    ...captureWindowScrollPosition(),
  };

  const resolver = appAlertModalResolve;
  appAlertModalResolve = null;
  if (resolver) {
    resolver();
  }

  hideModalWithInteraction(modal, restoreState, {
    ...options,
    restoreFocus: shouldRestoreFocus,
  });
  appAlertModalLastFocused = null;
  appAlertModalShouldRestoreFocus = true;
  if (options.syncHistory !== false) {
    consumeOrReplaceCurrentAppHistoryEntry();
  }
}

function openAppAlertModal(message, options = {}) {
  const modal = els.appAlertModal;
  const titleEl = els.appAlertModalTitle;
  const metaEl = els.appAlertModalMeta;
  const copyEl = els.appAlertModalCopy;
  const confirmBtn = els.appAlertModalConfirmBtn;
  const safeMessage = normalizeAppModalMessage(message);
  if (
    !(modal instanceof HTMLElement) ||
    !(titleEl instanceof HTMLElement) ||
    !(metaEl instanceof HTMLElement) ||
    !(copyEl instanceof HTMLElement) ||
    !(confirmBtn instanceof HTMLButtonElement)
  ) {
    window.alert(safeMessage);
    return Promise.resolve();
  }

  if (isAppAlertModalOpen()) {
    closeAppAlertModal({ restoreFocus: false, syncHistory: false });
  }

  const title = String(options.title || "확인이 필요합니다").trim() || "확인이 필요합니다";
  const meta = String(options.meta || "").trim();
  const confirmLabel = String(options.confirmLabel || "확인").trim() || "확인";
  const { wasOpen, restoreState } = getModalInteractionState(modal, options);
  if (!wasOpen) {
    appAlertModalLastFocused = restoreState?.focusTarget instanceof HTMLElement
      ? restoreState.focusTarget
      : null;
  }

  titleEl.textContent = title;
  metaEl.textContent = meta;
  metaEl.classList.toggle("hidden", !meta);
  copyEl.textContent = safeMessage;
  confirmBtn.textContent = confirmLabel;

  appAlertModalShouldRestoreFocus = options.restoreFocus !== false;
  showModalWithInteraction(modal, restoreState, wasOpen, options);

  return new Promise((resolve) => {
    appAlertModalResolve = resolve;
    scheduleFocusWithoutScroll(confirmBtn);
  });
}

function closeAppConfirmModal(confirmed = false, options = {}) {
  const modal = els.appConfirmModal;
  if (!(modal instanceof HTMLElement) || !isAppConfirmModalOpen()) {
    return;
  }
  const shouldRestoreFocus = options.restoreFocus ?? appConfirmModalShouldRestoreFocus;
  const restoreState = {
    focusTarget:
      shouldRestoreFocus !== false && appConfirmModalLastFocused instanceof HTMLElement
        ? appConfirmModalLastFocused
        : null,
    ...captureWindowScrollPosition(),
  };

  const resolver = appConfirmModalResolve;
  appConfirmModalResolve = null;
  const afterConfirmHistoryConsumed = confirmed && typeof appConfirmModalAfterConfirmHistoryConsumed === "function"
    ? appConfirmModalAfterConfirmHistoryConsumed
    : null;
  appConfirmModalAfterConfirmHistoryConsumed = null;
  if (resolver) {
    resolver(Boolean(confirmed));
  }

  hideModalWithInteraction(modal, restoreState, {
    ...options,
    restoreFocus: shouldRestoreFocus,
  });
  appConfirmModalLastFocused = null;
  appConfirmModalShouldRestoreFocus = true;
  if (options.syncHistory !== false) {
    const consumed = consumeOrReplaceCurrentAppHistoryEntry({
      onConsumed: afterConfirmHistoryConsumed || undefined,
    });
    if (!consumed && afterConfirmHistoryConsumed) {
      afterConfirmHistoryConsumed();
    }
    return;
  }
  if (afterConfirmHistoryConsumed) {
    afterConfirmHistoryConsumed();
  }
}

function openAppConfirmModal(options = {}) {
  const modal = els.appConfirmModal;
  const titleEl = els.appConfirmModalTitle;
  const metaEl = els.appConfirmModalMeta;
  const copyEl = els.appConfirmModalCopy;
  const cancelBtn = els.appConfirmModalCancelBtn;
  const confirmBtn = els.appConfirmModalConfirmBtn;
  const safeMessage = normalizeAppModalMessage(options.message, "계속 진행할까요?");
  if (
    !(modal instanceof HTMLElement) ||
    !(titleEl instanceof HTMLElement) ||
    !(metaEl instanceof HTMLElement) ||
    !(copyEl instanceof HTMLElement) ||
    !(cancelBtn instanceof HTMLButtonElement) ||
    !(confirmBtn instanceof HTMLButtonElement)
  ) {
    const confirmed = window.confirm(safeMessage);
    if (confirmed && typeof options.afterConfirmHistoryConsumed === "function") {
      options.afterConfirmHistoryConsumed();
    }
    return Promise.resolve(confirmed);
  }

  if (isAppConfirmModalOpen()) {
    closeAppConfirmModal(false, { restoreFocus: false, syncHistory: false });
  }

  const title = String(options.title || "확인이 필요합니다").trim() || "확인이 필요합니다";
  const meta = String(options.meta || "").trim();
  const confirmLabel = String(options.confirmLabel || "확인").trim() || "확인";
  const cancelLabel = String(options.cancelLabel || "취소").trim() || "취소";
  const { wasOpen, restoreState } = getModalInteractionState(modal, options);
  if (!wasOpen) {
    appConfirmModalLastFocused = restoreState?.focusTarget instanceof HTMLElement
      ? restoreState.focusTarget
      : null;
  }

  titleEl.textContent = title;
  metaEl.textContent = meta;
  metaEl.classList.toggle("hidden", !meta);
  copyEl.textContent = safeMessage;
  cancelBtn.textContent = cancelLabel;
  confirmBtn.textContent = confirmLabel;
  applyModalActionTone(confirmBtn, options.confirmTone || "primary");

  appConfirmModalShouldRestoreFocus = options.restoreFocus !== false;
  appConfirmModalAfterConfirmHistoryConsumed =
    typeof options.afterConfirmHistoryConsumed === "function"
      ? options.afterConfirmHistoryConsumed
      : null;
  showModalWithInteraction(modal, restoreState, wasOpen, options);

  return new Promise((resolve) => {
    appConfirmModalResolve = resolve;
    scheduleFocusWithoutScroll(cancelBtn);
  });
}

function closePwaInstallGuideModal(options = {}) {
  const modal = els.pwaInstallGuideModal;
  if (!(modal instanceof HTMLElement) || !isPwaInstallGuideModalOpen()) {
    return;
  }

  const restoreTarget = pwaInstallGuideModalLastFocused;
  delete modal.dataset.actionMode;
  closeModalWithInteraction(modal, restoreTarget, options);
  pwaInstallGuideModalLastFocused = null;
}

function closeCustomMusclePickerModal(options = {}) {
  const modal = els.customMusclePickerModal;
  if (!(modal instanceof HTMLElement) || !isCustomMusclePickerModalOpen()) {
    return;
  }

  const restoreTarget = customMusclePickerLastFocused;
  modal.removeAttribute("data-target");
  closeModalWithInteraction(modal, restoreTarget, options);
  customMusclePickerLastFocused = null;
}

const PROGRAM_OPTION_CARD_CHEVRON = `
  <svg class="program-option-card-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M9 18l6-6-6-6"></path>
  </svg>
`;

function closeProgramDayPickerModal(selectedDayIndex = null, options = {}) {
  const modal = els.programDayPickerModal;
  if (!(modal instanceof HTMLElement)) {
    return;
  }
  if (!isProgramDayPickerOpen()) {
    return;
  }

  modal.removeAttribute("data-program-id");
  modal.removeAttribute("data-mode");

  const resolver = programDayPickerResolve;
  programDayPickerResolve = null;
  const restoreState = {
    focusTarget: programDayPickerLastFocused instanceof HTMLElement ? programDayPickerLastFocused : null,
    ...captureWindowScrollPosition(),
  };

  if (resolver) {
    resolver(Number.isInteger(selectedDayIndex) ? Number(selectedDayIndex) : null);
  }

  hideModalWithInteraction(modal, restoreState, options);
  programDayPickerLastFocused = null;
  if (options.syncHistory !== false) {
    consumeOrReplaceCurrentAppHistoryEntry();
  }
}

function openProgramDayPickerModal(program, options = {}) {
  return new Promise((resolve) => {
    if (!program?.days) {
      resolve(null);
      return;
    }

    const modal = els.programDayPickerModal;
    const subtitle = els.programDayPickerSubtitle;
    const list = els.programDayPickerList;
    if (!(modal instanceof HTMLElement) || !(list instanceof HTMLElement)) {
      resolve(null);
      return;
    }

    if (isProgramDayPickerOpen()) {
      closeProgramDayPickerModal(null, { syncHistory: false });
    }

    programDayPickerResolve = resolve;
    const { wasOpen, restoreState } = getModalInteractionState(modal, options);
    if (!wasOpen) {
      programDayPickerLastFocused = restoreState?.focusTarget instanceof HTMLElement
        ? restoreState.focusTarget
        : null;
    }
    modal.dataset.mode = "program";

    const lengthDays = resolveProgramLengthDays(program);
    const todayDayIndex = resolveProgramTodayDayIndex(program);

    if (subtitle instanceof HTMLElement) {
      subtitle.textContent = `${program.name || "프로그램"}의 시작 Day를 선택하세요.`;
    }

    setElementHtml(list, Array.from({ length: lengthDays }, (_, index) => {
      const alias = String(program.dayAliases?.[String(index)] || `Day ${index + 1}`).trim();
      const dayItems = Object.keys(program.days?.[String(index)]?.items || {}).length;
      const defaultChip = index === todayDayIndex
        ? `<span class="program-day-picker-default-chip">현재</span>`
        : "";

      return `
        <button class="program-day-picker-option-btn btn-option-card ${index === todayDayIndex ? "is-current" : ""}" type="button" data-day-index="${index}">
          <span class="program-day-picker-option-main">
            <strong>DAY ${index + 1}</strong>
            <span>${escapeHtmlText(alias || `Day ${index + 1}`)}</span>
          </span>
          <span class="program-day-picker-option-side">
            <span class="program-day-picker-option-meta">
              ${defaultChip}
              <span>${dayItems}개 운동</span>
            </span>
            ${PROGRAM_OPTION_CARD_CHEVRON}
          </span>
        </button>
      `;
    }).join(""));

    modal.dataset.programId = String(program.id || "");
    showModalWithInteraction(modal, restoreState, wasOpen, options);

    const firstOption = list.querySelector(".program-day-picker-option-btn");
    if (firstOption instanceof HTMLButtonElement) {
      scheduleFocusWithoutScroll(firstOption);
    }
  });
}

function openProgramBuilderDayPickerModal(options = {}) {
  syncProgramBuilderFromDom();
  const builder = ensureProgramBuilder();
  const currentDayIndex = clampProgramBuilderDayIndex(getState().programBuilderDayIndex ?? 0, builder);
  const modal = els.programDayPickerModal;
  const subtitle = els.programDayPickerSubtitle;
  const list = els.programDayPickerList;
  if (!(modal instanceof HTMLElement) || !(list instanceof HTMLElement)) {
    return;
  }

  if (isProgramDayPickerOpen()) {
    closeProgramDayPickerModal(null, { syncHistory: false });
  }

  programDayPickerResolve = null;
  const { wasOpen, restoreState } = getModalInteractionState(modal, options);
  if (!wasOpen) {
    programDayPickerLastFocused = restoreState?.focusTarget instanceof HTMLElement
      ? restoreState.focusTarget
      : null;
  }
  modal.dataset.mode = "builder";

  if (subtitle instanceof HTMLElement) {
    subtitle.textContent = `이동할 DAY를 선택하세요. 현재 DAY ${currentDayIndex + 1}을 편집 중입니다.`;
  }

  const totalDays = Math.max(1, Number(builder?.days?.length || builder?.lengthDays || 1));
  setElementHtml(list, Array.from({ length: totalDays }, (_, index) => {
    const day = builder.days?.[index] || createBuilderDay(index);
    const alias = String(day?.alias || `Day ${index + 1}`).trim() || `Day ${index + 1}`;
    const configuredItems = (day?.items || []).filter((item) => String(item?.exerciseId || "").trim()).length;
    const stateChip = index === currentDayIndex
      ? "편집 중"
      : configuredItems > 0
        ? "구성됨"
        : "선택 필요";

    return `
      <button class="program-day-picker-option-btn btn-option-card ${index === currentDayIndex ? "is-current" : ""}" type="button" data-day-index="${index}">
        <span class="program-day-picker-option-main">
          <strong>DAY ${index + 1}</strong>
          <span>${escapeHtmlText(alias)}</span>
        </span>
        <span class="program-day-picker-option-side">
          <span class="program-day-picker-option-meta">
            <span class="program-day-picker-default-chip">${escapeHtmlText(stateChip)}</span>
            <span>${configuredItems}개 운동</span>
          </span>
          ${PROGRAM_OPTION_CARD_CHEVRON}
        </span>
      </button>
    `;
  }).join(""));

  showModalWithInteraction(modal, restoreState, wasOpen, options);

  const currentOption = list.querySelector(".program-day-picker-option-btn.is-current");
  const firstOption = list.querySelector(".program-day-picker-option-btn");
  const focusTarget = currentOption instanceof HTMLButtonElement ? currentOption : firstOption;
  if (focusTarget instanceof HTMLButtonElement) {
    scheduleFocusWithoutScroll(focusTarget);
  }
}

function resolveWorkoutSessionDisplayName(meta) {
  const source = meta?.source || {};
  const sourceAlias = String(source?.dayAlias || "").trim();
  if (sourceAlias) {
    return sourceAlias;
  }
  if (source?.type === "program") {
    const dayIndex = Number(source?.dayIndex);
    if (Number.isInteger(dayIndex) && dayIndex >= 0) {
      return `DAY ${dayIndex + 1}`;
    }
    return "프로그램 루틴";
  }
  if (source?.type === "manual") {
    return "오늘 운동";
  }
  return source?.type || "오늘 운동";
}

function summarizeWorkoutExerciseNames(workoutDetail) {
  const names = [];
  const seen = new Set();
  const sortedItems = Object.entries(workoutDetail?.items || {}).sort((a, b) => Number(a[0]) - Number(b[0]));

  for (const [, item] of sortedItems) {
    const name = String(item?.nameSnapshot || "").trim();
    const exerciseId = String(item?.exerciseId || "").trim();
    const dedupeKey = `${exerciseId}|${name}`;
    if (!name || seen.has(dedupeKey)) {
      continue;
    }
    seen.add(dedupeKey);
    names.push(name);
  }

  return names;
}

async function buildProgramDayImportEntries() {
  const state = getState();
  const workouts = Array.isArray(state.workouts) ? state.workouts : [];
  if (workouts.length === 0) {
    return [];
  }

  const detailCache = state.workoutDetailsById || {};
  const entries = await Promise.all(
    workouts.map(async (workoutMeta) => {
      const workoutId = String(workoutMeta?.workoutId || "").trim();
      if (!workoutId) {
        return null;
      }

      let workoutDetail = detailCache[workoutId];
      if (!workoutDetail) {
        try {
          workoutDetail = await api.getWorkout(workoutId);
        } catch (_) {
          return null;
        }
      }

      const exercises = summarizeWorkoutExerciseNames(workoutDetail);
      if (exercises.length === 0) {
        return null;
      }

      return {
        workoutId,
        name: resolveWorkoutSessionDisplayName(workoutMeta.meta),
        date: String(workoutMeta?.meta?.date || "-"),
        weekday: String(workoutMeta?.meta?.weekday || "").trim(),
        exercises,
        workoutDetail,
      };
    }),
  );

  return entries.filter((entry) => !!entry);
}

function renderProgramDayImportList(entries) {
  const list = els.programDayImportList;
  if (!(list instanceof HTMLElement)) {
    return;
  }

  if (!Array.isArray(entries) || entries.length === 0) {
    setElementHtml(list, `
      <div class="program-day-import-empty">
        <strong>불러올 수 있는 운동 기록이 없습니다</strong>
        <span>운동을 완료하면 이곳에서 이전 세션을 다시 사용할 수 있습니다.</span>
      </div>
    `);
    return;
  }

  setElementHtml(list, `
    ${entries
      .map((entry) => {
        const sortedItems = Object.values(entry?.workoutDetail?.items || {});
        const setCount = sortedItems.reduce((sum, item) => {
          const planned = Number(item?.setsPlan);
          const fromSets = Object.keys(item?.sets || {}).length;
          return sum + (Number.isInteger(planned) && planned > 0 ? planned : fromSets);
        }, 0);
        const exerciseText = entry.exercises.join(", ");
        const visibleExercises = entry.exercises.slice(0, 4);
        const hiddenExerciseCount = Math.max(0, entry.exercises.length - visibleExercises.length);
        const weekdayLabel = formatWeekdayLabel(entry.weekday);
        const dateText = weekdayLabel ? `${entry.date} (${weekdayLabel})` : entry.date;
        return `
          <button class="program-day-import-option-btn btn-option-card" type="button" data-workout-id="${escapeHtmlText(entry.workoutId)}">
            <span class="program-day-import-option-content">
              <span class="program-day-import-option-date">${escapeHtmlText(dateText)}</span>
              <span class="program-day-import-option-main">
                <strong>${escapeHtmlText(entry.name || "운동 기록")}</strong>
                <span>운동 ${entry.exercises.length}개 · 세트 ${setCount}개</span>
              </span>
              <span class="program-day-import-option-chips" aria-label="${escapeHtmlText(exerciseText)}">
                ${visibleExercises.map((exercise) => `<span>${escapeHtmlText(exercise)}</span>`).join("")}
                ${hiddenExerciseCount > 0 ? `<span>+${hiddenExerciseCount}</span>` : ""}
              </span>
            </span>
            ${PROGRAM_OPTION_CARD_CHEVRON}
          </button>
        `;
      })
      .join("")}
  `);
}

function closeProgramDayImportModal(options = {}) {
  const modal = els.programDayImportModal;
  const list = els.programDayImportList;
  if (!(modal instanceof HTMLElement)) {
    return;
  }
  if (!isProgramDayImportOpen()) {
    return;
  }

  modal.removeAttribute("data-day-index");
  modal.removeAttribute("data-import-target");
  if (list instanceof HTMLElement) {
    list.replaceChildren();
  }
  programDayImportWorkoutMap = new Map();
  hideModalWithInteraction(modal, {
    focusTarget: programDayImportLastFocused instanceof HTMLElement ? programDayImportLastFocused : null,
    ...captureWindowScrollPosition(),
  }, options);
  programDayImportLastFocused = null;
  if (options.syncHistory !== false) {
    consumeOrReplaceCurrentAppHistoryEntry();
  }
}

function closeProgramDeleteModal(options = {}) {
  const modal = els.programDeleteModal;
  if (!(modal instanceof HTMLElement) || !isProgramDeleteModalOpen()) return;
  const restoreTarget = programDeleteModalLastFocused;
  modal.removeAttribute("data-target-program-id");

  if (els.programDeleteModalName instanceof HTMLElement) {
    els.programDeleteModalName.textContent = "선택한 프로그램";
  }

  closeModalWithInteraction(modal, restoreTarget, options);
  programDeleteModalLastFocused = null;
}

function openProgramDeleteModal(programId, options = {}) {
  const modal = els.programDeleteModal;
  if (!(modal instanceof HTMLElement)) return;
  if (isProgramDeleteModalOpen()) {
    closeProgramDeleteModal({ restoreFocus: false, syncHistory: false });
  }

  const safeProgramId = String(programId || "").trim();
  if (!safeProgramId) {
    return;
  }
  const programName = ((getState().programs || []).find(
    (program) => String(program?.id || "") === safeProgramId,
  )?.name || "선택한 프로그램").trim();
  const { wasOpen, restoreState } = getModalInteractionState(modal, options);
  if (!wasOpen) {
    programDeleteModalLastFocused = restoreState?.focusTarget instanceof HTMLElement
      ? restoreState.focusTarget
      : null;
  }

  if (els.programDeleteModalName instanceof HTMLElement) {
    els.programDeleteModalName.textContent = programName;
  }
  modal.dataset.targetProgramId = safeProgramId;
  showModalWithInteraction(modal, restoreState, wasOpen, options);

  scheduleFocusWithoutScroll(els.programDeleteModalCancelBtn);
}

async function openProgramDayImportModal(options = {}) {
  const importTarget = String(options.importTarget || "program-day").trim() || "program-day";
  const isManualWorkoutImport = importTarget === "manual-workout";
  let dayIndex = 0;
  if (!isManualWorkoutImport) {
    syncProgramBuilderFromDom();
    const builder = ensureProgramBuilder();
    const requestedDayIndex = Number(options.dayIndex);
    dayIndex = clampProgramBuilderDayIndex(
      Number.isInteger(requestedDayIndex) ? requestedDayIndex : (getState().programBuilderDayIndex ?? 0),
      builder,
    );
  }

  const modal = els.programDayImportModal;
  const title = els.programDayImportTitle;
  const subtitle = els.programDayImportSubtitle;
  const list = els.programDayImportList;
  if (!(modal instanceof HTMLElement) || !(list instanceof HTMLElement)) {
    return;
  }

  if (isProgramDayImportOpen()) {
    closeProgramDayImportModal({ syncHistory: false });
  }

  const { wasOpen, restoreState } = getModalInteractionState(modal, options);
  if (!wasOpen) {
    programDayImportLastFocused = restoreState?.focusTarget instanceof HTMLElement
      ? restoreState.focusTarget
      : null;
  }
  modal.dataset.importTarget = importTarget;
  if (isManualWorkoutImport) {
    modal.removeAttribute("data-day-index");
  } else {
    modal.dataset.dayIndex = String(dayIndex);
  }
  if (title instanceof HTMLElement) {
    title.textContent = isManualWorkoutImport ? "지난 운동 불러오기" : "운동 기록 불러오기";
  }
  if (subtitle instanceof HTMLElement) {
    subtitle.textContent = isManualWorkoutImport
      ? "오늘 운동으로 불러올 이전 운동 기록을 선택하세요."
      : `DAY ${dayIndex + 1}에 반영할 운동 기록을 선택하세요.`;
  }
  setElementHtml(list, `<p class="meta">운동 기록 목록을 불러오는 중...</p>`);
  showModalWithInteraction(modal, restoreState, wasOpen, options);

  const requestSeq = ++programDayImportRequestSeq;
  const entries = await buildProgramDayImportEntries();
  if (requestSeq !== programDayImportRequestSeq || !isProgramDayImportOpen()) {
    return;
  }

  programDayImportWorkoutMap = new Map(entries.map((entry) => [entry.workoutId, entry]));
  renderProgramDayImportList(entries);

  const firstOption = list.querySelector(".program-day-import-option-btn");
  if (firstOption instanceof HTMLButtonElement) {
    scheduleFocusWithoutScroll(firstOption);
  }
}

function closeSessionCancelModal(options = {}) {
  const modal = els.sessionCancelModal;
  if (!(modal instanceof HTMLElement)) {
    return;
  }
  if (!isSessionCancelModalOpen()) {
    return;
  }

  const restoreTarget = sessionCancelModalLastFocused;
  closeModalWithInteraction(modal, restoreTarget, options);
  sessionCancelModalLastFocused = null;
}

function openSessionCancelModal(options = {}) {
  const modal = els.sessionCancelModal;
  if (!(modal instanceof HTMLElement) || isSessionCancelModalOpen()) {
    return;
  }

  const { wasOpen, restoreState } = getModalInteractionState(modal, options);
  if (!wasOpen) {
    sessionCancelModalLastFocused = restoreState?.focusTarget instanceof HTMLElement
      ? restoreState.focusTarget
      : null;
  }
  showModalWithInteraction(modal, restoreState, wasOpen, options);

  scheduleFocusWithoutScroll(els.sessionCancelModalKeepBtn);
}

function closeSessionFinishModal(options = {}) {
  const modal = els.sessionFinishModal;
  if (!(modal instanceof HTMLElement)) {
    return;
  }
  if (!isSessionFinishModalOpen()) {
    return;
  }

  const restoreTarget = sessionFinishModalLastFocused;
  modal.removeAttribute("data-workout-id");
  closeModalWithInteraction(modal, restoreTarget, options);
  sessionFinishModalLastFocused = null;
}

function openSessionFinishModal(workoutId, options = {}) {
  const modal = els.sessionFinishModal;
  const safeWorkoutId = String(workoutId || "").trim();
  if (!(modal instanceof HTMLElement) || isSessionFinishModalOpen() || !safeWorkoutId) {
    return;
  }

  const { wasOpen, restoreState } = getModalInteractionState(modal, options);
  if (!wasOpen) {
    sessionFinishModalLastFocused = restoreState?.focusTarget instanceof HTMLElement
      ? restoreState.focusTarget
      : null;
  }
  modal.dataset.workoutId = safeWorkoutId;
  showModalWithInteraction(modal, restoreState, wasOpen, options);

  scheduleFocusWithoutScroll(els.sessionFinishModalKeepBtn);
}

function closeSessionConflictModal(choice = "cancel", options = {}) {
  const modal = els.sessionConflictModal;
  if (!(modal instanceof HTMLElement) || !isSessionConflictModalOpen()) {
    return;
  }

  const restoreTarget = sessionConflictModalLastFocused;

  const resolver = sessionConflictModalResolve;
  sessionConflictModalResolve = null;
  if (resolver) {
    resolver(choice);
  }

  closeModalWithInteraction(modal, restoreTarget, options);
  sessionConflictModalLastFocused = null;
}

function openSessionConflictModal(options = {}) {
  const modal = els.sessionConflictModal;
  if (!(modal instanceof HTMLElement)) {
    return Promise.resolve("cancel");
  }

  if (isSessionConflictModalOpen()) {
    closeSessionConflictModal("cancel", { restoreFocus: false, syncHistory: false });
  }

  const { wasOpen, restoreState } = getModalInteractionState(modal, options);
  if (!wasOpen) {
    sessionConflictModalLastFocused = restoreState?.focusTarget instanceof HTMLElement
      ? restoreState.focusTarget
      : null;
  }

  return new Promise((resolve) => {
    sessionConflictModalResolve = resolve;
    showModalWithInteraction(modal, restoreState, wasOpen, options);

    scheduleFocusWithoutScroll(els.sessionConflictModalResumeBtn);
  });
}

function closeSessionNoticeModal(options = {}) {
  const modal = els.sessionNoticeModal;
  if (!(modal instanceof HTMLElement) || !isSessionNoticeModalOpen()) {
    return;
  }

  const restoreTarget = sessionNoticeModalLastFocused;
  closeModalWithInteraction(modal, restoreTarget, options);
  sessionNoticeModalLastFocused = null;
}

function normalizeSessionNoticeMessage(message) {
  const safeMessage = String(message || "").trim();
  if (!safeMessage) {
    return "요청을 완료하지 못했습니다.";
  }

  if (safeMessage === "weight 범위를 확인하세요") {
    return "중량 범위를 확인하세요.";
  }
  if (safeMessage === "reps 범위를 확인하세요") {
    return "횟수 범위를 확인하세요.";
  }
  if (safeMessage === "weight 형식이 올바르지 않습니다") {
    return "중량 형식이 올바르지 않습니다.";
  }
  if (safeMessage === "reps 형식이 올바르지 않습니다") {
    return "횟수 형식이 올바르지 않습니다.";
  }

  return safeMessage;
}

function buildSessionNoticeContent(message) {
  const normalizedMessage = normalizeSessionNoticeMessage(message);
  const isInputWarning = /중량|무게|횟수|입력값|입력|형식|범위/i.test(normalizedMessage);

  return {
    title: isInputWarning ? "입력값을 확인하세요" : "운동 진행 중 확인이 필요합니다",
    meta: isInputWarning
      ? "세트 입력값이 저장되지 않았습니다. 값을 다시 확인한 뒤 계속 진행하세요."
      : "요청을 완료하지 못했습니다. 내용을 확인한 뒤 다시 시도하세요.",
    message: normalizedMessage,
  };
}

function openSessionNoticeModal(message, options = {}) {
  const modal = els.sessionNoticeModal;
  const titleEl = els.sessionNoticeModalTitle;
  const metaEl = els.sessionNoticeModalMeta;
  const copyEl = els.sessionNoticeModalCopy;
  if (
    !(modal instanceof HTMLElement) ||
    !(titleEl instanceof HTMLElement) ||
    !(metaEl instanceof HTMLElement) ||
    !(copyEl instanceof HTMLElement)
  ) {
    const fallbackContent = {
      ...buildSessionNoticeContent(message),
      ...(options || {}),
    };
    void openAppAlertModal(fallbackContent.message, fallbackContent);
    return;
  }

  const content = {
    ...buildSessionNoticeContent(message),
    ...(options || {}),
  };

  titleEl.textContent = String(content.title || "운동 진행 중 확인이 필요합니다").trim() || "운동 진행 중 확인이 필요합니다";
  metaEl.textContent = String(content.meta || "").trim();
  copyEl.textContent = String(content.message || normalizeSessionNoticeMessage(message)).trim() || "요청을 완료하지 못했습니다.";

  const { wasOpen, restoreState } = getModalInteractionState(modal, options);
  if (!wasOpen) {
    sessionNoticeModalLastFocused = restoreState?.focusTarget instanceof HTMLElement
      ? restoreState.focusTarget
      : null;
  }
  showModalWithInteraction(modal, restoreState, wasOpen, options);

  scheduleFocusWithoutScroll(els.sessionNoticeModalConfirmBtn);
}

function getActiveSessionInlineNotice() {
  const activeWorkoutId = String(getState().activeWorkout?.workoutId || "").trim();
  const noticeWorkoutId = String(sessionInlineNotice?.workoutId || "").trim();
  if (!activeWorkoutId || !noticeWorkoutId || activeWorkoutId !== noticeWorkoutId) {
    return null;
  }
  return sessionInlineNotice;
}

function syncSessionInlineNoticeUi() {
  const mount = els.tabs.session?.querySelector("#sessionInlineNoticeMount");
  if (!(mount instanceof HTMLElement)) {
    return;
  }
  setElementHtml(mount, renderSessionInlineNotice(getActiveSessionInlineNotice()));
}

function setSessionInlineNotice(notice) {
  sessionInlineNotice =
    notice && typeof notice === "object"
      ? {
        workoutId: String(notice.workoutId || "").trim(),
        title: String(notice.title || "").trim(),
        meta: String(notice.meta || "").trim(),
        message: String(notice.message || "").trim(),
      }
      : null;

  if (!sessionInlineNotice?.workoutId || !sessionInlineNotice?.message) {
    sessionInlineNotice = null;
  }
  syncSessionInlineNoticeUi();
}

function clearSessionInlineNotice(workoutId = "") {
  if (!sessionInlineNotice) {
    return;
  }

  const safeWorkoutId = String(workoutId || "").trim();
  if (safeWorkoutId && String(sessionInlineNotice.workoutId || "").trim() !== safeWorkoutId) {
    return;
  }

  sessionInlineNotice = null;
  syncSessionInlineNoticeUi();
}

function isSessionInteractionTarget(target) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  if (target.closest("#tab-session")) {
    return true;
  }

  if (
    target.closest("#sessionCancelModal") ||
    target.closest("#sessionFinishModal") ||
    target.closest("#sessionConflictModal") ||
    target.closest("#sessionNoticeModal")
  ) {
    return true;
  }

  if (target.closest(".session-nav-btn")) {
    return true;
  }

  return false;
}

function convertWorkoutItemToBuilderItem(workoutItem) {
  const setsFromPlan = Number(workoutItem?.setsPlan);
  const setsFromMap = Object.keys(workoutItem?.sets || {}).length;
  const sets = Number.isInteger(setsFromPlan) && setsFromPlan > 0
    ? setsFromPlan
    : Math.max(1, setsFromMap || 3);

  return {
    exerciseId: String(workoutItem?.exerciseId || "").trim(),
    exerciseDisplayName: String(workoutItem?.nameSnapshot || "").trim(),
    exerciseQuery: "",
    exerciseSearchResults: [],
    sets: String(sets),
    restSec: "90",
    loadType: "none",
    percent: "",
    rmBase: "bench",
    repsMode: "none",
    fixedReps: "",
    rangeMin: "",
    rangeMax: "",
  };
}

function convertWorkoutItemToManualItem(workoutItem) {
  const setsFromPlan = Number(workoutItem?.setsPlan);
  const setsFromMap = Object.keys(workoutItem?.sets || {}).length;
  const rawSets = Number.isInteger(setsFromPlan) && setsFromPlan > 0
    ? setsFromPlan
    : Math.max(1, setsFromMap || 3);
  const sets = Math.min(MAX_SETS_PER_EXERCISE, Math.max(1, rawSets));
  const rawRestSec = Number(workoutItem?.restSec);
  const restSec = Number.isFinite(rawRestSec)
    ? Math.min(MAX_REST_SEC, Math.max(MIN_REST_SEC, rawRestSec))
    : 90;

  return {
    exerciseId: String(workoutItem?.exerciseId || "").trim(),
    sets,
    restSec,
    loadRule: null,
    repsRule: null,
  };
}

async function getProgramDayImportEntry(workoutId) {
  const safeWorkoutId = String(workoutId || "").trim();
  if (!safeWorkoutId) {
    return null;
  }

  let entry = programDayImportWorkoutMap.get(safeWorkoutId);
  if (entry) {
    return entry;
  }

  const workoutDetail = await api.getWorkout(safeWorkoutId);
  entry = {
    workoutId: safeWorkoutId,
    name: resolveWorkoutSessionDisplayName(workoutDetail?.meta),
    date: String(workoutDetail?.meta?.date || "-"),
    weekday: String(workoutDetail?.meta?.weekday || "").trim(),
    exercises: summarizeWorkoutExerciseNames(workoutDetail),
    workoutDetail,
  };
  programDayImportWorkoutMap.set(safeWorkoutId, entry);
  return entry;
}

async function handleStartManualWorkoutFromSession(workoutId) {
  const entry = await getProgramDayImportEntry(workoutId);
  const manualItems = Object.entries(entry?.workoutDetail?.items || {})
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([, item]) => convertWorkoutItemToManualItem(item))
    .filter((item) => item.exerciseId);

  if (manualItems.length === 0) {
    throw new Error("선택한 운동 기록에 불러올 운동이 없습니다");
  }

  closeProgramDayImportModal({ restoreFocus: false });

  const startOption = await resolveSessionConflictForNewWorkout();
  if (!startOption.proceed) {
    return;
  }

  const result = await api.trackJourney("start workout", async () => {
    return api.startWorkout({
      mode: "manual",
      manualItems,
      replaceActive: startOption.replaceActive,
    });
  });
  await enterStartedWorkout(result);
}

async function handleImportProgramDayFromSession(workoutId) {
  const safeWorkoutId = String(workoutId || "").trim();
  if (!safeWorkoutId) {
    return;
  }

  const modal = els.programDayImportModal;
  if (String(modal?.dataset?.importTarget || "program-day") === "manual-workout") {
    await handleStartManualWorkoutFromSession(safeWorkoutId);
    return;
  }

  const selectedDayIndex = clampProgramBuilderDayIndex(
    Number(modal?.dataset?.dayIndex ?? getState().programBuilderDayIndex ?? 0),
    ensureProgramBuilder(),
  );

  const entry = await getProgramDayImportEntry(safeWorkoutId);

  const sortedItems = Object.entries(entry?.workoutDetail?.items || {})
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([, item]) => convertWorkoutItemToBuilderItem(item))
    .filter((item) => item.exerciseId);

  if (sortedItems.length === 0) {
    throw new Error("선택한 운동 기록에 불러올 운동이 없습니다");
  }

  updateProgramBuilder((builder) => {
    if (!builder.days[selectedDayIndex]) {
      builder.days[selectedDayIndex] = createBuilderDay(selectedDayIndex);
    }
    builder.days[selectedDayIndex].items = sortedItems;
  });

  const importedExerciseNameMap = sortedItems.reduce((acc, item) => {
    if (item.exerciseId && item.exerciseDisplayName) {
      acc[item.exerciseId] = item.exerciseDisplayName;
    }
    return acc;
  }, {});
  if (Object.keys(importedExerciseNameMap).length > 0) {
    setState({
      exerciseNameMap: {
        ...(getState().exerciseNameMap || {}),
        ...importedExerciseNameMap,
      },
    });
  }

  closeProgramDayImportModal();
}

function focusOneRMInput(rmBase) {
  const inputId = RM_BASE_INPUT_IDS[rmBase];
  if (!inputId) {
    return;
  }

  scheduleFocusWithoutScroll(() => document.getElementById(inputId), { select: true });
}

function moveToProfileForMissingOneRM(missingBases) {
  if (!missingBases.length) {
    return;
  }

  const labels = missingBases.map((base) => RM_BASE_LABELS[base] || String(base));
  setState({ selectedTab: "profile" });
  scrollToTop();
  void openAppAlertModal(`필요 항목: ${labels.join(", ")}`, {
    title: "1RM 입력이 필요합니다",
    meta: "프로그램 시작 전에 아래 항목을 먼저 입력하세요.",
    restoreFocus: false,
  }).then(() => {
    focusOneRMInput(missingBases[0]);
  });
}

async function buildActiveProgramPreview(activeProgram) {
  if (!activeProgram?.days) {
    return null;
  }

  const dayIndex = resolveProgramTodayDayIndex(activeProgram);
  const day = activeProgram.days?.[String(dayIndex)];
  const dayAlias =
    activeProgram.dayAliases?.[String(dayIndex)] ||
    `Day ${dayIndex + 1}`;
  if (!day?.items) {
    return {
      dayIndex,
      dayAlias,
      items: [],
      totalItems: 0,
      muscles: [],
    };
  }

  const sortedItems = Object.entries(day.items).sort((a, b) => Number(a[0]) - Number(b[0]));
  const totalItems = sortedItems.length;
  const exerciseIds = Array.from(
    new Set(
      sortedItems
        .map(([, item]) => String(item?.exerciseId || "").trim())
        .filter(Boolean),
    ),
  );
  const exerciseMap = await fetchExerciseMapByIds(exerciseIds);

  const items = await Promise.all(
    sortedItems.map(async ([order, item]) => {
      let name = "운동";
      let primaryMuscle = "";
      const exercise = exerciseMap[item.exerciseId];
      if (exercise?.nameKo) {
        name = exercise.nameKo;
      }
      if (exercise?.primaryMuscle) {
        primaryMuscle = formatMuscleLabel(exercise.primaryMuscle);
      }

      return {
        order: Number(order),
        exerciseId: item.exerciseId,
        name,
        primaryMuscle,
        sets: Math.max(0, Number(item?.sets) || 0),
      };
    }),
  );
  const bodyHeatmap = createBodyHeatmapFromEntries(
    sortedItems.map(([, item]) => {
      const exercise = exerciseMap[item.exerciseId] || {};
      return {
        sets: Math.max(0, Number(item?.sets) || 0),
        primaryMuscle: exercise.primaryMuscle || "",
        secondaryMuscles: Array.isArray(exercise.secondaryMuscles) ? exercise.secondaryMuscles : [],
      };
    }),
  );

  return {
    dayIndex,
    dayAlias,
    items: items.slice(0, HOME_PREVIEW_ITEM_LIMIT),
    totalItems,
    muscles: Array.from(new Set(items.map((entry) => entry.primaryMuscle).filter(Boolean))),
    bodyHeatmap,
  };
}

function sortProgramsByUpdatedAt(programs) {
  return [...(programs || [])].sort((a, b) => (Number(b?.updatedAt) || 0) - (Number(a?.updatedAt) || 0));
}

function sortWorkoutSummariesByStartedAt(workouts) {
  return [...(workouts || [])].sort(
    (a, b) => (Number(b?.meta?.startedAt) || 0) - (Number(a?.meta?.startedAt) || 0),
  );
}

function resolveActiveProgramFromPrograms(programs, activeProgramId = "") {
  const safePrograms = Array.isArray(programs) ? programs : [];
  const safeActiveProgramId = String(activeProgramId || "").trim();
  return (
    safePrograms.find((program) => !!program?.isActive) ||
    safePrograms.find((program) => String(program?.id || "").trim() === safeActiveProgramId) ||
    null
  );
}

function mergeProgramIntoList(programs, program) {
  const safeProgramId = String(program?.id || "").trim();
  if (!safeProgramId) {
    return sortProgramsByUpdatedAt(programs);
  }

  const nextPrograms = (programs || [])
    .filter((entry) => String(entry?.id || "").trim() !== safeProgramId)
    .map((entry) =>
      program?.isActive
        ? {
          ...entry,
          isActive: false,
        }
        : entry,
    );

  nextPrograms.push(program);
  return sortProgramsByUpdatedAt(nextPrograms);
}

function removeProgramFromList(programs, programId) {
  const safeProgramId = String(programId || "").trim();
  return sortProgramsByUpdatedAt(
    (programs || []).filter((entry) => String(entry?.id || "").trim() !== safeProgramId),
  );
}

async function commitProgramsState(programs, options = {}) {
  const currentState = getState();
  const nextPrograms = sortProgramsByUpdatedAt(programs);
  const activeProgram = resolveActiveProgramFromPrograms(nextPrograms, options.activeProgramId);
  const nextState = {
    programs: nextPrograms,
    activeProgram,
  };

  if (Object.prototype.hasOwnProperty.call(options, "homePreview")) {
    nextState.homePreview = options.homePreview;
  } else if (options.skipHomePreview !== true) {
    nextState.homePreview = currentState.homePreview || null;
  } else if (currentState.homePreview) {
    nextState.homePreview = currentState.homePreview;
  }

  setState(nextState);
  return {
    programs: nextPrograms,
    activeProgram,
  };
}

function buildWorkoutSummaryEntry(workout) {
  const workoutId = String(workout?.workoutId || "").trim();
  if (!workoutId || !workout?.meta) {
    return null;
  }

  return {
    workoutId,
    meta: workout.meta,
    itemCount: Object.keys(workout?.items || {}).length,
  };
}

function upsertWorkoutSummary(workouts, workoutEntry, limit = WORKOUT_LIST_LIMIT) {
  const safeWorkoutId = String(workoutEntry?.workoutId || "").trim();
  if (!safeWorkoutId) {
    return sortWorkoutSummariesByStartedAt(workouts).slice(0, limit);
  }

  const nextWorkouts = (workouts || []).filter((entry) => String(entry?.workoutId || "").trim() !== safeWorkoutId);
  nextWorkouts.push(workoutEntry);
  return sortWorkoutSummariesByStartedAt(nextWorkouts).slice(0, limit);
}

async function fetchLatestWorkoutItemsByExercise(exerciseIds, excludeWorkoutId, options = {}) {
  const safeExerciseIds = Array.from(
    new Set(
      (exerciseIds || [])
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  );
  if (safeExerciseIds.length === 0) {
    return {};
  }

  try {
    const result = await api.getLatestWorkoutItems(safeExerciseIds, excludeWorkoutId, {
      forceRefresh: !!options.forceRefresh,
    });
    if (result?.records && typeof result.records === "object") {
      return result.records;
    }
  } catch (_) {
    // Fall back to single-item requests while the new endpoint rolls out.
  }

  const entries = await Promise.all(
    safeExerciseIds.map(async (exerciseId) => {
      try {
        const result = await api.getLatestWorkoutItem(exerciseId, excludeWorkoutId, {
          forceRefresh: !!options.forceRefresh,
        });
        return [exerciseId, result.record || null];
      } catch (_) {
        return [exerciseId, null];
      }
    }),
  );

  return entries.reduce((acc, [exerciseId, record]) => {
    acc[exerciseId] = record;
    return acc;
  }, {});
}

function convertProgramToBuilder(program) {
  const cycleType = "sequence";
  const lengthDays = Math.max(1, Number(program?.cycle?.lengthDays) || Object.keys(program?.days || {}).length || 1);
  const builder = createDefaultProgramBuilder(lengthDays);
  builder.targetProgramId = program.id;
  builder.name = program.name || "";
  builder.cycleType = cycleType;
  builder.lengthDays = String(lengthDays);
  builder.isActive = !!program.isActive;
  builder.trial = Number(program.trial) || 0;
  builder.days = Array.from({ length: lengthDays }, (_, index) => {
    const day = program.days?.[String(index)];
    const items = Object.entries(day?.items || {})
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([, item]) => ({
        exerciseId: item.exerciseId || "",
        exerciseDisplayName: "",
        exerciseQuery: "",
        exerciseSearchResults: [],
        pickerOpen: false,
        sets: String(item.sets || 3),
        restSec: String(item.restSec || 90),
        loadType: item.loadRule?.type === "percent1RM" ? "percent1RM" : "none",
        percent: item.loadRule?.type === "percent1RM" ? String(item.loadRule.percent ?? "") : "",
        rmBase: item.loadRule?.type === "percent1RM" ? item.loadRule.rmBase || "bench" : "bench",
        repsMode:
          item.repsRule?.type === "fixed"
            ? "fixed"
            : item.repsRule?.type === "range"
              ? "range"
              : "none",
        fixedReps: item.repsRule?.type === "fixed" ? String(item.repsRule.reps ?? "") : "",
        rangeMin: item.repsRule?.type === "range" ? String(item.repsRule.min ?? "") : "",
        rangeMax: item.repsRule?.type === "range" ? String(item.repsRule.max ?? "") : "",
      }));

    return {
      alias: program.dayAliases?.[String(index)] || `Day ${index + 1}`,
      weekday: "",
      items: items.length > 0 ? items : [createBuilderItem()],
    };
  });

  return builder;
}

function showLoginError(message = "") {
  els.loginError.textContent = message;
}

function showSignupError(message = "") {
  if (els.signupError instanceof HTMLElement) {
    els.signupError.textContent = message;
  }
}

function showConsentError(message = "") {
  if (els.consentError instanceof HTMLElement) {
    els.consentError.textContent = message;
  }
}

function buildRequiredLegalConsentPayload() {
  return {
    terms: {
      version: TERMS_CONSENT_VERSION,
      required: true,
    },
    privacyCollection: {
      version: PRIVACY_CONSENT_VERSION,
      required: true,
    },
  };
}

function hasRequiredLegalConsents(profile) {
  const consents = profile?.consents;
  return (
    String(consents?.terms?.version || "").trim() === TERMS_CONSENT_VERSION &&
    consents?.terms?.required === true &&
    Number(consents?.terms?.agreedAt) > 0 &&
    String(consents?.privacyCollection?.version || "").trim() === PRIVACY_CONSENT_VERSION &&
    consents?.privacyCollection?.required === true &&
    Number(consents?.privacyCollection?.agreedAt) > 0
  );
}

function clearSignupConsentInputs() {
  if (els.signupAgreeTermsInput instanceof HTMLInputElement) {
    els.signupAgreeTermsInput.checked = false;
  }
  if (els.signupAgreePrivacyInput instanceof HTMLInputElement) {
    els.signupAgreePrivacyInput.checked = false;
  }
}

function setSignupPasswordConfirmValidation(message = "") {
  const input = els.signupPasswordConfirmInput;
  const helper = els.signupPasswordConfirmHelper;
  const field = input instanceof HTMLElement ? input.closest(".field") : null;
  const safeMessage = String(message || "").trim();

  if (!(input instanceof HTMLInputElement) || !(field instanceof HTMLElement)) {
    return;
  }

  if (!safeMessage) {
    field.classList.remove("is-invalid");
    input.removeAttribute("aria-invalid");
    input.removeAttribute("aria-describedby");
    if (helper instanceof HTMLElement) {
      helper.textContent = "";
      helper.classList.remove("is-error");
      helper.classList.add("hidden");
    }
    return;
  }

  field.classList.add("is-invalid");
  input.setAttribute("aria-invalid", "true");
  if (helper instanceof HTMLElement) {
    helper.textContent = safeMessage;
    helper.classList.add("is-error");
    helper.classList.remove("hidden");
    input.setAttribute("aria-describedby", helper.id);
  }
}

function validateSignupPasswordConfirmation(options = {}) {
  const password = els.signupPasswordInput instanceof HTMLInputElement ? els.signupPasswordInput.value || "" : "";
  const passwordConfirm =
    els.signupPasswordConfirmInput instanceof HTMLInputElement ? els.signupPasswordConfirmInput.value || "" : "";
  const focusOnError = options?.focusOnError === true;
  const requireFilled = options?.requireFilled === true;

  if (!passwordConfirm) {
    if (!requireFilled) {
      setSignupPasswordConfirmValidation("");
      return true;
    }

    setSignupPasswordConfirmValidation("비밀번호 확인을 입력해 주세요");
    if (focusOnError && els.signupPasswordConfirmInput instanceof HTMLInputElement) {
      scheduleFocusWithoutScroll(els.signupPasswordConfirmInput);
    }
    return false;
  }

  if (password && passwordConfirm && password !== passwordConfirm) {
    setSignupPasswordConfirmValidation("비밀번호가 일치하지 않습니다");
    if (focusOnError && els.signupPasswordConfirmInput instanceof HTMLInputElement) {
      scheduleFocusWithoutScroll(els.signupPasswordConfirmInput);
    }
    return false;
  }

  setSignupPasswordConfirmValidation("");
  return true;
}

function resetConsentGateInputs(prechecked = false) {
  if (els.consentAgreeTermsInput instanceof HTMLInputElement) {
    els.consentAgreeTermsInput.checked = prechecked;
  }
  if (els.consentAgreePrivacyInput instanceof HTMLInputElement) {
    els.consentAgreePrivacyInput.checked = prechecked;
  }
}

function getConsentGateSelection() {
  return {
    agreeTerms: els.consentAgreeTermsInput instanceof HTMLInputElement && els.consentAgreeTermsInput.checked,
    agreePrivacy: els.consentAgreePrivacyInput instanceof HTMLInputElement && els.consentAgreePrivacyInput.checked,
  };
}

function createConsentGateCancelledError() {
  const error = new Error(CONSENT_GATE_CANCELLED_CODE);
  error.code = CONSENT_GATE_CANCELLED_CODE;
  return error;
}

function isConsentGateCancelledError(error) {
  return String(error?.code || error?.message || "").trim() === CONSENT_GATE_CANCELLED_CODE;
}

function getVisibleAuthFlowPanel() {
  if (els.consentPanel instanceof HTMLElement && !els.consentPanel.classList.contains("hidden")) {
    return AUTH_FLOW_PANELS.consent;
  }
  if (els.signupPanel instanceof HTMLElement && !els.signupPanel.classList.contains("hidden")) {
    return AUTH_FLOW_PANELS.signup;
  }
  if (els.loginPanel instanceof HTMLElement && !els.loginPanel.classList.contains("hidden")) {
    return AUTH_FLOW_PANELS.login;
  }
  return null;
}

function closeConsentPanel() {
  if (els.consentPanel instanceof HTMLElement) {
    els.consentPanel.classList.add("hidden");
  }
  showConsentError("");
  resetConsentGateInputs(false);
}

function settleConsentGate(type, value) {
  const resolve = consentGateResolve;
  const reject = consentGateReject;

  consentGatePromise = null;
  consentGateResolve = null;
  consentGateReject = null;
  closeConsentPanel();

  if (type === "resolve") {
    resolve?.(value);
    return;
  }

  reject?.(value);
}

async function exitConsentGateToLogin(options = {}) {
  settleConsentGate("reject", createConsentGateCancelledError());
  showLoginPanel({
    syncHistory: options?.syncHistory === true,
  });

  if (options?.signOut === false || !auth?.signOut) {
    return;
  }

  try {
    await auth.signOut();
  } catch (_) {
    // Ignore explicit sign-out failures while leaving the consent gate.
  }
}

async function applyAuthFlowSnapshot(snapshot) {
  const panel = normalizeAuthFlowPanel(snapshot?.panel);

  if (panel === AUTH_FLOW_PANELS.consent) {
    if (!auth?.currentUser && !getState().user) {
      showLoginPanel({ syncHistory: false });
      return;
    }
    await openConsentGate({
      prechecked: !!getPendingSignupConsents(),
      syncHistory: false,
    });
    return;
  }

  if (panel === AUTH_FLOW_PANELS.signup) {
    showSignupPanel({ syncHistory: false });
    return;
  }

  if (getVisibleAuthFlowPanel() === AUTH_FLOW_PANELS.consent) {
    await exitConsentGateToLogin({
      signOut: true,
      syncHistory: false,
    });
    return;
  }

  showLoginPanel({ syncHistory: false });
}

function openConsentGate(options = {}) {
  if (consentGatePromise) {
    return consentGatePromise;
  }

  if (els.loginPanel instanceof HTMLElement) {
    els.loginPanel.classList.add("hidden");
  }
  if (els.signupPanel instanceof HTMLElement) {
    els.signupPanel.classList.add("hidden");
  }
  if (els.consentPanel instanceof HTMLElement) {
    els.consentPanel.classList.remove("hidden");
  }
  if (els.authBadge instanceof HTMLElement) {
    els.authBadge.textContent = "필수 동의 필요";
  }
  els.appMain.classList.remove("active");
  els.bottomNav.classList.add("hidden");
  showLoginError("");
  showSignupError("");
  showConsentError("");
  resetConsentGateInputs(options?.prechecked === true);
  if (options?.syncHistory !== false) {
    replaceCurrentAuthFlowSnapshot({
      panel: AUTH_FLOW_PANELS.login,
    }, {
      depth: 0,
      force: true,
    });
    pushCurrentAuthFlowSnapshot({
      panel: AUTH_FLOW_PANELS.consent,
    });
  }

  consentGatePromise = new Promise((resolve, reject) => {
    consentGateResolve = resolve;
    consentGateReject = reject;
  });

  return consentGatePromise;
}

function cancelOpenConsentGate() {
  if (!consentGatePromise) {
    closeConsentPanel();
    return;
  }

  settleConsentGate("reject", createConsentGateCancelledError());
}

function getAuthErrorCode(error) {
  return String(error?.code || "").trim();
}

function resolveAuthErrorMessage(error, fallbackMessage) {
  const code = getAuthErrorCode(error);
  if (code === "auth/unauthorized-domain") {
    return `OAuth 도메인 허용 오류입니다. Firebase Authentication > Authorized domains에 '${window.location.hostname}'를 추가했는지 확인하세요.`;
  }
  if (code === "auth/web-storage-unsupported") {
    return "이 기기에서는 로그인 정보를 저장할 수 없습니다. 쿠키를 허용한 뒤 다시 시도해 주세요.";
  }
  if (
    code === "auth/popup-blocked" ||
    code === "auth/popup-closed-by-user" ||
    code === "auth/cancelled-popup-request" ||
    code === "auth/operation-not-supported-in-this-environment"
  ) {
    return "로그인 창을 열 수 없습니다. 팝업 차단을 해제한 뒤 다시 시도해 주세요.";
  }
  if (code === "auth/network-request-failed") {
    return "네트워크 연결을 확인한 뒤 다시 시도해 주세요.";
  }
  if (code === "auth/too-many-requests") {
    return "요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.";
  }
  return fallbackMessage;
}

async function lookupSignInMethodsForEmailAPI(email) {
  const normalizedEmail = String(email || "").trim();
  if (!normalizedEmail) {
    return null;
  }
  try {
    const res = await fetch(
      `/api/auth/email-sign-in-methods?email=${encodeURIComponent(normalizedEmail)}`,
      { method: "GET", cache: "no-store" }
    );
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json?.ok) {
      return null;
    }
    return {
      exists: json?.data?.exists === true,
      signInMethods: Array.isArray(json?.data?.signInMethods)
        ? json.data.signInMethods.map((s) => String(s || "").trim()).filter(Boolean)
        : [],
    };
  } catch (_) {
    return null;
  }
}

async function lookupSignInMethodsForEmail(email) {
  const normalizedEmail = String(email || "").trim();
  if (!normalizedEmail || !auth?.fetchSignInMethodsForEmail) {
    return null;
  }
  try {
    const methods = await auth.fetchSignInMethodsForEmail(normalizedEmail);
    return Array.isArray(methods) ? methods : [];
  } catch (_) {
    return null;
  }
}

async function resolveEmailLoginErrorMessage(error, email = "") {
  const code = getAuthErrorCode(error);
  if (code === "auth/missing-email") {
    return "이메일을 입력해 주세요.";
  }
  if (code === "auth/missing-password") {
    return "비밀번호를 입력해 주세요.";
  }
  if (code === "auth/invalid-email") {
    return "올바른 이메일 주소를 입력해 주세요.";
  }
  if (code === "auth/user-not-found") {
    return "가입되지 않은 이메일입니다. 이메일을 확인하거나 회원가입을 진행해 주세요.";
  }
  if (code === "auth/wrong-password") {
    return "비밀번호가 올바르지 않습니다. 다시 입력해 주세요.";
  }
  if (code === "auth/user-disabled") {
    return "사용이 중지된 계정입니다. 관리자에게 문의해 주세요.";
  }
  if (code === "auth/invalid-credential" || code === "auth/invalid-login-credentials") {
    const apiResult = await lookupSignInMethodsForEmailAPI(email);
    if (apiResult) {
      if (apiResult.exists) {
        if (apiResult.signInMethods.includes("password")) {
          return "비밀번호가 올바르지 않습니다. 다시 입력해 주세요.";
        }
        if (apiResult.signInMethods.includes("google.com")) {
          return "이 이메일은 Google 계정으로 가입되어 있습니다. Google 로그인을 이용해 주세요.";
        }
        return "이 이메일은 다른 로그인 방식으로 가입되어 있습니다. 해당 방식으로 로그인해 주세요.";
      }
      return "가입되지 않은 이메일입니다. 이메일을 확인하거나 회원가입을 진행해 주세요.";
    }

    const methods = await lookupSignInMethodsForEmail(email);
    if (Array.isArray(methods) && methods.length > 0) {
      if (methods.includes("password")) {
        return "비밀번호가 올바르지 않습니다. 다시 입력해 주세요.";
      }
      if (methods.includes("google.com")) {
        return "이 이메일은 Google 계정으로 가입되어 있습니다. Google 로그인을 이용해 주세요.";
      }
      return "이 이메일은 다른 로그인 방식으로 가입되어 있습니다. 해당 방식으로 로그인해 주세요.";
    }

    return "비밀번호가 올바르지 않습니다. 다시 입력해 주세요.";
  }
  return resolveAuthErrorMessage(error, "로그인에 실패했습니다. 다시 시도해 주세요.");
}

function resolveSignupErrorMessage(error) {
  const code = getAuthErrorCode(error);
  if (!code && error instanceof Error && error.message) {
    return error.message;
  }
  if (code === "auth/invalid-email") {
    return "올바른 이메일 주소를 입력해 주세요.";
  }
  if (code === "auth/email-already-in-use") {
    return "이미 가입된 이메일입니다. 로그인하거나 다른 이메일을 사용해 주세요.";
  }
  if (code === "auth/weak-password") {
    return "비밀번호는 6자 이상으로 설정해 주세요.";
  }
  if (code === "auth/user-disabled") {
    return "사용이 중지된 계정입니다. 관리자에게 문의해 주세요.";
  }
  return resolveAuthErrorMessage(error, "회원가입에 실패했습니다. 다시 시도해 주세요.");
}

function isInAppBrowser() {
  const ua = String(window.navigator?.userAgent || "").toLowerCase();
  return /fban|fbav|instagram|line|kakaotalk|naver|daumapps|; wv\)|webview/.test(ua);
}

function isIosSafari() {
  const ua = String(window.navigator?.userAgent || "").toLowerCase();
  const platform = String(window.navigator?.platform || "").toLowerCase();
  const maxTouchPoints = Number(window.navigator?.maxTouchPoints || 0);
  const iosDevice = /iphone|ipad|ipod/.test(ua) || (platform === "macintel" && maxTouchPoints > 1);
  const safariBrowser = /safari/.test(ua) && !/chrome|crios|fxios|edgios|opr|opera|android/.test(ua);
  return iosDevice && safariBrowser;
}

function normalizeAuthHost(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }

  try {
    const candidate = raw.includes("://") ? raw : `https://${raw}`;
    return new URL(candidate).hostname.toLowerCase();
  } catch (_) {
    return raw
      .replace(/^https?:\/\//i, "")
      .replace(/\/.*$/, "")
      .toLowerCase();
  }
}

function isSameSiteGoogleRedirectAvailable() {
  const currentHost = normalizeAuthHost(window.location?.hostname);
  const authDomainHost = normalizeAuthHost(auth?.app?.options?.authDomain);
  return !!currentHost && currentHost === authDomainHost;
}

async function signInGoogleWithFallback() {
  const provider = new window.firebase.auth.GoogleAuthProvider();
  provider.setCustomParameters({
    prompt: "select_account",
  });
  await applyAutoLoginPersistence();

  if (shouldForceRedirectGoogleSignIn()) {
    await auth.signInWithRedirect(provider);
    return;
  }

  // In-app browsers (Instagram, KakaoTalk, etc.) cannot open popups at all,
  // so go straight to redirect as the only option.
  if (isInAppBrowser()) {
    await auth.signInWithRedirect(provider);
    return;
  }

  // iOS Safari is much more reliable with redirect, but only when the auth
  // handler stays on the same site as the current page.
  if (isIosSafari() && isSameSiteGoogleRedirectAvailable()) {
    await auth.signInWithRedirect(provider);
    return;
  }

  // Default to popup for desktop and non-Safari mobile browsers. If popup
  // fails, fall back to redirect as a last resort.
  try {
    await auth.signInWithPopup(provider);
  } catch (error) {
    const code = String(error?.code || "").trim();
    const shouldFallbackToRedirect =
      code === "auth/popup-blocked" ||
      code === "auth/popup-closed-by-user" ||
      code === "auth/cancelled-popup-request" ||
      code === "auth/operation-not-supported-in-this-environment";
    if (!shouldFallbackToRedirect) {
      throw error;
    }
    // Last resort: try redirect (may still fail on custom domains).
    await auth.signInWithRedirect(provider);
  }
}

async function consumeGoogleRedirectResultIfAny() {
  if (!auth?.getRedirectResult) {
    return;
  }
  try {
    const redirectTimeoutMs = 12000;
    await Promise.race([
      auth.getRedirectResult(),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error("redirect-timeout")), redirectTimeoutMs);
      }),
    ]);
  } catch (error) {
    if (error instanceof Error && error.message === "redirect-timeout") {
      // Keep auth observer flow alive even when redirect result resolution is delayed.
      return;
    }
    showLoginError(resolveAuthErrorMessage(error, "Google 로그인 실패"));
  }
}

function loadAutoLoginPreference() {
  try {
    const raw = window.localStorage.getItem(AUTO_LOGIN_STORAGE_KEY);
    if (raw === null) {
      return true;
    }
    return raw === "1";
  } catch (_) {
    return true;
  }
}

function persistAutoLoginPreference(enabled) {
  try {
    window.localStorage.setItem(AUTO_LOGIN_STORAGE_KEY, enabled ? "1" : "0");
  } catch (_) {
    // Ignore localStorage failures in restricted environments.
  }
}

function refreshAutoLoginToggleButton() {
  const button = els.autoLoginToggleBtn;
  if (!(button instanceof HTMLButtonElement)) {
    return;
  }
  const enabled = autoLoginEnabled;
  button.setAttribute("aria-checked", enabled ? "true" : "false");
  button.setAttribute("title", enabled ? "자동 로그인 켜짐" : "자동 로그인 꺼짐");
  button.classList.toggle("is-enabled", autoLoginEnabled);
  if (els.autoLoginToggleState instanceof HTMLElement) {
    els.autoLoginToggleState.textContent = enabled ? "ON" : "OFF";
  }
  if (els.autoLoginToggleMeta instanceof HTMLElement) {
    els.autoLoginToggleMeta.textContent = enabled
      ? "이 기기에서 로그인 상태를 유지합니다."
      : "앱을 닫으면 다시 로그인해야 합니다.";
  }
}

function getAuthPersistenceByAutoLogin() {
  const persistence = window.firebase?.auth?.Auth?.Persistence;
  if (!persistence) {
    return null;
  }
  return autoLoginEnabled ? persistence.LOCAL : persistence.SESSION;
}

async function applyAutoLoginPersistence() {
  const persistence = getAuthPersistenceByAutoLogin();
  if (!persistence || !auth?.setPersistence) {
    return;
  }
  await auth.setPersistence(persistence);
}

async function handleEmailLogin() {
  showLoginError("");
  const { email, password } = getEmailPassword();
  if (!email) {
    showLoginError("이메일을 입력해 주세요.");
    return;
  }
  if (!password) {
    showLoginError("비밀번호를 입력해 주세요.");
    return;
  }

  try {
    await withGlobalLoading(async () => {
      await applyAutoLoginPersistence();
      await auth.signInWithEmailAndPassword(email, password);
    });
    clearEmailPassword();
  } catch (error) {
    showLoginError(await resolveEmailLoginErrorMessage(error, email));
  }
}

function getEmailPassword() {
  const email = (els.emailInput.value || "").trim();
  const password = els.passwordInput.value || "";
  return { email, password };
}

function getSignupPayload() {
  const email = (els.signupEmailInput?.value || "").trim();
  const password = els.signupPasswordInput?.value || "";
  const passwordConfirm = els.signupPasswordConfirmInput?.value || "";
  const nickname = (els.signupNicknameInput?.value || "").trim();
  const agreeTerms = els.signupAgreeTermsInput instanceof HTMLInputElement && els.signupAgreeTermsInput.checked;
  const agreePrivacy = els.signupAgreePrivacyInput instanceof HTMLInputElement && els.signupAgreePrivacyInput.checked;
  return {
    email,
    password,
    passwordConfirm,
    nickname,
    agreeTerms,
    agreePrivacy,
  };
}

function clearEmailPassword() {
  els.emailInput.value = "";
  els.passwordInput.value = "";
}

function clearSignupForm() {
  if (els.signupEmailInput instanceof HTMLInputElement) {
    els.signupEmailInput.value = "";
  }
  if (els.signupPasswordInput instanceof HTMLInputElement) {
    els.signupPasswordInput.value = "";
  }
  if (els.signupPasswordConfirmInput instanceof HTMLInputElement) {
    els.signupPasswordConfirmInput.value = "";
  }
  if (els.signupNicknameInput instanceof HTMLInputElement) {
    els.signupNicknameInput.value = "";
  }
  clearSignupConsentInputs();
  setSignupPasswordConfirmValidation("");
}

function showSignupPanel(options = {}) {
  if (els.loginPanel instanceof HTMLElement) {
    els.loginPanel.classList.add("hidden");
  }
  if (els.signupPanel instanceof HTMLElement) {
    els.signupPanel.classList.remove("hidden");
  }
  closeConsentPanel();
  showLoginError("");
  showSignupError("");
  setSignupPasswordConfirmValidation("");
  if (options?.syncHistory !== false) {
    replaceCurrentAuthFlowSnapshot({
      panel: AUTH_FLOW_PANELS.login,
    }, {
      depth: 0,
      force: true,
    });
    pushCurrentAuthFlowSnapshot({
      panel: AUTH_FLOW_PANELS.signup,
    });
  }
}

function setAuthSplashMessage(message) {
  if (!(els.authSplashMessage instanceof HTMLElement)) {
    return;
  }
  els.authSplashMessage.textContent = String(message || "").trim() || "회원 상태를 확인하고 있어요";
}

function startAuthSplashExit() {
  document.body.classList.remove("auth-pending");
  if (!(els.authSplash instanceof HTMLElement)) {
    return;
  }
  els.authSplash.classList.add("is-exiting");
  window.clearTimeout(authSplashHideTimer);
  authSplashHideTimer = window.setTimeout(() => {
    els.authSplash?.classList.add("hidden");
  }, AUTH_SPLASH_EXIT_MS);
}

function hideAuthSplash(options = {}) {
  if (authSplashReleased) {
    return;
  }
  authSplashReleased = true;

  const { immediate = false, message = "" } = options;
  if (message) {
    setAuthSplashMessage(message);
  }

  const now = typeof performance !== "undefined" ? performance.now() : Date.now();
  const elapsedMs = now - AUTH_SPLASH_STARTED_AT;
  const waitMs = immediate ? 0 : Math.max(0, AUTH_SPLASH_MIN_DISPLAY_MS - elapsedMs);

  window.clearTimeout(authSplashExitTimer);
  authSplashExitTimer = window.setTimeout(() => {
    startAuthSplashExit();
  }, waitMs);
}

function showLoginPanel(options = {}) {
  if (els.loginPanel instanceof HTMLElement) {
    els.loginPanel.classList.remove("hidden");
  }
  if (els.signupPanel instanceof HTMLElement) {
    els.signupPanel.classList.add("hidden");
  }
  closeConsentPanel();
  showLoginError("");
  showSignupError("");
  showConsentError("");
  setSignupPasswordConfirmValidation("");
  refreshAutoLoginToggleButton();
  if (options?.syncHistory !== false) {
    replaceCurrentAuthFlowSnapshot({
      panel: AUTH_FLOW_PANELS.login,
    }, {
      depth: 0,
      force: true,
    });
  }
}

function resolveAuthBadgeText(state = getState()) {
  const nickname = String(state.profile?.nickname || "").trim();
  const normalizedNickname = nickname === "LiftFlow User" ? "" : nickname;
  return normalizedNickname || state.user?.displayName || state.user?.email || "로그인";
}

function refreshAuthBadge(state = getState()) {
  const nextText = resolveAuthBadgeText(state);
  if (els.authBadge instanceof HTMLElement) {
    if (els.authBadge.textContent !== nextText) {
      els.authBadge.textContent = nextText;
    }
  }
}

function showAuthenticatedUI(user) {
  if (els.authBadge instanceof HTMLElement) {
    els.authBadge.textContent = user?.displayName || user?.email || "로그인";
  }
  if (els.loginPanel instanceof HTMLElement) {
    els.loginPanel.classList.add("hidden");
  }
  if (els.signupPanel instanceof HTMLElement) {
    els.signupPanel.classList.add("hidden");
  }
  closeConsentPanel();
  els.appMain.classList.add("active");
  els.bottomNav.classList.remove("hidden");
  resetAuthFlowNavigation(() => {
    clearAuthFlowHistoryState();
  });
}

function showLoggedOutUI() {
  cancelOpenConsentGate();
  clearAllDropSetAppliedKeys();
  clearAllSessionDraftBackups();
  clearSessionDraftPending();
  cancelSessionInputAutosave();
  if (els.authBadge instanceof HTMLElement) {
    els.authBadge.textContent = "로그인 필요";
  }
  showLoginPanel();
  els.appMain.classList.remove("active");
  els.bottomNav.classList.add("hidden");
  closeProgramDayPickerModal(null);
  closeProgramDayImportModal();
  closeProgramDeleteModal({ restoreFocus: false });
  stopSessionElapsedTimer();
  stopRestTimer();
  void releaseScreenWakeLock();
}

async function refreshUserData(options = {}) {
  const bootstrapOverride = options?.bootstrapData || null;
  const skipHomePreview = !!options?.skipHomePreview;
  const preserveCurrentActiveSession = !!options?.preserveCurrentActiveSession;
  const { uid } = getState();
  if (!uid) return;
  const currentState = getState();
  const currentBuilder = getState().programBuilder || createDefaultProgramBuilder();
  const currentBuilderDayIndex = getState().programBuilderDayIndex ?? 0;
  const currentBuilderStage = getState().programBuilderStage || "list";
  const currentProgramTemplateQuery = getState().programTemplateQuery || "";
  const currentExerciseNameMap = getState().exerciseNameMap || {};
  const currentWorkoutDetailsById = currentState.workoutDetailsById || {};
  const defaultLogsState = createDefaultLogsState();
  const nextLogsMode = currentState.logsMode || defaultLogsState.logsMode;
  const nextLogsSelectedDate = currentState.logsSelectedDate || defaultLogsState.logsSelectedDate;
  const nextLogsCalendarMonth = currentState.logsCalendarMonth || getMonthKeyFromDateKey(nextLogsSelectedDate);
  const nextLogsSelectedExerciseId = currentState.logsSelectedExerciseId || "";
  const nextLogsSelectedExerciseName = currentState.logsSelectedExerciseName || "";

  const bootstrapData = bootstrapOverride ? await Promise.resolve(bootstrapOverride) : await api.getBootstrap();
  const programs = Array.isArray(bootstrapData?.programs) ? bootstrapData.programs : [];
  const workouts = Array.isArray(bootstrapData?.workouts) ? bootstrapData.workouts : [];
  const customExercises = Array.isArray(bootstrapData?.customExercises) ? bootstrapData.customExercises : [];
  const activeProgram = resolveActiveProgramFromPrograms(programs, bootstrapData?.activeProgramId);
  const hasCachedHomePreview = !!currentState.homePreview;
  const homePreview = skipHomePreview && hasCachedHomePreview
    ? currentState.homePreview
    : Object.prototype.hasOwnProperty.call(bootstrapData || {}, "homePreview")
      ? bootstrapData.homePreview || null
      : currentState.homePreview || null;
  const nextExerciseNameMap = await resolveExerciseNameMap(
    collectExerciseIdsFromBuilder(currentBuilder),
    currentExerciseNameMap,
  );
  const workoutIdSet = new Set(workouts.map((workout) => workout.workoutId));
  const bundledActiveWorkoutId = String(bootstrapData?.activeWorkout?.workoutId || "").trim();
  if (bundledActiveWorkoutId) {
    workoutIdSet.add(bundledActiveWorkoutId);
  }
  const nextWorkoutDetailsById = Object.entries(currentWorkoutDetailsById).reduce((acc, [workoutId, detail]) => {
    if (workoutIdSet.has(workoutId)) {
      acc[workoutId] = detail;
    }
    return acc;
  }, {});
  if (bundledActiveWorkoutId && bootstrapData.activeWorkout) {
    nextWorkoutDetailsById[bundledActiveWorkoutId] = bootstrapData.activeWorkout;
  }
  const bootstrapActiveSession = bootstrapData.activeSession || null;
  const currentSessionWorkoutId = String(
    currentState.activeWorkout?.workoutId || currentState.activeSession?.workoutId || "",
  ).trim();
  const nextSessionWorkoutId = String(bootstrapActiveSession?.workoutId || "").trim();
  const nextActiveSession =
    preserveCurrentActiveSession &&
      currentState.activeSession &&
      currentSessionWorkoutId &&
      currentSessionWorkoutId === nextSessionWorkoutId
      ? currentState.activeSession
      : bootstrapActiveSession;

  setState({
    clientPlatform: resolveClientPlatform(),
    profile: bootstrapData.profile,
    settings: bootstrapData.settings,
    oneRM: bootstrapData.oneRM,
    estimatedOneRM: bootstrapData.estimatedOneRM || null,
    pushConfig: bootstrapData.pushConfig || null,
    androidConfig: bootstrapData.androidConfig || null,
    notificationCapabilities: bootstrapData.notificationCapabilities || null,
    programs,
    workouts,
    activeProgram,
    activeSession: nextActiveSession,
    customExercises,
    homePreview,
    programBuilder: currentBuilder,
    programBuilderStage: currentBuilderStage,
    programBuilderDayIndex: clampProgramBuilderDayIndex(currentBuilderDayIndex, currentBuilder),
    programTemplateQuery: currentProgramTemplateQuery,
    exerciseNameMap: nextExerciseNameMap,
    workoutDetailsById: nextWorkoutDetailsById,
    logsMode: nextLogsMode,
    logsSelectedDate: nextLogsSelectedDate,
    logsCalendarMonth: nextLogsCalendarMonth,
    logsCalendarDatesByMonth: {},
    logsWorkoutsByDate: {},
    logsSelectedExerciseId: nextLogsSelectedExerciseId,
    logsSelectedExerciseName: nextLogsSelectedExerciseName,
    logsExerciseHistory: {},
    logsDateExpandedWorkoutId: "",
    logsExerciseExpandedWorkoutId: "",
    logsLoadingMonth: "",
    logsLoadingDate: "",
    logsLoadingExercise: "",
    logsLoadingWorkoutId: "",
  });
  refreshPushPermissionState();
  void syncRestPushAvailability({ silent: true });
}

async function openSession(workoutId, options = {}) {
  if (!workoutId) return;
  const workout = options?.workout || await api.getWorkout(workoutId);
  const providedActiveSession = Object.prototype.hasOwnProperty.call(options, "activeSession")
    ? options.activeSession || null
    : getState().activeSession || null;
  const restoredDraft = restoreWorkoutFromSessionDraftBackup(workout, providedActiveSession);
  const sessionWorkout = restoredDraft.workout || workout;
  clearSessionDraftPending(workoutId);
  const state = getState();
  const sessionReturnTab =
    state.selectedTab && state.selectedTab !== "session"
      ? state.selectedTab
      : state.sessionReturnTab || "home";
  const currentWorkoutId = String(state.activeWorkout?.workoutId || "").trim();
  const sessionWorkoutId = String(sessionWorkout?.workoutId || "").trim();
  const existingPrevious =
    currentWorkoutId && currentWorkoutId === sessionWorkoutId
      ? state.sessionPreviousByExercise || {}
      : {};
  const sessionExerciseIds = collectWorkoutExerciseIds(sessionWorkout);
  const waitForPreviousByExercise = !!options?.waitForPreviousByExercise;
  const providedPreviousByExercise =
    options?.previousByExercise && typeof options.previousByExercise === "object"
      ? options.previousByExercise
      : null;
  const nextSessionState = {};
  if (restoredDraft.activeSession || Object.prototype.hasOwnProperty.call(options, "activeSession")) {
    nextSessionState.activeSession = restoredDraft.activeSession || options.activeSession || null;
  }
  const basePreviousByExercise = {
    ...existingPrevious,
    ...(providedPreviousByExercise || {}),
  };
  const shouldHydratePreviousByExercise =
    sessionExerciseIds.length > 0 &&
    sessionExerciseIds.some((exerciseId) => !basePreviousByExercise[exerciseId]);
  const hydratePreviousByExercise = async () => {
    if (!shouldHydratePreviousByExercise) {
      return basePreviousByExercise;
    }

    const fetchedPreviousByExercise = await fetchSessionPreviousByExercise(sessionWorkout, {
      forceRefresh: true,
    });
    return {
      ...basePreviousByExercise,
      ...fetchedPreviousByExercise,
    };
  };
  const initialPreviousByExercise = waitForPreviousByExercise
    ? await hydratePreviousByExercise().catch(() => basePreviousByExercise)
    : basePreviousByExercise;
  pendingSessionScrollToCurrent = true;
  if (options.syncHistory !== false) {
    appHistoryAutoReplaceSuspended = true;
  }
  setState({
    activeWorkout: sessionWorkout,
    ...nextSessionState,
    sessionPreviousByExercise: initialPreviousByExercise,
    sessionItemEditOrder: null,
    sessionItemReorderOrder: null,
    selectedTab: "session",
    sessionReturnTab,
  });
  startSessionElapsedTimer();
  if (options.syncHistory !== false) {
    pushCurrentAppHistorySnapshot();
    appHistoryAutoReplaceSuspended = false;
  }

  if (restoredDraft.restored && sessionWorkout?.workoutId) {
    enqueueSessionDraftPendingRows(sessionWorkout.workoutId, restoredDraft.sets || [], {
      compareWorkout: workout,
    });
    scheduleSessionInputAutosave(sessionWorkout.workoutId, {
      immediate: true,
      bypassThrottle: true,
    });
  }

  if (waitForPreviousByExercise || !shouldHydratePreviousByExercise) {
    if (options.syncHistory !== false) {
      appHistoryAutoReplaceSuspended = false;
    }
    return;
  }

  fetchSessionPreviousByExercise(sessionWorkout, {
    forceRefresh: true,
  })
    .then((sessionPreviousByExercise) => {
      const latestState = getState();
      if (String(latestState.activeWorkout?.workoutId || "") !== String(sessionWorkout.workoutId || "")) {
        return;
      }
      setState({
        sessionPreviousByExercise: {
          ...basePreviousByExercise,
          ...(sessionPreviousByExercise || {}),
        },
      });
    })
    .catch(() => undefined);
  if (options.syncHistory !== false) {
    appHistoryAutoReplaceSuspended = false;
  }
}

function resolveStartedWorkoutSession(startResult) {
  const nextActiveSession = startResult?.session || null;
  return {
    nextActiveSession,
    nextWorkout: startResult?.workout || null,
    nextWorkoutId:
      String(startResult?.workoutId || "").trim() ||
      String(startResult?.workout?.workoutId || "").trim() ||
      String(nextActiveSession?.workoutId || "").trim(),
    previousByExercise:
      startResult?.previousByExercise && typeof startResult.previousByExercise === "object"
        ? startResult.previousByExercise
        : {},
    homePreview: Object.prototype.hasOwnProperty.call(startResult || {}, "homePreview")
      ? startResult.homePreview || null
      : undefined,
  };
}

async function enterStartedWorkout(startResult) {
  const {
    nextActiveSession,
    nextWorkout,
    nextWorkoutId,
    previousByExercise,
    homePreview,
  } = resolveStartedWorkoutSession(startResult);
  if (!nextWorkoutId || !nextWorkout) {
    throw new Error("시작한 운동 세션 정보를 불러오지 못했습니다.");
  }

  const openSessionOptions = {
    previousByExercise,
    waitForPreviousByExercise: true,
  };
  if (nextActiveSession) {
    openSessionOptions.activeSession = nextActiveSession;
  }
  if (nextWorkout) {
    openSessionOptions.workout = nextWorkout;
  }

  await openSession(nextWorkoutId, openSessionOptions);
  const currentState = getState();
  const workoutSummary = buildWorkoutSummaryEntry(nextWorkout);
  const nextWorkoutDetailsById = {
    ...(currentState.workoutDetailsById || {}),
    [nextWorkoutId]: nextWorkout,
  };
  setState({
    workoutDetailsById: nextWorkoutDetailsById,
    workouts: workoutSummary
      ? upsertWorkoutSummary(currentState.workouts || [], workoutSummary, WORKOUT_LIST_LIMIT)
      : currentState.workouts || [],
    homePreview: homePreview === undefined ? currentState.homePreview || null : homePreview,
  });
}

function closeSession(options = {}) {
  const workoutId = String(getState().activeWorkout?.workoutId || "").trim();
  closeSessionCancelModal({ restoreFocus: false, syncHistory: false });
  closeSessionFinishModal({ restoreFocus: false, syncHistory: false });
  if (workoutId) {
    persistCurrentSessionDraftBackup(workoutId);
    clearSessionDraftPending(workoutId);
    clearSessionInlineNotice(workoutId);
  }
  cancelSessionInputAutosave();
  stopSessionElapsedTimer();
  void releaseScreenWakeLock();
  const { sessionReturnTab } = getState();
  if (options.syncHistory !== false) {
    appHistoryAutoReplaceSuspended = true;
  }
  setState({
    selectedTab: sessionReturnTab || "home",
    activeWorkout: null,
    sessionItemEditOrder: null,
    sessionItemReorderOrder: null,
    sessionPreviousItem: null,
    sessionPreviousByExercise: {},
    sessionReturnTab: "home",
  });
  syncRestTimerFromState();
  if (options.syncHistory !== false) {
    consumeOrReplaceCurrentAppHistoryEntry();
    appHistoryAutoReplaceSuspended = false;
  }
}

function setSessionItemContextMenuOpen(order, isOpen) {
  const safeOrder = String(order ?? "").trim();
  if (!safeOrder) {
    return;
  }

  const menu = document.getElementById(`sessionContextMenu-${safeOrder}`);
  const button = document.querySelector(`.session-item-more-btn[data-order="${safeOrder}"]`);
  if (menu instanceof HTMLElement) {
    menu.classList.toggle("hidden", !isOpen);
  }
  if (button instanceof HTMLButtonElement) {
    button.setAttribute("aria-expanded", isOpen ? "true" : "false");
    button.classList.toggle("is-open", isOpen);
  }
}

function closeSessionItemContextMenus(options = {}) {
  const exceptOrder = options?.exceptOrder === undefined || options?.exceptOrder === null
    ? null
    : String(options.exceptOrder).trim();

  document.querySelectorAll(".session-item-context-menu[data-order]").forEach((node) => {
    if (!(node instanceof HTMLElement)) {
      return;
    }
    const order = String(node.dataset.order || "").trim();
    setSessionItemContextMenuOpen(order, !!exceptOrder && order === exceptOrder);
  });
}

function pulseSessionItemReorderBar(order) {
  const safeOrder = String(order ?? "").trim();
  if (!safeOrder) {
    return;
  }

  document.querySelectorAll(".session-item-reorder-bar.is-revealed").forEach((node) => {
    if (node instanceof HTMLElement) {
      node.classList.remove("is-revealed");
    }
  });

  const reorderBar = document.querySelector(`.session-item-reorder-bar[data-order="${safeOrder}"]`);
  if (!(reorderBar instanceof HTMLElement)) {
    return;
  }

  reorderBar.classList.remove("is-revealed");
  void reorderBar.offsetWidth;
  reorderBar.classList.add("is-revealed");

  if (sessionItemReorderRevealTimer) {
    clearTimeout(sessionItemReorderRevealTimer);
  }
  sessionItemReorderRevealTimer = window.setTimeout(() => {
    reorderBar.classList.remove("is-revealed");
    sessionItemReorderRevealTimer = null;
  }, 820);
}

function closeSessionItemReorder(options = {}) {
  const restoreFocus = options?.restoreFocus !== false;
  const currentReorderOrder = resolveSessionItemReorderOrder();
  if (!Number.isInteger(currentReorderOrder)) {
    return null;
  }

  setState({
    sessionItemReorderOrder: null,
  });

  if (restoreFocus) {
    requestAnimationFrame(() => {
      const moreButton = document.querySelector(`.session-item-more-btn[data-order="${currentReorderOrder}"]`);
      if (moreButton instanceof HTMLButtonElement) {
        focusElementWithoutScroll(moreButton);
      }
    });
  }

  return currentReorderOrder;
}

function toggleSessionItemContextMenu(order) {
  const safeOrder = String(order ?? "").trim();
  if (!safeOrder) {
    return;
  }

  const menu = document.getElementById(`sessionContextMenu-${safeOrder}`);
  const willOpen = menu instanceof HTMLElement ? menu.classList.contains("hidden") : true;
  closeSessionItemContextMenus();
  closeSessionItemReorder({ restoreFocus: false });
  if (willOpen) {
    setSessionItemContextMenuOpen(safeOrder, true);
  }
}

function getSessionItemOrderSequence(workout = getState().activeWorkout) {
  return Object.keys(workout?.items || {})
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value >= 0)
    .sort((a, b) => a - b);
}

function remapSessionItemOrderAfterSwap(rawOrder, firstOrder, secondOrder) {
  const normalizedOrder =
    rawOrder === null || rawOrder === undefined || rawOrder === ""
      ? null
      : Number(rawOrder);
  if (!Number.isInteger(normalizedOrder) || normalizedOrder < 0) {
    return null;
  }
  if (normalizedOrder === firstOrder) {
    return secondOrder;
  }
  if (normalizedOrder === secondOrder) {
    return firstOrder;
  }
  return normalizedOrder;
}

function remapSessionItemOrderAfterRemoval(rawOrder, removedOrder) {
  const normalizedOrder =
    rawOrder === null || rawOrder === undefined || rawOrder === ""
      ? null
      : Number(rawOrder);
  if (!Number.isInteger(normalizedOrder) || normalizedOrder < 0) {
    return null;
  }
  if (normalizedOrder === removedOrder) {
    return null;
  }
  if (normalizedOrder > removedOrder) {
    return normalizedOrder - 1;
  }
  return normalizedOrder;
}

function focusSessionItemMoveControl(order, direction, options = {}) {
  const safeOrder = String(order ?? "").trim();
  const safeDirection = direction === "down" ? "down" : "up";
  const reorderBar = document.querySelector(`.session-item-reorder-bar[data-order="${safeOrder}"]`);
  const preferredMoveButton = reorderBar instanceof HTMLElement
    ? reorderBar.querySelector(
      `[data-action="move-session-item"][data-order="${safeOrder}"][data-direction="${safeDirection}"]:not(:disabled)`,
    )
    : null;
  const fallbackMoveButton = reorderBar instanceof HTMLElement
    ? reorderBar.querySelector(
      `[data-action="move-session-item"][data-order="${safeOrder}"]:not(:disabled)`,
    )
    : null;
  const doneButton = reorderBar instanceof HTMLElement
    ? reorderBar.querySelector(`[data-action="close-session-item-reorder"][data-order="${safeOrder}"]`)
    : null;
  const focusTarget = preferredMoveButton || fallbackMoveButton || doneButton;
  const revealAfterMove = options?.revealAfterMove === true;
  if (reorderBar instanceof HTMLElement) {
    const revealTarget = revealAfterMove
      ? reorderBar.closest(".session-item-card[data-order]") || reorderBar
      : reorderBar;
    const barRect = revealTarget.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const comfortableBuffer = revealAfterMove
      ? Math.max(72, Math.min(120, Math.round(viewportHeight * 0.16)))
      : 16;
    if (revealAfterMove || barRect.top < comfortableBuffer || barRect.bottom > viewportHeight - comfortableBuffer) {
      revealTarget.scrollIntoView({
        behavior: "smooth",
        block: revealAfterMove ? "start" : "nearest",
        inline: "nearest",
      });
    }
    if (revealAfterMove) {
      pulseSessionItemReorderBar(safeOrder);
    }
  }
  if (focusTarget instanceof HTMLButtonElement) {
    focusElementWithoutScroll(focusTarget);
    return;
  }
  const moreButton = document.querySelector(`.session-item-more-btn[data-order="${safeOrder}"]`);
  if (moreButton instanceof HTMLButtonElement) {
    focusElementWithoutScroll(moreButton);
  }
}

async function handleCancelActiveSession() {
  const state = getState();
  const workoutId = state.activeSession?.workoutId || state.activeWorkout?.workoutId;
  if (!workoutId) {
    closeSession();
    return;
  }

  closeSessionCancelModal({ restoreFocus: false });
  const result = await api.discardActiveSession();
  clearDropSetAppliedForWorkout(workoutId);
  clearSessionDraftBackup(workoutId);
  clearSessionDraftPending(workoutId);
  clearSessionInlineNotice(String(workoutId || "").trim());
  cancelSessionInputAutosave();
  stopSessionElapsedTimer();
  stopRestTimer();
  const discardedWorkoutId = String(result?.workoutId || workoutId || "").trim();
  const nextWorkouts = (state.workouts || []).filter(
    (entry) => String(entry?.workoutId || "").trim() !== discardedWorkoutId,
  );
  const nextWorkoutDetailsById = Object.entries(state.workoutDetailsById || {}).reduce((acc, [key, detail]) => {
    if (key !== discardedWorkoutId) {
      acc[key] = detail;
    }
    return acc;
  }, {});
  setState({
    workouts: nextWorkouts,
    workoutDetailsById: nextWorkoutDetailsById,
    activeSession: null,
    selectedTab: state.sessionReturnTab || "home",
    activeWorkout: null,
    sessionItemEditOrder: null,
    sessionItemReorderOrder: null,
    sessionPreviousItem: null,
    sessionPreviousByExercise: {},
    sessionReturnTab: "home",
  });
}

function requestFinishWorkout(workoutId) {
  const safeWorkoutId = String(workoutId || "").trim();
  if (!safeWorkoutId) {
    return;
  }
  const currentWorkout = getState().activeWorkout;
  if (String(currentWorkout?.workoutId || "") === safeWorkoutId) {
    const finishValidation = validateWorkoutFinish(currentWorkout);
    if (!finishValidation.valid) {
      openSessionNoticeModal(finishValidation.message, {
        title: "운동 완료 전 확인이 필요합니다",
      });
      return;
    }
  }
  openSessionFinishModal(safeWorkoutId);
}

function stopSessionElapsedTimer() {
  if (sessionElapsedTimer) {
    clearInterval(sessionElapsedTimer);
    sessionElapsedTimer = null;
  }
}

function formatElapsed(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const hour = String(Math.floor(totalSec / 3600)).padStart(2, "0");
  const minute = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
  const sec = String(totalSec % 60).padStart(2, "0");
  return `${hour}:${minute}:${sec}`;
}

function formatRestClock(ms, options = {}) {
  const roundMode = options?.round === "down" ? "down" : "up";
  const totalSecRaw = roundMode === "down" ? Math.floor(ms / 1000) : Math.ceil(ms / 1000);
  const totalSec = Math.max(0, totalSecRaw);
  const minute = String(Math.floor(totalSec / 60)).padStart(2, "0");
  const sec = String(totalSec % 60).padStart(2, "0");
  return `${minute}:${sec}`;
}

function getRestNavButtonState(rest, now = Date.now()) {
  const enabled = rest?.enabled ?? true;
  if (!enabled) {
    return {
      className: "secondary",
      label: "휴식 OFF",
      phase: "off",
      title: "상단에서 자동 휴식 기능을 켤 수 있습니다.",
    };
  }

  if (rest?.running && rest?.endAt) {
    const remainMs = Number(rest.endAt) - now;
    if (remainMs > 0) {
      return {
        className: "rest-running",
        label: formatRestClock(remainMs),
        phase: "running",
        title: "탭해서 현재 휴식을 종료합니다.",
      };
    }

    return {
      className: "rest-overdue",
      label: remainMs > -1000 ? "00:00" : `+${formatRestClock(Math.abs(remainMs), { round: "down" })}`,
      phase: "overdue",
      title: "탭해서 현재 휴식을 종료합니다.",
    };
  }

  return {
    className: "secondary",
    label: "휴식 대기",
    phase: "idle",
    title: "세트를 완료하면 휴식 타이머가 자동으로 시작됩니다.",
  };
}

function updateRestToggleNavButton(rest) {
  const button = document.getElementById("toggleRestTimerNavBtn");
  if (!(button instanceof HTMLButtonElement)) {
    return;
  }

  const label = button.querySelector(".session-nav-status-text");
  const next = getRestNavButtonState(rest);
  button.classList.remove("secondary", "warn", "rest-running", "rest-overdue");
  button.classList.add(next.className);
  button.title = next.title;
  button.setAttribute("aria-label", `${next.label}. ${next.title}`);
  button.dataset.restPhase = next.phase;
  if (label instanceof HTMLElement) {
    label.textContent = next.label;
  }
}

function stopRestTimer() {
  if (restTimerInterval) {
    clearInterval(restTimerInterval);
    restTimerInterval = null;
  }
  if (restTimerTimeout) {
    clearTimeout(restTimerTimeout);
    restTimerTimeout = null;
  }
}

function triggerRestTimerVibration() {
  const vibrationEnabled = getState().settings?.vibrationEnabled ?? true;
  if (!vibrationEnabled || !navigator.vibrate) {
    return;
  }

  try {
    navigator.vibrate(REST_TIMER_VIBRATION_PATTERN);
  } catch (_) {
    // Ignore vibration failures on unsupported devices/browsers.
  }
}

function playRestTimerAlarm() {
  const alarmEnabled = getState().settings?.alarmEnabled ?? true;
  if (!alarmEnabled) {
    return;
  }

  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  let playedWithWebAudio = false;

  if (AudioContextCtor) {
    try {
      if (!restAlarmAudioContext) {
        restAlarmAudioContext = new AudioContextCtor();
      }
      const context = restAlarmAudioContext;
      if (context.state !== "running") {
        context.resume().catch(() => undefined);
      }

      const now = context.currentTime;
      const compressor = context.createDynamicsCompressor();
      const masterGain = context.createGain();

      compressor.threshold.setValueAtTime(-12, now);
      compressor.knee.setValueAtTime(12, now);
      compressor.ratio.setValueAtTime(16, now);
      compressor.attack.setValueAtTime(0.003, now);
      compressor.release.setValueAtTime(0.18, now);
      masterGain.gain.setValueAtTime(1, now);

      compressor.connect(masterGain);
      masterGain.connect(context.destination);

      REST_TIMER_ALARM_TONES.forEach((tone) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const startAt = now + tone.offsetSec;
        const endAt = startAt + tone.durationSec;

        oscillator.type = tone.type;
        oscillator.frequency.setValueAtTime(tone.frequency, startAt);

        gain.gain.setValueAtTime(0.0001, startAt);
        gain.gain.exponentialRampToValueAtTime(tone.peakGain, startAt + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, endAt);

        oscillator.connect(gain);
        gain.connect(compressor);
        oscillator.start(startAt);
        oscillator.stop(endAt);
      });

      const lastToneEndSec = REST_TIMER_ALARM_TONES.reduce(
        (maxEnd, tone) => Math.max(maxEnd, tone.offsetSec + tone.durationSec),
        0,
      );
      window.setTimeout(() => {
        try {
          compressor.disconnect();
          masterGain.disconnect();
        } catch (_) {
          // Ignore cleanup failures.
        }
      }, Math.ceil((lastToneEndSec + 0.12) * 1000));
      playedWithWebAudio = true;
    } catch (_) {
      playedWithWebAudio = false;
    }
  }

  if (!playedWithWebAudio) {
    playRestTimerFallbackAudio();
  }
}

function reconcileRestTimerFromLifecycle() {
  const context = getActiveRestAlarmContext();
  if (!context) {
    syncRestTimerFromState();
    return;
  }

  if (context.endAt <= Date.now()) {
    updateRestToggleNavButton({
      enabled: true,
      running: true,
      endAt: context.endAt,
    });
    triggerRestAlarmIfNeeded(context, { force: true });
    return;
  }

  if (!restTimerInterval && !restTimerTimeout) {
    startRestTimer(context.endAt, context.workoutId);
  }
}

function startRestTimer(endAt, workoutId) {
  stopRestTimer();
  const timerKey = getRestAlarmKey(workoutId, endAt);

  const tick = () => {
    const currentContext = getActiveRestAlarmContext();
    if (!currentContext || currentContext.key !== timerKey) {
      stopRestTimer();
      if (!currentContext?.rest?.running) {
        restAlarmPlayedForEndAt = null;
      }
      updateRestToggleNavButton(currentContext?.rest || getState().activeSession?.rest);
      return;
    }

    const remainMs = currentContext.endAt - Date.now();
    updateRestToggleNavButton({
      enabled: true,
      running: true,
      endAt: currentContext.endAt,
    });

    if (remainMs <= 0) {
      triggerRestAlarmIfNeeded(currentContext, { force: true });
    }
  };

  tick();
  const timeoutDelayMs = Math.max(0, Number(endAt) - Date.now());
  restTimerTimeout = window.setTimeout(() => {
    restTimerTimeout = null;
    const latestContext = getActiveRestAlarmContext();
    if (!latestContext || latestContext.key !== timerKey) {
      return;
    }
    triggerRestAlarmIfNeeded(latestContext, { force: true });
    updateRestToggleNavButton({
      enabled: true,
      running: true,
      endAt: latestContext.endAt,
    });
  }, timeoutDelayMs);
  restTimerInterval = setInterval(tick, 250);
}

function getNextSetPointer(workout, currentOrder, currentSetIndex) {
  const itemEntries = Object.entries(workout?.items || {}).sort((a, b) => Number(a[0]) - Number(b[0]));
  const allSetEntries = [];

  for (const [orderKey, item] of itemEntries) {
    const order = Number(orderKey);
    const setEntries = Object.entries(item.sets || {}).sort((a, b) => Number(a[0]) - Number(b[0]));
    for (const [setKey, set] of setEntries) {
      allSetEntries.push({
        order,
        setIndex: Number(setKey),
        done: !!set.done,
      });
    }
  }

  const afterCurrent = allSetEntries.find(
    (entry) =>
      (entry.order > currentOrder || (entry.order === currentOrder && entry.setIndex > currentSetIndex)) &&
      !entry.done,
  );
  if (afterCurrent) {
    return { order: afterCurrent.order, setIndex: afterCurrent.setIndex };
  }

  const firstUndone = allSetEntries.find((entry) => !entry.done);
  if (firstUndone) {
    return { order: firstUndone.order, setIndex: firstUndone.setIndex };
  }

  return null;
}

function areAllSetsDone(workout) {
  const entries = Object.values(workout?.items || {});
  if (entries.length === 0) {
    return false;
  }

  return entries.every((item) => Object.values(item.sets || {}).every((set) => !!set.done));
}

function validateWorkoutFinish(workout) {
  const itemEntries = Object.values(workout?.items || {});
  if (itemEntries.length === 0) {
    return {
      valid: false,
      message: "운동 종목을 하나 이상 추가해야 완료할 수 있습니다.",
    };
  }

  for (const item of itemEntries) {
    const setEntries = Object.values(item?.sets || {});
    if (setEntries.length === 0) {
      return {
        valid: false,
        message: "완료할 세트 기록이 없습니다.",
      };
    }

    for (const set of setEntries) {
      if (!set?.done) {
        return {
          valid: false,
          message: "아직 완료되지 않은 세트가 있습니다.",
        };
      }

      if (typeof set.reps !== "number" || !Number.isFinite(set.reps) || set.reps < 0) {
        return {
          valid: false,
          message: "모든 세트의 중량과 횟수를 입력하고 완료해야 합니다.",
        };
      }

      if (
        item.weightRequiredSnapshot !== false &&
        (typeof set.weight !== "number" || !Number.isFinite(set.weight) || set.weight < 0)
      ) {
        return {
          valid: false,
          message: "모든 세트의 중량과 횟수를 입력하고 완료해야 합니다.",
        };
      }
    }
  }

  return {
    valid: true,
    message: "",
  };
}

function calcSetVolume(set) {
  if (!set?.done) {
    return 0;
  }
  const weight = Number(set?.weight);
  const reps = Number(set?.reps);
  if (!Number.isFinite(weight) || !Number.isFinite(reps)) {
    return 0;
  }
  return Math.max(0, weight) * Math.max(0, reps);
}

function calculateWorkoutTotalVolume(workout) {
  let totalVolume = 0;

  for (const item of Object.values(workout?.items || {})) {
    for (const set of Object.values(item?.sets || {})) {
      totalVolume += calcSetVolume(set);
    }
  }

  return totalVolume;
}

async function fetchSessionPreviousItem(workout, activeSession) {
  const order = Number(activeSession?.current?.order);
  if (!workout?.workoutId || !Number.isInteger(order) || order < 0) {
    return null;
  }

  const currentItem = workout.items?.[String(order)];
  if (!currentItem?.exerciseId) {
    return null;
  }

  try {
    const records = await fetchLatestWorkoutItemsByExercise([currentItem.exerciseId], workout.workoutId);
    return records[currentItem.exerciseId] || null;
  } catch (_) {
    return null;
  }
}

async function fetchSessionPreviousByExercise(workout, options = {}) {
  if (!workout?.workoutId) {
    return {};
  }

  const exerciseIds = Array.from(
    new Set(
      Object.values(workout.items || {})
        .map((item) => String(item?.exerciseId || "").trim())
        .filter(Boolean),
    ),
  );

  if (exerciseIds.length === 0) {
    return {};
  }

  const records = await fetchLatestWorkoutItemsByExercise(exerciseIds, workout.workoutId, options);

  return exerciseIds.reduce((acc, exerciseId) => {
    const record = records[exerciseId];
    if (record) {
      acc[exerciseId] = record;
    }
    return acc;
  }, {});
}

function collectWorkoutExerciseIds(workout) {
  return Array.from(
    new Set(
      Object.values(workout?.items || {})
        .map((item) => String(item?.exerciseId || "").trim())
        .filter(Boolean),
    ),
  );
}

function syncRestTimerFromState() {
  const context = getActiveRestAlarmContext();

  if (!context) {
    const rest = getState().activeSession?.rest;
    restAlarmPlayedForEndAt = null;
    updateRestToggleNavButton({
      enabled: rest?.enabled ?? false,
      running: false,
      endAt: rest?.enabled ? null : null,
    });
    stopRestTimer();
    void syncNativeRestAlarmState();
    return;
  }

  startRestTimer(context.endAt, context.workoutId);
  void syncNativeRestAlarmState();
}

function startSessionElapsedTimer() {
  stopSessionElapsedTimer();

  const workout = getState().activeWorkout;
  const startedAt = workout?.meta?.startedAt;
  if (!startedAt) return;

  const tick = () => {
    const target = document.getElementById("sessionElapsedText");
    if (!target) return;
    target.textContent = formatElapsed(Date.now() - startedAt);
  };

  tick();
  sessionElapsedTimer = setInterval(tick, 1000);
}

function scrollSessionToCurrentPosition() {
  const sessionTab = els.tabs.session;
  if (!(sessionTab instanceof HTMLElement)) {
    return;
  }

  const activeSet = sessionTab.querySelector(".set-grid.active-set");
  const activeItem = sessionTab.querySelector(".session-item-card.active-item-card");
  const target = activeSet instanceof HTMLElement
    ? activeSet
    : activeItem instanceof HTMLElement
      ? activeItem
      : null;
  if (!target) {
    return;
  }

  target.scrollIntoView({
    behavior: "smooth",
    block: "center",
    inline: "nearest",
  });
}

function getSessionSetGrid(order, setIndex) {
  const sessionTab = els.tabs.session;
  if (!(sessionTab instanceof HTMLElement)) {
    return null;
  }

  const safeOrder = Number(order);
  const safeSetIndex = Number(setIndex);
  if (!Number.isInteger(safeOrder) || safeOrder < 0 || !Number.isInteger(safeSetIndex) || safeSetIndex < 0) {
    return sessionTab.querySelector(".set-grid.active-set");
  }

  return sessionTab.querySelector(`.set-grid[data-order='${safeOrder}'][data-set-index='${safeSetIndex}']`)
    || sessionTab.querySelector(".set-grid.active-set");
}

function resolveSessionSetFocusTarget(gridEl) {
  if (!(gridEl instanceof HTMLElement)) {
    return null;
  }

  const weightInput = gridEl.querySelector("input[data-role='weight']");
  if (weightInput instanceof HTMLInputElement && !weightInput.disabled) {
    return weightInput;
  }

  const repsInput = gridEl.querySelector("input[data-role='reps']");
  if (repsInput instanceof HTMLInputElement && !repsInput.disabled) {
    return repsInput;
  }

  const doneButton = gridEl.querySelector("button[data-action='set-done']");
  if (doneButton instanceof HTMLButtonElement && !doneButton.disabled) {
    return doneButton;
  }

  const removeButton = gridEl.querySelector("button[data-action='set-remove']");
  if (removeButton instanceof HTMLButtonElement && !removeButton.disabled) {
    return removeButton;
  }

  return null;
}

function focusSessionSetPointer(pointer) {
  const gridEl = getSessionSetGrid(pointer?.order, pointer?.setIndex);
  if (!(gridEl instanceof HTMLElement)) {
    return false;
  }

  const target = resolveSessionSetFocusTarget(gridEl);
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const gridRect = gridEl.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const comfortableBuffer = Math.max(72, Math.min(120, Math.round(viewportHeight * 0.16)));
  if (gridRect.top < comfortableBuffer || gridRect.bottom > viewportHeight - comfortableBuffer) {
    gridEl.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
  }

  focusElementWithoutScroll(target);
  return true;
}

function getActiveSessionCurrentPointer(activeSession = getState().activeSession) {
  const order = Number(activeSession?.current?.order);
  const setIndex = Number(activeSession?.current?.setIndex);
  if (!Number.isInteger(order) || order < 0 || !Number.isInteger(setIndex) || setIndex < 0) {
    return null;
  }
  return { order, setIndex };
}

function isSameSessionSetPointer(a, b) {
  return (
    Number(a?.order) === Number(b?.order) &&
    Number(a?.setIndex) === Number(b?.setIndex)
  );
}

function syncSessionCurrentPointerUi(pointer, workout = getState().activeWorkout) {
  const sessionTab = els.tabs.session;
  if (!(sessionTab instanceof HTMLElement)) {
    return;
  }

  const gridEl = getSessionSetGrid(pointer?.order, pointer?.setIndex);
  if (!(gridEl instanceof HTMLElement)) {
    return;
  }

  const activeSet = sessionTab.querySelector(".set-grid.active-set");
  if (activeSet instanceof HTMLElement && activeSet !== gridEl) {
    activeSet.classList.remove("active-set");
  }
  gridEl.classList.add("active-set");

  const activeItem = sessionTab.querySelector(".session-item-card.active-item-card");
  const itemCard = gridEl.closest(".session-item-card[data-order]");
  if (activeItem instanceof HTMLElement && activeItem !== itemCard) {
    activeItem.classList.remove("active-item-card");
  }
  if (itemCard instanceof HTMLElement) {
    itemCard.classList.add("active-item-card");
  }

  refreshSessionSetDoneButtonsState(workout);
}

function syncSessionCurrentPointerFromFocus(target) {
  if (!(target instanceof HTMLElement)) {
    return;
  }

  const gridEl = target.closest(".set-grid[data-order][data-set-index]");
  const { activeWorkout, activeSession } = getState();
  const workoutId = String(activeWorkout?.workoutId || "").trim();
  if (
    !(gridEl instanceof HTMLElement) ||
    !workoutId ||
    String(activeSession?.workoutId || "").trim() !== workoutId
  ) {
    return;
  }

  const order = Number(gridEl.dataset.order);
  const setIndex = Number(gridEl.dataset.setIndex);
  if (!Number.isInteger(order) || order < 0 || !Number.isInteger(setIndex) || setIndex < 0) {
    return;
  }

  const pointer = { order, setIndex };
  if (!isSameSessionSetPointer(getActiveSessionCurrentPointer(activeSession), pointer)) {
    mutateState((state) => {
      if (String(state.activeSession?.workoutId || "").trim() === workoutId) {
        state.activeSession = {
          ...(state.activeSession || {}),
          current: pointer,
          lastHeartbeatAt: Date.now(),
        };
      }
    });
  }
  syncSessionCurrentPointerUi(pointer, activeWorkout);
}

function getSessionSetFocusPointerWithinItem(workout, order, setIndex) {
  const safeOrder = Number(order);
  const safeSetIndex = Number(setIndex);
  if (!Number.isInteger(safeOrder) || safeOrder < 0 || !Number.isInteger(safeSetIndex) || safeSetIndex < 0) {
    return null;
  }

  const item = workout?.items?.[String(safeOrder)];
  if (!item) {
    return null;
  }

  const setCount = Object.keys(item.sets || {}).length;
  if (setCount <= 0) {
    return null;
  }

  return {
    order: safeOrder,
    setIndex: Math.min(safeSetIndex, Math.max(0, setCount - 1)),
  };
}

function isSessionSetEmptyForFocus(set, item) {
  if (!set || set.done) {
    return false;
  }

  const hasReps = typeof set.reps === "number" && Number.isFinite(set.reps);
  if (item?.weightRequiredSnapshot === false) {
    return !hasReps;
  }

  const hasWeight = typeof set.weight === "number" && Number.isFinite(set.weight);
  return !hasWeight && !hasReps;
}

function buildWorkoutPreviewAfterSessionSetRemoval(workout, order, setIndex) {
  const safeOrder = Number(order);
  const safeSetIndex = Number(setIndex);
  if (!Number.isInteger(safeOrder) || safeOrder < 0 || !Number.isInteger(safeSetIndex) || safeSetIndex < 0) {
    return null;
  }

  const nextWorkout = deepClone(workout);
  const item = nextWorkout?.items?.[String(safeOrder)];
  if (!item?.sets || !Object.prototype.hasOwnProperty.call(item.sets, String(safeSetIndex))) {
    return null;
  }

  const mutableSets = { ...item.sets };
  delete mutableSets[String(safeSetIndex)];
  const compactedSets = normalizeSetMap(mutableSets);
  item.sets = compactedSets;
  item.setsPlan = Object.keys(compactedSets).length;
  return nextWorkout;
}

function getSessionSetFocusPointerAfterRemoval(workout, activeSession, order, setIndex) {
  const safeOrder = Number(order);
  const safeSetIndex = Number(setIndex);
  if (!Number.isInteger(safeOrder) || safeOrder < 0 || !Number.isInteger(safeSetIndex) || safeSetIndex < 0) {
    return {
      pendingFocus: null,
      sessionCurrent: null,
    };
  }

  const previewWorkout = buildWorkoutPreviewAfterSessionSetRemoval(workout, safeOrder, safeSetIndex);
  if (!previewWorkout) {
    return {
      pendingFocus: null,
      sessionCurrent: null,
    };
  }

  const defaultPointer = getSessionSetFocusPointerWithinItem(previewWorkout, safeOrder, safeSetIndex);
  const item = workout?.items?.[String(safeOrder)];
  const sortedSetIndices = Object.keys(item?.sets || {})
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value >= 0)
    .sort((a, b) => a - b);
  const lastSetIndex = sortedSetIndices.length > 0 ? sortedSetIndices[sortedSetIndices.length - 1] : -1;
  const isRemovingFocusedLastSet =
    Number(activeSession?.current?.order) === safeOrder &&
    Number(activeSession?.current?.setIndex) === safeSetIndex &&
    safeSetIndex === lastSetIndex;

  if (isRemovingFocusedLastSet) {
    if (defaultPointer) {
      const previousItem = previewWorkout?.items?.[String(defaultPointer.order)];
      const previousSet = previousItem?.sets?.[String(defaultPointer.setIndex)];
      if (isSessionSetEmptyForFocus(previousSet, previousItem)) {
        return {
          pendingFocus: defaultPointer,
          sessionCurrent: defaultPointer,
        };
      }
    }

    const nextUndonePointer = getNextSetPointer(previewWorkout, safeOrder, safeSetIndex);
    if (nextUndonePointer) {
      return {
        pendingFocus: nextUndonePointer,
        sessionCurrent: nextUndonePointer,
      };
    }
  }

  return {
    pendingFocus: defaultPointer,
    sessionCurrent: null,
  };
}

function scrollLogsDateWorkoutToTop(workoutId) {
  const safeWorkoutId = String(workoutId || "").trim();
  if (!safeWorkoutId || !(els.tabs.logs instanceof HTMLElement)) {
    return;
  }

  const target = els.tabs.logs.querySelector(`.log-card[data-workout-id="${CSS.escape(safeWorkoutId)}"]`);
  if (!(target instanceof HTMLElement)) {
    return;
  }

  target.scrollIntoView({
    behavior: "smooth",
    block: "start",
    inline: "nearest",
  });
}

async function waitForNextPaint(frames = 2) {
  const safeFrames = Math.max(1, Number(frames) || 1);
  for (let index = 0; index < safeFrames; index += 1) {
    await new Promise((resolve) => requestAnimationFrame(resolve));
  }
}

function renderSession() {
  if (!(els.tabs.session instanceof HTMLElement)) {
    return;
  }
  const { activeWorkout, activeSession, sessionPreviousByExercise, sessionItemEditOrder, sessionItemReorderOrder } = getState();
  setElementHtml(els.tabs.session, renderSessionPanel({
    workout: activeWorkout,
    activeSession,
    previousByExercise: sessionPreviousByExercise || {},
    sessionItemEditOrder,
    sessionItemReorderOrder,
    inlineNotice: getActiveSessionInlineNotice(),
  }));
  syncSessionInlineNoticeUi();
  refreshConstraintValidationUi(els.tabs.session, {
    requireFilled: false,
  });
  refreshSessionSetDoneButtonsState(activeWorkout);
  syncRestTimerFromState();
  const pendingReorderFocus = pendingSessionReorderFocus;
  if (pendingReorderFocus && Number.isInteger(Number(pendingReorderFocus.order))) {
    pendingSessionReorderFocus = null;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const currentReorderOrder = resolveSessionItemReorderOrder();
        const targetOrder = Number.isInteger(currentReorderOrder)
          ? currentReorderOrder
          : Number(pendingReorderFocus.order);
        if (Number.isInteger(targetOrder)) {
          focusSessionItemMoveControl(targetOrder, pendingReorderFocus.direction, {
            revealAfterMove: pendingReorderFocus.revealAfterMove === true,
          });
        }
      });
    });
    return;
  }
  const pendingSetFocus = pendingSessionSetFocus;
  if (pendingSetFocus) {
    pendingSessionSetFocus = null;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!focusSessionSetPointer(pendingSetFocus)) {
          scrollSessionToCurrentPosition();
        }
      });
    });
    return;
  }
  if (pendingSessionScrollToCurrent) {
    pendingSessionScrollToCurrent = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollSessionToCurrentPosition();
      });
    });
  }
}

function renderBottomNav(state) {
  if (!(els.bottomNav instanceof HTMLElement)) {
    return;
  }

  const isSessionTab = state.selectedTab === "session";
  const restUi = isSessionTab ? getRestNavButtonState(state.activeSession?.rest) : null;
  const workoutId = isSessionTab ? (state.activeWorkout?.workoutId || state.activeSession?.workoutId || "") : "";
  const finishValidation = isSessionTab ? validateWorkoutFinish(state.activeWorkout) : { valid: false, message: "" };
  const canFinishWorkout = isSessionTab ? finishValidation.valid : false;
  const finishButtonTitle = canFinishWorkout
    ? "운동 완료"
    : finishValidation.message || "모든 세트 기록을 완료해야 합니다.";
  const renderKey = isSessionTab
    ? `session|${workoutId}|${restUi?.className || ""}|${restUi?.label || ""}|${canFinishWorkout ? 1 : 0}|${finishButtonTitle}`
    : "default";

  if (els.bottomNav.dataset.renderKey === renderKey) {
    return;
  }

  els.bottomNav.dataset.renderKey = renderKey;

  if (isSessionTab) {
    els.bottomNav.classList.add("session-mode");
    setElementHtml(els.bottomNav, `
      <button class="secondary session-nav-btn" id="closeSessionNavBtn">
        <svg class="session-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        닫기
      </button>
      <button class="${restUi.className} session-nav-btn" id="toggleRestTimerNavBtn" type="button" title="${restUi.title}" data-rest-phase="${restUi.phase}">
        <svg class="session-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        <span class="session-nav-status-text">${restUi.label}</span>
      </button>
      <button class="danger session-nav-btn" id="finishWorkoutNavBtn" data-workout-id="${workoutId}" ${canFinishWorkout ? "" : "disabled"} title="${finishButtonTitle}">
        <svg class="session-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        운동 완료
      </button>
    `);
    return;
  }

  els.bottomNav.classList.remove("session-mode");
  setElementHtml(els.bottomNav, DEFAULT_BOTTOM_NAV_HTML);
}

function hasRelevantStateChanges(changedKeySet, dependencyKeys) {
  if (!(changedKeySet instanceof Set) || changedKeySet.size <= 0) {
    return true;
  }
  if (!(dependencyKeys instanceof Set) || dependencyKeys.size <= 0) {
    return false;
  }
  for (const key of dependencyKeys) {
    if (changedKeySet.has(key)) {
      return true;
    }
  }
  return false;
}

function syncActiveTabVisibility(selectedTab) {
  Object.entries(els.tabs).forEach(([key, element]) => {
    if (!(element instanceof HTMLElement)) {
      return;
    }
    element.classList.toggle("active", selectedTab === key);
  });
}

function syncBottomNavTabButtons(selectedTab) {
  if (!(els.bottomNav instanceof HTMLElement)) {
    return;
  }
  els.bottomNav.querySelectorAll(".tab-btn").forEach((node) => {
    if (!(node instanceof HTMLElement)) {
      return;
    }
    node.classList.toggle("active", node.dataset.tab === selectedTab);
  });
}

function getLogsRenderOptions(state = getState()) {
  const defaults = createDefaultLogsState();
  const selectedDate = state.logsSelectedDate || defaults.logsSelectedDate;
  const calendarMonth = state.logsCalendarMonth || getMonthKeyFromDateKey(selectedDate);
  const selectedExerciseId = String(state.logsSelectedExerciseId || "").trim();
  return {
    mode: state.logsMode === "exercise" ? "exercise" : "date",
    selectedDate,
    calendarMonth,
    calendarDates: state.logsCalendarDatesByMonth?.[calendarMonth] || [],
    dateWorkouts: state.logsWorkoutsByDate?.[selectedDate] || [],
    selectedExerciseId,
    selectedExerciseName: state.logsSelectedExerciseName || "",
    exerciseHistory: selectedExerciseId
      ? state.logsExerciseHistory?.[selectedExerciseId] || null
      : null,
    dateExpandedWorkoutId: state.logsDateExpandedWorkoutId || "",
    exerciseExpandedWorkoutId: state.logsExerciseExpandedWorkoutId || "",
    workoutDetailsById: state.workoutDetailsById || {},
    logsLoadingMonth: state.logsLoadingMonth || "",
    logsLoadingDate: state.logsLoadingDate || "",
    logsLoadingExercise: state.logsLoadingExercise || "",
    logsLoadingWorkoutId: state.logsLoadingWorkoutId || "",
  };
}

// Trusted renderer boundary: only pass HTML returned by local renderer functions
// that escape all dynamic text and attributes before reaching the DOM.
function setElementHtml(element, html) {
  if (!(element instanceof HTMLElement)) {
    return false;
  }
  if (element.innerHTML === html) {
    return true;
  }
  element.innerHTML = html;
  return true;
}

// Trusted renderer boundary for partial logs updates. Do not pass user-built HTML.
function replaceElementOuterHtml(element, html) {
  if (!(element instanceof HTMLElement) || !element.parentElement) {
    return false;
  }
  if (element.outerHTML === html) {
    return true;
  }
  element.outerHTML = html;
  return true;
}

function tryRenderLogsTabPartial(state, changedKeySet) {
  if (!(els.tabs.logs instanceof HTMLElement) || !(changedKeySet instanceof Set) || changedKeySet.size <= 0) {
    return false;
  }

  const options = getLogsRenderOptions(state);
  const modePanel = els.tabs.logs.querySelector("[data-role='logs-mode-panel']");
  if (!(modePanel instanceof HTMLElement)) {
    return false;
  }
  if (changedKeySet.has("logsMode")) {
    return false;
  }

  const renderedMode = String(modePanel.dataset.logsMode || "");
  if (renderedMode !== options.mode) {
    return false;
  }

  if (options.mode === "date") {
    if (hasRelevantStateChanges(changedKeySet, LOGS_DATE_CALENDAR_RENDER_KEYS)) {
      const calendarPanel = els.tabs.logs.querySelector("[data-role='logs-calendar-panel']");
      if (!setElementHtml(calendarPanel, renderLogsCalendar({
        calendarMonth: options.calendarMonth,
        selectedDate: options.selectedDate,
        calendarDates: options.calendarDates,
        logsLoadingMonth: options.logsLoadingMonth,
      }))) {
        return false;
      }
    }

    if (hasRelevantStateChanges(changedKeySet, LOGS_DATE_HEADER_RENDER_KEYS)) {
      const head = els.tabs.logs.querySelector("[data-role='logs-date-list-head']");
      if (!setElementHtml(head, renderLogsDateListHead(options.selectedDate))) {
        return false;
      }
    }

    if (hasRelevantStateChanges(changedKeySet, LOGS_DATE_LIST_RENDER_KEYS)) {
      const list = els.tabs.logs.querySelector("[data-role='logs-date-list']");
      if (!setElementHtml(list, renderLogsDateList(options))) {
        return false;
      }
    }

    return true;
  }

  if (options.mode === "exercise") {
    if (hasRelevantStateChanges(changedKeySet, LOGS_EXERCISE_SEARCH_RENDER_KEYS)) {
      const searchCard = els.tabs.logs.querySelector("[data-role='logs-exercise-search-card']");
      if (!replaceElementOuterHtml(searchCard, renderLogsExerciseSearchCard(options))) {
        return false;
      }
    }

    if (hasRelevantStateChanges(changedKeySet, LOGS_EXERCISE_LIST_RENDER_KEYS)) {
      const list = els.tabs.logs.querySelector("[data-role='logs-exercise-list']");
      if (!setElementHtml(list, renderLogsExerciseList(options))) {
        return false;
      }
    }

    return true;
  }

  return false;
}

function renderSelectedTab(state) {
  const selectedTab = String(state.selectedTab || "home");
  const wakeLockStatus = computeWakeLockStatus(state);
  const notificationStatus = computeRestNotificationStatus(state);

  if (selectedTab === "home" && els.tabs.home instanceof HTMLElement) {
    setElementHtml(els.tabs.home, renderHomeTab({
      activeProgram: state.activeProgram,
      activeSession: state.activeSession,
      workouts: state.workouts || [],
      homePreview: state.homePreview,
    }));
    return;
  }

  if (selectedTab === "logs" && els.tabs.logs instanceof HTMLElement) {
    setElementHtml(els.tabs.logs, renderLogsTab(getLogsRenderOptions(state)));
    return;
  }

  if (selectedTab === "programs" && els.tabs.programs instanceof HTMLElement) {
    setElementHtml(els.tabs.programs, renderProgramsTab(
      state.programs || [],
      state.programBuilder || createDefaultProgramBuilder(),
      state.programBuilderDayIndex || 0,
      state.programBuilderStage || "list",
      PROGRAM_TEMPLATES,
      state.programTemplateQuery || "",
      state.exerciseNameMap || {},
    ));
    refreshConstraintValidationUi(els.tabs.programs, {
      requireFilled: false,
    });
    syncProgramBuilderInfoStepUi();
    return;
  }

  if (selectedTab === "analytics" && els.tabs.analytics instanceof HTMLElement) {
    setElementHtml(els.tabs.analytics, renderAnalyticsTab(
      {
        ...(state.analytics || {
          query: "",
          searchItems: [],
          selectedExerciseId: "",
          selectedExerciseName: "",
          volumeSeries: null,
          customExercises: state.customExercises || [],
        }),
        estimatedOneRM: state.estimatedOneRM || null,
      },
    ));
    requestAnimationFrame(() => {
      const chartScroll = els.tabs.analytics.querySelector(".analytics-chart-scroll");
      if (chartScroll instanceof HTMLElement) {
        chartScroll.scrollLeft = chartScroll.scrollWidth;
      }
    });
    return;
  }

  if (selectedTab === "profile" && els.tabs.profile instanceof HTMLElement) {
    setElementHtml(els.tabs.profile, renderProfileTab({
      profile: state.profile,
      oneRM: state.oneRM,
      customExercises: state.customExercises || [],
      accountEmail: state.user?.email || "",
      settings: state.settings || {},
      wakeLockStatus,
      notificationStatus,
      notificationCapabilities: state.notificationCapabilities,
    }));
    syncCustomMuscleFieldUi();
    syncProfileCustomExerciseFilter();
    return;
  }

  if (selectedTab === "session") {
    renderSession();
  }
}

function renderTabs(state = getState(), meta = null) {
  const changedKeySet = Array.isArray(meta?.changedKeys) ? new Set(meta.changedKeys) : null;
  const selectedTabChanged = !changedKeySet || changedKeySet.has("selectedTab");

  if (!changedKeySet || hasRelevantStateChanges(changedKeySet, SHELL_RENDER_KEYS)) {
    if (!changedKeySet || changedKeySet.has("profile") || changedKeySet.has("user")) {
      refreshAuthBadge(state);
    }
    if (!changedKeySet || changedKeySet.has("selectedTab") || changedKeySet.has("activeSession") || changedKeySet.has("activeWorkout")) {
      renderBottomNav(state);
    }
    if (selectedTabChanged) {
      if (els.topbar instanceof HTMLElement) {
        els.topbar.classList.toggle("hidden", state.selectedTab === "session");
      }
      syncActiveTabVisibility(state.selectedTab);
      syncBottomNavTabButtons(state.selectedTab);
    }
  }

  const selectedTab = String(state.selectedTab || "home");
  const tabDependencies = TAB_RENDER_DEPENDENCIES[selectedTab] || new Set();
  const shouldRenderSelectedTab = !changedKeySet || selectedTabChanged || hasRelevantStateChanges(changedKeySet, tabDependencies);
  if (!shouldRenderSelectedTab) {
    return;
  }

  if (selectedTab === "logs" && !selectedTabChanged && changedKeySet && tryRenderLogsTabPartial(state, changedKeySet)) {
    return;
  }

  if (shouldRenderSelectedTab) {
    renderSelectedTab(state);
    if (shouldScrollToTopForUiTransition(state, meta)) {
      scheduleUiTransitionScrollToTop();
    }
  }
}

async function handleStartProgramWorkout(programId, options = {}) {
  const state = getState();
  const program = (state.programs || []).find((entry) => String(entry?.id || "") === String(programId || ""));
  if (!program) {
    await openAppAlertModal("프로그램 정보를 찾을 수 없습니다.", {
      title: "프로그램을 시작할 수 없습니다",
    });
    return;
  }
  const selectedDayIndexRaw = Number(options?.dayIndex);
  const selectedDayIndex = Number.isInteger(selectedDayIndexRaw)
    ? Math.min(Math.max(0, selectedDayIndexRaw), resolveProgramLengthDays(program) - 1)
    : undefined;
  const missingOneRMBases = getMissingOneRMBasesForProgramStart(program, state.oneRM, selectedDayIndex);
  if (missingOneRMBases.length > 0) {
    moveToProfileForMissingOneRM(missingOneRMBases);
    return;
  }

  const startOption = await resolveSessionConflictForNewWorkout();
  if (!startOption.proceed) {
    return;
  }

  const result = await api.trackJourney("start workout", async () => {
    return api.startWorkout({
      mode: "program",
      programId,
      dayIndex: selectedDayIndex,
      replaceActive: startOption.replaceActive,
    });
  });
  await enterStartedWorkout(result);
}

async function handleStartManualWorkout() {
  const startOption = await resolveSessionConflictForNewWorkout();
  if (!startOption.proceed) {
    return;
  }

  const result = await api.trackJourney("start workout", async () => {
    return api.startWorkout({
      mode: "manual",
      manualItems: [],
      replaceActive: startOption.replaceActive,
    });
  });
  await enterStartedWorkout(result);
}

async function resolveSessionConflictForNewWorkout() {
  let activeSession = getState().activeSession;
  if (!activeSession?.workoutId) {
    return {
      proceed: true,
      replaceActive: false,
    };
  }

  const localActiveWorkoutId = String(getState().activeWorkout?.workoutId || "").trim();
  const localSessionWorkoutId = String(activeSession.workoutId || "").trim();
  if (!localActiveWorkoutId || localActiveWorkoutId !== localSessionWorkoutId) {
    try {
      const bootstrapData = await api.getBootstrap();
      const serverActiveSession = bootstrapData?.activeSession || null;
      const serverWorkoutId = String(serverActiveSession?.workoutId || "").trim();

      if (!serverWorkoutId) {
        setState({
          activeSession: null,
        });
        return {
          proceed: true,
          replaceActive: false,
        };
      }

      if (serverWorkoutId !== localSessionWorkoutId) {
        setState({
          activeSession: serverActiveSession,
        });
      }
      activeSession = serverActiveSession;
    } catch {
      activeSession = getState().activeSession;
    }
  }

  if (!activeSession?.workoutId) {
    return {
      proceed: true,
      replaceActive: false,
    };
  }

  const action = await openSessionConflictModal();

  if (action === "resume") {
    await openSession(activeSession.workoutId);
    return {
      proceed: false,
      replaceActive: false,
    };
  }

  if (action !== "replace") {
    return {
      proceed: false,
      replaceActive: false,
    };
  }

  cancelSessionInputAutosave();
  clearSessionDraftPending();
  clearAllSessionDraftBackups();
  clearSessionInlineNotice(String(activeSession?.workoutId || getState().activeWorkout?.workoutId || "").trim());
  stopSessionElapsedTimer();
  stopRestTimer();
  setState({
    activeSession: null,
    activeWorkout: null,
    sessionItemEditOrder: null,
    sessionItemReorderOrder: null,
    sessionPreviousItem: null,
    sessionPreviousByExercise: {},
    sessionReturnTab: "home",
  });
  return {
    proceed: true,
    replaceActive: true,
  };
}

async function handleCreateTemplateProgram(templateId) {
  const safeTemplateId = String(templateId || "").trim();
  if (!safeTemplateId) {
    throw new Error("템플릿 ID가 올바르지 않습니다");
  }

  const payload = createProgramPayloadFromTemplate(safeTemplateId);
  if (!payload) {
    throw new Error("지원하지 않는 템플릿입니다");
  }

  const currentState = getState();
  const currentPrograms = Array.isArray(currentState.programs) ? currentState.programs : [];
  const hasActiveProgram = !!resolveActiveProgramFromPrograms(
    currentPrograms,
    currentState.activeProgram?.id,
  );
  if (currentPrograms.length === 0 || !hasActiveProgram) {
    payload.isActive = true;
  }

  const result = await api.createProgram(payload);
  await commitProgramsState(mergeProgramIntoList(getState().programs || [], result?.program || null), {
    homePreview: result?.homePreview,
  });
  closeProgramTemplateSearchModal({ restoreFocus: false });
  setState({
    programBuilderStage: "list",
    programTemplateQuery: "",
  });
}

async function handleCreateProgramFromBuilder() {
  try {
    syncProgramBuilderFromDom();
    const visibleValidation = validateProgramBuilderVisibleFields({
      requireFilled: true,
      applyUi: true,
    });
    if (!visibleValidation.valid) {
      throw new Error(visibleValidation.message);
    }
    const builder = ensureProgramBuilder();
    const payload = buildProgramPayloadFromBuilder(builder);
    let result = null;

    if (builder.targetProgramId) {
      result = await api.updateProgram(builder.targetProgramId, payload);
    } else {
      payload.trial = 0;
      result = await api.createProgram(payload);
    }

    clearInlineTextMessage("programError");
    await commitProgramsState(mergeProgramIntoList(getState().programs || [], result?.program || null), {
      homePreview: result?.homePreview,
    });
    appHistoryAutoReplaceSuspended = true;
    setState({
      programBuilder: createDefaultProgramBuilder(),
      programBuilderBaseline: null,
      programBuilderStage: "list",
      programBuilderDayIndex: 0,
      programTemplateQuery: "",
    });
    if (!consumeCurrentAppHistoryEntry()) {
      appHistoryAutoReplaceSuspended = false;
      replaceCurrentAppHistorySnapshot({ force: true });
    } else {
      appHistoryAutoReplaceSuspended = false;
    }
  } catch (error) {
    clearInlineTextMessage("programError");
    await openAppAlertModal(error instanceof Error ? error.message : "프로그램 생성 실패", {
      title: "프로그램 저장 전 확인이 필요합니다",
      meta: "잘못된 값은 화면에서 빨간색으로 표시됩니다. 내용을 확인한 뒤 다시 저장하세요.",
    });
  }
}

async function handleCreateCustomExercise() {
  try {
    const payload = {
      nameKo: (document.getElementById("customNameKo")?.value || "").trim(),
      nameEn: (document.getElementById("customNameEn")?.value || "").trim(),
      abbr: (document.getElementById("customAbbr")?.value || "").trim(),
      aliases: (document.getElementById("customAliases")?.value || "")
        .split(",")
        .map((entry) => entry.trim())
        .filter(Boolean),
      primaryMuscle: (document.getElementById("customPrimaryMuscle")?.value || "").trim(),
      secondaryMuscles: (document.getElementById("customSecondaryMuscles")?.value || "")
        .split(",")
        .map((entry) => entry.trim())
        .filter(Boolean),
      weightRequired: document.getElementById("customWeightRequired")?.checked ?? true,
      isBodyweight: document.getElementById("customBodyweight")?.checked ?? false,
    };

    const result = await api.createCustomExercise(payload);

    clearInlineTextMessage("customExerciseError");
    [
      "customNameKo",
      "customNameEn",
      "customAbbr",
      "customAliases",
      "customPrimaryMuscle",
      "customSecondaryMuscles",
    ].forEach((id) => {
      const input = document.getElementById(id);
      if (input) input.value = "";
    });
    syncCustomMuscleFieldUi();
    closeCustomMusclePickerModal({ restoreFocus: false });

    const nextExercise = result?.exercise || null;
    if (nextExercise?.id) {
      setState({
        customExercises: [
          nextExercise,
          ...(getState().customExercises || []).filter(
            (entry) => String(entry?.id || "").trim() !== String(nextExercise.id || "").trim(),
          ),
        ].sort((a, b) => (Number(b?.createdAt) || 0) - (Number(a?.createdAt) || 0)),
      });
    }
  } catch (error) {
    setInlineTextMessage("customExerciseError", error instanceof Error ? error.message : "커스텀 운동 등록 실패");
  }
}

async function handleSelectExercise(exerciseId, exerciseName = "") {
  const safeExerciseId = String(exerciseId || "").trim();
  if (!safeExerciseId) {
    return;
  }

  const currentAnalytics = getState().analytics || {};
  const fallbackName = (currentAnalytics.searchItems || []).find(
    (item) => String(item?.id || "") === safeExerciseId,
  )?.nameKo;
  const selectedName = String(exerciseName || fallbackName || "").trim();

  const volumeSeries = await api.getVolumeSeries(safeExerciseId);
  setState({
    analytics: {
      ...currentAnalytics,
      query: selectedName || currentAnalytics.query || "",
      searchItems: [],
      selectedExerciseId: safeExerciseId,
      selectedExerciseName: selectedName,
      volumeSeries,
    },
  });
}

function getExerciseSearchModalContext() {
  const rawContext = String(els.analyticsSearchModal?.dataset.context || "analytics").trim();
  return EXERCISE_SEARCH_MODAL_CONTEXTS[rawContext] ? rawContext : "analytics";
}

function applyExerciseSearchModalContext(context) {
  const safeContext = EXERCISE_SEARCH_MODAL_CONTEXTS[context] ? context : "analytics";
  const config = EXERCISE_SEARCH_MODAL_CONTEXTS[safeContext];
  if (els.analyticsSearchModal instanceof HTMLElement) {
    els.analyticsSearchModal.dataset.context = safeContext;
  }
  if (els.analyticsSearchModalTitle instanceof HTMLElement) {
    els.analyticsSearchModalTitle.textContent = config.title;
  }
  if (els.analyticsSearchModalMeta instanceof HTMLElement) {
    els.analyticsSearchModalMeta.textContent = config.meta;
  }
  if (els.analyticsSearchModalInputLabel instanceof HTMLElement) {
    els.analyticsSearchModalInputLabel.textContent = config.inputLabel;
  }
  const input = document.getElementById("analyticsModalSearchInput");
  if (input instanceof HTMLInputElement) {
    input.placeholder = config.placeholder;
  }
}

function openAnalyticsSearchModal(context = "analytics", options = {}) {
  const modal = els.analyticsSearchModal;
  if (!(modal instanceof HTMLElement)) {
    return;
  }
  const { wasOpen, restoreState } = getModalInteractionState(modal, options);
  if (!wasOpen) {
    analyticsSearchModalLastState = restoreState;
  }

  applyExerciseSearchModalContext(context);
  if (Number.isInteger(options.dayIndex) && options.dayIndex >= 0) {
    modal.dataset.dayIndex = String(options.dayIndex);
  } else {
    modal.removeAttribute("data-day-index");
  }
  if (Number.isInteger(options.itemIndex) && options.itemIndex >= 0) {
    modal.dataset.itemIndex = String(options.itemIndex);
  } else {
    modal.removeAttribute("data-item-index");
  }

  const input = document.getElementById("analyticsModalSearchInput");
  if (input instanceof HTMLInputElement) {
    input.value = "";
    scheduleFocusWithoutScroll(input, { delay: 80 });
  }
  searchOverlayController.resetAnalyticsSearchState(context);
  showModalWithInteraction(modal, analyticsSearchModalLastState, wasOpen, options);
}

function closeAnalyticsSearchModal(options = {}) {
  const modal = els.analyticsSearchModal;
  if (!(modal instanceof HTMLElement) || modal.classList.contains("hidden")) {
    return;
  }

  const restoreState = analyticsSearchModalLastState;
  clearAnalyticsSearchModalState();
  modal.removeAttribute("data-day-index");
  modal.removeAttribute("data-item-index");
  hideModalWithInteraction(modal, restoreState, options);
  analyticsSearchModalLastState = null;
  if (options.syncHistory !== false) {
    consumeOrReplaceCurrentAppHistoryEntry();
  }
}

function openLogsExerciseSearchModal(options = {}) {
  openAnalyticsSearchModal("logs-exercise", options);
}

function openProgramTemplateSearchModal(options = {}) {
  const modal = els.programTemplateSearchModal;
  const input = els.programTemplateSearchModalInput;
  if (!(modal instanceof HTMLElement) || !(input instanceof HTMLInputElement)) {
    return;
  }

  const { wasOpen, restoreState } = getModalInteractionState(modal, options);
  if (!wasOpen) {
    programTemplateSearchModalLastFocused = restoreState;
  }

  input.value = String(getState().programTemplateQuery || "");
  showModalWithInteraction(modal, programTemplateSearchModalLastFocused, wasOpen, options);
  searchOverlayController.renderProgramTemplateSearchResultsInPlace(String(getState().programTemplateQuery || ""));

  scheduleFocusWithoutScroll(input, { delay: 80, select: true });
}

function closeProgramTemplateSearchModal(options = {}) {
  const modal = els.programTemplateSearchModal;
  if (!(modal instanceof HTMLElement) || !isProgramTemplateSearchModalOpen()) {
    return;
  }

  const restoreState = programTemplateSearchModalLastFocused;
  clearProgramTemplateSearchState();
  hideModalWithInteraction(modal, restoreState, options);
  programTemplateSearchModalLastFocused = null;
  if (options.syncHistory !== false) {
    consumeOrReplaceCurrentAppHistoryEntry();
  }
}

function openCustomMusclePickerModal(targetKey, options = {}) {
  const modal = els.customMusclePickerModal;
  const titleEl = els.customMusclePickerTitle;
  const metaEl = els.customMusclePickerMeta;
  const clearBtn = els.customMusclePickerClearBtn;
  const input = els.customMusclePickerSearchInput;
  if (
    !(modal instanceof HTMLElement) ||
    !(titleEl instanceof HTMLElement) ||
    !(metaEl instanceof HTMLElement) ||
    !(clearBtn instanceof HTMLElement) ||
    !(input instanceof HTMLInputElement)
  ) {
    return;
  }

  const target = targetKey === "secondary" ? "secondary" : "primary";
  const config = getCustomMuscleTargetConfig(target);
  const { wasOpen, restoreState } = getModalInteractionState(modal, options);
  if (!wasOpen) {
    customMusclePickerLastFocused = restoreState?.focusTarget instanceof HTMLElement
      ? restoreState.focusTarget
      : null;
  }

  modal.dataset.target = target;
  titleEl.textContent = config.modalTitle;
  metaEl.textContent = config.modalMeta;
  clearBtn.textContent = config.clearLabel;
  input.value = "";

  showModalWithInteraction(modal, restoreState, wasOpen, options);
  refreshCustomMusclePickerUi();

  scheduleFocusWithoutScroll(input, { delay: 80, select: true });
}

function handleCustomMusclePickerInput() {
  renderCustomMusclePickerResults(els.customMusclePickerSearchInput?.value || "");
}

function handleSelectCustomMuscle(muscleId) {
  const safeMuscleId = String(muscleId || "").trim();
  const targetKey = getCustomMusclePickerTarget();
  if (!safeMuscleId || (targetKey !== "primary" && targetKey !== "secondary")) {
    return;
  }

  if (targetKey === "primary") {
    setCustomMuscleSelections("primary", [safeMuscleId]);
    closeCustomMusclePickerModal();
    return;
  }

  const current = getCustomMuscleSelections("secondary");
  if (current.includes(safeMuscleId)) {
    setCustomMuscleSelections(
      "secondary",
      current.filter((value) => value !== safeMuscleId),
    );
    return;
  }

  const primarySelection = getCustomMuscleSelections("primary")[0] || "";
  if (primarySelection && primarySelection === safeMuscleId) {
    return;
  }

  setCustomMuscleSelections("secondary", [...current, safeMuscleId]);
}

function handleRemoveCustomMuscle(targetKey, muscleId) {
  const target = targetKey === "secondary" ? "secondary" : "primary";
  const safeMuscleId = String(muscleId || "").trim();
  if (!safeMuscleId) {
    return;
  }

  setCustomMuscleSelections(
    target,
    getCustomMuscleSelections(target).filter((value) => value !== safeMuscleId),
  );
}

function clearCustomMuscleSelection(targetKey) {
  const target = targetKey === "secondary" ? "secondary" : "primary";
  setCustomMuscleSelections(target, []);
}

function createAnalyticsExerciseSearchHandlers(context) {
  return {
    isStale() {
      return context !== getExerciseSearchModalContext();
    },
    onEmpty() {
      searchOverlayController.resetAnalyticsSearchState(context);
    },
    onResults(items, query) {
      if (context === "analytics") {
        searchOverlayController.applyAnalyticsSearchResults(query, items);
        return;
      }
      searchOverlayController.renderExerciseSearchResultsInPlace(items, context);
    },
  };
}

function handleExerciseSearchModalInput(inputId) {
  const input = document.getElementById(inputId || "analyticsModalSearchInput");
  const query = String(input?.value || "").trim();
  const context = getExerciseSearchModalContext();
  analyticsExerciseSearch.schedule(query, {
    ...createAnalyticsExerciseSearchHandlers(context),
    onError() {
      // Ignore transient search failures while typing.
    },
  });
}

async function handleSearchExercise(options = {}) {
  const inputId = options?.inputId || "analyticsModalSearchInput";
  const input = document.getElementById(inputId);
  const explicitQuery = String(options?.query || "").trim();
  const query = explicitQuery || String(input?.value || "").trim();
  const context = EXERCISE_SEARCH_MODAL_CONTEXTS[options?.context] ? options.context : getExerciseSearchModalContext();
  return analyticsExerciseSearch.search(query, createAnalyticsExerciseSearchHandlers(context));
}

async function handleSaveOneRM() {
  const payload = {
    squat: Number(document.getElementById("squatInput")?.value || 0),
    bench: Number(document.getElementById("benchInput")?.value || 0),
    deadlift: Number(document.getElementById("deadliftInput")?.value || 0),
  };

  const result = await api.updateOneRM(payload);
  if (result?.oneRM) {
    setState({
      oneRM: result.oneRM,
    });
  }
}

async function handleSaveRestSupportSettings() {
  const previousSettings = getState().settings || {};
  const payload = {
    alarmEnabled: document.getElementById("settingsAlarmEnabled")?.checked ?? true,
    vibrationEnabled: document.getElementById("settingsVibrationEnabled")?.checked ?? true,
    restWakeLockEnabled: document.getElementById("settingsRestWakeLockEnabled")?.checked ?? true,
    restNotificationsEnabled: document.getElementById("settingsRestNotificationsEnabled")?.checked ?? true,
  };

  const result = await api.updateSettings(payload);
  const nextSettings = result?.settings || {
    ...previousSettings,
    ...payload,
  };

  setState({
    settings: nextSettings,
  });

  if (payload.restNotificationsEnabled === false) {
    await unregisterRestPushSubscription({ silent: true });
  } else {
    await syncRestPushAvailability({ silent: true });
  }

  await syncScreenWakeLock();
}

async function refreshAndroidTwaBridgeStatus(options = {}) {
  if (!isAndroidTwaEnvironment()) {
    return false;
  }

  if (!androidTwaAdapter?.isSupported?.()) {
    return false;
  }

  const timeoutMs = Number.isFinite(options?.timeoutMs)
    ? Math.max(0, Number(options.timeoutMs) || 0)
    : ANDROID_TWA_BRIDGE_READY_TIMEOUT_MS;
  const waitForReady = options?.waitForReady === true;
  const ready = await androidTwaAdapter.waitForReady(waitForReady ? timeoutMs : 0).catch(() => false);
  if (!ready) {
    const fallbackStatus = await requestAndroidTwaBootstrapStatus(waitForReady ? timeoutMs : 0);
    if (fallbackStatus) {
      applyAndroidBridgeStatus({
        ...fallbackStatus,
        bridgeReady: true,
      });
      return true;
    }
    return false;
  }

  if (getState().androidBridgeReady !== true) {
    setState({
      androidBridgeReady: true,
    });
  }

  const nextStatus = await androidTwaAdapter.refreshStatus().catch(() => null);
  if (nextStatus) {
    applyAndroidBridgeStatus({
      ...nextStatus,
      bridgeReady: androidTwaAdapter?.isReady?.() === true,
    });
    return true;
  }

  const fallbackStatus = await requestAndroidTwaBootstrapStatus(waitForReady ? timeoutMs : 0);
  if (fallbackStatus) {
    applyAndroidBridgeStatus({
      ...fallbackStatus,
      bridgeReady: true,
    });
    return true;
  }

  return false;
}

async function requestAndroidTwaBootstrapStatus(timeoutMs = ANDROID_TWA_BRIDGE_READY_TIMEOUT_MS) {
  if (typeof window === "undefined") {
    return null;
  }

  const port = window.__liftflowAndroidTwaBridgeBootstrap?.port;
  if (!(port instanceof MessagePort)) {
    return null;
  }

  const waitMs = Number.isFinite(timeoutMs)
    ? Math.max(0, Number(timeoutMs) || 0)
    : ANDROID_TWA_BRIDGE_READY_TIMEOUT_MS;
  const requestId = `android-bootstrap-status-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  return await new Promise((resolve) => {
    let settled = false;
    let timer = null;

    const finish = (value) => {
      if (settled) {
        return;
      }
      settled = true;
      if (timer) {
        window.clearTimeout(timer);
      }
      port.removeEventListener("message", handleMessage);
      resolve(value);
    };

    const handleMessage = (event) => {
      let message = null;
      try {
        message = typeof event?.data === "string" ? JSON.parse(event.data) : event?.data;
      } catch (_) {
        message = null;
      }
      if (!message || String(message.replyTo || "") !== requestId) {
        return;
      }
      finish(message.payload || null);
    };

    port.addEventListener("message", handleMessage);

    try {
      port.start();
      port.postMessage(JSON.stringify({
        type: "get-status",
        requestId,
        payload: {},
      }));
    } catch (_) {
      finish(null);
      return;
    }

    timer = window.setTimeout(() => finish(null), waitMs > 0 ? waitMs : 0);
  });
}

function ensureAndroidTwaBridgeStatusEventually() {
  if (!isAndroidTwaEnvironment()) {
    return Promise.resolve(false);
  }

  if (!androidTwaAdapter?.isSupported?.()) {
    return Promise.resolve(false);
  }

  if (androidTwaBridgeReadyRefreshPromise) {
    return androidTwaBridgeReadyRefreshPromise;
  }

  androidTwaBridgeReadyRefreshPromise = refreshAndroidTwaBridgeStatus({
    waitForReady: true,
  })
    .catch(() => false)
    .finally(() => {
      androidTwaBridgeReadyRefreshPromise = null;
    });

  return androidTwaBridgeReadyRefreshPromise;
}

function refreshAndroidTwaBridgeStatusInBackground(options = {}) {
  if (!isAndroidTwaEnvironment()) {
    return;
  }

  void refreshAndroidTwaBridgeStatus({
    waitForReady: true,
    ...options,
  }).catch(() => undefined);
}

async function handlePrepareRestNotifications() {
  const status = computeRestNotificationStatus();
  if (isAndroidTwaEnvironment()) {
    const bridgeReady = await refreshAndroidTwaBridgeStatus({
      waitForReady: true,
    }).catch(() => false);
    if (!bridgeReady) {
      throw new Error("안드로이드 앱 연결이 아직 준비되지 않았습니다. 잠시 후 다시 시도해 주세요.");
    }

    let permission = String(getState().androidNotificationPermission || "default");
    if (permission !== "granted") {
      const permissionResult = await androidTwaAdapter?.requestNotificationPermission?.();
      applyAndroidBridgeStatus({
        ...(permissionResult || {}),
        bridgeReady: androidTwaAdapter?.isReady?.() === true,
      });
      permission = String(getState().androidNotificationPermission || "default");
    }

    if (permission !== "granted") {
      throw new Error("안드로이드 알림 권한이 필요합니다.");
    }

    if (getState().androidExactAlarmSupported !== false && getState().androidExactAlarmAllowed === false) {
      const exactAlarmResult = await androidTwaAdapter?.requestExactAlarmPermission?.();
      applyAndroidBridgeStatus({
        ...(exactAlarmResult || {}),
        bridgeReady: androidTwaAdapter?.isReady?.() === true,
      });
      await new Promise((resolve) => window.setTimeout(resolve, 250));
      await refreshAndroidTwaBridgeStatus({
        waitForReady: true,
      }).catch(() => false);
    }

    if (getState().androidExactAlarmSupported !== false && getState().androidExactAlarmAllowed === false) {
      throw new Error("기기 알림 설정이 필요합니다. 안드로이드 설정에서 허용해 주세요.");
    }

    const latestStatus = androidTwaAdapter?.getStatus?.() || {};
    await syncAndroidDeviceTokenWithServer(latestStatus);
    return;
  }

  if (status.action === "install") {
    await handlePwaInstallAction(status.actionMode || "browser-guide");
    refreshPushPermissionState();
    void syncRestPushAvailability({ silent: true });
    return;
  }

  const result = await ensureRestPushSubscription({
    promptPermission: true,
    forceResubscribe: false,
  });

  if (!result?.ready) {
    const permission = getNotificationPermissionState();
    if (permission === "denied") {
      throw new Error("알림 권한이 차단되어 있습니다. 설정에서 다시 허용해 주세요.");
    }
    if (!isInstalledPwaEnvironment()) {
      throw new Error("알림을 받으려면 앱을 설치해 주세요.");
    }
    throw new Error("배경 알림 준비에 실패했습니다.");
  }
}

function shouldAutoPromptRestNotifications(state = getState()) {
  if (restNotificationAutoPromptAttempted) {
    return false;
  }

  if (!state?.uid || !state?.user) {
    return false;
  }

  if (state.settings?.restNotificationsEnabled === false) {
    return false;
  }

  if (isAndroidTwaEnvironment()) {
    return (
      state.androidNotificationPermission !== "granted" ||
      (
        state.androidExactAlarmSupported !== false &&
        state.androidExactAlarmAllowed !== true
      )
    );
  }

  if (state.pushSubscriptionReady) {
    return false;
  }

  if (!state.pushConfig?.supported || !isPushApiSupported()) {
    return false;
  }

  return getNotificationPermissionState() === "default";
}

async function maybeAutoPrepareRestNotificationsOnLaunch() {
  if (!shouldAutoPromptRestNotifications()) {
    return;
  }

  restNotificationAutoPromptAttempted = true;
  if (isAndroidTwaEnvironment()) {
    await handlePrepareRestNotifications().catch(() => undefined);
    return;
  }

  await ensureRestPushSubscription({
    promptPermission: true,
    forceResubscribe: false,
  }).catch(() => undefined);
}

function readLaunchRestNotificationPayload() {
  try {
    const url = new URL(window.location.href);
    const notification = String(url.searchParams.get("notification") || "").trim().toLowerCase();
    const source = String(url.searchParams.get("source") || "").trim().toLowerCase();
    const workoutId = String(url.searchParams.get("workoutId") || "").trim();
    if (notification !== "rest" || source !== "android_notification") {
      return null;
    }
    return {
      workoutId,
      source,
      notification,
    };
  } catch (_) {
    return null;
  }
}

async function handleRestNotificationClickMessage(payload = {}) {
  const nextWorkoutId = String(payload?.workoutId || getState().activeSession?.workoutId || "").trim();
  if (!nextWorkoutId) {
    return;
  }

  await openSession(nextWorkoutId, {
    activeSession: getState().activeSession || null,
    workout:
      String(getState().activeWorkout?.workoutId || "").trim() === nextWorkoutId
        ? getState().activeWorkout
        : null,
  }).catch(() => undefined);

  reconcileRestTimerFromLifecycle();
}

function initAndroidTwaBridge() {
  if (!isAndroidTwaEnvironment()) {
    return;
  }

  if (!androidTwaAdapter) {
    androidTwaAdapter = createAndroidTwaAdapter({
      onStatusChange(status) {
        applyAndroidBridgeStatus({
          ...(status || {}),
          bridgeReady: androidTwaAdapter?.isReady?.() === true,
        });
      },
      onNotificationOpen(payload) {
        void handleRestNotificationClickMessage(payload || {});
      },
    });
  }

  void refreshAndroidTwaBridgeStatus({ timeoutMs: 0 })
    .then((ready) => {
      if (!ready) {
        void ensureAndroidTwaBridgeStatusEventually().catch(() => undefined);
      }
    })
    .catch(() => undefined);
}

async function handleChangePassword() {
  const next = document.getElementById("newPasswordInput")?.value || "";
  const confirm = document.getElementById("newPasswordConfirmInput")?.value || "";

  if (next.length < 6) {
    throw new Error("비밀번호는 6자 이상이어야 합니다");
  }

  if (next !== confirm) {
    throw new Error("비밀번호 확인 값이 일치하지 않습니다");
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error("로그인이 필요합니다");
  }

  await user.updatePassword(next);

  const first = document.getElementById("newPasswordInput");
  const second = document.getElementById("newPasswordConfirmInput");
  if (first) first.value = "";
  if (second) second.value = "";
}

async function handleDeleteAccount() {
  const user = auth?.currentUser;
  if (!user) {
    throw new Error("로그인이 필요합니다");
  }

  const confirmed = await openAppConfirmModal({
    title: "회원탈퇴 확인",
    meta: "계정과 모든 기록이 삭제되며 복구할 수 없습니다.",
    message: "정말로 회원탈퇴를 진행할까요?",
    confirmLabel: "회원탈퇴",
    confirmTone: "danger",
  });
  if (!confirmed) {
    return;
  }

  await unregisterAndroidDeviceTokenForCurrentUser();
  await api.deleteAccount();
  try {
    await auth.signOut();
  } catch (_) {
    // Ignore signOut failure after account deletion.
  }
  await openAppAlertModal("회원탈퇴가 완료되었습니다.", {
    title: "회원탈퇴 완료",
    restoreFocus: false,
  });
}

function getConstraintFieldHelperId(target) {
  if (!(target instanceof HTMLElement)) {
    return "field-helper";
  }

  const parts = [
    target.id,
    target.dataset.role,
    target.dataset.builderRoot,
    target.dataset.builderItemField,
    target.dataset.dayIndex,
    target.dataset.itemIndex,
  ];
  const setGrid = target.closest(".set-grid[data-order][data-set-index]");
  if (setGrid instanceof HTMLElement) {
    parts.push("set", setGrid.dataset.order, setGrid.dataset.setIndex);
  }

  const token = parts
    .filter((value) => String(value || "").trim().length > 0)
    .join("-")
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  return token ? `field-helper-${token}` : "field-helper";
}

function ensureConstraintFieldHelper(target) {
  const field = target instanceof HTMLElement ? target.closest(".field") : null;
  if (!(field instanceof HTMLElement)) {
    return null;
  }

  let helper = field.querySelector(".field-helper");
  if (!(helper instanceof HTMLElement)) {
    helper = document.createElement("p");
    helper.className = "field-helper";
    field.append(helper);
  }
  helper.id = getConstraintFieldHelperId(target);
  return helper;
}

function clearConstraintFieldValidationUi(target) {
  if (!(target instanceof HTMLElement)) {
    return;
  }

  const field = target.closest(".field");
  if (field instanceof HTMLElement) {
    field.classList.remove("is-invalid");
    const helper = field.querySelector(".field-helper");
    if (helper instanceof HTMLElement) {
      helper.remove();
    }
  }

  target.removeAttribute("aria-invalid");
  target.removeAttribute("aria-describedby");
}

function applyConstraintFieldValidationUi(target, message) {
  if (!(target instanceof HTMLElement)) {
    return;
  }

  const safeMessage = String(message || "").trim();
  if (!safeMessage) {
    clearConstraintFieldValidationUi(target);
    return;
  }

  const field = target.closest(".field");
  const helper = ensureConstraintFieldHelper(target);
  if (field instanceof HTMLElement) {
    field.classList.add("is-invalid");
  }
  if (helper instanceof HTMLElement) {
    helper.textContent = safeMessage;
    helper.classList.add("is-error");
    target.setAttribute("aria-describedby", helper.id);
  }
  target.setAttribute("aria-invalid", "true");
}

function isAlignedToStep(value, step, base = 0) {
  if (!Number.isFinite(value) || !Number.isFinite(step) || step <= 0) {
    return true;
  }

  const normalized = (value - base) / step;
  return Math.abs(normalized - Math.round(normalized)) < 1e-9;
}

function getProgramBuilderPeerInput(target, itemField) {
  if (!(target instanceof HTMLElement)) {
    return null;
  }

  const dayIndex = String(target.dataset.dayIndex || "").trim();
  const itemIndex = String(target.dataset.itemIndex || "").trim();
  if (!dayIndex || !itemIndex) {
    return null;
  }

  return document.querySelector(
    `[data-builder-item-field="${itemField}"][data-day-index="${dayIndex}"][data-item-index="${itemIndex}"]`,
  );
}

function getConstraintFieldSpec(target) {
  if (!(target instanceof HTMLInputElement)) {
    return null;
  }

  if (target.id === "sessionAddExerciseId") {
    return {
      type: "text",
      emptyMessage: getSessionAddExerciseSelectionMessage(),
      validate: (_value, inputEl) => {
        return getSessionAddSelectedExerciseId(inputEl) ? "" : getSessionAddExerciseSelectionMessage();
      },
    };
  }

  if (isSessionSetFieldInput(target)) {
    if (target.dataset.role === "weight") {
      return {
        type: "number",
        min: 0,
        max: MAX_WEIGHT,
        step: 0.5,
        invalidMessage: `무게는 0~${MAX_WEIGHT}kg, 0.5kg 단위로 입력하세요.`,
        emptyMessage: "무게를 입력하세요.",
      };
    }

    if (target.dataset.role === "reps") {
      return {
        type: "number",
        min: 0,
        max: MAX_REPS,
        integer: true,
        invalidMessage: `횟수는 0~${MAX_REPS}회 정수로 입력하세요.`,
        emptyMessage: "횟수를 입력하세요.",
      };
    }
  }

  if (target.id === "sessionAddSets") {
    return {
      type: "number",
      min: 1,
      max: MAX_SETS_PER_EXERCISE,
      integer: true,
      invalidMessage: `세트 수는 1~${MAX_SETS_PER_EXERCISE} 정수로 입력하세요.`,
      emptyMessage: "세트 수를 입력하세요.",
    };
  }

  if (target.id === "sessionAddRestSec") {
    return {
      type: "number",
      min: MIN_REST_SEC,
      max: MAX_REST_SEC,
      integer: true,
      invalidMessage: `휴식 시간은 ${MIN_REST_SEC}~${MAX_REST_SEC}초 정수로 입력하세요.`,
      emptyMessage: "휴식 시간을 입력하세요.",
    };
  }

  if (target.dataset.builderRoot === "name") {
    return {
      type: "text",
      emptyMessage: "프로그램명을 입력하세요.",
    };
  }

  if (target.dataset.builderRoot === "lengthDays") {
    return {
      type: "number",
      min: 1,
      max: MAX_PROGRAM_LENGTH_DAYS,
      integer: true,
      invalidMessage: `루틴 길이는 1~${MAX_PROGRAM_LENGTH_DAYS}일 정수로 입력하세요.`,
      emptyMessage: "루틴 길이를 입력하세요.",
    };
  }

  if (target.dataset.builderItemField === "sets") {
    return {
      type: "number",
      min: 1,
      max: MAX_SETS_PER_EXERCISE,
      integer: true,
      invalidMessage: `세트 수는 1~${MAX_SETS_PER_EXERCISE} 정수로 입력하세요.`,
      emptyMessage: "세트 수를 입력하세요.",
    };
  }

  if (target.dataset.builderItemField === "restSec") {
    return {
      type: "number",
      min: MIN_REST_SEC,
      max: MAX_REST_SEC,
      integer: true,
      invalidMessage: `휴식 시간은 ${MIN_REST_SEC}~${MAX_REST_SEC}초 정수로 입력하세요.`,
      emptyMessage: "휴식 시간을 입력하세요.",
    };
  }

  if (target.dataset.builderItemField === "percent") {
    return {
      type: "number",
      min: 1,
      max: MAX_PERCENT_1RM,
      invalidMessage: `% 값은 1~${MAX_PERCENT_1RM} 사이로 입력하세요.`,
      emptyMessage: "% 값을 입력하세요.",
    };
  }

  if (target.dataset.builderItemField === "fixedReps") {
    return {
      type: "number",
      min: 1,
      max: 100,
      integer: true,
      invalidMessage: "고정 reps는 1~100 정수로 입력하세요.",
      emptyMessage: "고정 reps를 입력하세요.",
    };
  }

  if (target.dataset.builderItemField === "rangeMin") {
    return {
      type: "number",
      min: 1,
      max: 100,
      integer: true,
      invalidMessage: "min reps는 1~100 정수로 입력하세요.",
      emptyMessage: "min reps를 입력하세요.",
      validate: (value, inputEl) => {
        const maxInput = getProgramBuilderPeerInput(inputEl, "rangeMax");
        const rawMax = String(maxInput?.value || "").trim();
        if (!rawMax) {
          return "";
        }
        const maxValue = Number(rawMax);
        if (!Number.isInteger(maxValue)) {
          return "";
        }
        return value > maxValue ? "min reps는 max reps 이하여야 합니다." : "";
      },
    };
  }

  if (target.dataset.builderItemField === "rangeMax") {
    return {
      type: "number",
      min: 1,
      max: 100,
      integer: true,
      invalidMessage: "max reps는 1~100 정수로 입력하세요.",
      emptyMessage: "max reps를 입력하세요.",
      validate: (value, inputEl) => {
        const minInput = getProgramBuilderPeerInput(inputEl, "rangeMin");
        const rawMin = String(minInput?.value || "").trim();
        if (!rawMin) {
          return "";
        }
        const minValue = Number(rawMin);
        if (!Number.isInteger(minValue)) {
          return "";
        }
        return value < minValue ? "max reps는 min reps 이상이어야 합니다." : "";
      },
    };
  }

  return null;
}

function validateConstraintField(target, options = {}) {
  const spec = getConstraintFieldSpec(target);
  if (!spec || !(target instanceof HTMLInputElement)) {
    return {
      valid: true,
      message: "",
      input: target instanceof HTMLInputElement ? target : null,
      isFilled: false,
    };
  }

  const requireFilled = options.requireFilled === true;
  const applyUi = options.applyUi !== false;
  const rawValue = String(target.value || "").trim();
  const isFilled = rawValue.length > 0;
  let message = "";

  if (!isFilled) {
    if (requireFilled) {
      message = String(spec.emptyMessage || spec.invalidMessage || "입력값을 확인하세요.").trim();
    }
  } else if (spec.type === "text") {
    if (typeof spec.validate === "function") {
      message = String(spec.validate(rawValue, target) || "").trim();
    }
  } else {
    const numericValue = Number(rawValue);
    if (!Number.isFinite(numericValue)) {
      message = String(spec.invalidMessage || "입력값을 확인하세요.").trim();
    } else if (spec.integer && !Number.isInteger(numericValue)) {
      message = String(spec.invalidMessage || "입력값을 확인하세요.").trim();
    } else if (Number.isFinite(spec.min) && numericValue < spec.min) {
      message = String(spec.invalidMessage || "입력값을 확인하세요.").trim();
    } else if (Number.isFinite(spec.max) && numericValue > spec.max) {
      message = String(spec.invalidMessage || "입력값을 확인하세요.").trim();
    } else if (Number.isFinite(spec.step) && !isAlignedToStep(numericValue, spec.step, spec.min ?? 0)) {
      message = String(spec.invalidMessage || "입력값을 확인하세요.").trim();
    } else if (typeof spec.validate === "function") {
      message = String(spec.validate(numericValue, target) || "").trim();
    }
  }

  if (applyUi) {
    if (message) {
      applyConstraintFieldValidationUi(target, message);
    } else {
      clearConstraintFieldValidationUi(target);
    }
  }

  return {
    valid: !message,
    message,
    input: target,
    isFilled,
  };
}

function syncConstraintFieldValidationUi(target, options = {}) {
  const result = validateConstraintField(target, options);
  if (!(target instanceof HTMLInputElement)) {
    return result;
  }

  const fieldName = String(target.dataset.builderItemField || "").trim();
  if (fieldName === "rangeMin" || fieldName === "rangeMax") {
    const peerFieldName = fieldName === "rangeMin" ? "rangeMax" : "rangeMin";
    const peerInput = getProgramBuilderPeerInput(target, peerFieldName);
    if (peerInput instanceof HTMLInputElement) {
      validateConstraintField(peerInput, options);
    }
  }

  return result;
}

function getConstraintValidationTargets(root = document) {
  if (!root || typeof root.querySelectorAll !== "function") {
    return [];
  }

  return Array.from(
    root.querySelectorAll(
      "input[data-role='weight'], input[data-role='reps'], #sessionAddExerciseId, #sessionAddSets, #sessionAddRestSec, input[data-builder-root='name'], input[data-builder-root='lengthDays'], input[data-builder-item-field='sets'], input[data-builder-item-field='restSec'], input[data-builder-item-field='percent'], input[data-builder-item-field='fixedReps'], input[data-builder-item-field='rangeMin'], input[data-builder-item-field='rangeMax']",
    ),
  ).filter((node) => node instanceof HTMLInputElement);
}

function refreshConstraintValidationUi(root = document, options = {}) {
  getConstraintValidationTargets(root).forEach((target) => {
    syncConstraintFieldValidationUi(target, options);
  });
}

function validateConstraintTargets(targets, options = {}) {
  let firstInvalid = null;

  targets.forEach((target) => {
    const result = syncConstraintFieldValidationUi(target, options);
    if (!result.valid && !firstInvalid) {
      firstInvalid = result;
    }
  });

  return firstInvalid || {
    valid: true,
    message: "",
    input: null,
    isFilled: true,
  };
}

function validateSessionAddComposer(options = {}) {
  const targets = [
    getSessionAddSearchInput(),
    document.getElementById("sessionAddSets"),
    document.getElementById("sessionAddRestSec"),
  ].filter((target) => target instanceof HTMLInputElement);

  return validateConstraintTargets(targets, options);
}

function validateSessionAddComposerForSubmission() {
  const validation = validateSessionAddComposer({
    requireFilled: true,
    applyUi: true,
  });

  if (!validation.valid && validation.input instanceof HTMLInputElement) {
    scheduleFocusWithoutScroll(validation.input, { select: true });
  }

  return validation;
}

function validateProgramBuilderVisibleFields(options = {}) {
  const root = els.tabs.programs instanceof HTMLElement ? els.tabs.programs : document;
  const targets = getConstraintValidationTargets(root).filter((target) => {
    return (
      target.dataset.builderRoot !== undefined ||
      target.dataset.builderItemField !== undefined
    );
  });
  return validateConstraintTargets(targets, options);
}

function getSessionSetInputValidation(gridEl, workout = getState().activeWorkout, options = {}) {
  if (!(gridEl instanceof HTMLElement)) {
    return {
      valid: false,
      message: "세트 정보를 확인할 수 없습니다.",
    };
  }

  const order = Number(gridEl.dataset.order);
  const setIndex = Number(gridEl.dataset.setIndex);
  const item = workout?.items?.[String(order)];
  const set = item?.sets?.[String(setIndex)];
  if (!item || !set) {
    return {
      valid: false,
      message: "세트 정보를 찾을 수 없습니다.",
    };
  }

  const repsInput = gridEl.querySelector("input[data-role='reps']");
  const repsValidation = syncConstraintFieldValidationUi(repsInput, {
    requireFilled: true,
    applyUi: options.applyUi === true,
  });
  if (!repsValidation.valid) {
    return {
      valid: false,
      message: repsValidation.isFilled
        ? repsValidation.message
        : "횟수를 입력해야 완료할 수 있습니다.",
    };
  }

  if (item.weightRequiredSnapshot !== false) {
    const weightInput = gridEl.querySelector("input[data-role='weight']");
    const weightValidation = syncConstraintFieldValidationUi(weightInput, {
      requireFilled: true,
      applyUi: options.applyUi === true,
    });
    if (!weightValidation.valid) {
      return {
        valid: false,
        message: weightValidation.isFilled
          ? weightValidation.message
          : "무게와 횟수를 모두 입력해야 완료할 수 있습니다.",
      };
    }
  }

  return {
    valid: true,
    message: "",
  };
}

function syncSessionSetDoneButtonTone(doneButton, toneClass) {
  if (!(doneButton instanceof HTMLButtonElement)) {
    return;
  }

  doneButton.classList.remove("secondary", "warn");
  if (toneClass) {
    doneButton.classList.add(toneClass);
  }
}

function syncSessionSetDoneButtonPresentation(doneButton, options = {}) {
  if (!(doneButton instanceof HTMLButtonElement)) {
    return;
  }

  const isDone = options.isDone === true;
  const title = String(options.title || "").trim();
  doneButton.setAttribute("aria-pressed", isDone ? "true" : "false");
  doneButton.setAttribute(
    "aria-label",
    isDone
      ? "완료 해제 버튼. 다시 누르면 완료 상태가 해제됩니다."
      : title || "세트를 완료 처리합니다.",
  );
  doneButton.title = title;

  const label = doneButton.querySelector(".set-done-btn-label");
  if (label instanceof HTMLElement) {
    label.textContent = isDone ? "완료 해제" : "완료";
  }
}

function updateSessionSetDoneButtonState(gridEl, workout = getState().activeWorkout) {
  if (!(gridEl instanceof HTMLElement)) {
    return;
  }
  const doneButton = gridEl.querySelector("button[data-action='set-done']");
  if (!(doneButton instanceof HTMLButtonElement)) {
    return;
  }

  const order = Number(gridEl.dataset.order);
  const setIndex = Number(gridEl.dataset.setIndex);
  const isAlreadyDone =
    !!workout?.items?.[String(order)]?.sets?.[String(setIndex)]?.done ||
    gridEl.classList.contains("done-set") ||
    doneButton.classList.contains("warn");
  if (isAlreadyDone) {
    syncSessionSetDoneButtonTone(doneButton, "warn");
    doneButton.disabled = false;
    syncSessionSetDoneButtonPresentation(doneButton, {
      isDone: true,
      title: "완료 상태를 해제합니다.",
    });
    return;
  }

  const validation = getSessionSetInputValidation(gridEl, workout, {
    applyUi: false,
  });
  const toneClass = gridEl.classList.contains("active-set") && validation.valid ? "" : "secondary";
  syncSessionSetDoneButtonTone(doneButton, toneClass);
  doneButton.disabled = false;
  syncSessionSetDoneButtonPresentation(doneButton, {
    isDone: false,
    title: validation.valid ? "세트를 완료 처리합니다." : validation.message,
  });
}

function getSessionSetDropValidation(gridEl, workout = getState().activeWorkout, options = {}) {
  if (!(gridEl instanceof HTMLElement)) {
    return {
      valid: false,
      message: "드랍세트 정보를 확인할 수 없습니다.",
    };
  }

  const order = Number(gridEl.dataset.order);
  const setIndex = Number(gridEl.dataset.setIndex);
  const item = workout?.items?.[String(order)];
  const set = item?.sets?.[String(setIndex)];
  if (!item || !set) {
    return {
      valid: false,
      message: "드랍세트 정보를 찾을 수 없습니다.",
    };
  }

  const sortedSetIndices = Object.keys(item.sets || {})
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value >= 0)
    .sort((a, b) => a - b);
  const lastSetIndex = sortedSetIndices.length > 0 ? sortedSetIndices[sortedSetIndices.length - 1] : -1;
  if (setIndex !== lastSetIndex) {
    return {
      valid: false,
      message: "드랍세트는 마지막 세트에서만 추가할 수 있습니다.",
    };
  }

  if (item.weightRequiredSnapshot === false) {
    return {
      valid: false,
      message: "무게 기반 운동에서만 드랍세트를 추가할 수 있습니다.",
    };
  }

  const workoutId = String(workout?.workoutId || "").trim();
  const dropSetKey = getDropSetItemKey(workoutId, order);
  if (dropSetKey && pendingDropSetKeys.has(dropSetKey)) {
    return {
      valid: false,
      message: "드랍세트를 추가하는 중입니다.",
    };
  }

  const weightInput = gridEl.querySelector("input[data-role='weight']");
  const weightValidation = syncConstraintFieldValidationUi(weightInput, {
    requireFilled: true,
    applyUi: options.applyUi === true,
  });
  if (!weightValidation.valid) {
    return {
      valid: false,
      message: weightValidation.isFilled
        ? weightValidation.message
        : "마지막 세트의 무게를 입력해야 드랍세트를 추가할 수 있습니다.",
    };
  }

  const currentWeight = parseInputNumberOrNull(
    weightInput,
    typeof set?.weight === "number" ? set.weight : null,
  );
  if (currentWeight === null) {
    return {
      valid: false,
      message: "마지막 세트의 무게를 입력해야 드랍세트를 추가할 수 있습니다.",
    };
  }

  return {
    valid: true,
    message: "",
  };
}

function getSessionItemDropTargetGrid(sourceEl, workout = getState().activeWorkout) {
  if (!(sourceEl instanceof HTMLElement)) {
    return null;
  }

  const itemCard = sourceEl.closest(".session-item-card[data-order]");
  if (!(itemCard instanceof HTMLElement)) {
    return null;
  }

  const order = Number(itemCard.dataset.order);
  const item = workout?.items?.[String(order)];
  if (!Number.isInteger(order) || order < 0 || !item) {
    return null;
  }

  const sortedSetIndices = Object.keys(item.sets || {})
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value >= 0)
    .sort((a, b) => a - b);
  const lastSetIndex = sortedSetIndices.length > 0 ? sortedSetIndices[sortedSetIndices.length - 1] : -1;
  if (lastSetIndex < 0) {
    return null;
  }

  return itemCard.querySelector(`.set-grid[data-order="${order}"][data-set-index="${lastSetIndex}"]`);
}

function updateSessionSetDropButtonState(gridEl, workout = getState().activeWorkout) {
  if (!(gridEl instanceof HTMLElement)) {
    return;
  }

  const itemCard = gridEl.closest(".session-item-card[data-order]");
  const dropButton = itemCard?.querySelector(".add-drop-session-set-btn");
  if (!(dropButton instanceof HTMLButtonElement)) {
    return;
  }

  const dropTargetGrid = getSessionItemDropTargetGrid(gridEl, workout);
  if (!(dropTargetGrid instanceof HTMLElement)) {
    dropButton.disabled = true;
    dropButton.title = "드랍세트를 추가할 수 없습니다.";
    return;
  }

  dropButton.dataset.order = String(dropTargetGrid.dataset.order || "");
  dropButton.dataset.setIndex = String(dropTargetGrid.dataset.setIndex || "");
  const validation = getSessionSetDropValidation(dropTargetGrid, workout, {
    applyUi: false,
  });
  dropButton.disabled = !validation.valid;
  dropButton.title = validation.valid ? "드랍세트를 추가합니다." : validation.message;
}

function refreshSessionSetDoneButtonsState(workout = getState().activeWorkout) {
  const sessionTab = els.tabs.session;
  if (!(sessionTab instanceof HTMLElement)) {
    return;
  }

  const processedDropOrders = new Set();
  sessionTab.querySelectorAll(".set-grid[data-order][data-set-index]").forEach((node) => {
    if (node instanceof HTMLElement) {
      updateSessionSetDoneButtonState(node, workout);
      const order = String(node.dataset.order || "").trim();
      if (!processedDropOrders.has(order)) {
        processedDropOrders.add(order);
        updateSessionSetDropButtonState(node, workout);
      }
    }
  });
}

function buildSetUpdatesFromWorkout(workout) {
  return Object.entries(workout?.items || {})
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .flatMap(([order, item]) =>
      Object.entries(item?.sets || {})
        .sort((a, b) => Number(a[0]) - Number(b[0]))
        .map(([setIndex, set]) => ({
          order: Number(order),
          setIndex: Number(setIndex),
          weight: item?.weightRequiredSnapshot === false ? null : (typeof set?.weight === "number" ? set.weight : null),
          reps: typeof set?.reps === "number" ? set.reps : null,
          done: !!set?.done,
        })),
    )
    .filter((row) => Number.isInteger(row.order) && row.order >= 0 && Number.isInteger(row.setIndex) && row.setIndex >= 0);
}

function applySetUpdatesToWorkout(workout, setUpdates) {
  if (!workout || !Array.isArray(setUpdates) || setUpdates.length === 0) {
    return workout;
  }

  const nextWorkout = deepClone(workout);
  setUpdates.forEach((row) => {
    const orderKey = String(row.order);
    const setKey = String(row.setIndex);
    const targetSet = nextWorkout?.items?.[orderKey]?.sets?.[setKey];
    if (!targetSet) {
      return;
    }
    targetSet.weight = row.weight;
    targetSet.reps = row.reps;
    targetSet.done = !!row.done;
  });

  return nextWorkout;
}

function hasSetChangesAgainstWorkout(workout, setUpdates) {
  if (!workout || !Array.isArray(setUpdates) || setUpdates.length === 0) {
    return false;
  }

  return setUpdates.some((row) => {
    const baseSet = workout?.items?.[String(row.order)]?.sets?.[String(row.setIndex)];
    if (!baseSet) {
      return true;
    }
    const baseWeight = typeof baseSet.weight === "number" ? baseSet.weight : null;
    const baseReps = typeof baseSet.reps === "number" ? baseSet.reps : null;
    const baseDone = !!baseSet.done;
    return baseWeight !== row.weight || baseReps !== row.reps || baseDone !== !!row.done;
  });
}

function normalizeSetMap(sets) {
  return Object.entries(sets || {})
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .reduce((acc, [, set], index) => {
      acc[String(index)] = set;
      return acc;
    }, {});
}

function collectSessionSetUpdateFromGrid(workout, gridEl, overrideSet) {
  if (!(gridEl instanceof HTMLElement)) {
    return null;
  }

  const order = Number(gridEl.dataset.order);
  const setIndex = Number(gridEl.dataset.setIndex);
  if (!Number.isInteger(order) || order < 0 || !Number.isInteger(setIndex) || setIndex < 0) {
    return null;
  }

  const item = workout?.items?.[String(order)];
  const baseSet = item?.sets?.[String(setIndex)];
  if (!item || !baseSet) {
    return null;
  }

  const weightInput = gridEl.querySelector("input[data-role='weight']");
  const repsInput = gridEl.querySelector("input[data-role='reps']");
  const doneButton = gridEl.querySelector("button[data-action='set-done']");

  let weight =
    item.weightRequiredSnapshot === false
      ? null
      : parseInputNumberOrNull(
        weightInput,
        typeof baseSet?.weight === "number" ? baseSet.weight : null,
      ) ?? null;
  let reps =
    parseInputNumberOrNull(
      repsInput,
      typeof baseSet?.reps === "number" ? baseSet.reps : null,
    ) ?? null;
  let done = doneButton instanceof HTMLButtonElement
    ? doneButton.classList.contains("warn")
    : !!baseSet.done;

  if (overrideSet && overrideSet.order === order && overrideSet.setIndex === setIndex) {
    if (overrideSet.weight !== undefined) {
      weight = overrideSet.weight;
    }
    if (overrideSet.reps !== undefined) {
      reps = overrideSet.reps;
    }
    if (overrideSet.done !== undefined) {
      done = !!overrideSet.done;
    }
  }

  return {
    order,
    setIndex,
    weight: item.weightRequiredSnapshot === false ? null : weight,
    reps,
    done,
  };
}

function captureSessionDraftPendingFromDom(workoutId, options = {}) {
  const { activeWorkout } = getState();
  const activeWorkoutId = String(activeWorkout?.workoutId || "").trim();
  const safeWorkoutId = String(workoutId || activeWorkoutId).trim();
  if (!activeWorkout || !safeWorkoutId || safeWorkoutId !== activeWorkoutId) {
    return [];
  }

  const rows = normalizeDraftSetUpdates(
    collectSessionSetUpdatesFromDom(activeWorkout, options.overrideSet),
  );
  enqueueSessionDraftPendingRows(safeWorkoutId, rows, {
    compareWorkout: activeWorkout,
  });
  return getSessionDraftPendingRows(safeWorkoutId);
}

function collectSessionSetUpdatesFromDom(workout, overrideSet) {
  const map = new Map();
  const sessionTab = els.tabs.session;
  if (!(sessionTab instanceof HTMLElement)) {
    return buildSetUpdatesFromWorkout(workout);
  }

  const grids = sessionTab.querySelectorAll(".set-grid[data-order][data-set-index]");
  if (!grids.length) {
    return buildSetUpdatesFromWorkout(workout);
  }

  grids.forEach((grid) => {
    if (!(grid instanceof HTMLElement)) {
      return;
    }
    const row = collectSessionSetUpdateFromGrid(workout, grid, overrideSet);
    if (!row) {
      return;
    }
    map.set(getSessionDraftRowKey(row.order, row.setIndex), row);
  });

  const updates = Array.from(map.values())
    .sort((a, b) => (a.order - b.order) || (a.setIndex - b.setIndex));
  return updates.length > 0 ? updates : buildSetUpdatesFromWorkout(workout);
}

async function syncSessionWorkoutToServer(workoutId, options = {}) {
  const { activeWorkout } = getState();
  const activeWorkoutId = String(activeWorkout?.workoutId || "").trim();
  const targetWorkoutId = String(workoutId || activeWorkoutId).trim();
  if (!activeWorkoutId || !targetWorkoutId || activeWorkoutId !== targetWorkoutId) {
    return null;
  }
  const forceSync = !!options.force;
  const explicitSetUpdates = normalizeDraftSetUpdates(options.sets);
  const mutation =
    options?.mutation && typeof options.mutation === "object"
      ? options.mutation
      : { type: "none" };
  const hasSessionPatch =
    options?.sessionPatch &&
    typeof options.sessionPatch === "object" &&
    (
      Object.prototype.hasOwnProperty.call(options.sessionPatch, "current") ||
      Object.prototype.hasOwnProperty.call(options.sessionPatch, "rest")
    );

  let sets = explicitSetUpdates.length > 0
    ? explicitSetUpdates
    : collectSessionSetUpdatesFromDom(activeWorkout, options.overrideSet);

  const hasDraftSetChanges = Array.isArray(sets) && sets.length > 0 && hasSetChangesAgainstWorkout(activeWorkout, sets);
  if (!hasDraftSetChanges) {
    sets = [];
  }

  if (!forceSync && !hasDraftSetChanges && !hasSessionPatch && mutation.type === "none") {
    return {
      workoutId: activeWorkoutId,
      sets,
      syncedWorkout: activeWorkout,
      session: getState().activeSession || null,
      result: null,
      skipped: true,
      networkSynced: false,
    };
  }

  const result = await api.commitWorkoutSession({
    workoutId: activeWorkoutId,
    draftSets: sets.length > 0 ? sets : undefined,
    sessionPatch: hasSessionPatch ? options.sessionPatch : undefined,
    mutation,
  });
  const mutationType = String(mutation?.type || "none");
  const nextSession = Object.prototype.hasOwnProperty.call(result || {}, "session")
    ? result.session || null
    : null;
  if (mutationType !== "finish") {
    const sessionWorkoutId = String(nextSession?.workoutId || "").trim();
    if (!sessionWorkoutId || sessionWorkoutId !== activeWorkoutId) {
      throw new Error("운동 세션 상태를 동기화하지 못했습니다.");
    }
  }

  return {
    workoutId: activeWorkoutId,
    sets,
    syncedWorkout: result?.workout || (sets.length > 0 ? applySetUpdatesToWorkout(activeWorkout, sets) : activeWorkout),
    session: nextSession,
    result,
    networkSynced: true,
  };
}

function enqueueSessionDraftSync(task) {
  const nextTask = sessionDraftSyncQueue
    .catch(() => undefined)
    .then(task);
  sessionDraftSyncQueue = nextTask.catch(() => undefined);
  return nextTask;
}

function resolveSessionCommitMode(options = {}) {
  return options?.mode === SESSION_COMMIT_MODE_BACKGROUND
    ? SESSION_COMMIT_MODE_BACKGROUND
    : SESSION_COMMIT_MODE_INTERACTIVE;
}

function buildSyncedWorkoutStatePatch(syncedWorkout, options = {}) {
  const safeWorkoutId = String(syncedWorkout?.workoutId || "").trim();
  if (!safeWorkoutId) {
    return null;
  }

  const latestState = getState();
  if (String(latestState.activeWorkout?.workoutId || "").trim() !== safeWorkoutId) {
    return null;
  }

  const nextSession = Object.prototype.hasOwnProperty.call(options, "session")
    ? options.session
    : latestState.activeSession || null;
  const nextWorkoutDetailsById = {
    ...(latestState.workoutDetailsById || {}),
    [safeWorkoutId]: syncedWorkout,
  };
  const nextWorkouts = options.workoutSummary
    ? upsertWorkoutSummary(latestState.workouts || [], options.workoutSummary, WORKOUT_LIST_LIMIT)
    : latestState.workouts || [];

  return {
    activeWorkout: syncedWorkout,
    activeSession: nextSession,
    workoutDetailsById: nextWorkoutDetailsById,
    workouts: nextWorkouts,
    ...(options.statePatch && typeof options.statePatch === "object" ? options.statePatch : {}),
  };
}

function applySyncedWorkoutStateInteractive(syncedWorkout, options = {}) {
  const patch = buildSyncedWorkoutStatePatch(syncedWorkout, options);
  if (!patch) {
    return null;
  }
  setState(patch);
  return patch.activeWorkout;
}

function reconcileSyncedWorkoutStateInPlace(syncedWorkout, options = {}) {
  const patch = buildSyncedWorkoutStatePatch(syncedWorkout, options);
  if (!patch) {
    return null;
  }
  mutateState((state) => {
    Object.assign(state, patch);
  });
  return patch.activeWorkout;
}

async function commitSessionWorkoutDraft(workoutId, options = {}) {
  const targetWorkoutId = String(workoutId || getState().activeWorkout?.workoutId || "").trim();
  if (!targetWorkoutId) {
    return null;
  }

  const activeWorkout = getState().activeWorkout;
  const activeWorkoutId = String(activeWorkout?.workoutId || "").trim();
  if (!activeWorkoutId || activeWorkoutId !== targetWorkoutId) {
    return null;
  }

  if (!options.skipBackupWrite) {
    persistCurrentSessionDraftBackup(targetWorkoutId, {
      overrideSet: options.overrideSet,
    });
  }

  if (options.overrideSet && typeof options.overrideSet === "object") {
    enqueueSessionDraftPendingRows(targetWorkoutId, [options.overrideSet], {
      compareWorkout: activeWorkout,
    });
  }
  if (Array.isArray(options.sets) && options.sets.length > 0) {
    enqueueSessionDraftPendingRows(targetWorkoutId, options.sets, {
      compareWorkout: activeWorkout,
    });
  }
  if (!!options.force && !options.overrideSet && (!Array.isArray(options.sets) || options.sets.length <= 0)) {
    captureSessionDraftPendingFromDom(targetWorkoutId);
  }

  return enqueueSessionDraftSync(async () => {
    const mode = resolveSessionCommitMode(options);
    const mutation =
      options?.mutation && typeof options.mutation === "object"
        ? options.mutation
        : { type: "none" };
    const hasSessionPatch =
      options?.sessionPatch &&
      typeof options.sessionPatch === "object" &&
      (
        Object.prototype.hasOwnProperty.call(options.sessionPatch, "current") ||
        Object.prototype.hasOwnProperty.call(options.sessionPatch, "rest")
      );
    let rowsToSync = getSessionDraftPendingRows(targetWorkoutId);
    if (rowsToSync.length <= 0 && !!options.force) {
      rowsToSync = captureSessionDraftPendingFromDom(targetWorkoutId, {
        overrideSet: options.overrideSet,
      });
    }
    if (rowsToSync.length <= 0 && mutation.type === "none" && !hasSessionPatch) {
      return {
        workoutId: targetWorkoutId,
        sets: [],
        syncedWorkout: getState().activeWorkout || null,
        session: getState().activeSession || null,
        result: null,
        skipped: true,
        networkSynced: false,
      };
    }

    const syncResult = await syncSessionWorkoutToServer(targetWorkoutId, {
      sets: rowsToSync,
      force: true,
      mutation,
      sessionPatch: options.sessionPatch,
    });
    if (syncResult?.syncedWorkout) {
      const applyOptions = {
        session: syncResult.session,
        workoutSummary: syncResult?.result?.workoutSummary || null,
        statePatch: options?.statePatch && typeof options.statePatch === "object"
          ? options.statePatch
          : null,
      };
      if (mode === SESSION_COMMIT_MODE_BACKGROUND) {
        reconcileSyncedWorkoutStateInPlace(syncResult.syncedWorkout, applyOptions);
      } else {
        applySyncedWorkoutStateInteractive(syncResult.syncedWorkout, applyOptions);
      }
    }
    if (syncResult) {
      removeSessionDraftPendingRows(targetWorkoutId, rowsToSync, {
        onlyIfSame: true,
      });
      if (getSessionDraftPendingRows(targetWorkoutId).length <= 0) {
        clearSessionDraftBackup(targetWorkoutId);
      }
      if (syncResult.networkSynced) {
        sessionDraftLastNetworkSyncAt = Date.now();
        clearSessionInlineNotice(targetWorkoutId);
      }
    }
    return syncResult;
  });
}

function isSessionSetFieldInput(target) {
  return (
    target instanceof HTMLInputElement &&
    (target.dataset.role === "weight" || target.dataset.role === "reps") &&
    !!target.closest(".set-grid[data-order][data-set-index]")
  );
}

function handleSessionSetFieldInput(target) {
  if (!isSessionSetFieldInput(target)) {
    return;
  }

  syncConstraintFieldValidationUi(target, {
    requireFilled: false,
    applyUi: true,
  });

  const gridEl = target.closest(".set-grid[data-order][data-set-index]");
  const activeWorkout = getState().activeWorkout;
  const workoutId = String(activeWorkout?.workoutId || "").trim();
  if (!workoutId || !(gridEl instanceof HTMLElement)) {
    return;
  }
  updateSessionSetDoneButtonState(gridEl);
  updateSessionSetDropButtonState(gridEl);

  const row = collectSessionSetUpdateFromGrid(activeWorkout, gridEl);
  if (row) {
    enqueueSessionDraftPendingRows(workoutId, [row], {
      compareWorkout: activeWorkout,
    });
  }

  persistCurrentSessionDraftBackup(workoutId);
  scheduleSessionInputAutosave(workoutId);
}

function handleSessionSetFieldCommit(target) {
  if (!isSessionSetFieldInput(target)) {
    return;
  }

  syncConstraintFieldValidationUi(target, {
    requireFilled: false,
    applyUi: true,
  });

  const gridEl = target.closest(".set-grid[data-order][data-set-index]");
  const activeWorkout = getState().activeWorkout;
  const workoutId = String(activeWorkout?.workoutId || "").trim();
  if (!workoutId || !(gridEl instanceof HTMLElement)) {
    return;
  }
  updateSessionSetDoneButtonState(gridEl);
  updateSessionSetDropButtonState(gridEl);

  const row = collectSessionSetUpdateFromGrid(activeWorkout, gridEl);
  if (row) {
    enqueueSessionDraftPendingRows(workoutId, [row], {
      compareWorkout: activeWorkout,
    });
  }

  persistCurrentSessionDraftBackup(workoutId);
  scheduleSessionInputAutosave(workoutId);
}

async function handleSetDone(gridEl) {
  return api.trackJourney("set done", async () => {
    const { activeWorkout, activeSession } = getState();
    if (!activeWorkout?.workoutId) return;
    cancelSessionInputAutosave();

    const order = Number(gridEl.dataset.order);
    const setIndex = Number(gridEl.dataset.setIndex);
    const baseSet = activeWorkout?.items?.[String(order)]?.sets?.[String(setIndex)];
    const weightInput = gridEl.querySelector("input[data-role='weight']");
    const repsInput = gridEl.querySelector("input[data-role='reps']");
    const doneButton = gridEl.querySelector("button[data-action='set-done']");

    const togglingToDone = !(doneButton?.classList.contains("warn") || false);
    if (togglingToDone) {
      const validation = getSessionSetInputValidation(gridEl, activeWorkout, {
        applyUi: true,
      });
      if (!validation.valid) {
        throw new Error(validation.message);
      }
    }

    const done = !(doneButton?.classList.contains("warn") || false);
    const weight = parseInputNumberOrNull(
      weightInput,
      typeof baseSet?.weight === "number" ? baseSet.weight : null,
    );
    const reps = parseInputNumberOrNull(
      repsInput,
      typeof baseSet?.reps === "number" ? baseSet.reps : null,
    );

    captureSessionDraftPendingFromDom(activeWorkout.workoutId);
    const rowsToSync = normalizeDraftSetUpdates([
      ...getSessionDraftPendingRows(activeWorkout.workoutId),
      {
        order,
        setIndex,
        weight,
        reps,
        done,
      },
    ]);
    const previewWorkout = applySetUpdatesToWorkout(activeWorkout, rowsToSync);
    const nextPointer = done ? getNextSetPointer(previewWorkout, order, setIndex) : { order, setIndex };
    const allSetsDoneAfterUpdate = done && areAllSetsDone(previewWorkout);
    const pendingFocus = allSetsDoneAfterUpdate
      ? null
      : {
        order: Number(nextPointer?.order),
        setIndex: Number(nextPointer?.setIndex),
      };
    const restEnabled = activeSession?.rest?.enabled ?? true;
    const restTargetOrder =
      nextPointer && Number.isInteger(Number(nextPointer.order))
        ? Number(nextPointer.order)
        : order;
    const restTargetItem =
      previewWorkout.items?.[String(restTargetOrder)]
      || previewWorkout.items?.[String(order)]
      || null;
    const restPatch =
      done && restEnabled
        ? {
          running: true,
          // Follow the next highlighted set/item so item-level rest edits apply immediately.
          endAt: Date.now() + Number(restTargetItem?.restSec || 90) * 1000,
        }
        : {
          running: false,
          endAt: null,
        };

    pendingSessionSetFocus = pendingFocus;

    let syncResult;
    try {
      syncResult = await commitSessionWorkoutDraft(activeWorkout.workoutId, {
        sets: rowsToSync,
        force: true,
        mode: SESSION_COMMIT_MODE_INTERACTIVE,
        sessionPatch: {
          current: nextPointer || { order, setIndex },
          rest: restPatch,
        },
      });
    } catch (error) {
      if (pendingSessionSetFocus === pendingFocus) {
        pendingSessionSetFocus = null;
      }
      throw error;
    }

    const updatedWorkout = syncResult?.syncedWorkout || previewWorkout;
    const finishValidation = validateWorkoutFinish(updatedWorkout);
    if (done && finishValidation.valid) {
      const refreshedDoneButton = els.tabs.session?.querySelector(
        `.set-grid[data-order='${order}'][data-set-index='${setIndex}'] button[data-action='set-done']`,
      );
      if (refreshedDoneButton instanceof HTMLButtonElement) {
        scheduleFocusWithoutScroll(refreshedDoneButton);
      }
      requestFinishWorkout(activeWorkout.workoutId);
    }
  });
}

function canvasToBlob(canvas, type = "image/png") {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }
      reject(new Error("이미지 변환에 실패했습니다."));
    }, type);
  });
}

function normalizeShareFileName(fileName) {
  const raw = String(fileName || "").trim();
  if (!raw) {
    return "workout_summary.png";
  }
  return raw.replace(/[^a-zA-Z0-9._-]+/g, "_");
}

async function captureWorkoutShareCard(overlay) {
  if (!(overlay instanceof HTMLElement)) {
    throw new Error("공유 레이어를 찾을 수 없습니다.");
  }

  const shareCard = overlay.querySelector("#shareCardElement");
  if (!(shareCard instanceof HTMLElement)) {
    throw new Error("공유 카드를 찾을 수 없습니다.");
  }

  shareCard.scrollTop = 0;
  shareCard.scrollLeft = 0;
  const shareCardRect = shareCard.getBoundingClientRect();
  const captureWidth = Math.max(Math.round(shareCardRect.width), 1);
  const captureHeight = Math.max(Math.round(shareCard.scrollHeight), Math.round(shareCardRect.height), 1);
  const html2canvas = await loadHtml2Canvas();
  return html2canvas(shareCard, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#121212",
    scrollX: 0,
    scrollY: 0,
    width: captureWidth,
    height: captureHeight,
    windowWidth: captureWidth,
    windowHeight: captureHeight,
    onclone: (clonedDocument) => {
      const clonedShareCard = clonedDocument.getElementById("shareCardElement");
      if (!(clonedShareCard instanceof HTMLElement)) {
        return;
      }

      clonedShareCard.style.width = `${captureWidth}px`;
      clonedShareCard.style.minWidth = `${captureWidth}px`;
      clonedShareCard.style.maxWidth = `${captureWidth}px`;
      clonedShareCard.style.minHeight = `${captureHeight}px`;
      clonedShareCard.style.height = `${captureHeight}px`;
      clonedShareCard.style.overflow = "hidden";

      clonedShareCard.querySelectorAll(".share-heatmap-figure").forEach((figure) => {
        if (figure instanceof HTMLElement) {
          figure.style.alignItems = "center";
        }
      });

      clonedShareCard.querySelectorAll(".share-heatmap-svg-wrap").forEach((wrap) => {
        if (wrap instanceof HTMLElement) {
          wrap.style.display = "grid";
          wrap.style.placeItems = "center";
          wrap.style.width = "100%";
        }
      });

      clonedShareCard.querySelectorAll(".share-heatmap-svg-frame").forEach((frame) => {
        if (frame instanceof HTMLElement) {
          frame.style.width = "80px";
          frame.style.maxWidth = "80px";
          frame.style.margin = "0 auto";
          frame.style.display = "flex";
          frame.style.alignItems = "center";
          frame.style.justifyContent = "center";
        }
      });

      clonedShareCard.querySelectorAll(".share-heatmap-figure .home-heatmap-figure-svg").forEach((svg) => {
        if (svg instanceof SVGElement) {
          svg.style.width = "100%";
          svg.style.maxWidth = "none";
          svg.style.margin = "0";
          svg.style.display = "block";
          svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
        }
      });
    },
  });
}

async function downloadShareOverlayImage(overlay, fileName) {
  const canvas = await captureWorkoutShareCard(overlay);
  const url = canvas.toDataURL("image/png");
  const a = document.createElement("a");
  a.href = url;
  a.download = normalizeShareFileName(fileName);
  a.click();
}

function openShareSummaryOverlay(summaryHtml, options = {}) {
  // `summaryHtml` must come from `renderWorkoutCompleteSummary`, which escapes
  // dynamic values in `views.js` before this trusted insertion point.
  const wrapper = document.createElement("div");
  setElementHtml(wrapper, summaryHtml);
  const overlay = wrapper.firstElementChild;
  if (!(overlay instanceof HTMLElement)) {
    return null;
  }

  const existingOverlay = document.getElementById("workoutSummaryOverlay");
  if (existingOverlay instanceof HTMLElement) {
    existingOverlay.remove();
  }

  document.body.appendChild(overlay);

  const closeBtn = overlay.querySelector("#shareCloseBtn");
  const downloadBtn = overlay.querySelector("#shareDownloadBtn");
  const igBtn = overlay.querySelector("#shareIgBtn");
  const shareTitle = String(options?.shareTitle || "LiftFlow 운동 기록").trim() || "LiftFlow 운동 기록";
  const shareText = String(options?.shareText || shareTitle).trim() || shareTitle;
  const fileName = normalizeShareFileName(options?.fileName || "workout_summary.png");

  closeBtn?.addEventListener("click", () => {
    overlay.remove();
  });

  loadHtml2Canvas().catch(() => undefined);

  igBtn?.addEventListener("click", async () => {
    try {
      const canvas = await captureWorkoutShareCard(overlay);
      const blob = await canvasToBlob(canvas);
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: shareTitle,
          text: shareText,
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
        await openAppAlertModal("공유 API를 지원하지 않는 기기라 이미지를 기기에 저장합니다.", {
          title: "이미지를 저장했어요",
        });
      }
    } catch (err) {
      console.error("공유 실패:", err);
      await openAppAlertModal("이미지 생성에 실패했습니다.", {
        title: "공유를 완료하지 못했습니다",
      });
    }
  });

  downloadBtn?.addEventListener("click", async () => {
    try {
      await downloadShareOverlayImage(overlay, fileName);
    } catch (err) {
      console.error(err);
    }
  });

  return overlay;
}

function openWorkoutRecordShareOverlay(workout, options = {}) {
  if (!workout) {
    return null;
  }

  const preferredTotalVolume = Number(options?.totalVolume);
  const metaTotalVolume = Number(workout?.meta?.totalVolume);
  const totalVolume =
    Number.isFinite(preferredTotalVolume) && preferredTotalVolume >= 0
      ? preferredTotalVolume
      : Number.isFinite(metaTotalVolume) && metaTotalVolume >= 0
        ? metaTotalVolume
        : calculateWorkoutTotalVolume(workout);
  const summaryHtml = renderWorkoutCompleteSummary(workout, { totalVolume });
  const shareDate = typeof workout?.meta?.date === "string" ? workout.meta.date : "";
  const shareTitle = shareDate ? `${shareDate} 운동 기록` : "운동 기록";

  return openShareSummaryOverlay(summaryHtml, {
    shareTitle,
    shareText: shareTitle,
    fileName: "workout_summary.png",
  });
}

async function handleFinishWorkout(workoutId) {
  const currentState = getState();
  const currentWorkout = currentState.activeWorkout;
  if (String(currentWorkout?.workoutId || "") === String(workoutId || "")) {
    const finishValidation = validateWorkoutFinish(currentWorkout);
    if (!finishValidation.valid) {
      openSessionNoticeModal(finishValidation.message, {
        title: "운동 완료 전 확인이 필요합니다",
      });
      return;
    }
  }

  closeSessionFinishModal({ restoreFocus: false });
  // Finish applies a custom home/log/program merge right after commit, so avoid
  // the intermediate session rerender from the generic interactive reconcile path.
  const finishResult = await commitSessionWorkoutDraft(workoutId, {
    force: true,
    mode: SESSION_COMMIT_MODE_BACKGROUND,
    mutation: {
      type: "finish",
    },
  });
  const result = finishResult?.result || {};
  clearDropSetAppliedForWorkout(workoutId);
  clearSessionDraftBackup(workoutId);
  clearSessionDraftPending(workoutId);
  cancelSessionInputAutosave();
  stopSessionElapsedTimer();
  stopRestTimer();
  const finishedWorkout = deepClone(finishResult?.syncedWorkout || currentWorkout || {});
  const workoutSummary = buildWorkoutSummaryEntry(finishedWorkout);
  const latestState = getState();
  let nextPrograms = latestState.programs || [];
  let nextActiveProgram = latestState.activeProgram || null;
  let nextHomePreview = Object.prototype.hasOwnProperty.call(result, "homePreview")
    ? result.homePreview || null
    : latestState.homePreview || null;

  if (result?.program) {
    nextPrograms = mergeProgramIntoList(nextPrograms, result.program);
    nextActiveProgram = resolveActiveProgramFromPrograms(nextPrograms);
  }

  const nextWorkoutDetailsById = {
    ...(latestState.workoutDetailsById || {}),
  };
  if (workoutSummary) {
    nextWorkoutDetailsById[workoutSummary.workoutId] = finishedWorkout;
  }

  appHistoryAutoReplaceSuspended = true;
  setState({
    workouts: workoutSummary
      ? upsertWorkoutSummary(latestState.workouts || [], workoutSummary, WORKOUT_LIST_LIMIT)
      : latestState.workouts || [],
    workoutDetailsById: nextWorkoutDetailsById,
    programs: nextPrograms,
    activeProgram: nextActiveProgram,
    homePreview: nextHomePreview,
    estimatedOneRM: result?.estimatedOneRM || latestState.estimatedOneRM || null,
    activeSession: null,
    activeWorkout: null,
    sessionItemEditOrder: null,
    sessionItemReorderOrder: null,
    sessionPreviousItem: null,
    sessionPreviousByExercise: {},
    selectedTab: "home",
    sessionReturnTab: "home",
  });
  if (!consumeCurrentAppHistoryEntry()) {
    appHistoryAutoReplaceSuspended = false;
    replaceCurrentAppHistorySnapshot({ force: true });
  } else {
    appHistoryAutoReplaceSuspended = false;
  }

  if (!result?.alreadyDone) {
    const metaTotalVolume = Number(finishedWorkout?.meta?.totalVolume);
    const currentTotalVolume =
      Number.isFinite(metaTotalVolume) && metaTotalVolume >= 0
        ? metaTotalVolume
        : calculateWorkoutTotalVolume(finishedWorkout);
    openWorkoutRecordShareOverlay(finishedWorkout, {
      totalVolume: currentTotalVolume,
    });
  }
}

async function handleAddSessionItem() {
  const mode = resolveSessionAddMode();
  if (mode.isReplace && Number.isInteger(mode.editOrder)) {
    await handleReplaceSessionItem(mode.editOrder);
    return;
  }

  const { activeWorkout } = getState();
  if (!activeWorkout?.workoutId) return;

  const exerciseInput = getSessionAddSearchInput();
  const composerValidation = validateSessionAddComposerForSubmission();
  if (!composerValidation.valid) {
    return null;
  }
  const exerciseId = getSessionAddSelectedExerciseId(exerciseInput);
  if (!exerciseId) {
    return null;
  }
  const sets = Number(document.getElementById("sessionAddSets")?.value || 3);
  const restSec = Number(document.getElementById("sessionAddRestSec")?.value || 90);

  const mutation = await api.trackJourney("add item", async () => {
    return commitSessionWorkoutDraft(activeWorkout.workoutId, {
      force: true,
      mode: SESSION_COMMIT_MODE_INTERACTIVE,
      mutation: {
        type: "add-item",
        item: {
          exerciseId,
          sets,
          restSec,
          loadRule: null,
          repsRule: null,
        },
      },
    });
  });

  if (mutation?.syncedWorkout) {
    refreshSessionPreviousByExerciseState(mutation.syncedWorkout);
  }
  clearSessionAddComposer(exerciseInput);
  return mutation;
}

async function handleReplaceSessionItem(order) {
  const { activeWorkout, activeSession } = getState();
  if (!activeWorkout?.workoutId) return;

  const safeOrder = Number(order);
  if (!Number.isInteger(safeOrder) || safeOrder < 0) {
    throw new Error("유효하지 않은 운동 순서입니다");
  }

  const exerciseInput = getSessionAddSearchInput();
  const composerValidation = validateSessionAddComposerForSubmission();
  if (!composerValidation.valid) {
    return null;
  }
  const exerciseId = getSessionAddSelectedExerciseId(exerciseInput);
  if (!exerciseId) {
    return null;
  }
  const sets = Number(document.getElementById("sessionAddSets")?.value || 3);
  const restSec = Number(document.getElementById("sessionAddRestSec")?.value || 90);
  const currentItem = activeWorkout.items?.[String(safeOrder)] || null;

  const mutation = await api.trackJourney("replace item", async () => {
    const shouldResetFocusedSetToTop =
      Number(activeSession?.current?.order) === safeOrder &&
      String(currentItem?.exerciseId || "").trim() !== String(exerciseId || "").trim();
    const pendingFocus = shouldResetFocusedSetToTop
      ? {
        order: safeOrder,
        setIndex: 0,
      }
      : null;

    pendingSessionSetFocus = pendingFocus;

    try {
      return await commitSessionWorkoutDraft(activeWorkout.workoutId, {
        force: true,
        mode: SESSION_COMMIT_MODE_INTERACTIVE,
        mutation: {
          type: "replace-item",
          order: safeOrder,
          item: {
            exerciseId,
            sets,
            restSec,
            loadRule: null,
            repsRule: null,
          },
        },
        sessionPatch: shouldResetFocusedSetToTop
          ? {
            current: {
              order: safeOrder,
              setIndex: 0,
            },
          }
          : undefined,
        statePatch: {
          sessionItemEditOrder: null,
          sessionItemReorderOrder: null,
        },
      });
    } catch (error) {
      if (pendingSessionSetFocus === pendingFocus) {
        pendingSessionSetFocus = null;
      }
      throw error;
    }
  });

  closeSessionAddPanel({ clearSelection: true });
  if (mutation?.syncedWorkout) {
    refreshSessionPreviousByExerciseState(mutation.syncedWorkout);
  }
}

function resolveSessionItemReorderOrder() {
  const state = getState();
  const rawOrder = state.sessionItemReorderOrder;
  if (rawOrder === null || rawOrder === undefined || rawOrder === "") {
    return null;
  }
  const order = Number(rawOrder);
  if (!Number.isInteger(order) || order < 0) {
    return null;
  }
  const item = state.activeWorkout?.items?.[String(order)];
  if (!item) {
    return null;
  }
  return order;
}

function handleToggleSessionAddPanel() {
  const panel = getSessionAddPanelEl();
  const editOrder = resolveSessionItemEditOrder();
  const reorderOrder = resolveSessionItemReorderOrder();

  if (Number.isInteger(reorderOrder)) {
    setState({
      sessionItemReorderOrder: null,
    });
    requestAnimationFrame(() => {
      openSessionAddPanelForCreate();
    });
    return;
  }

  if (Number.isInteger(editOrder)) {
    openSessionAddPanelForCreate();
    return;
  }

  if (panel instanceof HTMLElement && !panel.classList.contains("hidden")) {
    scheduleFocusWithoutScroll(() => getSessionAddSearchInput(), { select: true });
    return;
  }

  openSessionAddPanelForCreate();
}

async function handleAddSessionSet(order, options = {}) {
  const { activeWorkout } = getState();
  if (!activeWorkout?.workoutId) return;

  const safeOrder = Number(order);
  if (!Number.isInteger(safeOrder) || safeOrder < 0) {
    throw new Error("유효하지 않은 운동 순서입니다");
  }
  const dropSetDefaults = options?.dropSetDefaults || null;
  const item = activeWorkout.items?.[String(safeOrder)] || null;
  const mutation = {
    type: "add-set",
    order: safeOrder,
  };
  if (dropSetDefaults && typeof dropSetDefaults === "object") {
    mutation.set = {
      weight:
        item?.weightRequiredSnapshot === false
          ? null
          : typeof dropSetDefaults.weight === "number" && Number.isFinite(dropSetDefaults.weight)
            ? dropSetDefaults.weight
            : dropSetDefaults.weight === null
              ? null
              : null,
      reps:
        typeof dropSetDefaults.reps === "number" && Number.isFinite(dropSetDefaults.reps)
          ? dropSetDefaults.reps
          : dropSetDefaults.reps === null
            ? null
            : null,
      done: false,
    };
  }

  const sessionPatch =
    options?.sessionPatch && typeof options.sessionPatch === "object"
      ? options.sessionPatch
      : undefined;
  const rawPendingFocus = options?.pendingFocus;
  const pendingFocus =
    Number.isInteger(Number(rawPendingFocus?.order)) &&
      Number(rawPendingFocus.order) >= 0 &&
      Number.isInteger(Number(rawPendingFocus?.setIndex)) &&
      Number(rawPendingFocus.setIndex) >= 0
      ? {
        order: Number(rawPendingFocus.order),
        setIndex: Number(rawPendingFocus.setIndex),
      }
      : null;

  pendingSessionSetFocus = pendingFocus;

  try {
    const syncResult = await commitSessionWorkoutDraft(activeWorkout.workoutId, {
      force: true,
      mode: SESSION_COMMIT_MODE_INTERACTIVE,
      mutation,
      sessionPatch,
    });

    return syncResult?.syncedWorkout || null;
  } catch (error) {
    if (pendingSessionSetFocus === pendingFocus) {
      pendingSessionSetFocus = null;
    }
    throw error;
  }
}

async function handleAddDropSessionSet(gridEl) {
  const { activeWorkout } = getState();
  if (!activeWorkout?.workoutId || !(gridEl instanceof HTMLElement)) {
    return;
  }
  const workoutId = String(activeWorkout.workoutId || "").trim();

  const safeOrder = Number(gridEl.dataset.order);
  const safeSetIndex = Number(gridEl.dataset.setIndex);
  if (!Number.isInteger(safeOrder) || safeOrder < 0 || !Number.isInteger(safeSetIndex) || safeSetIndex < 0) {
    throw new Error("유효하지 않은 세트 정보입니다");
  }
  const dropSetKey = getDropSetItemKey(workoutId, safeOrder);
  if (!dropSetKey) {
    throw new Error("드랍세트 키를 생성할 수 없습니다");
  }
  if (pendingDropSetKeys.has(dropSetKey)) {
    return;
  }

  const item = activeWorkout.items?.[String(safeOrder)];
  if (!item) {
    throw new Error("운동 정보를 찾을 수 없습니다");
  }

  const sortedSetIndices = Object.keys(item.sets || {})
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value >= 0)
    .sort((a, b) => a - b);
  const lastSetIndex = sortedSetIndices.length > 0 ? sortedSetIndices[sortedSetIndices.length - 1] : -1;
  if (safeSetIndex !== lastSetIndex) {
    throw new Error("드랍세트는 마지막 세트에서만 추가할 수 있습니다");
  }

  if (item.weightRequiredSnapshot === false) {
    throw new Error("무게 기반 운동에서만 드랍세트를 추가할 수 있습니다");
  }

  const baseSet = item.sets?.[String(safeSetIndex)];
  const baseSetDone = !!baseSet?.done;
  const weightInput = gridEl.querySelector("input[data-role='weight']");
  const repsInput = gridEl.querySelector("input[data-role='reps']");
  const dropValidation = getSessionSetDropValidation(gridEl, activeWorkout, {
    applyUi: true,
  });
  if (!dropValidation.valid) {
    throw new Error(dropValidation.message);
  }

  const baseWeight = typeof baseSet?.weight === "number" ? baseSet.weight : null;
  const baseReps = typeof baseSet?.reps === "number" ? baseSet.reps : null;
  const currentWeight = parseInputNumberOrNull(weightInput, baseWeight);
  const currentReps = parseInputNumberOrNull(repsInput, baseReps);
  if (currentWeight === null) {
    throw new Error("마지막 세트의 무게를 입력해야 드랍세트를 추가할 수 있습니다");
  }
  const dropWeight20 = currentWeight === null ? null : roundToNearestHalf(currentWeight * 0.8);
  const dropWeight40 = currentWeight === null ? null : roundToNearestHalf(currentWeight * 0.6);
  const pendingFocus = {
    order: safeOrder,
    setIndex: baseSetDone ? safeSetIndex + 1 : safeSetIndex,
  };

  pendingDropSetKeys.add(dropSetKey);
  updateSessionSetDropButtonState(gridEl, activeWorkout);
  try {
    await handleAddSessionSet(safeOrder, {
      dropSetDefaults: {
        weight: dropWeight20,
        reps: currentReps,
      },
      skipDropSetDefaultsSync: true,
    });

    await handleAddSessionSet(safeOrder, {
      dropSetDefaults: {
        weight: dropWeight40,
        reps: currentReps,
      },
      pendingFocus,
      sessionPatch: {
        current: pendingFocus,
      },
    });
  } finally {
    pendingDropSetKeys.delete(dropSetKey);
    updateSessionSetDropButtonState(gridEl, getState().activeWorkout);
  }
}

async function handleRemoveSessionSet(order, setIndex) {
  const { activeWorkout, activeSession } = getState();
  if (!activeWorkout?.workoutId) return;

  const safeOrder = Number(order);
  const safeSetIndex = Number(setIndex);
  if (!Number.isInteger(safeOrder) || safeOrder < 0 || !Number.isInteger(safeSetIndex) || safeSetIndex < 0) {
    throw new Error("유효하지 않은 세트 정보입니다");
  }

  const { pendingFocus, sessionCurrent } = getSessionSetFocusPointerAfterRemoval(
    activeWorkout,
    activeSession,
    safeOrder,
    safeSetIndex,
  );
  pendingSessionSetFocus = pendingFocus;

  try {
    await commitSessionWorkoutDraft(activeWorkout.workoutId, {
      force: true,
      mode: SESSION_COMMIT_MODE_INTERACTIVE,
      mutation: {
        type: "remove-set",
        order: safeOrder,
        setIndex: safeSetIndex,
      },
      sessionPatch: sessionCurrent
        ? {
          current: sessionCurrent,
        }
        : undefined,
    });
  } catch (error) {
    if (pendingSessionSetFocus === pendingFocus) {
      pendingSessionSetFocus = null;
    }
    throw error;
  }
}

async function handleRemoveSessionItem(order) {
  const { activeWorkout, sessionItemReorderOrder } = getState();
  if (!activeWorkout?.workoutId) return;

  const safeOrder = Number(order);
  if (!Number.isInteger(safeOrder) || safeOrder < 0) {
    throw new Error("유효하지 않은 운동 순서입니다");
  }

  const mutation = await api.trackJourney("remove item", async () => {
    return commitSessionWorkoutDraft(activeWorkout.workoutId, {
      force: true,
      mode: SESSION_COMMIT_MODE_INTERACTIVE,
      mutation: {
        type: "remove-item",
        order: safeOrder,
      },
    });
  });

  const latestState = getState();
  const rawCurrentEditOrder = latestState.sessionItemEditOrder;
  const currentEditOrder =
    rawCurrentEditOrder === null || rawCurrentEditOrder === undefined || rawCurrentEditOrder === ""
      ? null
      : Number(rawCurrentEditOrder);
  let nextEditOrder = latestState.sessionItemEditOrder;
  if (Number.isInteger(currentEditOrder) && currentEditOrder >= 0) {
    if (safeOrder === currentEditOrder) {
      nextEditOrder = null;
    } else if (safeOrder < currentEditOrder) {
      nextEditOrder = currentEditOrder - 1;
    }
  }
  const nextReorderOrder = remapSessionItemOrderAfterRemoval(sessionItemReorderOrder, safeOrder);

  setState({
    sessionItemEditOrder: nextEditOrder,
    sessionItemReorderOrder: nextReorderOrder,
  });
  if (mutation?.syncedWorkout) {
    refreshSessionPreviousByExerciseState(mutation.syncedWorkout);
  }
}

function handleToggleSessionItemReorder(order) {
  const safeOrder = Number(order);
  if (!Number.isInteger(safeOrder) || safeOrder < 0) {
    throw new Error("유효하지 않은 운동 순서입니다");
  }

  const currentReorderOrder = resolveSessionItemReorderOrder();
  closeSessionItemContextMenus();
  closeSessionAddPanel({ clearSelection: true, syncHistory: false });
  const nextReorderOrder = currentReorderOrder === safeOrder ? null : safeOrder;
  setState({
    sessionItemEditOrder: null,
    sessionItemReorderOrder: nextReorderOrder,
  });
  if (Number.isInteger(nextReorderOrder)) {
    requestAnimationFrame(() => {
      focusSessionItemMoveControl(nextReorderOrder, "up");
    });
  }
}

function handleCloseSessionItemReorder() {
  closeSessionItemReorder();
}

async function handleMoveSessionItem(order, direction) {
  const { activeWorkout, sessionItemEditOrder, sessionItemReorderOrder } = getState();
  if (!activeWorkout?.workoutId) return;

  const safeOrder = Number(order);
  if (!Number.isInteger(safeOrder) || safeOrder < 0) {
    throw new Error("유효하지 않은 운동 순서입니다");
  }

  const safeDirection = direction === "down" ? "down" : "up";
  const itemOrderSequence = getSessionItemOrderSequence(activeWorkout);
  const currentIndex = itemOrderSequence.indexOf(safeOrder);
  if (currentIndex < 0) {
    throw new Error("이동할 운동을 찾을 수 없습니다");
  }

  const targetIndex = safeDirection === "up" ? currentIndex - 1 : currentIndex + 1;
  if (targetIndex < 0 || targetIndex >= itemOrderSequence.length) {
    return;
  }

  const swappedOrder = itemOrderSequence[targetIndex];
  const nextOrderSequence = [...itemOrderSequence];
  [nextOrderSequence[currentIndex], nextOrderSequence[targetIndex]] = [nextOrderSequence[targetIndex], nextOrderSequence[currentIndex]];

  const nextEditOrder = remapSessionItemOrderAfterSwap(sessionItemEditOrder, safeOrder, swappedOrder);
  const nextReorderOrder = remapSessionItemOrderAfterSwap(sessionItemReorderOrder, safeOrder, swappedOrder);
  const pendingFocus = {
    order: nextReorderOrder,
    direction: safeDirection,
    revealAfterMove: true,
  };
  pendingSessionReorderFocus = pendingFocus;

  try {
    await api.trackJourney("reorder item", async () => {
      return commitSessionWorkoutDraft(activeWorkout.workoutId, {
        force: true,
        mode: SESSION_COMMIT_MODE_INTERACTIVE,
        mutation: {
          type: "reorder-items",
          orderSequence: nextOrderSequence,
        },
        statePatch: {
          sessionItemEditOrder: nextEditOrder,
          sessionItemReorderOrder: nextReorderOrder,
        },
      });
    });
  } catch (error) {
    if (pendingSessionReorderFocus === pendingFocus) {
      pendingSessionReorderFocus = null;
    }
    throw error;
  }

  if (pendingSessionReorderFocus === pendingFocus) {
    pendingSessionReorderFocus = null;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const currentReorderOrder = resolveSessionItemReorderOrder();
        const revealOrder = Number.isInteger(currentReorderOrder) ? currentReorderOrder : nextReorderOrder;
        if (Number.isInteger(revealOrder)) {
          focusSessionItemMoveControl(revealOrder, safeDirection, { revealAfterMove: true });
        }
      });
    });
  }
}

async function handleDeleteProgram(programId) {
  const safeProgramId = String(programId || "").trim();
  if (!safeProgramId) {
    throw new Error("삭제할 프로그램 ID가 올바르지 않습니다");
  }

  const result = await api.deleteProgram(safeProgramId);
  await commitProgramsState(removeProgramFromList(getState().programs || [], safeProgramId), {
    homePreview: result?.homePreview,
  });
  if (getState().programBuilder?.targetProgramId === safeProgramId) {
    setState({
      programBuilder: createDefaultProgramBuilder(),
      programBuilderStage: "list",
      programBuilderDayIndex: 0,
      programTemplateQuery: "",
    });
  }
}

function sortWorkoutRowsByStartedAt(workouts = []) {
  return [...(workouts || [])].sort((a, b) => (Number(b?.meta?.startedAt) || 0) - (Number(a?.meta?.startedAt) || 0));
}

async function loadLogsCalendarMonth(month, options = {}) {
  const safeMonth = String(month || "").trim();
  if (!safeMonth) {
    return;
  }

  const currentState = getState();
  if (!options.force && currentState.logsCalendarDatesByMonth?.[safeMonth]) {
    return;
  }
  if (currentState.logsLoadingMonth === safeMonth && options.allowCurrentLoading !== true) {
    return;
  }

  if (!options.skipLoadingState) {
    setState({
      logsLoadingMonth: safeMonth,
    });
  }

  try {
    const result = await api.getWorkoutCalendarMonth(safeMonth);
    const nextDates = Array.isArray(result?.dates) ? result.dates : [];
    setState({
      logsCalendarDatesByMonth: {
        ...(getState().logsCalendarDatesByMonth || {}),
        [safeMonth]: nextDates,
      },
    });
  } finally {
    if (getState().logsLoadingMonth === safeMonth) {
      setState({
        logsLoadingMonth: "",
      });
    }
  }
}

async function loadLogsDate(date, options = {}) {
  const safeDate = normalizeDateKey(date);
  if (!safeDate) {
    return;
  }

  const currentState = getState();
  if (!options.force && currentState.logsWorkoutsByDate?.[safeDate]) {
    return;
  }
  if (currentState.logsLoadingDate === safeDate && options.allowCurrentLoading !== true) {
    return;
  }

  if (!options.skipLoadingState) {
    setState({
      logsLoadingDate: safeDate,
    });
  }

  try {
    const result = await api.getWorkoutsByDate(safeDate);
    const nextWorkouts = sortWorkoutRowsByStartedAt(Array.isArray(result?.workouts) ? result.workouts : []);
    setState({
      logsWorkoutsByDate: {
        ...(getState().logsWorkoutsByDate || {}),
        [safeDate]: nextWorkouts,
      },
    });
  } finally {
    if (getState().logsLoadingDate === safeDate) {
      setState({
        logsLoadingDate: "",
      });
    }
  }
}

async function loadLogsExerciseHistory(exerciseId, exerciseName = "", options = {}) {
  const safeExerciseId = String(exerciseId || "").trim();
  if (!safeExerciseId) {
    return;
  }

  const currentState = getState();
  if (!options.force && currentState.logsExerciseHistory?.[safeExerciseId]) {
    if (exerciseName && currentState.logsSelectedExerciseId === safeExerciseId) {
      setState({
        logsSelectedExerciseName: exerciseName,
      });
    }
    return;
  }
  if (currentState.logsLoadingExercise === safeExerciseId && options.allowCurrentLoading !== true) {
    return;
  }

  if (!options.skipLoadingState) {
    setState({
      logsLoadingExercise: safeExerciseId,
    });
  }

  try {
    const result = await api.getWorkoutsByExercise(safeExerciseId);
    setState({
      logsExerciseHistory: {
        ...(getState().logsExerciseHistory || {}),
        [safeExerciseId]: result,
      },
      ...(exerciseName && getState().logsSelectedExerciseId === safeExerciseId
        ? { logsSelectedExerciseName: exerciseName }
        : {}),
    });
  } finally {
    if (getState().logsLoadingExercise === safeExerciseId) {
      setState({
        logsLoadingExercise: "",
      });
    }
  }
}

function maybeInitializeLogsView(state = getState()) {
  let nextState = state;
  if (!nextState.logsSelectedDate || !nextState.logsCalendarMonth) {
    const defaults = createDefaultLogsState();
    const patch = {
      ...defaults,
      logsMode: nextState.logsMode || defaults.logsMode,
      logsSelectedExerciseId: nextState.logsSelectedExerciseId || "",
      logsSelectedExerciseName: nextState.logsSelectedExerciseName || "",
    };
    setState(patch);
    nextState = {
      ...nextState,
      ...patch,
    };
  }

  if (String(nextState.logsMode || "date") === "exercise") {
    if (nextState.logsSelectedExerciseId) {
      void loadLogsExerciseHistory(nextState.logsSelectedExerciseId, nextState.logsSelectedExerciseName || "");
    }
    return;
  }

  if (nextState.logsCalendarMonth) {
    void loadLogsCalendarMonth(nextState.logsCalendarMonth);
  }
  if (nextState.logsSelectedDate) {
    void loadLogsDate(nextState.logsSelectedDate);
  }
}

function openLogsTab(options = {}) {
  const currentState = getState();
  const defaults = createDefaultLogsState();
  const nextMode = currentState.logsMode === "exercise" ? "exercise" : "date";
  const nextSelectedDate = currentState.logsSelectedDate || defaults.logsSelectedDate;
  const nextCalendarMonth = currentState.logsCalendarMonth || getMonthKeyFromDateKey(nextSelectedDate);
  const nextPatch = {
    selectedTab: "logs",
    logsMode: nextMode,
    logsSelectedDate: nextSelectedDate,
    logsCalendarMonth: nextCalendarMonth,
  };

  if (nextMode === "exercise") {
    const selectedExerciseId = String(currentState.logsSelectedExerciseId || "").trim();
    if (
      selectedExerciseId &&
      !currentState.logsExerciseHistory?.[selectedExerciseId] &&
      currentState.logsLoadingExercise !== selectedExerciseId
    ) {
      nextPatch.logsLoadingExercise = selectedExerciseId;
    }
  } else {
    if (
      !currentState.logsCalendarDatesByMonth?.[nextCalendarMonth] &&
      currentState.logsLoadingMonth !== nextCalendarMonth
    ) {
      nextPatch.logsLoadingMonth = nextCalendarMonth;
    }
    if (
      !currentState.logsWorkoutsByDate?.[nextSelectedDate] &&
      currentState.logsLoadingDate !== nextSelectedDate
    ) {
      nextPatch.logsLoadingDate = nextSelectedDate;
    }
  }

  if (options.syncHistory !== false) {
    appHistoryAutoReplaceSuspended = true;
  }
  setState(nextPatch);

  if (nextMode === "exercise") {
    const selectedExerciseId = String(currentState.logsSelectedExerciseId || "").trim();
    if (selectedExerciseId && !currentState.logsExerciseHistory?.[selectedExerciseId]) {
      void loadLogsExerciseHistory(
        selectedExerciseId,
        currentState.logsSelectedExerciseName || "",
        { skipLoadingState: true, allowCurrentLoading: true },
      );
    }
    if (options.syncHistory !== false) {
      pushCurrentAppHistorySnapshot();
      appHistoryAutoReplaceSuspended = false;
    }
    return;
  }

  if (!currentState.logsCalendarDatesByMonth?.[nextCalendarMonth]) {
    void loadLogsCalendarMonth(nextCalendarMonth, {
      skipLoadingState: true,
      allowCurrentLoading: true,
    });
  }
  if (!currentState.logsWorkoutsByDate?.[nextSelectedDate]) {
    void loadLogsDate(nextSelectedDate, {
      skipLoadingState: true,
      allowCurrentLoading: true,
    });
  }
  if (options.syncHistory !== false) {
    pushCurrentAppHistorySnapshot();
    appHistoryAutoReplaceSuspended = false;
  }
}

function handleSetLogsMode(mode) {
  const nextMode = mode === "exercise" ? "exercise" : "date";
  const currentState = getState();
  const defaults = createDefaultLogsState();
  const patch = {
    logsMode: nextMode,
    logsSelectedDate: currentState.logsSelectedDate || defaults.logsSelectedDate,
    logsCalendarMonth: currentState.logsCalendarMonth || defaults.logsCalendarMonth,
    logsDateExpandedWorkoutId: "",
    logsExerciseExpandedWorkoutId: "",
    logsLoadingWorkoutId: "",
  };
  setState(patch);
  maybeInitializeLogsView({
    ...currentState,
    ...patch,
  });
}

function handleSelectLogsDate(date) {
  const safeDate = normalizeDateKey(date);
  if (!safeDate) {
    return;
  }
  const nextMonth = getMonthKeyFromDateKey(safeDate);
  const patch = {
    logsMode: "date",
    logsSelectedDate: safeDate,
    logsCalendarMonth: nextMonth,
    logsDateExpandedWorkoutId: "",
    logsLoadingWorkoutId: "",
  };
  setState(patch);
  void loadLogsCalendarMonth(nextMonth);
  void loadLogsDate(safeDate);
}

function handleShiftLogsCalendarMonth(delta) {
  const currentState = getState();
  const fallbackState = createDefaultLogsState();
  const currentMonth = currentState.logsCalendarMonth || fallbackState.logsCalendarMonth;
  const currentDate = currentState.logsSelectedDate || fallbackState.logsSelectedDate;
  const nextMonth = shiftMonthKey(currentMonth, delta);
  const nextDate = clampDateKeyToMonth(currentDate, nextMonth);

  setState({
    logsMode: "date",
    logsCalendarMonth: nextMonth,
    logsSelectedDate: nextDate,
    logsDateExpandedWorkoutId: "",
    logsLoadingWorkoutId: "",
  });
  void loadLogsCalendarMonth(nextMonth);
  void loadLogsDate(nextDate);
}

async function handleSelectLogsExercise(exerciseId, exerciseName = "") {
  const safeExerciseId = String(exerciseId || "").trim();
  if (!safeExerciseId) {
    return;
  }

  const selectedName = String(exerciseName || "").trim();
  setState({
    logsMode: "exercise",
    logsSelectedExerciseId: safeExerciseId,
    logsSelectedExerciseName: selectedName,
    logsExerciseExpandedWorkoutId: "",
    logsLoadingWorkoutId: "",
  });
  await loadLogsExerciseHistory(safeExerciseId, selectedName, { force: true });
}

async function handleToggleWorkoutLog(workoutId, context = "date") {
  const safeWorkoutId = String(workoutId || "").trim();
  if (!safeWorkoutId) {
    return;
  }

  if (context === "exercise") {
    const currentExpandedWorkoutId = String(getState().logsExerciseExpandedWorkoutId || "");
    setState({
      logsExerciseExpandedWorkoutId: currentExpandedWorkoutId === safeWorkoutId ? "" : safeWorkoutId,
    });
    return;
  }

  const currentState = getState();
  if (currentState.logsDateExpandedWorkoutId === safeWorkoutId) {
    setState({
      logsDateExpandedWorkoutId: "",
      logsLoadingWorkoutId: "",
    });
    return;
  }

  const existingDetail = currentState.workoutDetailsById?.[safeWorkoutId];
  if (existingDetail) {
    setState({
      logsDateExpandedWorkoutId: safeWorkoutId,
      logsLoadingWorkoutId: "",
    });
    return;
  }

  setState({
    logsDateExpandedWorkoutId: safeWorkoutId,
    logsLoadingWorkoutId: safeWorkoutId,
  });

  try {
    const detail = await api.getWorkout(safeWorkoutId);
    setState({
      workoutDetailsById: {
        ...(getState().workoutDetailsById || {}),
        [safeWorkoutId]: detail,
      },
    });
  } finally {
    const latestState = getState();
    if (latestState.logsDateExpandedWorkoutId === safeWorkoutId) {
      setState({
        logsLoadingWorkoutId: "",
      });
    }
  }
}

async function handleOpenLogsFullSession(workoutId, date) {
  const safeWorkoutId = String(workoutId || "").trim();
  const safeDate = normalizeDateKey(date);
  if (!safeWorkoutId || !safeDate) {
    return;
  }

  const nextMonth = getMonthKeyFromDateKey(safeDate);
  const hasWorkoutDetail = !!getState().workoutDetailsById?.[safeWorkoutId];

  setState({
    logsMode: "date",
    logsSelectedDate: safeDate,
    logsCalendarMonth: nextMonth,
    logsDateExpandedWorkoutId: safeWorkoutId,
    logsExerciseExpandedWorkoutId: "",
    logsLoadingWorkoutId: hasWorkoutDetail ? "" : safeWorkoutId,
  });

  await Promise.all([
    loadLogsCalendarMonth(nextMonth),
    loadLogsDate(safeDate),
  ]);

  if (hasWorkoutDetail) {
    await waitForNextPaint();
    scrollLogsDateWorkoutToTop(safeWorkoutId);
    return;
  }

  try {
    const detail = await api.getWorkout(safeWorkoutId);
    setState({
      workoutDetailsById: {
        ...(getState().workoutDetailsById || {}),
        [safeWorkoutId]: detail,
      },
    });
  } finally {
    if (getState().logsDateExpandedWorkoutId === safeWorkoutId) {
      setState({
        logsLoadingWorkoutId: "",
      });
    }
  }

  await waitForNextPaint();
  scrollLogsDateWorkoutToTop(safeWorkoutId);
}

async function handleOpenLogWorkoutShare(workoutId) {
  const safeWorkoutId = String(workoutId || "").trim();
  if (!safeWorkoutId) {
    return;
  }

  let workout = getState().workoutDetailsById?.[safeWorkoutId] || null;
  if (!workout) {
    workout = await api.getWorkout(safeWorkoutId);
    setState({
      workoutDetailsById: {
        ...(getState().workoutDetailsById || {}),
        [safeWorkoutId]: workout,
      },
    });
  }

  openWorkoutRecordShareOverlay(workout);
}

function handleOpenCreateProgramBuilder() {
  const nextBuilder = createDefaultProgramBuilder();
  appHistoryAutoReplaceSuspended = true;
  setState({
    selectedTab: "programs",
    programBuilder: nextBuilder,
    programBuilderBaseline: createProgramBuilderBaseline(nextBuilder),
    programBuilderStage: "info",
    programBuilderDayIndex: 0,
  });
  pushCurrentAppHistorySnapshot();
  appHistoryAutoReplaceSuspended = false;
}

function handleOpenProgramTemplateSearch() {
  const nextState = {};
  if (getState().selectedTab !== "programs") {
    nextState.selectedTab = "programs";
  }
  if (getState().programBuilderStage === "search") {
    nextState.programBuilderStage = "list";
  }
  if (getState().programTemplateQuery) {
    nextState.programTemplateQuery = "";
  }
  if (Object.keys(nextState).length > 0) {
    setState(nextState);
  }
  openProgramTemplateSearchModal();
}

function handleCancelProgramBuilder() {
  setState({
    programBuilder: createDefaultProgramBuilder(),
    programBuilderBaseline: null,
    programBuilderStage: "list",
    programBuilderDayIndex: 0,
    programTemplateQuery: "",
  });
}

function handleOpenProgramInfoStage() {
  syncProgramBuilderFromDom();
  setState({
    programBuilderStage: "info",
  });
}

function handleOpenProgramDayStage(dayIndex, options = {}) {
  const draft = readProgramBuilderInfoDraftFromDom();
  if (draft && !isProgramBuilderInfoReady(draft)) {
    setInlineTextMessage("programError", "프로그램명과 루틴 길이를 먼저 입력하세요.");
    syncProgramBuilderInfoStepUi();
    return;
  }

  syncProgramBuilderFromDom();
  const builder = ensureProgramBuilder();
  if (!isProgramBuilderInfoReady(builder)) {
    setInlineTextMessage("programError", "프로그램명과 루틴 길이를 먼저 입력하세요.");
    syncProgramBuilderInfoStepUi();
    return;
  }
  setState({
    programBuilderStage: "day",
    programBuilderDayIndex: clampProgramBuilderDayIndex(dayIndex, builder),
  });
}

function scrollToTop(options = {}) {
  const behavior = options.behavior === "auto" ? "auto" : "smooth";
  window.scrollTo({
    top: 0,
    behavior,
  });
}

function scheduleUiTransitionScrollToTop() {
  if (typeof window === "undefined") {
    return;
  }

  const currentScrollY = Number(window.scrollY || window.pageYOffset || 0);
  if (currentScrollY <= 0) {
    return;
  }

  if (pendingUiTransitionScrollFrame) {
    window.cancelAnimationFrame(pendingUiTransitionScrollFrame);
  }

  pendingUiTransitionScrollFrame = window.requestAnimationFrame(() => {
    pendingUiTransitionScrollFrame = 0;
    scrollToTop({ behavior: "auto" });
  });
}

function shouldScrollToTopForUiTransition(state = getState(), meta = null) {
  const previousState = meta?.previousState && typeof meta.previousState === "object"
    ? meta.previousState
    : null;
  const changedKeySet = Array.isArray(meta?.changedKeys) ? new Set(meta.changedKeys) : null;
  if (!previousState || !changedKeySet || changedKeySet.size <= 0) {
    return false;
  }

  const selectedTab = String(state.selectedTab || "home").trim() || "home";
  const previousSelectedTab = String(previousState.selectedTab || "home").trim() || "home";
  if (selectedTab === "session") {
    return false;
  }

  if (changedKeySet.has("selectedTab") && selectedTab !== previousSelectedTab) {
    return true;
  }

  if (selectedTab === "programs") {
    const programBuilderStage = String(state.programBuilderStage || "list").trim() || "list";
    const previousProgramBuilderStage = String(previousState.programBuilderStage || "list").trim() || "list";
    if (changedKeySet.has("programBuilderStage") && programBuilderStage !== previousProgramBuilderStage) {
      return true;
    }
    if (
      programBuilderStage === "day" &&
      changedKeySet.has("programBuilderDayIndex") &&
      Number(state.programBuilderDayIndex) !== Number(previousState.programBuilderDayIndex)
    ) {
      return true;
    }
  }

  if (selectedTab === "logs") {
    const logsMode = String(state.logsMode || "date").trim() || "date";
    const previousLogsMode = String(previousState.logsMode || "date").trim() || "date";
    if (changedKeySet.has("logsMode") && logsMode !== previousLogsMode) {
      return true;
    }
  }

  return false;
}

function getProgramItemSearchKey(dayIndex, itemIndex) {
  return `${dayIndex}:${itemIndex}`;
}

function getProgramItemSearchInput(dayIndex, itemIndex) {
  return document.getElementById(`programItemExerciseQuery-${dayIndex}-${itemIndex}`);
}

function getProgramItemSearchResultsWrap(dayIndex, itemIndex) {
  return document.getElementById(`programItemExerciseResults-${dayIndex}-${itemIndex}`);
}

function clearProgramItemSearchResults(dayIndex, itemIndex) {
  const resultsEl = getProgramItemSearchResultsWrap(dayIndex, itemIndex);
  if (resultsEl instanceof HTMLElement) {
    resultsEl.replaceChildren();
  }
}

function renderProgramItemSearchResults(dayIndex, itemIndex, items) {
  const resultsEl = getProgramItemSearchResultsWrap(dayIndex, itemIndex);
  if (!(resultsEl instanceof HTMLElement)) {
    return;
  }

  setElementHtml(resultsEl, renderExerciseSearchResults(items, {
    action: "programs.select-item-exercise",
    emptyClassName: "program-item-search-empty",
    extraAttributes: () => ({
      "day-index": dayIndex,
      "item-index": itemIndex,
    }),
  }));
}

function createProgramItemExerciseSearchHandlers(dayIndex, itemIndex) {
  return {
    onEmpty() {
      clearProgramItemSearchResults(dayIndex, itemIndex);
    },
    onError() {
      clearProgramItemSearchResults(dayIndex, itemIndex);
    },
    onResults(items) {
      renderProgramItemSearchResults(dayIndex, itemIndex, items);
    },
  };
}

async function searchProgramItemExercises(dayIndex, itemIndex, query) {
  const key = getProgramItemSearchKey(dayIndex, itemIndex);
  return programItemExerciseSearchSessions.search(key, query, createProgramItemExerciseSearchHandlers(dayIndex, itemIndex));
}

function focusProgramItemSearchInput(dayIndex, itemIndex) {
  scheduleFocusWithoutScroll(() => getProgramItemSearchInput(dayIndex, itemIndex), { select: true });
}

function setProgramItemPickerState(dayIndex, itemIndex, shouldOpen) {
  updateProgramBuilder((builder) => {
    (builder.days || []).forEach((day) => {
      (day?.items || []).forEach((item) => {
        if (item) {
          item.pickerOpen = false;
          item.exerciseQuery = "";
          item.exerciseSearchResults = [];
        }
      });
    });

    const targetItem = getBuilderItemByIndex(builder, dayIndex, itemIndex);
    if (!targetItem) {
      return;
    }
    targetItem.pickerOpen = !!shouldOpen;
  });

  if (shouldOpen) {
    focusProgramItemSearchInput(dayIndex, itemIndex);
    return;
  }

  const key = getProgramItemSearchKey(dayIndex, itemIndex);
  programItemExerciseSearchSessions.invalidate(key);
  clearProgramItemSearchResults(dayIndex, itemIndex);
}

function toggleProgramItemPicker(dayIndex, itemIndex) {
  const builder = ensureProgramBuilder();
  const item = getBuilderItemByIndex(builder, dayIndex, itemIndex);
  if (!item) {
    return;
  }
  setProgramItemPickerState(dayIndex, itemIndex, !item.pickerOpen);
}

function handleProgramItemExerciseInput(dayIndex, itemIndex) {
  if (!Number.isInteger(dayIndex) || !Number.isInteger(itemIndex) || dayIndex < 0 || itemIndex < 0) {
    return;
  }

  const key = getProgramItemSearchKey(dayIndex, itemIndex);
  const input = getProgramItemSearchInput(dayIndex, itemIndex);
  const query = String(input?.value || "").trim();
  programItemExerciseSearchSessions.schedule(key, query, createProgramItemExerciseSearchHandlers(dayIndex, itemIndex));
}

async function handleLoadProgramToBuilder(programId) {
  let target = (getState().programs || []).find((program) => program.id === programId);
  if (!target) {
    const response = await api.getProgram(programId);
    target = response.program;
  }
  if (!target) {
    throw new Error("프로그램을 찾을 수 없습니다");
  }

  const builder = convertProgramToBuilder(target);
  const exerciseNameMap = await resolveExerciseNameMap(
    collectExerciseIdsFromBuilder(builder),
    getState().exerciseNameMap || {},
  );

  appHistoryAutoReplaceSuspended = true;
  setState({
    selectedTab: "programs",
    programBuilder: builder,
    programBuilderBaseline: createProgramBuilderBaseline(builder),
    programBuilderStage: "info",
    programBuilderDayIndex: 0,
    programTemplateQuery: "",
    exerciseNameMap,
  });
  pushCurrentAppHistorySnapshot();
  appHistoryAutoReplaceSuspended = false;
}

function getBuilderItemByIndex(builder, dayIndex, itemIndex) {
  const day = builder?.days?.[dayIndex];
  if (!day) {
    return null;
  }
  return day.items?.[itemIndex] || null;
}

async function handleSearchProgramItemExercise(dayIndex, itemIndex) {
  const input = getProgramItemSearchInput(dayIndex, itemIndex);
  const query = String(input?.value || "").trim();
  await searchProgramItemExercises(dayIndex, itemIndex, query);
}

function handleSelectProgramItemExercise(dayIndex, itemIndex, exerciseId, exerciseName = "") {
  const safeExerciseId = String(exerciseId || "").trim();
  if (!safeExerciseId) {
    return;
  }
  if (!Number.isInteger(dayIndex) || !Number.isInteger(itemIndex) || dayIndex < 0 || itemIndex < 0) {
    return;
  }

  const builder = ensureProgramBuilder();
  const item = getBuilderItemByIndex(builder, dayIndex, itemIndex);
  const displayName = String(exerciseName || item?.exerciseDisplayName || "").trim();

  syncProgramBuilderFromDom();
  updateProgramBuilder((nextBuilder) => {
    if (!nextBuilder.days[dayIndex]) nextBuilder.days[dayIndex] = createBuilderDay(dayIndex);
    while ((nextBuilder.days[dayIndex].items?.length || 0) <= itemIndex) {
      nextBuilder.days[dayIndex].items.push(createBuilderItem());
    }
    if (!nextBuilder.days[dayIndex].items[itemIndex]) {
      nextBuilder.days[dayIndex].items[itemIndex] = createBuilderItem();
    }
    const nextItem = nextBuilder.days[dayIndex].items[itemIndex];
    nextItem.exerciseId = safeExerciseId;
    nextItem.exerciseDisplayName = displayName;
  });

  if (displayName) {
    setState({
      exerciseNameMap: {
        ...(getState().exerciseNameMap || {}),
        [safeExerciseId]: displayName,
      },
    });
  }
}

let programItemRuleLastFocused = null;

function renderProgramItemRuleModalBody(ruleType, dayIndex, itemIndex) {
  const bodyEl = els.programItemRuleModalBody;
  const titleEl = els.programItemRuleModalTitle;
  const subtitleEl = els.programItemRuleModalSubtitle;
  if (!(bodyEl instanceof HTMLElement)) return;

  const item = (ensureProgramBuilder()?.days?.[dayIndex]?.items?.[itemIndex]) || createBuilderItem();
  const exerciseName = String(item.exerciseDisplayName || "").trim() || `운동 ${itemIndex + 1}`;
  const isLoad = ruleType === "load";
  const ruleKey = isLoad ? "load" : "reps";
  const loadType = item.loadType === "percent1RM" ? "percent1RM" : "none";
  const repsMode = item.repsMode === "fixed" || item.repsMode === "range" ? item.repsMode : "none";
  const isRange = repsMode === "range";
  const displayRepsMode = isRange ? "none" : repsMode;
  const uniqueKey = `${ruleKey}-${dayIndex}-${itemIndex}`;
  const percentInputId = `program-item-rule-percent-${uniqueKey}`;
  const percentInputName = `programItemRulePercent-${uniqueKey}`;
  const rmBaseLabelId = `program-item-rule-rm-base-${uniqueKey}`;
  const fixedRepsInputId = `program-item-rule-fixed-reps-${uniqueKey}`;
  const fixedRepsInputName = `programItemRuleFixedReps-${uniqueKey}`;

  if (titleEl instanceof HTMLElement) {
    titleEl.textContent = isLoad ? "중량 규칙 설정" : "반복 규칙 설정";
  }
  if (subtitleEl instanceof HTMLElement) {
    subtitleEl.textContent = `${exerciseName}의 ${isLoad ? "중량" : "반복"} 규칙을 설정하세요.`;
  }

  const html = isLoad
    ? `
      <div class="program-item-rule-modal-body">
        <div class="program-item-rule-modal-head">
          <p class="program-item-section-title">중량 규칙</p>
          <p class="small program-item-rule-modal-description">운동 시작 시 자동 제안할 중량 규칙을 정합니다.</p>
        </div>
        <div class="program-item-search-results program-item-rule-option-list">
          <div
            class="program-search-result program-item-rule-option-card ${loadType === "none" ? "is-selected" : ""} select-program-item-rule-option-card"
            data-rule-type="load" data-rule-option="none"
            data-day-index="${dayIndex}" data-item-index="${itemIndex}"
            role="button" tabindex="0" aria-pressed="${loadType === "none" ? "true" : "false"}"
          >
            <div class="program-search-result-content">
              <p class="program-search-result-name">직접 입력</p>
              <p class="small">운동 진행 중 사용자가 중량을 직접 입력합니다.</p>
            </div>
            <span class="program-item-rule-option-state">${loadType === "none" ? "선택됨" : "선택"}</span>
            ${loadType === "none" ? `
              <div class="program-item-rule-inline-body">
                <p class="small program-item-rule-modal-hint">중량은 운동 진행 중 사용자가 직접 입력합니다.</p>
              </div>
            ` : ""}
          </div>
          <div
            class="program-search-result program-item-rule-option-card ${loadType === "percent1RM" ? "is-selected" : ""} select-program-item-rule-option-card"
            data-rule-type="load" data-rule-option="percent1RM"
            data-day-index="${dayIndex}" data-item-index="${itemIndex}"
            role="button" tabindex="0" aria-pressed="${loadType === "percent1RM" ? "true" : "false"}"
          >
            <div class="program-search-result-content">
              <p class="program-search-result-name">%1RM 사용</p>
              <p class="small">기준 1RM과 비율을 바탕으로 시작 중량을 제안합니다.</p>
            </div>
            <span class="program-item-rule-option-state">${loadType === "percent1RM" ? "선택됨" : "선택"}</span>
            ${loadType === "percent1RM" ? `
              <div class="program-item-rule-inline-body">
                <div class="field">
                  <label for="${percentInputId}">% 값</label>
                  <input id="${percentInputId}" name="${percentInputName}" data-builder-item-field="percent" data-day-index="${dayIndex}" data-item-index="${itemIndex}" data-rule-modal-type="${ruleKey}" type="number" min="1" max="${MAX_PERCENT_1RM}" value="${escapeHtmlText(item.percent || "")}" />
                </div>
                <div class="field">
                  <label id="${rmBaseLabelId}">1RM 기준</label>
                  <div class="program-item-rule-segmented" role="radiogroup" aria-labelledby="${rmBaseLabelId}">
                    ${["squat", "bench", "deadlift"].map((k) => `
                      <button
                        type="button"
                        class="${["program-item-rule-segment", item.rmBase === k ? "is-selected" : "", "select-program-item-rm-base-btn"].filter(Boolean).join(" ")}"
                        data-day-index="${dayIndex}" data-item-index="${itemIndex}" data-rm-base="${k}"
                        aria-pressed="${item.rmBase === k ? "true" : "false"}"
                      >${k === "squat" ? "Squat" : k === "bench" ? "Bench" : "Deadlift"}</button>
                    `).join("")}
                  </div>
                </div>
              </div>
            ` : ""}
          </div>
        </div>
      </div>
    `
    : `
      <div class="program-item-rule-modal-body">
        <div class="program-item-rule-modal-head">
          <p class="program-item-section-title">반복 규칙</p>
          <p class="small program-item-rule-modal-description">운동 중 기본으로 보여줄 반복 수 규칙을 정합니다.</p>
          ${isRange ? '<p class="small program-item-rule-modal-description">기존 범위 반복 설정은 새로 선택할 수 없습니다. 직접 입력 또는 고정 반복으로만 변경할 수 있습니다.</p>' : ""}
        </div>
        <div class="program-item-search-results program-item-rule-option-list">
          <div
            class="program-search-result program-item-rule-option-card ${displayRepsMode === "none" ? "is-selected" : ""} select-program-item-rule-option-card"
            data-rule-type="reps" data-rule-option="none"
            data-day-index="${dayIndex}" data-item-index="${itemIndex}"
            role="button" tabindex="0" aria-pressed="${displayRepsMode === "none" ? "true" : "false"}"
          >
            <div class="program-search-result-content">
              <p class="program-search-result-name">직접 입력</p>
              <p class="small">반복 수는 운동 진행 중 자유 입력합니다.</p>
            </div>
            <span class="program-item-rule-option-state">${displayRepsMode === "none" ? "선택됨" : "선택"}</span>
            ${displayRepsMode === "none" ? `
              <div class="program-item-rule-inline-body">
                <p class="small program-item-rule-modal-hint">반복 수는 운동 진행 중 자유 입력합니다.</p>
              </div>
            ` : ""}
          </div>
          <div
            class="program-search-result program-item-rule-option-card ${displayRepsMode === "fixed" ? "is-selected" : ""} select-program-item-rule-option-card"
            data-rule-type="reps" data-rule-option="fixed"
            data-day-index="${dayIndex}" data-item-index="${itemIndex}"
            role="button" tabindex="0" aria-pressed="${displayRepsMode === "fixed" ? "true" : "false"}"
          >
            <div class="program-search-result-content">
              <p class="program-search-result-name">고정 반복</p>
              <p class="small">모든 세트에 같은 반복 수를 기본값으로 사용합니다.</p>
            </div>
            <span class="program-item-rule-option-state">${displayRepsMode === "fixed" ? "선택됨" : "선택"}</span>
            ${displayRepsMode === "fixed" ? `
              <div class="program-item-rule-inline-body">
                <div class="field">
                  <label for="${fixedRepsInputId}">고정 reps</label>
                  <input id="${fixedRepsInputId}" name="${fixedRepsInputName}" data-builder-item-field="fixedReps" data-day-index="${dayIndex}" data-item-index="${itemIndex}" data-rule-modal-type="${ruleKey}" type="number" min="1" max="100" value="${escapeHtmlText(item.fixedReps || "")}" />
                </div>
              </div>
            ` : ""}
          </div>
        </div>
      </div>
    `;

  setElementHtml(bodyEl, html);
  syncModalBodyLock();
}

function openProgramItemRuleModal(dayIndex, itemIndex, ruleType, options = {}) {
  if (!Number.isInteger(dayIndex) || !Number.isInteger(itemIndex) || dayIndex < 0 || itemIndex < 0) {
    return;
  }
  const modal = els.programItemRuleModal;
  if (!(modal instanceof HTMLElement)) return;
  const wasOpen = isProgramItemRuleModalOpen();
  const openState = captureModalInteractionState();
  syncProgramBuilderFromDom();

  if (wasOpen) {
    closeProgramItemRuleModal({ restoreFocus: false, syncHistory: false });
  }

  programItemRuleLastFocused = openState.focusTarget;
  modal.dataset.dayIndex = String(dayIndex);
  modal.dataset.itemIndex = String(itemIndex);
  modal.dataset.ruleType = ruleType === "load" ? "load" : "reps";

  renderProgramItemRuleModalBody(modal.dataset.ruleType, dayIndex, itemIndex);
  showModalWithInteraction(modal, openState, wasOpen, options);

  scheduleFocusWithoutScroll(() => {
    const firstInput = modal.querySelector("select, input");
    return firstInput instanceof HTMLElement ? firstInput : els.programItemRuleModalDoneBtn;
  });
}

function closeProgramItemRuleModal(options = {}) {
  const modal = els.programItemRuleModal;
  const bodyEl = els.programItemRuleModalBody;
  if (!(modal instanceof HTMLElement) || !isProgramItemRuleModalOpen()) return;

  // Capture first; syncing the builder can rerender and disturb the current scroll position.
  const restoreState = {
    focusTarget: programItemRuleLastFocused instanceof HTMLElement ? programItemRuleLastFocused : null,
    ...captureWindowScrollPosition(),
  };

  syncProgramBuilderFromDom({ forceNotify: true });

  modal.removeAttribute("data-day-index");
  modal.removeAttribute("data-item-index");
  modal.removeAttribute("data-rule-type");
  if (bodyEl instanceof HTMLElement) bodyEl.replaceChildren();
  hideModalWithInteraction(modal, restoreState, options);
  programItemRuleLastFocused = null;

  if (options.syncHistory !== false) {
    consumeOrReplaceCurrentAppHistoryEntry();
  }
}

function refreshProgramItemRuleModal() {
  const modal = els.programItemRuleModal;
  if (!(modal instanceof HTMLElement) || !isProgramItemRuleModalOpen()) return;
  const dayIndex = Number(modal.dataset.dayIndex);
  const itemIndex = Number(modal.dataset.itemIndex);
  renderProgramItemRuleModalBody(String(modal.dataset.ruleType || "load"), dayIndex, itemIndex);
}

function handleSelectProgramItemRuleOption(dayIndex, itemIndex, ruleType, ruleOption) {
  if (!Number.isInteger(dayIndex) || !Number.isInteger(itemIndex) || dayIndex < 0 || itemIndex < 0) return;
  const safeRuleType = ruleType === "load" ? "load" : "reps";

  syncProgramBuilderFromDom();
  updateProgramBuilder((builder) => {
    if (!builder.days[dayIndex]) builder.days[dayIndex] = createBuilderDay(dayIndex);
    if (!builder.days[dayIndex].items[itemIndex]) builder.days[dayIndex].items[itemIndex] = createBuilderItem();
    if (safeRuleType === "load") {
      builder.days[dayIndex].items[itemIndex].loadType = ruleOption === "percent1RM" ? "percent1RM" : "none";
    } else {
      builder.days[dayIndex].items[itemIndex].repsMode = (ruleOption === "fixed" || ruleOption === "range") ? ruleOption : "none";
    }
  });

  refreshProgramItemRuleModal();
}

function handleSelectProgramItemRmBase(dayIndex, itemIndex, rmBase) {
  if (!Number.isInteger(dayIndex) || !Number.isInteger(itemIndex) || dayIndex < 0 || itemIndex < 0) return;
  const safeRmBase = rmBase === "squat" || rmBase === "deadlift" ? rmBase : "bench";

  syncProgramBuilderFromDom();
  updateProgramBuilder((builder) => {
    if (!builder.days[dayIndex]) builder.days[dayIndex] = createBuilderDay(dayIndex);
    if (!builder.days[dayIndex].items[itemIndex]) builder.days[dayIndex].items[itemIndex] = createBuilderItem();
    builder.days[dayIndex].items[itemIndex].rmBase = safeRmBase;
  });

  refreshProgramItemRuleModal();
}

function openProgramItemExerciseSearchModal(dayIndex, itemIndex, options = {}) {
  if (!Number.isInteger(dayIndex) || !Number.isInteger(itemIndex) || dayIndex < 0 || itemIndex < 0) {
    return;
  }

  openAnalyticsSearchModal("program-item", {
    ...options,
    dayIndex,
    itemIndex,
  });
}

function getSessionAddSearchInput() {
  return document.getElementById("sessionAddExerciseId");
}

function getSessionAddSearchDatalist() {
  return document.getElementById("sessionAddExerciseSuggest");
}

function getSessionAddSearchResultsWrap() {
  return document.getElementById("sessionAddExerciseResults");
}

function getSessionAddSelectedNameEl() {
  return document.getElementById("sessionAddSelectedExerciseName");
}

function getSessionAddModeTextEl() {
  return document.getElementById("sessionAddModeText");
}

function getSessionAddPanelEl() {
  return document.getElementById("sessionAddPanel");
}

function getSessionAddToggleButton() {
  return document.getElementById("toggleSessionAddPanelBtn");
}

const SESSION_ADD_EXERCISE_SELECTION_MESSAGE = "검색 결과에서 운동을 선택하세요.";

function getSessionAddExerciseSelectionMessage() {
  return SESSION_ADD_EXERCISE_SELECTION_MESSAGE;
}

function getSessionAddSelectedExerciseId(exerciseInput = getSessionAddSearchInput()) {
  if (!(exerciseInput instanceof HTMLInputElement)) {
    return "";
  }

  return String(exerciseInput.dataset.selectedExerciseId || "").trim();
}

function resolveSessionItemEditOrder() {
  const state = getState();
  const rawOrder = state.sessionItemEditOrder;
  if (rawOrder === null || rawOrder === undefined || rawOrder === "") {
    return null;
  }
  const order = Number(rawOrder);
  if (!Number.isInteger(order) || order < 0) {
    return null;
  }
  const item = state.activeWorkout?.items?.[String(order)];
  if (!item) {
    return null;
  }
  return order;
}

function resolveSessionAddMode() {
  const editOrder = resolveSessionItemEditOrder();
  return {
    isReplace: Number.isInteger(editOrder),
    editOrder,
  };
}

function updateSessionAddToggleButtonText(isPanelOpen) {
  const button = getSessionAddToggleButton();
  if (!(button instanceof HTMLButtonElement)) {
    return;
  }
  const shouldHide = isPanelOpen && !resolveSessionAddMode().isReplace;
  button.classList.toggle("hidden", shouldHide);
}

function refreshSessionAddModeText() {
  const modeTextEl = getSessionAddModeTextEl();
  if (!(modeTextEl instanceof HTMLElement)) {
    return;
  }
  const state = getState();
  const editOrder = resolveSessionItemEditOrder();
  if (Number.isInteger(editOrder)) {
    const targetName = String(state.activeWorkout?.items?.[String(editOrder)]?.nameSnapshot || "운동").trim();
    modeTextEl.textContent = `편집 대상: ${editOrder + 1}. ${targetName}`;
  } else {
    modeTextEl.textContent = "신규 운동을 현재 진행 운동에 추가합니다.";
  }
}

function openSessionAddPanel(options = {}) {
  const panel = getSessionAddPanelEl();
  if (!(panel instanceof HTMLElement)) {
    return;
  }
  panel.classList.remove("hidden");
  updateSessionAddToggleButtonText(true);
  refreshSessionAddModeText();

  scheduleFocusWithoutScroll(() => getSessionAddSearchInput(), { select: true });
}

function closeSessionAddPanel({ clearSelection = true, syncHistory = true } = {}) {
  const panel = getSessionAddPanelEl();
  if (!(panel instanceof HTMLElement)) {
    return;
  }
  panel.classList.add("hidden");
  updateSessionAddToggleButtonText(false);
  sessionAddExerciseSearch.invalidate();
  clearSessionAddSearchResults();
  clearSessionAddAutocompleteOptions();
  if (clearSelection) {
    setSessionAddSelectedExercise("", "");
  }
}

function openSessionAddPanelForCreate() {
  const openFreshAddPanel = () => {
    const panel = getSessionAddPanelEl();
    if (!(panel instanceof HTMLElement)) {
      return;
    }
    closeSessionAddPanel({ clearSelection: true, syncHistory: false });
    openSessionAddPanel();
  };

  if (Number.isInteger(resolveSessionItemEditOrder())) {
    setState({
      sessionItemEditOrder: null,
    });
    requestAnimationFrame(() => {
      openFreshAddPanel();
    });
    return;
  }

  openFreshAddPanel();
}

function clearSessionAddSearchResults() {
  const resultsEl = getSessionAddSearchResultsWrap();
  if (resultsEl instanceof HTMLElement) {
    resultsEl.replaceChildren();
  }
}

function setSessionAddSelectedExercise(exerciseId, displayName) {
  const safeExerciseId = String(exerciseId || "").trim();
  const safeDisplayName = String(displayName || "").trim();
  const input = getSessionAddSearchInput();
  const selectedNameEl = getSessionAddSelectedNameEl();
  if (!(input instanceof HTMLInputElement)) {
    return;
  }

  if (safeExerciseId && safeDisplayName) {
    input.dataset.selectedExerciseId = safeExerciseId;
    input.dataset.selectedExerciseName = safeDisplayName;
    input.value = safeDisplayName;
    if (selectedNameEl instanceof HTMLElement) {
      selectedNameEl.textContent = `선택됨: ${safeDisplayName}`;
    }
  } else {
    delete input.dataset.selectedExerciseId;
    delete input.dataset.selectedExerciseName;
    if (selectedNameEl instanceof HTMLElement) {
      selectedNameEl.textContent = "선택된 운동 없음";
    }
  }

  clearConstraintFieldValidationUi(input);
}

function clearSessionAddAutocompleteOptions() {
  const input = getSessionAddSearchInput();
  const datalist = getSessionAddSearchDatalist();
  if (input instanceof HTMLInputElement) {
    delete input.dataset.autocompleteMap;
  }
  if (datalist instanceof HTMLDataListElement) {
    datalist.replaceChildren();
  }
}

function setSessionAddAutocompleteOptions(items) {
  const input = getSessionAddSearchInput();
  const datalist = getSessionAddSearchDatalist();
  if (!(input instanceof HTMLInputElement) || !(datalist instanceof HTMLDataListElement)) {
    return;
  }

  const map = {};
  datalist.replaceChildren();

  items.slice(0, 8).forEach((entry) => {
    const label = String(entry?.nameKo || entry?.nameEn || "").trim();
    const exerciseId = String(entry?.id || "").trim();
    if (!label || !exerciseId) {
      return;
    }

    if (!map[label]) {
      map[label] = exerciseId;
      const option = document.createElement("option");
      option.value = label;
      const parts = [];
      if (entry?.primaryMuscle) {
        parts.push(String(entry.primaryMuscle));
      }
      if (entry?.abbr) {
        parts.push(`약칭 ${entry.abbr}`);
      }
      if (parts.length > 0) {
        option.label = parts.join(" · ");
      }
      datalist.append(option);
    }
  });

  input.dataset.autocompleteMap = JSON.stringify(map);
}

function getSessionAddAutocompleteExerciseId(inputEl) {
  if (!(inputEl instanceof HTMLInputElement)) {
    return "";
  }

  const raw = inputEl.dataset.autocompleteMap || "{}";
  const query = String(inputEl.value || "").trim();
  if (!query) {
    return "";
  }

  try {
    const parsed = JSON.parse(raw);
    if (parsed?.[query]) {
      return String(parsed[query]);
    }
    const lower = query.toLowerCase();
    const exactKey = Object.keys(parsed || {}).find((key) => key.toLowerCase() === lower);
    if (exactKey && parsed[exactKey]) {
      return String(parsed[exactKey]);
    }
  } catch (_) {
    return "";
  }

  return "";
}

function clearSessionAddComposer(exerciseInput = getSessionAddSearchInput()) {
  setSessionAddSelectedExercise("", "");
  clearSessionAddSearchResults();
  clearSessionAddAutocompleteOptions();
  if (exerciseInput instanceof HTMLInputElement) {
    exerciseInput.value = "";
    clearConstraintFieldValidationUi(exerciseInput);
  }
}

function refreshSessionPreviousByExerciseState(nextWorkout) {
  if (!nextWorkout?.workoutId) {
    return;
  }

  const currentState = getState();
  if (String(currentState.activeWorkout?.workoutId || "") !== String(nextWorkout.workoutId || "")) {
    return;
  }

  const nextExerciseIds = collectWorkoutExerciseIds(nextWorkout);
  if (nextExerciseIds.length <= 0) {
    setState({
      sessionPreviousByExercise: {},
    });
    return;
  }

  const existingPrevious = currentState.sessionPreviousByExercise || {};
  const missingExerciseIds = nextExerciseIds.filter((exerciseId) => !existingPrevious[exerciseId]);
  const removedExerciseIds = Object.keys(existingPrevious).filter((exerciseId) => !nextExerciseIds.includes(exerciseId));

  if (missingExerciseIds.length <= 0 && removedExerciseIds.length <= 0) {
    return;
  }

  fetchLatestWorkoutItemsByExercise(missingExerciseIds, nextWorkout.workoutId, { forceRefresh: true })
    .then((records) => {
      const currentState = getState();
      if (String(currentState.activeWorkout?.workoutId || "") !== String(nextWorkout.workoutId || "")) {
        return;
      }
      const mergedPrevious = nextExerciseIds.reduce((acc, exerciseId) => {
        const record = records?.[exerciseId] || existingPrevious[exerciseId] || null;
        if (record) {
          acc[exerciseId] = record;
        }
        return acc;
      }, {});
      setState({
        sessionPreviousByExercise: mergedPrevious,
      });
    })
    .catch(() => undefined);
}

function renderSessionAddSearchResults(items) {
  const resultsEl = getSessionAddSearchResultsWrap();
  if (!(resultsEl instanceof HTMLElement)) {
    return;
  }

  setElementHtml(resultsEl, renderExerciseSearchResults(items, {
    action: "session.select-add-exercise",
  }));
}

function createSessionAddExerciseSearchHandlers() {
  return {
    onEmpty() {
      clearSessionAddAutocompleteOptions();
      clearSessionAddSearchResults();
    },
    onError() {
      clearSessionAddSearchResults();
    },
    onResults(items) {
      setSessionAddAutocompleteOptions(items);
      renderSessionAddSearchResults(items);
    },
  };
}

async function searchSessionAddExercises(query) {
  return sessionAddExerciseSearch.search(query, createSessionAddExerciseSearchHandlers());
}

function handleSessionAddExerciseInput() {
  const input = getSessionAddSearchInput();
  if (!(input instanceof HTMLInputElement)) {
    return;
  }

  clearConstraintFieldValidationUi(input);
  const query = String(input.value || "").trim();
  const selectedName = String(input.dataset.selectedExerciseName || "").trim();
  if (selectedName && query !== selectedName) {
    setSessionAddSelectedExercise("", "");
  }

  sessionAddExerciseSearch.schedule(query, createSessionAddExerciseSearchHandlers());
}

async function handleSearchSessionAddExercise() {
  const input = getSessionAddSearchInput();
  if (!(input instanceof HTMLInputElement)) {
    return;
  }

  const query = String(input.value || "").trim();
  if (!query) {
    clearSessionAddAutocompleteOptions();
    clearSessionAddSearchResults();
    setSessionAddSelectedExercise("", "");
    return;
  }

  const selectedByAutocomplete = getSessionAddAutocompleteExerciseId(input);
  if (selectedByAutocomplete) {
    setSessionAddSelectedExercise(selectedByAutocomplete, query);
    clearSessionAddSearchResults();
    return;
  }

  await searchSessionAddExercises(query);
}

function handleSelectSessionAddExercise(exerciseId, exerciseName) {
  const safeExerciseId = String(exerciseId || "").trim();
  if (!safeExerciseId) {
    return;
  }
  const safeExerciseName = String(exerciseName || "").trim();
  setSessionAddSelectedExercise(safeExerciseId, safeExerciseName);
  syncConstraintFieldValidationUi(getSessionAddSearchInput(), {
    requireFilled: false,
    applyUi: true,
  });
  clearSessionAddSearchResults();
}

async function handleToggleRestTimer() {
  const { activeWorkout, activeSession } = getState();
  if (!activeWorkout?.workoutId || !activeSession) {
    return;
  }
  const nextEnabled = !(activeSession.rest?.enabled ?? true);

  await commitSessionWorkoutDraft(activeWorkout.workoutId, {
    force: true,
    mode: SESSION_COMMIT_MODE_INTERACTIVE,
    sessionPatch: {
      rest: {
        enabled: nextEnabled,
        running: false,
        endAt: null,
      },
    },
  });
}

async function handleRestNavButtonPress() {
  const { activeWorkout, activeSession } = getState();
  const workoutId = String(activeWorkout?.workoutId || activeSession?.workoutId || "").trim();
  if (!workoutId || !activeSession?.rest?.enabled || !activeSession.rest?.running) {
    return;
  }

  await commitSessionWorkoutDraft(workoutId, {
    force: true,
    mode: SESSION_COMMIT_MODE_INTERACTIVE,
    sessionPatch: {
      rest: {
        running: false,
        endAt: null,
      },
    },
  });
}

function handleBuilderFieldChange(target) {
  if (!(target instanceof HTMLElement)) {
    return;
  }

  const rootField = target.dataset.builderRoot;
  if (rootField) {
    const builder = ensureProgramBuilder();
    const nextValue = readBuilderFieldValue(target);
    const currentValue = rootField === "isActive"
      ? !!builder[rootField]
      : String(builder[rootField] ?? "");
    const normalizedNextValue = rootField === "isActive"
      ? !!nextValue
      : String(nextValue ?? "");
    if (Object.is(currentValue, normalizedNextValue)) {
      return;
    }

    updateProgramBuilder((builder) => {
      if (rootField === "isActive") {
        builder[rootField] = target instanceof HTMLInputElement ? target.checked : false;
        return;
      }
      builder[rootField] = target instanceof HTMLInputElement || target instanceof HTMLSelectElement ? target.value : "";
    });
    return;
  }

  const dayField = target.dataset.builderDayField;
  if (dayField !== undefined) {
    const dayIndex = Number(target.dataset.dayIndex);
    if (!Number.isInteger(dayIndex) || dayIndex < 0) return;
    const builder = ensureProgramBuilder();
    const nextValue = String(readBuilderFieldValue(target) ?? "");
    const currentValue = String(builder.days?.[dayIndex]?.[dayField] ?? "");
    if (currentValue === nextValue) {
      return;
    }

    updateProgramBuilder((builder) => {
      if (!builder.days[dayIndex]) {
        builder.days[dayIndex] = createBuilderDay(dayIndex);
      }
      builder.days[dayIndex][dayField] = target instanceof HTMLInputElement || target instanceof HTMLSelectElement ? target.value : "";
    });
    return;
  }

  const itemField = target.dataset.builderItemField;
  if (itemField !== undefined) {
    const dayIndex = Number(target.dataset.dayIndex);
    const itemIndex = Number(target.dataset.itemIndex);
    if (!Number.isInteger(dayIndex) || !Number.isInteger(itemIndex) || dayIndex < 0 || itemIndex < 0) return;
    const builder = ensureProgramBuilder();
    const nextValue = String(readBuilderFieldValue(target) ?? "");
    const currentValue = String(builder.days?.[dayIndex]?.items?.[itemIndex]?.[itemField] ?? "");
    if (currentValue === nextValue) {
      return;
    }

    updateProgramBuilder((builder) => {
      if (!builder.days[dayIndex]) {
        builder.days[dayIndex] = createBuilderDay(dayIndex);
      }
      if (!builder.days[dayIndex].items[itemIndex]) {
        builder.days[dayIndex].items[itemIndex] = createBuilderItem();
      }
      const value = target instanceof HTMLInputElement || target instanceof HTMLSelectElement ? target.value : "";
      builder.days[dayIndex].items[itemIndex][itemField] = value;
      if (itemField === "exerciseQuery") {
        builder.days[dayIndex].items[itemIndex].exerciseSearchResults = [];
      }
    });
  }
}

function syncProgramBuilderDraftFieldFromInput(target) {
  if (
    !(target instanceof HTMLInputElement) &&
    !(target instanceof HTMLTextAreaElement)
  ) {
    return;
  }

  const rootField = target.dataset.builderRoot;
  if (rootField) {
    const builder = ensureProgramBuilder();
    const nextValue = readBuilderFieldValue(target);
    const currentValue = rootField === "isActive"
      ? !!builder[rootField]
      : String(builder[rootField] ?? "");
    const normalizedNextValue = rootField === "isActive"
      ? !!nextValue
      : String(nextValue ?? "");
    if (Object.is(currentValue, normalizedNextValue)) {
      return;
    }

    updateProgramBuilder((draftBuilder) => {
      if (rootField === "isActive") {
        draftBuilder[rootField] = target instanceof HTMLInputElement ? target.checked : false;
        return;
      }
      draftBuilder[rootField] = target.value;
    }, { notify: false, normalize: false });
    return;
  }

  const dayField = target.dataset.builderDayField;
  if (dayField !== undefined) {
    const dayIndex = Number(target.dataset.dayIndex);
    if (!Number.isInteger(dayIndex) || dayIndex < 0) {
      return;
    }
    const builder = ensureProgramBuilder();
    const nextValue = String(readBuilderFieldValue(target) ?? "");
    const currentValue = String(builder.days?.[dayIndex]?.[dayField] ?? "");
    if (currentValue === nextValue) {
      return;
    }

    updateProgramBuilder((draftBuilder) => {
      if (!draftBuilder.days[dayIndex]) {
        draftBuilder.days[dayIndex] = createBuilderDay(dayIndex);
      }
      draftBuilder.days[dayIndex][dayField] = nextValue;
    }, { notify: false, normalize: false });
    return;
  }

  const itemField = target.dataset.builderItemField;
  if (itemField !== undefined) {
    const dayIndex = Number(target.dataset.dayIndex);
    const itemIndex = Number(target.dataset.itemIndex);
    if (!Number.isInteger(dayIndex) || !Number.isInteger(itemIndex) || dayIndex < 0 || itemIndex < 0) {
      return;
    }
    const builder = ensureProgramBuilder();
    const nextValue = String(readBuilderFieldValue(target) ?? "");
    const currentValue = String(builder.days?.[dayIndex]?.items?.[itemIndex]?.[itemField] ?? "");
    if (currentValue === nextValue) {
      return;
    }

    updateProgramBuilder((draftBuilder) => {
      if (!draftBuilder.days[dayIndex]) {
        draftBuilder.days[dayIndex] = createBuilderDay(dayIndex);
      }
      if (!draftBuilder.days[dayIndex].items[itemIndex]) {
        draftBuilder.days[dayIndex].items[itemIndex] = createBuilderItem();
      }
      draftBuilder.days[dayIndex].items[itemIndex][itemField] = nextValue;
    }, { notify: false, normalize: false });
  }
}

function readBuilderFieldValue(target) {
  if (target instanceof HTMLInputElement) {
    if (target.type === "checkbox") {
      return target.checked;
    }
    return target.value;
  }
  if (target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement) {
    return target.value;
  }
  return "";
}

function syncProgramBuilderFromDom(options = {}) {
  const currentBuilder = getState().programBuilder;
  if (!currentBuilder) {
    return false;
  }

  const nextBuilder = deepClone(currentBuilder);
  let touched = false;

  document.querySelectorAll("[data-builder-root]").forEach((node) => {
    if (!(node instanceof HTMLElement)) {
      return;
    }
    const rootField = String(node.dataset.builderRoot || "").trim();
    if (!rootField) {
      return;
    }
    const value = readBuilderFieldValue(node);
    nextBuilder[rootField] = rootField === "isActive" ? !!value : String(value ?? "");
    touched = true;
  });

  document.querySelectorAll("[data-builder-day-field][data-day-index]").forEach((node) => {
    if (!(node instanceof HTMLElement)) {
      return;
    }
    const dayField = String(node.dataset.builderDayField || "").trim();
    const dayIndex = Number(node.dataset.dayIndex);
    if (!dayField || !Number.isInteger(dayIndex) || dayIndex < 0) {
      return;
    }
    while ((nextBuilder.days?.length || 0) <= dayIndex) {
      nextBuilder.days.push(createBuilderDay(nextBuilder.days.length));
    }
    if (!nextBuilder.days[dayIndex]) {
      nextBuilder.days[dayIndex] = createBuilderDay(dayIndex);
    }
    nextBuilder.days[dayIndex][dayField] = String(readBuilderFieldValue(node) ?? "");
    touched = true;
  });

  document.querySelectorAll("[data-builder-item-field][data-day-index][data-item-index]").forEach((node) => {
    if (!(node instanceof HTMLElement)) {
      return;
    }
    const itemField = String(node.dataset.builderItemField || "").trim();
    const dayIndex = Number(node.dataset.dayIndex);
    const itemIndex = Number(node.dataset.itemIndex);
    if (!itemField || !Number.isInteger(dayIndex) || !Number.isInteger(itemIndex) || dayIndex < 0 || itemIndex < 0) {
      return;
    }
    while ((nextBuilder.days?.length || 0) <= dayIndex) {
      nextBuilder.days.push(createBuilderDay(nextBuilder.days.length));
    }
    if (!nextBuilder.days[dayIndex]) {
      nextBuilder.days[dayIndex] = createBuilderDay(dayIndex);
    }
    while ((nextBuilder.days[dayIndex].items?.length || 0) <= itemIndex) {
      nextBuilder.days[dayIndex].items.push(createBuilderItem());
    }
    if (!nextBuilder.days[dayIndex].items[itemIndex]) {
      nextBuilder.days[dayIndex].items[itemIndex] = createBuilderItem();
    }
    nextBuilder.days[dayIndex].items[itemIndex][itemField] = String(readBuilderFieldValue(node) ?? "");
    touched = true;
  });

  if (!touched && options.forceNotify !== true) {
    return false;
  }

  syncBuilderDayLength(nextBuilder);
  const nextDayIndex = clampProgramBuilderDayIndex(getState().programBuilderDayIndex ?? 0, nextBuilder);
  if (
    options.forceNotify === true &&
    areProgramBuilderValuesEqual(currentBuilder, nextBuilder) &&
    Number(getState().programBuilderDayIndex ?? 0) === nextDayIndex
  ) {
    setState({
      programBuilder: nextBuilder,
      programBuilderDayIndex: nextDayIndex,
    });
    return true;
  }
  return commitProgramBuilderState(nextBuilder, nextDayIndex);
}

const handleMappedClickAction = createMappedClickActionHandler({
  getState,
  captureCurrentAppTask,
  confirmProgramBuilderExit,
  handleCancelProgramBuilder,
  resetAppNavigationToHome,
  openTopLevelTabFromRoot,
  consumeCurrentAppHistoryEntry,
  setState,
  openLogsTab,
  replaceCurrentAppHistorySnapshot,
  pushCurrentAppHistorySnapshot,
  setAppHistoryAutoReplaceSuspended,
  handleSelectExercise,
  isAnalyticsSearchModalOpen,
  closeAnalyticsSearchModal,
  handleSelectLogsExercise,
  handleSelectProgramItemExercise,
  handleSelectSessionAddExercise,
});

const programClickControllerDeps = {
  els,
  getState,
  setState,
  pushCurrentAppHistorySnapshot,
  setAppHistoryAutoReplaceSuspended,
  handleStartProgramWorkout,
  openProgramDayPickerModal,
  closeProgramDayPickerModal,
  closeProgramDayImportModal,
  handleImportProgramDayFromSession,
  handleOpenProgramDayStage,
  handleOpenProgramInfoStage,
  handleOpenProgramTemplateSearch,
  handleCreateTemplateProgram,
  handleCreateProgramFromBuilder,
  handleOpenCreateProgramBuilder,
  confirmProgramBuilderExit,
  exitProgramBuilderToParent,
  openProgramBuilderDayPickerModal,
  ensureProgramBuilder,
  createDefaultProgramBuilder,
  updateProgramBuilder,
  createBuilderDay,
  createBuilderItem,
  focusProgramItemSearchInput,
  toggleProgramItemPicker,
  setProgramItemPickerState,
  handleSelectProgramItemExercise,
  getApi: () => api,
  commitProgramsState,
  mergeProgramIntoList,
  handleLoadProgramToBuilder,
  openProgramDeleteModal,
  openProgramDayImportModal,
  openProgramItemRuleModal,
  closeProgramItemRuleModal,
  handleSelectProgramItemRuleOption,
  handleSelectProgramItemRmBase,
  openProgramItemExerciseSearchModal,
};

const logsClickControllerDeps = {
  handleOpenLogWorkoutShare,
  handleOpenLogsFullSession,
  handleToggleWorkoutLog,
  handleSetLogsMode,
  handleShiftLogsCalendarMonth,
  handleSelectLogsDate,
  openLogsExerciseSearchModal,
};

const profileClickControllerDeps = {
  openCustomMusclePickerModal,
  handleSelectCustomMuscle,
  handleRemoveCustomMuscle,
  closeCustomMusclePickerModal,
  clearCustomMuscleSelection,
  getCustomMusclePickerTarget,
  handleSaveOneRM,
  handleSaveRestSupportSettings,
  handleChangePassword,
  handleCreateCustomExercise,
  unregisterAndroidDeviceTokenForCurrentUser,
  getAuth: () => auth,
  handleDeleteAccount,
};

const sessionClickControllerDeps = {
  els,
  getState,
  closeSessionItemContextMenus,
  toggleSessionItemContextMenu,
  openSessionCancelModal,
  openSession,
  handleStartManualWorkout,
  openProgramDayImportModal,
  clearSessionInlineNotice,
  closeSessionConflictModal,
  closeSessionNoticeModal,
  closeSessionCancelModal,
  closeSessionFinishModal,
  closeSession,
  handleCancelActiveSession,
  handleFinishWorkout,
  requestFinishWorkout,
  handleAddSessionItem,
  handleToggleSessionAddPanel,
  closeSessionAddPanel,
  handleAddSessionSet,
  handleAddDropSessionSet,
  handleToggleSessionItemReorder,
  handleCloseSessionItemReorder,
  setState,
  openSessionAddPanel,
  refreshSessionAddModeText,
  setSessionAddSelectedExercise,
  clearSessionAddSearchResults,
  handleMoveSessionItem,
  handleRemoveSessionItem,
  handleToggleRestTimer,
  handleRestNavButtonPress,
  handleRemoveSessionSet,
  handleSetDone,
};

function hasOpenSessionContextMenus() {
  return !!document.querySelector(".session-item-context-menu:not(.hidden)");
}

async function attachGlobalEvents() {
  refreshAutoLoginToggleButton();
  refreshPushPermissionState();

  window.addEventListener("popstate", (event) => {
    const nextHistoryState = event.state;
    const nextAuthSnapshot = syncAuthFlowHistoryState(nextHistoryState);
    const visibleAuthPanel = getVisibleAuthFlowPanel();
    const shouldSkipAuthSnapshotApply = !!authFlowPendingRootContinuation && authFlowHistoryDepth === 0;
    if (!shouldSkipAuthSnapshotApply) {
      const targetAuthSnapshot = nextAuthSnapshot || (visibleAuthPanel ? {
        panel: AUTH_FLOW_PANELS.login,
      } : null);
      if (targetAuthSnapshot) {
        void applyAuthFlowSnapshot(targetAuthSnapshot).catch(() => undefined);
      }
    }
    if (!nextHistoryState?.[APP_HISTORY_STATE_KEY]) {
      const shouldConsumeWithoutApply = appHistoryIgnoreNextPop;
      const consumeContinuation = appHistoryConsumeContinuation;
      appHistoryInitialized = false;
      appHistoryDepth = 0;
      appHistoryIgnoreNextPop = false;
      appHistoryApplySnapshotOnConsume = false;
      appHistoryConsumeContinuation = null;
      appHistoryPendingRootContinuation = null;
      if (shouldConsumeWithoutApply && typeof consumeContinuation === "function") {
        consumeContinuation();
        return;
      }
      if (authFlowPendingRootContinuation && authFlowHistoryDepth === 0) {
        const continuation = authFlowPendingRootContinuation;
        authFlowPendingRootContinuation = null;
        continuation();
      }
      return;
    }
    appHistoryInitialized = true;
    appHistoryDepth = Math.max(0, Number(nextHistoryState.depth) || 0);
    if (appHistoryIgnoreNextPop) {
      appHistoryIgnoreNextPop = false;
      appHistoryApplySnapshotOnConsume = false;
      if (typeof appHistoryConsumeContinuation === "function") {
        const continuation = appHistoryConsumeContinuation;
        appHistoryConsumeContinuation = null;
        continuation();
        return;
      }
      appHistoryConsumeContinuation = null;
      replaceCurrentAppHistorySnapshot();
      return;
    }
    if (!appHistoryPendingRootContinuation) {
      let localBackAction = null;
      appHistoryAutoReplaceSuspended = true;
      try {
        localBackAction = handleLocalBackBeforeSnapshotApply(nextHistoryState.snapshot);
        if (localBackAction === "stay") {
          pushCurrentAppHistorySnapshot();
          return;
        }
        if (localBackAction === "confirm-program-builder-exit") {
          pushCurrentAppHistorySnapshot();
          return void confirmProgramBuilderExit(() => {
            exitProgramBuilderToParent();
          });
        }
      } finally {
        appHistoryAutoReplaceSuspended = false;
      }
    }
    void applyAppHistorySnapshot(nextHistoryState.snapshot)
      .then(() => {
        if (authFlowPendingRootContinuation && authFlowHistoryDepth === 0) {
          const continuation = authFlowPendingRootContinuation;
          authFlowPendingRootContinuation = null;
          continuation();
        }
        if (appHistoryPendingRootContinuation && appHistoryDepth === 0) {
          const continuation = appHistoryPendingRootContinuation;
          appHistoryPendingRootContinuation = null;
          continuation();
        }
      })
      .catch(() => {
        authFlowPendingRootContinuation = null;
        appHistoryPendingRootContinuation = null;
      });
  });

  document.body.addEventListener("pointerdown", primeRestAlarmAudio, {
    once: true,
    passive: true,
  });
  document.body.addEventListener("click", primeRestAlarmAudio, {
    once: true,
    capture: true,
  });
  window.addEventListener("online", () => {
    void syncRestPushAvailability({ silent: true });
  });

  els.autoLoginToggleBtn?.addEventListener("click", async () => {
    const previous = autoLoginEnabled;
    autoLoginEnabled = !autoLoginEnabled;
    persistAutoLoginPreference(autoLoginEnabled);
    refreshAutoLoginToggleButton();

    try {
      await withGlobalLoading(async () => {
        await applyAutoLoginPersistence();
      });
      showLoginError("");
    } catch (error) {
      autoLoginEnabled = previous;
      persistAutoLoginPreference(autoLoginEnabled);
      refreshAutoLoginToggleButton();
      showLoginError(error instanceof Error ? error.message : "자동 로그인 설정 변경 실패");
    }
  });

  els.googleLoginBtn.addEventListener("click", () => {
    showLoginError("");
    applyGlobalLoadingDelta(1);
    // Keep Google sign-in call on the direct click path for mobile browsers.
    signInGoogleWithFallback()
      .catch((error) => {
        showLoginError(resolveAuthErrorMessage(error, "Google 로그인 실패"));
      })
      .finally(() => {
        applyGlobalLoadingDelta(-1);
      });
  });

  els.emailLoginBtn.addEventListener("click", () => {
    void handleEmailLogin();
  });

  const handleLoginFieldKeydown = (event) => {
    if (!(event instanceof KeyboardEvent) || event.key !== "Enter") {
      return;
    }
    event.preventDefault();
    void handleEmailLogin();
  };

  els.emailInput?.addEventListener("keydown", handleLoginFieldKeydown);
  els.passwordInput?.addEventListener("keydown", handleLoginFieldKeydown);
  els.emailInput?.addEventListener("input", () => showLoginError(""));
  els.passwordInput?.addEventListener("input", () => showLoginError(""));

  els.emailSignupBtn.addEventListener("click", () => {
    showSignupPanel();
  });

  els.signupBackBtn?.addEventListener("click", () => {
    if (consumeCurrentAuthFlowEntry()) {
      return;
    }
    showLoginPanel();
  });

  els.emailSignupSubmitBtn?.addEventListener("click", async () => {
    showSignupError("");
    const { email, password, nickname, agreeTerms, agreePrivacy } = getSignupPayload();
    if (!email) {
      showSignupError("이메일을 입력하세요");
      return;
    }
    if (nickname.length < 2) {
      showSignupError("회원 별명은 2자 이상 입력하세요");
      return;
    }
    if (password.length < 6) {
      showSignupError("비밀번호는 6자 이상이어야 합니다");
      return;
    }
    if (!validateSignupPasswordConfirmation({ requireFilled: true, focusOnError: true })) {
      showSignupError("");
      return;
    }
    if (!agreeTerms) {
      showSignupError("서비스 이용약관 동의가 필요합니다");
      return;
    }
    if (!agreePrivacy) {
      showSignupError("개인정보 수집·이용 동의가 필요합니다");
      return;
    }

    try {
      await withGlobalLoading(async () => {
        await applyAutoLoginPersistence();
        pendingSignupNickname = nickname;
        pendingSignupConsents = buildRequiredLegalConsentPayload();
        const credential = await auth.createUserWithEmailAndPassword(email, password);
        if (credential?.user?.updateProfile) {
          await credential.user.updateProfile({
            displayName: nickname,
          });
        }
      });
      clearSignupForm();
      showSignupError("");
    } catch (error) {
      pendingSignupNickname = "";
      pendingSignupConsents = null;
      showSignupError(resolveSignupErrorMessage(error));
    }
  });

  els.signupEmailInput?.addEventListener("input", () => showSignupError(""));
  els.signupNicknameInput?.addEventListener("input", () => showSignupError(""));
  els.signupPasswordInput?.addEventListener("input", () => showSignupError(""));
  els.signupPasswordInput?.addEventListener("input", () => validateSignupPasswordConfirmation());
  els.signupPasswordConfirmInput?.addEventListener("input", () => {
    showSignupError("");
    validateSignupPasswordConfirmation();
  });
  els.signupAgreeTermsInput?.addEventListener("input", () => showSignupError(""));
  els.signupAgreePrivacyInput?.addEventListener("input", () => showSignupError(""));
  els.consentAgreeTermsInput?.addEventListener("input", () => showConsentError(""));
  els.consentAgreePrivacyInput?.addEventListener("input", () => showConsentError(""));

  els.consentSubmitBtn?.addEventListener("click", async () => {
    showConsentError("");
    try {
      const { agreeTerms, agreePrivacy } = getConsentGateSelection();
      if (!agreeTerms) {
        throw new Error("서비스 이용약관 동의가 필요합니다");
      }
      if (!agreePrivacy) {
        throw new Error("개인정보 수집·이용 동의가 필요합니다");
      }

      const result = await withGlobalLoading(async () =>
        api.updateProfile({
          consents: buildRequiredLegalConsentPayload(),
        }));

      setPendingSignupConsentsValue(null);
      settleConsentGate("resolve", result);
    } catch (error) {
      showConsentError(error instanceof Error ? error.message : "필수 동의 저장에 실패했습니다");
    }
  });

  els.consentLogoutBtn?.addEventListener("click", async () => {
    if (consumeCurrentAuthFlowEntry()) {
      return;
    }
    await exitConsentGateToLogin({
      signOut: true,
      syncHistory: false,
    });
  });

  window.addEventListener("resize", () => {
    if (hasOpenModal()) {
      scheduleModalViewportCompensation();
    }
  });
  window.visualViewport?.addEventListener("resize", () => {
    if (hasOpenModal()) {
      scheduleModalViewportCompensation();
    }
  });
  window.visualViewport?.addEventListener("scroll", () => {
    if (hasOpenModal()) {
      scheduleModalViewportCompensation();
    }
  });

  document.body.addEventListener("focusin", (event) => {
    const target = event.target;
    if (target instanceof HTMLElement) {
      syncSessionCurrentPointerFromFocus(target);
      if (isModalTextEntryTarget(target)) {
        scheduleModalViewportCompensation(target);
      }
    }
  });

  document.body.addEventListener("focusout", (event) => {
    const target = event.target;
    if (!isModalTextEntryTarget(target)) {
      return;
    }

    window.setTimeout(() => {
      const activeElement = document.activeElement;
      if (isModalTextEntryTarget(activeElement)) {
        scheduleModalViewportCompensation(activeElement);
        return;
      }
      modalViewportFocusedTarget = null;
      if (hasOpenModal()) {
        scheduleModalViewportCompensation();
      }
    }, 0);
  });

  document.body.addEventListener("input", (event) => {
    const target = event.target;
    if (isModalTextEntryTarget(target)) {
      scheduleModalViewportCompensation(target);
    }
  });

  document.body.addEventListener("input", (event) => {
    const target = event.target;
    if (target instanceof HTMLElement && target.classList.contains("program-item-exercise-search-input")) {
      handleProgramItemExerciseInput(Number(target.dataset.dayIndex), Number(target.dataset.itemIndex));
      return;
    }
    if (target instanceof HTMLElement && target.classList.contains("session-add-exercise-search-input")) {
      handleSessionAddExerciseInput();
      return;
    }
    if (target instanceof HTMLInputElement && (target.id === "sessionAddSets" || target.id === "sessionAddRestSec")) {
      syncConstraintFieldValidationUi(target, {
        requireFilled: false,
        applyUi: true,
      });
      return;
    }
    if (target instanceof HTMLInputElement && target.id === "analyticsModalSearchInput") {
      handleExerciseSearchModalInput("analyticsModalSearchInput");
      return;
    }
    if (target instanceof HTMLInputElement && target.id === "programTemplateSearchModalInput") {
      searchOverlayController.updateProgramTemplateQuery(String(target.value || "").trim());
      return;
    }
    if (target instanceof HTMLInputElement && target.id === "customMusclePickerSearchInput") {
      handleCustomMusclePickerInput();
      return;
    }
    if (target instanceof HTMLInputElement && target.id === "profileCustomExerciseSearchInput") {
      syncProfileCustomExerciseFilter();
      return;
    }
    if (isSessionSetFieldInput(target)) {
      handleSessionSetFieldInput(target);
      return;
    }
    // Program builder text/number typing should not trigger full rerender on every keystroke.
    // Persist builder fields on "change" (blur/commit) to avoid refresh-like flicker while typing.
    if (
      target instanceof HTMLElement &&
      (target.dataset.builderRoot !== undefined ||
        target.dataset.builderDayField !== undefined ||
        target.dataset.builderItemField !== undefined)
    ) {
      syncConstraintFieldValidationUi(target, {
        requireFilled: false,
        applyUi: true,
      });
      syncProgramBuilderDraftFieldFromInput(target);
      if (
        target.dataset.builderRoot === "name" ||
        target.dataset.builderRoot === "lengthDays"
      ) {
        clearInlineTextMessage("programError");
        syncProgramBuilderInfoStepUi();
      }
      return;
    }
    handleBuilderFieldChange(target);
  });

  document.body.addEventListener("change", (event) => {
    const target = event.target;
    if (isSessionSetFieldInput(target)) {
      handleSessionSetFieldCommit(target);
      return;
    }
    if (target instanceof HTMLElement && target.classList.contains("program-item-exercise-search-input")) {
      const dayIndex = Number(target.dataset.dayIndex);
      const itemIndex = Number(target.dataset.itemIndex);
      const query = String(target.value || "").trim();
      if (!query) {
        clearProgramItemSearchResults(dayIndex, itemIndex);
      }
      return;
    }
    if (target instanceof HTMLElement && target.classList.contains("session-add-exercise-search-input")) {
      const selectedId = getSessionAddAutocompleteExerciseId(target);
      if (selectedId) {
        handleSelectSessionAddExercise(selectedId, String(target.value || "").trim());
      }
      return;
    }
    if (target instanceof HTMLInputElement && (target.id === "sessionAddSets" || target.id === "sessionAddRestSec")) {
      syncConstraintFieldValidationUi(target, {
        requireFilled: false,
        applyUi: true,
      });
      return;
    }
    handleBuilderFieldChange(target);
  });

  document.body.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && isAppAlertModalOpen()) {
      event.preventDefault();
      closeAppAlertModal();
      return;
    }
    if (event.key === "Escape" && isAppConfirmModalOpen()) {
      event.preventDefault();
      closeAppConfirmModal(false);
      return;
    }
    if (event.key === "Escape" && isPwaInstallGuideModalOpen()) {
      event.preventDefault();
      closePwaInstallGuideModal();
      return;
    }
    if (event.key === "Escape" && isAnalyticsSearchModalOpen()) {
      event.preventDefault();
      closeAnalyticsSearchModal();
      return;
    }
    if (event.key === "Escape" && isProgramTemplateSearchModalOpen()) {
      event.preventDefault();
      closeProgramTemplateSearchModal();
      return;
    }
    if (event.key === "Escape" && isCustomMusclePickerModalOpen()) {
      event.preventDefault();
      closeCustomMusclePickerModal();
      return;
    }
    if (event.key === "Escape" && isProgramDayPickerOpen()) {
      event.preventDefault();
      closeProgramDayPickerModal(null);
      return;
    }
    if (event.key === "Escape" && isProgramDayImportOpen()) {
      event.preventDefault();
      closeProgramDayImportModal();
      return;
    }
    if (event.key === "Escape" && isProgramDeleteModalOpen()) {
      event.preventDefault();
      closeProgramDeleteModal();
      return;
    }
    if (event.key === "Escape" && isSessionConflictModalOpen()) {
      event.preventDefault();
      closeSessionConflictModal();
      return;
    }
    if (event.key === "Escape" && isSessionNoticeModalOpen()) {
      event.preventDefault();
      closeSessionNoticeModal();
      return;
    }
    if (event.key === "Escape" && isSessionCancelModalOpen()) {
      event.preventDefault();
      closeSessionCancelModal();
      return;
    }
    if (event.key === "Escape" && isSessionFinishModalOpen()) {
      event.preventDefault();
      closeSessionFinishModal();
      return;
    }
    if (event.key === "Escape" && hasOpenSessionContextMenus()) {
      event.preventDefault();
      closeSessionItemContextMenus();
      return;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (event.key !== "Enter") return;
    if (target.classList.contains("program-item-exercise-search-input")) {
      event.preventDefault();
      handleSearchProgramItemExercise(Number(target.dataset.dayIndex), Number(target.dataset.itemIndex)).catch((error) => {
        void openAppAlertModal(error instanceof Error ? error.message : "운동 검색 중 오류가 발생했습니다", {
          title: "운동 검색 오류",
        });
      });
      return;
    }
    if (target.classList.contains("session-add-exercise-search-input")) {
      event.preventDefault();
      handleSearchSessionAddExercise().catch((error) => {
        void openAppAlertModal(error instanceof Error ? error.message : "운동 검색 중 오류가 발생했습니다", {
          title: "운동 검색 오류",
        });
      });
      return;
    }
    if (target instanceof HTMLInputElement && target.id === "analyticsModalSearchInput") {
      event.preventDefault();
      handleSearchExercise({
        query: String(target.value || "").trim(),
        inputId: "analyticsModalSearchInput",
        context: getExerciseSearchModalContext(),
      }).catch((error) => {
        void openAppAlertModal(error instanceof Error ? error.message : "운동 검색 중 오류가 발생했습니다", {
          title: "운동 검색 오류",
        });
      });
      return;
    }
    if (target instanceof HTMLInputElement && target.id === "programTemplateSearchModalInput") {
      event.preventDefault();
      searchOverlayController.updateProgramTemplateQuery(String(target.value || "").trim());
      return;
    }
    if (target instanceof HTMLInputElement && target.id === "customMusclePickerSearchInput") {
      event.preventDefault();
      const firstResultButton = els.customMusclePickerResults?.querySelector(".custom-muscle-result-btn:not([disabled])");
      if (firstResultButton instanceof HTMLButtonElement) {
        firstResultButton.click();
      }
    }
  });
  // Chart tooltip touch/click delegation
  document.body.addEventListener("click", (event) => {
    const hitArea = event.target instanceof Element ? event.target.closest(".analytics-chart-hit-area") : null;
    const tooltip = document.getElementById("analyticsChartTooltip");
    if (!tooltip) return;

    if (hitArea) {
      const date = hitArea.getAttribute("data-chart-tooltip-date") || "";
      const value = hitArea.getAttribute("data-chart-tooltip-value") || "";
      const cx = parseFloat(hitArea.getAttribute("data-chart-tooltip-cx") || "0");
      const cy = parseFloat(hitArea.getAttribute("data-chart-tooltip-cy") || "0");
      const svg = hitArea.closest("svg");
      const scrollWrap = hitArea.closest(".analytics-chart-scroll");
      if (svg && scrollWrap) {
        const svgRect = svg.getBoundingClientRect();
        const svgViewBox = svg.viewBox?.baseVal;
        const scaleX = svgRect.width / (svgViewBox?.width || 760);
        const scaleY = svgRect.height / (svgViewBox?.height || 340);
        const scrollRect = scrollWrap.getBoundingClientRect();
        const px = svgRect.left - scrollRect.left + cx * scaleX;
        const py = svgRect.top - scrollRect.top + cy * scaleY;
        tooltip.textContent = `${date}  ${value}`;
        tooltip.style.left = `${px}px`;
        tooltip.style.top = `${py}px`;
        tooltip.classList.add("visible");
      }
    } else {
      tooltip.classList.remove("visible");
    }
  }, true);

  document.body.addEventListener("click", async (event) => {
    const rawTarget = event.target;
    if (!(rawTarget instanceof Element)) return;
    const target = rawTarget.closest("button, a, [data-action], [data-tab], input, label, .session-item-more-btn") instanceof HTMLElement
      ? rawTarget.closest("button, a, [data-action], [data-tab], input, label, .session-item-more-btn")
      : rawTarget;
    if (!(target instanceof HTMLElement)) return;

    const clickedSessionMenuRegion = target.closest(".session-item-context-menu, .session-item-more-btn");
    if (!(clickedSessionMenuRegion instanceof HTMLElement)) {
      closeSessionItemContextMenus();
    }

    try {
      if (await handleMappedClickAction(target)) {
        return;
      }

      if (target.id === "authBadge" && getState().user) {
        openTopLevelTabFromRoot("profile");
        return;
      }

      if (target.id === "pwaInstallDismissBtn") {
        snoozePwaInstallPrompt();
        hidePwaInstallPrompt();
      }

      if (target.id === "pwaInstallActionBtn") {
        const actionMode = String(target.dataset.actionMode || "prompt");
        await handlePwaInstallAction(actionMode);
        refreshPushPermissionState();
        void syncRestPushAvailability({ silent: true });
      }

      if (target.id === "openRestInstallGuideBtn") {
        await handlePwaInstallAction(String(target.dataset.guideMode || "browser-guide"));
        refreshPushPermissionState();
        void syncRestPushAvailability({ silent: true });
      }

      if (target.id === "setupRestNotificationsBtn") {
        await handlePrepareRestNotifications();
      }

      if (await handleLogsClick(target, logsClickControllerDeps)) {
        return;
      }

      if (await handleProgramsClick(target, programClickControllerDeps)) {
        return;
      }

      if (target.classList.contains("select-exercise-btn")) {
        const exerciseSearchContext = getExerciseSearchModalContext();
        if (exerciseSearchContext === "programs.select-item-exercise" || exerciseSearchContext === "program-item") {
          // program-item context: 프로그램 아이템 운동 선택
          const modal = els.analyticsSearchModal;
          const dayIndex = Number(modal?.dataset.dayIndex);
          const itemIndex = Number(modal?.dataset.itemIndex);
          handleSelectProgramItemExercise(dayIndex, itemIndex, target.dataset.exerciseId, target.dataset.exerciseName);
          if (isAnalyticsSearchModalOpen()) {
            closeAnalyticsSearchModal();
          }
        } else {
          await handleSelectExercise(target.dataset.exerciseId, target.dataset.exerciseName);
          if (isAnalyticsSearchModalOpen()) {
            closeAnalyticsSearchModal();
          }
        }
      }

      if (target.id === "openAnalyticsSearchModalBtn" || target.closest("#openAnalyticsSearchModalBtn")) {
        openAnalyticsSearchModal();
        return;
      }

      if (target.id === "openLogsExerciseSearchModalBtn" || target.closest("#openLogsExerciseSearchModalBtn")) {
        openLogsExerciseSearchModal();
        return;
      }

      if (
        target.id === "appAlertModalCloseBtn" ||
        target.id === "appAlertModalConfirmBtn" ||
        target.id === "appAlertModal"
      ) {
        closeAppAlertModal();
      }

      if (
        target.id === "appConfirmModalCloseBtn" ||
        target.id === "appConfirmModalCancelBtn" ||
        target.id === "appConfirmModal"
      ) {
        closeAppConfirmModal(false);
      }

      if (target.id === "appConfirmModalConfirmBtn") {
        closeAppConfirmModal(true, { restoreFocus: false });
      }

      if (
        target.id === "pwaInstallGuideCloseBtn" ||
        target.id === "pwaInstallGuideConfirmBtn" ||
        target.id === "pwaInstallGuideModal"
      ) {
        closePwaInstallGuideModal();
      }

      if (
        target.id === "analyticsSearchModalCloseBtn" ||
        target.id === "analyticsSearchModal"
      ) {
        closeAnalyticsSearchModal();
      }

      if (
        target.id === "programTemplateSearchModalCloseBtn" ||
        target.id === "programTemplateSearchModal"
      ) {
        closeProgramTemplateSearchModal();
      }

      if (
        target.id === "programDeleteModalCloseBtn" ||
        target.id === "programDeleteModalCancelBtn" ||
        target.id === "programDeleteModal"
      ) {
        closeProgramDeleteModal();
      }

      if (target.id === "confirmProgramDeleteBtn") {
        const modal = els.programDeleteModal;
        if (modal instanceof HTMLElement && modal.dataset.targetProgramId) {
          await handleDeleteProgram(modal.dataset.targetProgramId);
          closeProgramDeleteModal();
        }
      }

      if (await handleProfileClick(target, profileClickControllerDeps)) {
        return;
      }

      if (await handleSessionClick(target, sessionClickControllerDeps)) {
        return;
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "요청 처리 중 오류가 발생했습니다";
      if (isSessionInteractionTarget(target)) {
        openSessionNoticeModal(message);
        return;
      }
      void openAppAlertModal(message, {
        title: "요청을 완료하지 못했습니다",
      });
    }
  });
}

const initAuthObserver = createAuthObserver({
  getAuth: () => auth,
  getApi: () => api,
  getState,
  setState,
  resolveClientPlatform,
  getNotificationPermissionState,
  createDefaultLogsState,
  createDefaultProgramBuilder,
  resetObserverRuntimeState: resetAuthObserverRuntimeState,
  setAuthSplashMessage,
  unregisterRestPushSubscription,
  releaseScreenWakeLock,
  showLoggedOutUI,
  hideAuthSplash,
  refreshUserData,
  openSession,
  showAuthenticatedUI,
  refreshAuthBadge,
  maybeAutoPrepareRestNotificationsOnLaunch,
  readLaunchRestNotificationPayload,
  handleRestNotificationClickMessage,
  openAppAlertModal,
  getPendingSignupNickname,
  setPendingSignupNickname: setPendingSignupNicknameValue,
  getPendingSignupConsents,
  setPendingSignupConsents: setPendingSignupConsentsValue,
  hasRequiredLegalConsents,
  openConsentGate,
  isConsentGateCancelledError,
});

async function registerServiceWorker() {
  if ("serviceWorker" in navigator) {
    try {
      serviceWorkerRegistration = await navigator.serviceWorker.register("/sw.js");
      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data?.type === "rest-notification-click") {
          void handleRestNotificationClickMessage(event.data?.data || {});
        }
      });
      serviceWorkerRegistration = await navigator.serviceWorker.ready.catch(() => serviceWorkerRegistration);
      void syncRestPushAvailability({ silent: true });
    } catch (_) {
      // Ignore registration failures in unsupported environments.
    }
  }
}

async function bootstrap() {
  initAndroidTwaBridge();
  const client = await getFirebaseClient();
  auth = client.auth;
  loadDropSetAppliedKeys();
  autoLoginEnabled = loadAutoLoginPreference();
  refreshAutoLoginToggleButton();
  setAuthSplashMessage(autoLoginEnabled ? "자동 로그인 상태를 확인하고 있어요" : "로그인 화면을 준비하고 있어요");
  setState({
    clientPlatform: resolveClientPlatform(),
  });

  try {
    await applyAutoLoginPersistence();
  } catch (_) {
    // Ignore persistence setup failure and continue app bootstrap.
  }

  api = new ApiClient(async () => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("로그인이 필요합니다");
    }
    return user.getIdToken();
  });

  bindGlobalLoadingEvents();
  bindSessionDraftLifecycleEvents();
  bindPwaInstallPromptEvents();
  await attachGlobalEvents();

  subscribe((nextState, meta) => {
    renderTabs(nextState, meta);
    void syncScreenWakeLock();
    void syncNativeRestAlarmState(nextState);
    replaceCurrentAppHistorySnapshot();
  });
  initAppHistoryState();

  await registerServiceWorker();
  // Consume redirect result BEFORE registering the auth observer so
  // that the observer's first fire already reflects the redirect user.
  await consumeGoogleRedirectResultIfAny().catch(() => undefined);
  await initAuthObserver();
  setState({ ready: true });
}

bootstrap().catch((error) => {
  hideAuthSplash({ immediate: true, message: "앱 시작에 실패했어요" });
  void openAppAlertModal(error instanceof Error ? error.message : "앱 초기화에 실패했습니다", {
    title: "앱 초기화 실패",
  });
});
